import { useState, useRef, useEffect, useCallback, useMemo, useReducer } from "react";
import { Elements, PaymentElement, CardElement, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { getProducts, getCategories, getTaxRate, createOnlineOrder, confirmOrderPayment, getMyOrders, cancelOrder, confirmPickup, searchProducts, sendOtp, verifyOtp, getMe, updateMe, deleteMe, saveAuth, clearAuth, loadStoredUser, chatSend, chatList, mapsAutocomplete, mapsPlaceDetails, mapsReverseGeocode, getOrderTracking, getAddresses, createAddress, updateAddress, deleteAddress, setDefaultAddress, getCloudCart, putCloudCart, getCloudWishlist, putCloudWishlist, getAppVersion, compareVersions, APP_VERSION, getMyCoupons, redeemCoupon, previewCoupon, getBanners, createStripePaymentIntent, getSavedPaymentMethods, setDefaultPaymentMethod, deletePaymentMethod, chargeSavedCard, createSetupIntent, getPointProducts, getPointBalance, getPointHistory, redeemPointProduct, getMyRefunds, createRefundRequest, cancelRefundRequest, getProfileStats, getDeliveryEstimate, getMyReferral, claimReferralCode, getRecommendations, signInWithApple, submitFeedback, getOrderById, imageSearch, getAppConfig, uploadImage, getPurchasedProducts, FREE_SHIP_THRESHOLD, SHIP_CA, SHIP_OTHER, DELIVERY_ZIPS, isDeliverableAddress, PICKUP_ADDRESS, PICKUP_HOURS, CONTACT_PHONE, CONTACT_PHONE_DISPLAY, CONTACT_HOURS, onConfigReady } from "./api";

// Stripe.js — 完全后端控制,前端不再硬编码 publishable key
//
// 后端 Railway 环境变量 STRIPE_PUBLISHABLE_KEY 决定用哪个 key。
// 切换 Stripe 账号、test → live、应急轮换,改 Railway 即可,APP 不用重新上架。
//
// stripePromise 启动时拉一次 /config 拿 pk,resolve 到 Stripe 实例(或 null)。
// _stripePkValue 同步保存,给原生 Apple Pay 插件用(它需要 pk 字符串)。
let _stripePkValue = null;
let _stripePkResolved = false; // 网络请求已完成(成功或失败)
const STRIPE_PK_CACHE = "syj_stripe_pk";

// 失败重试:Railway 冷启动首次 /config 经常 5xx,重试 3 次(1s/2s 退避)
async function fetchStripeConfigWithRetry() {
  let lastErr;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const config = await getAppConfig();
      if (config?.stripe_pk) {
        _stripePkValue = config.stripe_pk;
        try { localStorage.setItem(STRIPE_PK_CACHE, JSON.stringify({ pk: config.stripe_pk, ts: Date.now() })); } catch {}
        return config.stripe_pk;
      }
      // /config 返回了但没 stripe_pk 字段 — 配置缺失
      throw new Error("后端未配置 STRIPE_PUBLISHABLE_KEY");
    } catch (e) {
      lastErr = e;
      console.warn(`[stripe] /config fetch failed (${attempt + 1}/3):`, e?.message);
      if (attempt < 2) await new Promise(r => setTimeout(r, (attempt + 1) * 1000));
    }
  }
  throw lastErr || new Error("Stripe 配置加载失败");
}

const stripePromise = (async () => {
  // 1. 先看 localStorage 缓存(7 天 TTL)— 二次启动 instant load,体验更好
  let cachedPk = null;
  try {
    const raw = localStorage.getItem(STRIPE_PK_CACHE);
    if (raw) {
      const { pk, ts } = JSON.parse(raw);
      if (pk && Date.now() - ts < 7 * 24 * 3600 * 1000) cachedPk = pk;
    }
  } catch {}
  // 2. 后台异步拉最新配置(无论缓存是否命中)— 拉到了更新缓存,下次启动用新值
  fetchStripeConfigWithRetry()
    .catch(()=>{}) // 缓存命中时 silently 失败 OK
    .finally(()=>{ _stripePkResolved = true; });
  // 3. 缓存命中 → 用缓存立刻 loadStripe(快);缓存没命中 → 等网络
  if (cachedPk) {
    _stripePkValue = cachedPk;
    return loadStripe(cachedPk);
  }
  // 没缓存 → 等网络(带重试)
  try {
    const pk = await fetchStripeConfigWithRetry();
    _stripePkResolved = true;
    return loadStripe(pk);
  } catch (e) {
    _stripePkResolved = true;
    return null;
  }
})();

// 同步取(Stripe.js Elements 用,允许返回 null)
function getStripePk() { return _stripePkValue; }

// 异步等待 pk 就绪 — Apple Pay handler 用,避免点早了直接报"配置未加载"。
// 优先级:有值立刻返回;否则等 stripePromise 解析(带 timeout 兜底)。
// timeout 后再查一次:有 pk 就用,真没有再算失败。
async function awaitStripePk(timeoutMs = 8000) {
  if (_stripePkValue) return _stripePkValue;
  // 已经 resolve 了但 pk 还没值 → /config 失败,直接重试一次
  if (_stripePkResolved) {
    try { return await fetchStripeConfigWithRetry(); } catch { return null; }
  }
  // 没 resolve → 等 stripePromise 或 timeout
  await Promise.race([
    stripePromise.catch(()=>null),
    new Promise(r => setTimeout(r, timeoutMs)),
  ]);
  if (_stripePkValue) return _stripePkValue;
  // timeout 后再 fallback retry 一次
  try { return await fetchStripeConfigWithRetry(); } catch { return null; }
}

// ─────────────────────────────────────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────────────────────────────────────
// 默认轮播兜底文案 — 后端 banners 表有数据时优先用后端的;
// 这里的内容是 APP 首次加载或 API 失败时显示,确保对 App Store 审核员看到的都是中性内容
const SLIDERS = [
  { id:1, label:"春季新品上市", sub:"NEW ARRIVALS",  accent:"#e8c4b0", bg:"#f7ede4", text:"#5a3020" },
  { id:2, label:"品牌热销榜单", sub:"BESTSELLERS",   accent:"#b0c4e8", bg:"#e4eef7", text:"#20305a" },
  { id:3, label:"美妆护肤精选", sub:"BEAUTY PICKS",  accent:"#b0e8c4", bg:"#e4f7ee", text:"#20453a" },
  { id:4, label:"日用百货专场", sub:"LIFESTYLE",     accent:"#e8e0b0", bg:"#f7f4e4", text:"#453a20" },
];
// 静态分类兜底 — 仅用于首次加载或后端 categories API 失败时,
// 实际用的是 backend 数据(包含 hidden_in_app 控制)。这里只放对 App Store 审核中性的分类。
const QUICK_CATS = [
  {id:"new",emoji:"✨",label:"新品上新"},{id:"hot",emoji:"🔥",label:"品牌热销"},
  {id:"dress",emoji:"👗",label:"连衣裙"},{id:"tshirt",emoji:"👕",label:"T恤"},
  {id:"yoga",emoji:"🧘",label:"瑜伽裤"},{id:"jeans",emoji:"👖",label:"牛仔裤"},
  {id:"shoes",emoji:"👠",label:"鞋子"},{id:"beauty",emoji:"💄",label:"化妆品"},
  {id:"daily",emoji:"🏠",label:"日用百货"},
];
const CATEGORIES = [
  {id:"new",label:"新品上新",emoji:"✨",sub:["本周上新","限时特惠","编辑推荐"]},
  {id:"hot",label:"品牌热销",emoji:"🔥",sub:["本周榜单","明星同款","口碑精选"]},
  {id:"clothing",label:"服饰鞋包",emoji:"👗",sub:["连衣裙","T恤","瑜伽裤","牛仔裤","鞋子"]},
  {id:"beauty",label:"化妆品",emoji:"💄",sub:["洗护用品","护肤品","彩妆"]},
  {id:"daily",label:"日用百货",emoji:"🏠",sub:["家居用品","厨房百货","清洁卫生"]},
];
// 二级分类 — 搜索框下方自动滚动 pills(2 行,反向滚动)
const SUB_CATS_ROW1 = [
  {id:"dress",emoji:"👗",label:"连衣裙",tint:"#fce4ec"},
  {id:"tshirt",emoji:"👕",label:"T恤",tint:"#fff3e0"},
  {id:"yoga",emoji:"🧘",label:"瑜伽裤",tint:"#e8f5e9"},
  {id:"jeans",emoji:"👖",label:"牛仔裤",tint:"#e3f2fd"},
  {id:"lingerie",emoji:"🎀",label:"内衣",tint:"#f8bbd0"},
  {id:"shoes",emoji:"👠",label:"鞋子",tint:"#f3e5f5"},
  {id:"makeup",emoji:"💄",label:"彩妆",tint:"#fce4ec"},
  {id:"skincare",emoji:"🌿",label:"护肤",tint:"#f1f8e9"},
];
const SUB_CATS_ROW2 = [
  {id:"wash",emoji:"🧴",label:"洗护",tint:"#e8f5e9"},
  {id:"home",emoji:"🏠",label:"家居",tint:"#fff3e0"},
  {id:"kitchen",emoji:"🍳",label:"厨房",tint:"#fff8e1"},
  {id:"clean",emoji:"🧹",label:"清洁",tint:"#e1f5fe"},
  {id:"bag",emoji:"👜",label:"箱包",tint:"#fff8e1"},
  {id:"jewelry",emoji:"💍",label:"饰品",tint:"#fce4ec"},
];
// (已删 BASE_PRODUCTS 和 RECO_POOL — 都没在 render 路径里用过,纯死代码)
const IMG_COLORS = {
  dress:["#f5e0d5","#e8c5b0"],tshirt:["#d5e8f5","#b0c8e8"],yoga:["#d5d5f5","#b0b0e8"],
  jeans:["#c8d5e8","#a5b5cc"],lingerie:["#f5d5e8","#e8b0cc"],shoes:["#ede5d8","#d8c8b5"],
  serum:["#d5f0e5","#b0dcc8"],lipstick:["#f5d5d5","#e8b0b0"],cleanser:["#d5eef5","#b0d5e5"],
  condom:["#f5f0d5","#e8e0b0"],ky:["#d5f0d5","#b0dcb0"],toy:["#f0d5f5","#dcb0e8"],
  slipper:["#f0ead5","#dcd0b0"],mystery:["#f5f0e0","#e8e0c0"],knit:["#e8d5f0","#ccb0e0"],
  spf:["#d5f0f5","#b0dce8"],vest:["#f0e8d5","#dcc8a8"],cream:["#fae8e0","#ecd0c0"],
  shower:["#e0f0f5","#c0dce8"],skirt:["#f5e5f0","#e8c8dc"],
};
const ZIPCODES = ["91776","91732","91745","91754","90032","91803"];
// STORE_ADDR 不再硬编码,改用 api.js 导出的 PICKUP_ADDRESS(后端 /config 控制)
const PAYMENT_METHODS = [
  {id:"credit", label:"信用卡 / 储蓄卡", icon:"💳", note:"Visa · Mastercard · Amex · Discover · Apple Pay · Google Pay"},
  {id:"cod",    label:"现金当面付款",    icon:"💵", note:"送达时付现金给配送员", deliveryOnly:true},
];

// 字体栈:iOS 原生先用 SF Pro(英文/数字),中文用 PingFang(系统自带,质感最好)
// 这样英文数字看起来像 Apple 原生,中文也是高质量字体
const FF    = "-apple-system,BlinkMacSystemFont,'PingFang SC','Noto Sans SC','Microsoft YaHei','Helvetica Neue',sans-serif";
// SERIF 暂用同一栈,后续如果想要标题字体差异化,可换 'New York','Songti SC' 等
const SERIF = "-apple-system,BlinkMacSystemFont,'PingFang SC','Noto Sans SC','Microsoft YaHei','Helvetica Neue',sans-serif";

// ─────────────────────────────────────────────────────────────────────────────
// 物流状态翻译 — EasyPost / UPS / USPS / FedEx 通用
// ─────────────────────────────────────────────────────────────────────────────
// EasyPost 高层状态(tracker.status)→ 中文
const TRACKING_STATUS_CN = {
  pre_transit:         "等待揽收",
  in_transit:          "运输中",
  out_for_delivery:    "派送中",
  available_for_pickup:"可前往自取",
  delivered:           "已送达",
  return_to_sender:    "退回发件人",
  failure:             "派送失败",
  cancelled:           "已取消",
  error:               "查询异常",
  unknown:             "未知状态",
};

// 承运商人工消息(tracking_details[].message)→ 中文
// 关键字按优先级匹配,放更具体的 key 在前面,通用的在后面
//
// 来源覆盖:UPS、USPS、FedEx、DHL、OnTrac、LaserShip 等常见承运商的
// EasyPost tracker.tracking_details[].message 字段实际值
const TRACKING_MESSAGE_RULES = [
  // ── 标签 / 揽收前 ────────────────────────────────────────
  // "Shipper created a label, UPS has not received the package yet" 这句的两段都覆盖到:
  //   - "shipper created (a )?label" / "created (a/the) label" — 卡号生成
  //   - "has(n'?t) (yet )?(been )?received" / "not yet received" — 承运商尚未揽收
  // 同时具体规则 in 前,泛 in 后(同 phrase 命中第一个就 return,所以"创建标签"优先)
  [/shipper created (?:a |the )?(?:shipping )?label|shipping label created|label has been created|label created|shipment information (?:sent|received)|electronic notification|order processed:?\s*ready/i, "已创建运单标签 · 等待揽收"],
  [/(?:has |have )?(?:n'?t |not )(?:yet )?(?:been )?received(?: the package)?(?:[\s\S]*?yet)?|awaiting carrier pickup|preparing for shipment|pre-?shipment/i, "承运商尚未揽收"],
  [/origin scan/i,                                                                            "始发地扫描 · 已揽收"],
  [/picked up|pickup completed|acceptance|received by/i,                                      "已揽收"],
  [/dropped off at|customer dropped off|tendered to/i,                                        "已交至承运商"],

  // ── 干线运输 ─────────────────────────────────────────────
  [/loaded on (?:the )?delivery vehicle|loaded for delivery|on (?:delivery )?vehicle for delivery/i, "装车等待派送"],
  [/processing at .*facility|in process at|currently being processed|package processed/i,     "在转运中心处理中"],
  [/processed (?:through|at) facility|sorting complete|sorted|sort complete/i,                "中转分拣完成"],
  [/departure scan|departed (?:from )?(?:facility|origin|us|ups|usps|fedex)/i,                "已离开转运中心"],
  [/arrival scan|arrived at (?:facility|destination|usps|ups|fedex|sort)/i,                   "已到达转运中心"],
  [/in transit to next facility|in transit/i,                                                 "运输中"],
  [/forwarded|in transit \(forwarded\)/i,                                                     "已转运"],
  [/on (?:the )?way|en route|transit complete/i,                                              "在途运输"],
  [/awaiting delivery scan|awaiting carrier/i,                                                "等待派送扫描"],

  // ── 派送 ────────────────────────────────────────────────
  [/out for delivery(?: today)?/i,                                                            "派送中"],
  [/delivery attempted|attempted delivery|no one home|customer not (?:home|available)|undeliverable as addressed/i, "派送失败 · 收件人不在"],
  [/notice left|delivery notice/i,                                                            "已留派送通知"],
  [/redelivery scheduled|reattempt scheduled|second (?:delivery )?attempt|next business day/i, "已安排重新派送"],
  [/held (?:at|for pickup)|available for pickup at|ready for pickup|hold at location/i,      "包裹已暂存,可前往自取"],

  // ── 完成 ────────────────────────────────────────────────
  // 注意:更具体的"投递到 mailbox/前门"放前面,通用 "delivered" 兜底
  [/delivered to (?:mailbox|front door|reception|neighbor|mail ?room|porch|garage)/i,         "已投递到指定位置"],
  [/signed by|signature on file/i,                                                            "已签收"],
  [/delivered to|left at|delivered|delivery complete/i,                                       "已送达"],

  // ── 异常 / 退回 ─────────────────────────────────────────
  [/return(?:ed|ing)? to sender|undeliverable.*return|return label/i,                         "退回发件人"],
  [/address (?:correction|undeliverable|invalid|insufficient|change requested|verification)/i, "地址异常,需更正"],
  [/refused by (?:recipient|customer)|customer refused/i,                                     "收件人拒收"],
  [/damaged|damage in transit|package damaged/i,                                              "包裹损坏"],
  [/lost in transit|package missing|missing/i,                                                "包裹丢失"],
  [/delivery exception|shipment exception|exception/i,                                        "派送异常"],
  [/weather delay|service delay|delay(?:ed)?/i,                                               "派送延迟"],

  // ── 海关 / 国际 ─────────────────────────────────────────
  [/(?:held by|in) customs|customs (?:clearance|hold|inspection)/i,                           "海关清关中"],
  [/customs released|released by customs|cleared customs/i,                                   "海关已放行"],
  [/import scan|export scan|international shipment release|international/i,                   "国际运输"],

  // ── 兜底 ────────────────────────────────────────────────
  [/(?:^|\s)scan(?:ned)?/i,                                                                   "扫描更新"],
  [/status updated|update/i,                                                                  "状态更新"],
];

function translateTrackingMessage(en) {
  if (!en) return "";
  const s = String(en).trim();
  if (!s) return "";
  // 已经是中文(包含 CJK 字符)→ 直接返回,不要重复翻译
  if (/[一-龥]/.test(s)) return s;
  for (const [re, cn] of TRACKING_MESSAGE_RULES) {
    if (re.test(s)) return cn;
  }
  // 没匹配 → 保留原文(给极端情况一个兜底,而非显示空白)
  return s;
}

function translateTrackingStatus(code) {
  if (!code) return "";
  return TRACKING_STATUS_CN[code] || code;
}

// ─────────────────────────────────────────────────────────────────────────────
// SHARED STYLES
// ─────────────────────────────────────────────────────────────────────────────
const primaryBtn = {width:"100%",background:"#1a1a1a",color:"#fff",border:"none",borderRadius:11,padding:"14px 0",fontSize:14,letterSpacing:"0.06em",cursor:"pointer",fontFamily:FF};
const ghostBtn   = {width:"100%",background:"none",color:"#1a1a1a",border:"1.5px solid #1a1a1a",borderRadius:11,padding:"13px 0",fontSize:14,cursor:"pointer",fontFamily:FF};
const inStyle    = {padding:"13px 15px",borderRadius:10,border:"1.5px solid #ececec",outline:"none",fontSize:14,color:"#1a1a1a",fontFamily:FF,background:"#fafaf9",width:"100%",boxSizing:"border-box"};

// ─────────────────────────────────────────────────────────────────────────────
// ATOMS
// ─────────────────────────────────────────────────────────────────────────────
// 图片优化:
// - 灰色背景做 LQIP 占位,加载时不空白
// - loading=lazy + decoding=async 让浏览器智能调度
// - 图集若是 Cloudflare R2 链接,可后续启用 cdn-cgi/image 自动 webp/resize(下面预留 hook)
// - 错误时回落到品类色块占位,不显示 broken image icon
function buildSrcSet(url, widths) {
  // 当前 R2 直链不支持 query 参数压缩。如果你后续启用 Cloudflare Images,
  // 把下面 url 加工成 https://your.domain/cdn-cgi/image/width=W,format=auto/原 url
  // 现在直接返回 url(浏览器忽略 srcset 等同于普通 img)
  if (!url) return undefined;
  return widths.map(w => `${url} ${w}w`).join(", ");
}

// 隐写水印工具 — 把用户标记编码到图片像素的最低位 bit(LSB Steganography)
// 视觉上 100% 看不到(每个 RGB 通道仅修改 1 bit = 0.4% 灰度变化)
// 但保存到本地的图片像素里有数据,用 decoder.js 可以解码出原始文字
//
// 编码格式:
//   Pixel 0~15 红通道 LSB = 16 bit 内容字节长度(big-endian)
//   Pixel 16+ 红通道 LSB = UTF-8 字节流的每一 bit
//
// 限制:
//   - 受 JPEG 压缩破坏(用户截图通常是 PNG / HEIC,LSB 仍保留)
//   - 跨域图片需要 Access-Control-Allow-Origin: * 才能 getImageData
//     (R2 默认允许;失败时 silent fallback,图片仍正常显示无水印)
function getWatermarkTag() {
  try {
    const raw = localStorage.getItem("syj_user");
    if (raw) {
      const u = JSON.parse(raw);
      const phone = (u?.phone || "").replace(/\D/g, "");
      const id = u?.id || "";
      if (phone.length >= 4) return `SYJ|${phone.slice(-4)}|${id.slice(0,8)}|${Date.now()}`;
    }
  } catch {}
  return `SYJ|guest|${Date.now()}`;
}

function embedLSB(data, text) {
  // UTF-8 编码 → 字节
  const bytes = new TextEncoder().encode(text);
  if (bytes.length > 65535) return false;
  // 16 bit length prefix + 字节流
  const bits = [];
  for (let i = 15; i >= 0; i--) bits.push((bytes.length >> i) & 1);
  for (const b of bytes) {
    for (let i = 7; i >= 0; i--) bits.push((b >> i) & 1);
  }
  // 嵌入红通道 LSB(每像素步长 4 = RGBA)
  const need = bits.length * 4;
  if (need > data.length) return false;
  for (let i = 0; i < bits.length; i++) {
    data[i * 4] = (data[i * 4] & 0xFE) | bits[i];
  }
  return true;
}

// 隐形水印图片组件 — 优先 canvas + LSB(隐写水印);CORS 失败时 fallback 到普通 <img>
// 这样即使 R2 没配 CORS,图片至少能正常显示,只是没追踪水印
function WatermarkedImage({ src, alt, style, onTap }) {
  const canvasRef = useRef(null);
  const [mode, setMode] = useState("loading"); // loading | canvas | fallback
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!src) { setMode("loading"); return; }
    setMode("loading"); setReady(false);

    const mark = getWatermarkTag();
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (!ctx) { setMode("fallback"); return; }
      const max = 1280;
      let w = img.naturalWidth, h = img.naturalHeight;
      if (w > max || h > max) {
        if (w > h) { h = Math.round(h * max / w); w = max; }
        else       { w = Math.round(w * max / h); h = max; }
      }
      canvas.width = w; canvas.height = h;
      try {
        ctx.drawImage(img, 0, 0, w, h);
        const imgData = ctx.getImageData(0, 0, w, h);
        embedLSB(imgData.data, mark);
        ctx.putImageData(imgData, 0, 0);
        setMode("canvas");
        setReady(true);
      } catch (e) {
        // getImageData 抛错 — canvas 被 tainted(CORS 没头)→ 退回 <img>
        console.warn("[wm] CORS not allowed, falling back to <img>:", e?.message);
        setMode("fallback");
      }
    };
    img.onerror = () => {
      // CORS 拦截或图片 404 — 都退到 <img>(浏览器自己渲染没那么严格)
      setMode("fallback");
    };
    img.src = src;
  }, [src]);

  // Fallback:R2 没配 CORS → 直接用普通 <img>(无水印但能显示)
  if (mode === "fallback") {
    return (
      <img
        src={src}
        alt={alt || ""}
        onClick={onTap}
        onContextMenu={(e)=>e.preventDefault()}
        draggable={false}
        style={style}
      />
    );
  }

  return (
    <canvas
      ref={canvasRef}
      onClick={onTap}
      onContextMenu={(e)=>e.preventDefault()}
      style={{ ...style, opacity: ready ? 1 : 0.6, transition: "opacity 0.15s" }}
      aria-label={alt || ""}
    />
  );
}

function ProductImage({img,imgUrl,name,aspect=1.3,sizes="(max-width:640px) 50vw, 200px"}){
  const [c1,c2]=IMG_COLORS[img]||["#f0f0f0","#e0e0e0"];
  const [errored,setErrored]=useState(false);
  if(imgUrl&&!errored){
    return(
      <div style={{width:"100%",paddingBottom:`${aspect*100}%`,position:"relative",overflow:"hidden",background:`linear-gradient(145deg,${c1},${c2})`}}>
        <img
          src={imgUrl}
          srcSet={buildSrcSet(imgUrl,[200,400,800])}
          sizes={sizes}
          alt={name||""}
          loading="lazy"
          decoding="async"
          style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",pointerEvents:"none"}}
          onContextMenu={(e)=>e.preventDefault()}
          draggable={false}
          onError={()=>setErrored(true)}
        />
      </div>
    );
  }
  const ch=name?.[0]||"·";
  return(
    <div style={{width:"100%",paddingBottom:`${aspect*100}%`,background:`linear-gradient(145deg,${c1},${c2})`,position:"relative",overflow:"hidden"}}>
      <span style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",fontSize:34,opacity:0.12,userSelect:"none",pointerEvents:"none"}}>{ch}</span>
    </div>
  );
}
function SectionLabel({children}){return <div style={{fontSize:11,letterSpacing:"0.12em",color:"#aaa",fontFamily:FF,marginBottom:10,textTransform:"uppercase"}}>{children}</div>;}
function SumRow({label,val,bold,accent}){
  return(
    <div style={{display:"flex",justifyContent:"space-between",marginBottom:7}}>
      <span style={{fontSize:13,color:bold?"#1a1a1a":"#888",fontFamily:FF,fontWeight:bold?600:400}}>{label}</span>
      <span style={{fontSize:13,fontFamily:FF,fontWeight:bold?700:400,color:accent?"#e03030":"#1a1a1a"}}>{val}</span>
    </div>
  );
}
function Radio({active}){
  return(
    <div style={{width:18,height:18,borderRadius:"50%",border:`2px solid ${active?"#1a1a1a":"#ddd"}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
      {active&&<div style={{width:9,height:9,borderRadius:"50%",background:"#1a1a1a"}}/>}
    </div>
  );
}
function BackHeader({title,onBack,right}){
  return(
    <div style={{background:"#fff",padding:"14px 16px 14px",borderBottom:"1px solid #f0f0f0",display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
      <button onClick={onBack} style={{background:"none",border:"none",cursor:"pointer",padding:0}}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
      </button>
      <span style={{fontFamily:SERIF,fontSize:18,flex:1}}>{title}</span>
      {right}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 库存判断 — 拿到当前 product / cart item 的剩余库存
// 返回 number 或 null(null 表示后端未跟踪库存,不限制)
// 把送达日(YYYY-MM-DD 或完整 ISO 时间戳都接受)转成「明天」/「周X 月日」中文标签
function formatDeliveryLabel(input) {
  if (!input) return "—";
  // 截前 10 位拿 YYYY-MM-DD,兼容 "2026-05-09" 和 "2026-05-09T07:00:00.000Z"
  const ymd = String(input).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return "—";
  const todayLA = new Date().toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" });
  const tmrDate = new Date(todayLA + "T00:00:00Z");
  tmrDate.setUTCDate(tmrDate.getUTCDate() + 1);
  const tmrLA = tmrDate.toISOString().slice(0, 10);
  if (ymd === todayLA) return "今天";
  if (ymd === tmrLA) return "明天";
  const target = new Date(ymd + "T00:00:00Z");
  const dow = ["周日","周一","周二","周三","周四","周五","周六"][target.getUTCDay()];
  const m = target.getUTCMonth() + 1;
  const d = target.getUTCDate();
  return `${m}月${d}日 (${dow})`;
}

function getStockOf(p) {
  if (!p) return null;
  if (p.variantId && Array.isArray(p.variants)) {
    const v = p.variants.find(x => x.id === p.variantId);
    if (typeof v?.stock === "number") return v.stock;
  }
  if (typeof p.totalStock === "number") return p.totalStock;
  return null;
}

// 共用的「加购物车 + 库存上限校验」工厂函数
// 用法: const {addToCart, increment, StockToast} = useStockGuardedCart(cart, setCart);
function useStockGuardedCart(cart, setCart) {
  const [toast, setToast] = useState("");
  useEffect(()=>{
    if(!toast)return;
    const t=setTimeout(()=>setToast(""),2400);
    return()=>clearTimeout(t);
  },[toast]);

  const addToCart = (p) => {
    // 单变体(如均码)商品 → 自动绑定该唯一变体的 id
    // 否则后端 inventory 用 product.id 查不到(库存其实存在 variant.id 下)
    let toAdd = p;
    if (!p.variantId && Array.isArray(p.variants) && p.variants.length === 1) {
      const v = p.variants[0];
      toAdd = {
        ...p,
        variantId: v.id,
        variantInfo: (v.color_name||v.size) ? { color: v.color_name, size: v.size } : null,
        price: v.price ? parseFloat(v.price) : p.price,
      };
    }
    const vId = toAdd.variantId || null;
    const stock = getStockOf(toAdd);
    const inCart = cart.find(i=>i.id===toAdd.id&&(i.variantId||null)===vId)?.qty || 0;
    if (stock != null && inCart + 1 > stock) {
      setToast(`「${toAdd.name}」剩余 ${stock} 件,已达上限`);
      return false;
    }
    setCart(c=>{
      const ex = c.find(i=>i.id===toAdd.id&&(i.variantId||null)===vId);
      if(ex) return c.map(i=>(i.id===toAdd.id&&(i.variantId||null)===vId)?{...i,qty:i.qty+1}:i);
      return[...c,{...toAdd,qty:1}];
    });
    return true;
  };

  const increment = (id, vId) => {
    const item = cart.find(i=>i.id===id&&(i.variantId||null)===(vId||null));
    if (!item) return false;
    const stock = getStockOf(item);
    if (stock != null && item.qty + 1 > stock) {
      setToast(`「${item.name}」剩余 ${stock} 件,已达上限`);
      return false;
    }
    setCart(c=>c.map(i=>(i.id===id&&(i.variantId||null)===(vId||null))?{...i,qty:i.qty+1}:i));
    return true;
  };

  const StockToast = toast ? (
    <div style={{
      position:"fixed", bottom: 100, left:"50%", transform:"translateX(-50%)",
      background:"rgba(20,20,20,0.92)", color:"#fff",
      padding:"10px 18px", borderRadius:22,
      fontSize:12.5, fontFamily:FF, zIndex:9999,
      maxWidth:280, textAlign:"center", lineHeight:1.5,
      boxShadow:"0 6px 20px rgba(0,0,0,0.25)",
      animation:"toastFade 2.4s ease-out forwards",
      pointerEvents:"none",
    }}>{toast}</div>
  ) : null;

  return { addToCart, increment, StockToast };
}

// ─────────────────────────────────────────────────────────────────────────────
// PRODUCT CARD
// ─────────────────────────────────────────────────────────────────────────────
function ProductCard({p,inWish,onWish,onAdd,onOpen}){
  const [added,setAdded]=useState(false);
  const hasVariants = (p.variants?.length||0) > 1;
  const doAdd=(e)=>{
    e.stopPropagation();
    // 多变体商品(多颜色/尺码)— 跳到详情页让用户选,避免误买错颜色
    if(hasVariants){
      onOpen?.(p);
      return;
    }
    // 全局飞入动画 — 与商品详情页一致
    try{
      const rect=e.currentTarget.getBoundingClientRect();
      window.__syjFlyToCart?.(rect);
    }catch{}
    onAdd();
    setAdded(true);
    setTimeout(()=>setAdded(false),900);
  };
  const discount=p.origPrice&&p.origPrice>p.price?Math.round((1-p.price/p.origPrice)*100):0;
  // 折扣优先,没有折扣才显示 NEW/HOT 标签
  const showDiscount=discount>0;
  const showTag=!showDiscount&&!!p.tag;
  const open=()=>onOpen?.(p);
  return(
    <div style={{background:"#fff",borderRadius:14,overflow:"hidden",boxShadow:"0 1px 6px rgba(0,0,0,0.05)",cursor:onOpen?"pointer":"default",position:"relative"}}>
      <div style={{position:"relative",background:"#fff"}} onClick={open}>
        <ProductImage img={p.img} imgUrl={p.imgUrl} name={p.name}/>
        {showDiscount&&<span style={{position:"absolute",top:8,left:8,background:"#e03030",color:"#fff",fontSize:11,fontWeight:600,padding:"3px 8px",borderRadius:5,fontFamily:FF,letterSpacing:"0.02em"}}>{discount}% off</span>}
        {showTag&&<span style={{position:"absolute",top:8,left:8,background:"#1a1a1a",color:"#fff",fontSize:10,letterSpacing:"0.1em",padding:"3px 8px",borderRadius:5,fontFamily:FF}}>{p.tag}</span>}
        <button onClick={e=>{e.stopPropagation();onWish();}} style={{position:"absolute",top:6,right:4,background:"none",border:"none",cursor:"pointer",padding:6}}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill={inWish?"#1f5fb8":"none"} stroke={inWish?"#1f5fb8":"#1f5fb8"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/>
          </svg>
        </button>
        <button
          onClick={doAdd}
          aria-label={hasVariants?"选规格":"加入购物车"}
          style={{position:"absolute",bottom:8,right:8,background:added?"#3a9e3a":"#fff",border:"none",borderRadius:"50%",width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",boxShadow:"0 2px 8px rgba(0,0,0,0.12)",transition:"background 0.2s"}}
        >
          {added
            ?<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            : hasVariants
              // 多变体 — 显示「···」三点暗示要进详情挑
              ?<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1f5fb8" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="12" r="1.6" fill="#1f5fb8"/><circle cx="12" cy="12" r="1.6" fill="#1f5fb8"/><circle cx="19" cy="12" r="1.6" fill="#1f5fb8"/></svg>
              :<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1f5fb8" strokeWidth="2.6" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>}
        </button>
      </div>
      <div style={{padding:"8px 10px 12px"}} onClick={open}>
        <div style={{marginBottom:4,display:"flex",alignItems:"baseline",gap:6}}>
          <span style={{fontSize:18,fontWeight:700,color:"#e03030",fontFamily:FF,letterSpacing:"-0.01em"}}>${p.price}</span>
          {p.origPrice&&<span style={{fontSize:12,color:"#bbb",textDecoration:"line-through",fontFamily:FF}}>${p.origPrice}</span>}
        </div>
        <div style={{fontSize:13,color:"#222",lineHeight:1.4,fontFamily:FF,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden",minHeight:36}}>{p.name}</div>
        {p.sold>10&&<div style={{fontSize:11,color:"#999",fontFamily:FF,marginTop:6}}>周销量 {p.sold>=1000?`${(p.sold/1000).toFixed(1)}k+`:`${p.sold}+`}</div>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MASONRY GRID — no manual offset, equal gaps both columns
// ─────────────────────────────────────────────────────────────────────────────
function MasonryGrid({products,wishlist,onWish,onAdd,onOpen,keyPrefix=""}){
  const col1=products.filter((_,i)=>i%2===0);
  const col2=products.filter((_,i)=>i%2!==0);
  return(
    <div style={{display:"flex",gap:10,padding:"0 12px",alignItems:"flex-start"}}>
      {[col1,col2].map((col,ci)=>(
        <div key={ci} style={{flex:1,display:"flex",flexDirection:"column",gap:10}}>
          {col.map((p,i)=>(
            <ProductCard key={`${keyPrefix}${p._key??p.id}-${ci}-${i}`} p={p}
              inWish={wishlist.includes(p.id)} onWish={()=>onWish(p.id)} onAdd={()=>onAdd(p)} onOpen={onOpen}/>
          ))}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// INFINITE CAROUSEL
// ─────────────────────────────────────────────────────────────────────────────
function InfiniteCarousel({items,renderItem,cardWidth,gap=12,peekLeft=24,autoMs=4000}){
  const N=items.length;
  const [vIdx,setVIdx]=useState(N);
  const [animate,setAnimate]=useState(false);
  const [dragDx,setDragDx]=useState(0);
  const drag=useRef(false),sx=useRef(0),timer=useRef(null);
  const all=[...items,...items,...items],sw=cardWidth+gap;
  // items 长度变化时(比如 SLIDERS fallback → 后端 banner 异步到达)重置位置
  // 否则 vIdx 还在旧 N 值上,translateX 把内容推到屏幕外
  useEffect(()=>{ setAnimate(false); setVIdx(N); },[N]);
  const onTEnd=()=>{setAnimate(false);setVIdx(v=>v<N?v+N:v>=2*N?v-N:v);};
  const startAuto=useCallback(()=>{clearInterval(timer.current);timer.current=setInterval(()=>{setAnimate(true);setVIdx(v=>v+1);},autoMs);},[autoMs]);
  useEffect(()=>{startAuto();return()=>clearInterval(timer.current);},[startAuto]);
  const pD=(e)=>{clearInterval(timer.current);drag.current=true;sx.current=e.clientX??e.touches?.[0]?.clientX??0;setDragDx(0);setAnimate(false);};
  const pM=(e)=>{if(!drag.current)return;setDragDx((e.clientX??e.touches?.[0]?.clientX??0)-sx.current);};
  // 松手:无论翻不翻页,都开启过渡动画 → 弹回 0 也是顺滑的(之前是抖一下回去)
  // 阈值降到 15%(原 25% 太死,短滑都不切换显得不响应)
  const pU=()=>{
    if(!drag.current)return;
    drag.current=false;
    setAnimate(true); // 关键:先开动画,再调整 dx → 弹回有过渡
    if(Math.abs(dragDx)>cardWidth*0.15){
      setVIdx(v=>v+(dragDx<0?1:-1));
    }
    setDragDx(0);
    startAuto();
  };
  return(
    <div style={{overflow:"hidden",touchAction:"pan-y"}} onPointerDown={pD} onPointerMove={pM} onPointerUp={pU} onPointerLeave={pU}>
      <div onTransitionEnd={onTEnd} style={{display:"flex",gap,transform:`translateX(${peekLeft-vIdx*sw+dragDx}px)`,transition:animate?"transform 0.44s cubic-bezier(.4,0,.2,1)":"none",willChange:"transform",userSelect:"none",cursor:drag.current?"grabbing":"grab"}}>
        {all.map((item,i)=><div key={i} style={{width:cardWidth,flexShrink:0}}>{renderItem(item,i%N)}</div>)}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// INFINITE CAT ROW
// ─────────────────────────────────────────────────────────────────────────────
function InfiniteCatRow({cats,activeCat,onSelect}){
  // W=86 让 iPhone(390-430px 宽)一屏看到约 4.5 个,提示用户可以右划
  const N=cats.length,W=86,G=4,SW=W+G;
  const [vIdx,setVIdx]=useState(N);
  const [animate,setAnimate]=useState(false);
  const [dragDx,setDragDx]=useState(0);
  const drag=useRef(false),sx=useRef(0),moved=useRef(false);
  const all=[...cats,...cats,...cats];
  const onTEnd=()=>{setAnimate(false);setVIdx(v=>v<N?v+N:v>=2*N?v-N:v);};
  const pD=(e)=>{drag.current=true;moved.current=false;sx.current=e.clientX??e.touches?.[0]?.clientX??0;setDragDx(0);setAnimate(false);};
  const pM=(e)=>{if(!drag.current)return;const dx=(e.clientX??e.touches?.[0]?.clientX??0)-sx.current;if(Math.abs(dx)>4)moved.current=true;setDragDx(dx);};
  const pU=()=>{if(!drag.current)return;drag.current=false;if(Math.abs(dragDx)>W*0.35){setAnimate(true);setVIdx(v=>v+(dragDx<0?Math.max(1,Math.round(Math.abs(dragDx)/SW)):-Math.max(1,Math.round(Math.abs(dragDx)/SW))));}setDragDx(0);};
  return(
    <div style={{overflow:"hidden",touchAction:"pan-y"}} onPointerDown={pD} onPointerMove={pM} onPointerUp={pU} onPointerLeave={pU}>
      <div onTransitionEnd={onTEnd} style={{display:"flex",gap:G,transform:`translateX(${10-vIdx*SW+dragDx}px)`,transition:animate?"transform 0.35s cubic-bezier(.4,0,.2,1)":"none",willChange:"transform",userSelect:"none"}}>
        {all.map((c,i)=>{const a=activeCat===c.id;return(
          <button key={i} onClick={()=>{if(!moved.current)onSelect(c.id);}} style={{width:W,flexShrink:0,display:"flex",flexDirection:"column",alignItems:"center",gap:6,background:"none",border:"none",cursor:"pointer",padding:"5px 0"}}>
            <div style={{width:64,height:64,borderRadius:"50%",background:a?"#1a1a1a":"#fff",border:`1.5px solid ${a?"#1a1a1a":"#e8e8e8"}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,transition:"all 0.18s",boxShadow:"0 2px 6px rgba(0,0,0,0.06)"}}>{c.emoji}</div>
            <span style={{fontSize:11.5,color:a?"#1a1a1a":"#666",fontFamily:FF,whiteSpace:"nowrap",fontWeight:a?600:500}}>{c.label}</span>
          </button>
        );})}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AutoScrollPills — 2 rows of small pill icons that loop horizontally,
// rows scroll in opposite directions. Used for L2 categories under the search.
// ─────────────────────────────────────────────────────────────────────────────
function AutoScrollPills({rows,onSelect}){
  return(
    <div style={{display:"flex",flexDirection:"column",gap:6,margin:"0 -14px"}}>
      {rows.map((row,ri)=>{
        const dup=[...row,...row,...row];
        const dur=row.length*3.2+ri*2; // slight speed difference between rows
        return(
          <div key={ri} style={{overflow:"hidden",WebkitMaskImage:"linear-gradient(90deg,transparent 0,#000 18px,#000 calc(100% - 18px),transparent 100%)",maskImage:"linear-gradient(90deg,transparent 0,#000 18px,#000 calc(100% - 18px),transparent 100%)"}}>
            <div style={{display:"inline-flex",gap:6,paddingLeft:14,paddingRight:14,whiteSpace:"nowrap",willChange:"transform",animation:`pillScroll${ri%2} ${dur}s linear infinite`}}>
              {dup.map((p,i)=>(
                <button key={i} onClick={()=>onSelect?.(p.id)} style={{display:"inline-flex",alignItems:"center",gap:5,background:"rgba(255,255,255,0.92)",border:"1px solid rgba(0,0,0,0.05)",borderRadius:16,padding:"3px 11px 3px 3px",cursor:"pointer",flexShrink:0,boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
                  <span style={{width:22,height:22,borderRadius:"50%",background:p.tint,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,flexShrink:0}}>{p.emoji}</span>
                  <span style={{fontSize:11.5,color:"#1a1a1a",fontFamily:FF,fontWeight:500}}>{p.label}</span>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HOME PAGE
// Header: fully opaque at top → fades transparent on scroll → logo collapses
// IMPORTANT: paddingTop on content = exact tall header height so slider is never hidden
// ─────────────────────────────────────────────────────────────────────────────
function HomePage({cart,setCart,wishlist,setWishlist,setTab,loggedIn,onOpenProduct,products,categories=[],loading,error,onRetry}){
  const [search,setSearch]=useState("");
  const [searchResults,setSearchResults]=useState(null); // null = 未搜索;数组 = 后端结果
  const [searchLoading,setSearchLoading]=useState(false);
  const [searchFocused,setSearchFocused]=useState(false);
  const [recentSearches,setRecentSearches]=useState(()=>{
    try{
      const raw=localStorage.getItem("syj_recent_searches");
      return raw?JSON.parse(raw):[];
    }catch{return [];}
  });
  // 把关键词加入最近搜索 — FIFO 8 条
  const pushRecentSearch=(kw)=>{
    const k=String(kw||"").trim();
    if(!k)return;
    setRecentSearches(prev=>{
      const next=[k,...prev.filter(x=>x.toLowerCase()!==k.toLowerCase())].slice(0,8);
      try{localStorage.setItem("syj_recent_searches",JSON.stringify(next));}catch{}
      return next;
    });
  };
  const clearRecentSearches=()=>{
    setRecentSearches([]);
    try{localStorage.removeItem("syj_recent_searches");}catch{}
  };
  // 热门搜索 — 从分类名 + 真实商品名取最常见词
  const hotSearches=(()=>{
    const fromCats=(categories||[]).slice(0,6).map(c=>c.name).filter(Boolean);
    return [...new Set([...fromCats,"连衣裙","T恤","美妆","内衣","新品","包邮"])].slice(0,8);
  })();
  // 输入联想 — 当前商品里 top 6 匹配
  const suggestions=search.trim()?(products||[])
    .filter(p=>p.name?.toLowerCase().includes(search.toLowerCase()))
    .slice(0,6):[];
  const searchTimer=useRef(null);
  const [activeCat,setActiveCat]=useState(null);
  // 后端 banner — 拉到非空就用,空则 fallback 到硬编码 SLIDERS
  const [banners,setBanners]=useState([]);
  useEffect(()=>{
    let alive=true;
    getBanners().then(d=>alive&&setBanners(d||[])).catch(()=>{});
    return()=>{alive=false;};
  },[]);

  // debounce 后端搜索:输入超过 1 字符,300ms 后调 /products?search=
  useEffect(()=>{
    clearTimeout(searchTimer.current);
    if(!search.trim()){
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    searchTimer.current=setTimeout(async()=>{
      try{
        const data=await searchProducts(search,50);
        setSearchResults(data);
      }catch{setSearchResults([]);}
      setSearchLoading(false);
    },300);
    return()=>clearTimeout(searchTimer.current);
  },[search]);

  // L1 一级:虚拟「新品 / 热销」+ 后端真实顶层分类
  const quickCats=[
    {id:"__new",emoji:"✨",label:"新品上新",virtual:"new"},
    {id:"__hot",emoji:"🔥",label:"品牌热销",virtual:"hot"},
    ...categories.map(c=>({id:c.id,emoji:catEmoji(c.name),label:c.name,children:c.children||[]})),
  ];
  // L2 二级:把所有顶层分类的 children 拍平,平均分到 2 行 pill
  const allSubs=categories.flatMap(c=>(c.children||[]).map(s=>({id:s.id,emoji:catEmoji(s.name),label:s.name})));
  const SUB_TINTS=["#fce4ec","#fff3e0","#e8f5e9","#e3f2fd","#f3e5f5","#e0f7fa","#fff8e1","#e1f5fe","#f1f8e9","#ffebee"];
  const subWithTint=allSubs.map((s,i)=>({...s,tint:SUB_TINTS[i%SUB_TINTS.length]}));
  const halfIdx=Math.ceil(subWithTint.length/2);
  const subRows=[subWithTint.slice(0,halfIdx),subWithTint.slice(halfIdx)];

  // 扁平 id → 分类节点 lookup(任意层)
  const flatCats=[];
  const walkCat=(c)=>{flatCats.push(c);(c.children||[]).forEach(walkCat);};
  categories.forEach(walkCat);
  const findCat=(id)=>flatCats.find(c=>c.id===id);
  const collectIds=(c)=>{const ids=[c.id];(c.children||[]).forEach(child=>ids.push(...collectIds(child)));return ids;};
  const [scrollY,setScrollY]=useState(0);
  const [showFab,setShowFab]=useState(false);
  const [visibleCount,setVisibleCount]=useState(16);
  // LOGO 图片加载失败 → fallback 到文字
  const [logoFailed,setLogoFailed]=useState(false);
  // 邮编 — 自动定位获取(7 天缓存),不再让用户手输
  const ZIP_CACHE_KEY = "syj_zip_cache";
  const ZIP_TTL_MS = 7 * 24 * 60 * 60 * 1000;
  const [zipcode,setZipcode]=useState(()=>{
    try{
      const raw=localStorage.getItem(ZIP_CACHE_KEY);
      if(raw){
        const {zip,ts}=JSON.parse(raw);
        if(Date.now()-ts < ZIP_TTL_MS) return zip;
      }
    }catch{}
    return ZIPCODES[0]; // 兜底默认
  });
  const [zipDetecting,setZipDetecting]=useState(false);
  const [zipError,setZipError]=useState(""); // 用户主动点定位时显示的错误
  const scrollRef=useRef(null),loadingRef=useRef(null);

  const detectZipcode=useCallback(async(force=false)=>{
    // 不强制 + 已有缓存(7 天内)就跳过
    if(!force){
      try{
        const raw=localStorage.getItem(ZIP_CACHE_KEY);
        if(raw){
          const {ts}=JSON.parse(raw);
          if(Date.now()-ts < ZIP_TTL_MS) return;
        }
      }catch{}
    }
    const geo=navigator.geolocation||window.__syjGetLocation;
    if(!geo){
      if(force)setZipError("当前设备不支持定位");
      return;
    }
    setZipDetecting(true);setZipError("");
    const onSuccess=async(pos)=>{
      try{
        const{latitude:lat,longitude:lng}=pos.coords;
        const d=await mapsReverseGeocode(lat,lng);
        const z=d?.zip||"";
        if(/^\d{5}$/.test(z)){
          setZipcode(z);
          try{localStorage.setItem(ZIP_CACHE_KEY,JSON.stringify({zip:z,ts:Date.now()}));}catch{}
        }else if(force){
          setZipError("已定位但暂无法识别邮编");
        }
      }catch(e){
        if(force)setZipError("地址解析失败,请稍后重试");
      }finally{setZipDetecting(false);}
    };
    const onError=(err)=>{
      setZipDetecting(false);
      if(force){
        // code 1 = 拒绝授权(已在 native.js 写明引导文案);其他 = GPS / 超时
        setZipError(err?.message || (err?.code===1 ? "定位权限未开启" : "无法获取位置,请稍后重试"));
        setTimeout(()=>setZipError(""),5500);
      }
    };
    if(navigator.geolocation?.getCurrentPosition){
      navigator.geolocation.getCurrentPosition(onSuccess,onError,{enableHighAccuracy:false,timeout:10_000,maximumAge:60_000});
    }else if(window.__syjGetLocation){
      window.__syjGetLocation(onSuccess,onError,{enableHighAccuracy:false,timeout:10_000,maximumAge:60_000});
    }
  },[]);

  // 进入主页第一次自动定位(若无缓存)
  useEffect(()=>{detectZipcode(false);},[detectZipcode]);

  const onScroll=useCallback(()=>{
    const el=scrollRef.current;if(!el)return;
    setScrollY(el.scrollTop);setShowFab(el.scrollTop>280);
  },[]);
  useEffect(()=>{const el=scrollRef.current;el?.addEventListener("scroll",onScroll,{passive:true});return()=>el?.removeEventListener("scroll",onScroll);},[onScroll]);
  useEffect(()=>{
    const s=loadingRef.current;if(!s)return;
    const obs=new IntersectionObserver(es=>{if(es[0].isIntersecting)setVisibleCount(c=>c+8);},{threshold:0.1});
    obs.observe(s);return()=>obs.disconnect();
  },[]);

  const {addToCart, increment: _inc, StockToast} = useStockGuardedCart(cart, setCart);
  const handleWish=(id)=>{if(!loggedIn){setTab("profile");return;}setWishlist(w=>w.includes(id)?w.filter(x=>x!==id):[...w,id]);};
  const scrollToTop=()=>scrollRef.current?.scrollTo({top:0,behavior:"smooth"});

  // Animation phases
  const p1=Math.min(1,Math.max(0,scrollY/80));   // 0→80px: bg fades to transparent
  const p2=Math.min(1,Math.max(0,(scrollY-80)/80)); // 80→160px: logo collapses

  // Header height tracking — needed to push content down correctly
  // Tall header: 8(top) + 52(logo) + 8(m) + 38(search) + 10(m) + 66(pills) + 10(pb) = 192px
  // Compact header: 8(top) + 38(search) + 10(pb) = 56px
  const HEADER_TALL = 192;

  const bgAlpha=1-p1*0.85;
  const blurPx=p1*10;

  // 当天稳定的随机排序 — 每天 0 点重洗一次(localStorage 缓存当日顺序)
  // 详情页返回 / 切 tab 不会重排,商品位置不会丢
  const shuffled=useMemo(()=>getDailyShuffle(products),[products]);
  // 新品:按 createdAt 倒序,只保留最新 50 个
  const newestProducts=useMemo(()=>(
    [...products]
      .sort((a,b)=>new Date(b.createdAt||0)-new Date(a.createdAt||0))
      .slice(0,50)
  ),[products]);
  // 热销:按销量倒序,销量相同随机
  const hottestProducts=useMemo(()=>(
    [...products].sort((a,b)=>{
      const diff=(b.sold||0)-(a.sold||0);
      return diff!==0?diff:Math.random()-0.5;
    })
  ),[products]);

  let filtered;
  if(activeCat==="__new")filtered=newestProducts;
  else if(activeCat==="__hot")filtered=hottestProducts;
  else if(activeCat){
    const cat=findCat(activeCat);
    const ids=cat?collectIds(cat):[activeCat];
    filtered=shuffled.filter(p=>ids.includes(p.cat));
  }else{
    filtered=shuffled;
  }
  // 搜索:正式结果未到时先用本地即时过滤(更快响应),到了用后端结果(更全)
  const isSearching=!!search.trim();
  if(isSearching){
    if(searchResults!==null){
      filtered=searchResults; // 后端已返回
    }else{
      filtered=filtered.filter(p=>p.name?.toLowerCase().includes(search.toLowerCase()));
    }
  }
  const POOL=isSearching?filtered:(filtered.length>0?filtered:products);
  // 搜索状态下不再循环填充(后端结果有限,不该重复)
  const prodList=POOL.length>0
    ?(isSearching?POOL.slice(0,visibleCount):Array.from({length:Math.min(visibleCount,POOL.length*8)},(_,i)=>({...POOL[i%POOL.length],_key:i})))
    :[];
  // 个性化推荐 — 优先后端,失败用本地兜底
  const [recoProds,setRecoProds]=useState([]);
  const [recoSource,setRecoSource]=useState("local"); // 'personalized' | 'trending' | 'local'
  useEffect(()=>{
    let alive=true;
    getRecommendations({limit:8}).then(({products:rec,source})=>{
      if(!alive)return;
      const filtered=rec.filter(p=>!cart.find(c=>c.id===p.id));
      if(filtered.length>0){setRecoProds(filtered);setRecoSource(source||"trending");}
      else{setRecoProds(products.filter(p=>!cart.find(c=>c.id===p.id)).slice(0,8));setRecoSource("local");}
    }).catch(()=>{
      if(alive){setRecoProds(products.filter(p=>!cart.find(c=>c.id===p.id)).slice(0,8));setRecoSource("local");}
    });
    return()=>{alive=false;};
  },[loggedIn,products.length,cart.length]);
  const SLIDE_W=294;
  const cartCount=cart.reduce((a,i)=>a+i.qty,0);

  const renderSlide=(s)=>(
    <div style={{height:188,borderRadius:14,background:s.bg,overflow:"hidden",display:"flex",flexDirection:"column",justifyContent:"center",padding:"0 24px",boxShadow:"0 2px 10px rgba(0,0,0,0.08)",position:"relative"}}>
      <div style={{position:"absolute",right:-20,top:-20,width:120,height:120,borderRadius:"50%",background:s.accent,opacity:0.5,pointerEvents:"none"}}/>
      <div style={{position:"absolute",right:30,bottom:-30,width:80,height:80,borderRadius:"50%",background:s.accent,opacity:0.25,pointerEvents:"none"}}/>
      <div style={{fontSize:10,letterSpacing:"0.22em",color:s.text,opacity:0.6,fontFamily:FF,marginBottom:6}}>{s.sub}</div>
      <div style={{fontFamily:SERIF,fontSize:22,color:s.text,lineHeight:1.25}}>{s.label}</div>
      <div style={{marginTop:14,background:s.text,color:"#fff",display:"inline-block",padding:"7px 18px",borderRadius:6,fontSize:11,letterSpacing:"0.1em",fontFamily:FF,width:"fit-content",opacity:0.9}}>立即选购</div>
    </div>
  );

  // 点击 banner 后的跳转
  const onBannerClick=(b)=>{
    if(b.link_type==="category"&&b.link_target){
      setActiveCat(b.link_target);
      // 滚到商品列表
      setTimeout(()=>{
        const el=scrollRef.current;
        if(el)el.scrollTo({top:HEADER_TALL+220,behavior:"smooth"});
      },100);
    }else if(b.link_type==="product"&&b.link_target){
      const p=products.find(p=>String(p.id)===String(b.link_target));
      if(p)onOpenProduct?.(p);
    }else if(b.link_type==="url"&&b.link_target){
      window.open(b.link_target,"_blank","noopener");
    }
  };

  // 后端 banner 渲染(图片 + 可选标题)
  const renderBackendBanner=(b)=>(
    <div
      onClick={()=>onBannerClick(b)}
      style={{
        position:"relative",height:188,width:"100%",
        borderRadius:14,overflow:"hidden",
        background:"#f3f4f6",
        boxShadow:"0 2px 10px rgba(0,0,0,0.08)",
        cursor:"pointer",
      }}
      role="button"
      aria-label={b.title||"banner"}
    >
      <img
        src={b.image_url}
        alt={b.title||""}
        loading="eager"
        decoding="async"
        style={{
          position:"absolute",inset:0,
          width:"100%",height:"100%",
          objectFit:"cover",
          display:"block",
        }}
      />
      {(b.title||b.subtitle)&&(
        <div style={{
          position:"absolute",left:0,right:0,bottom:0,
          padding:"30px 22px 16px",
          background:"linear-gradient(to top, rgba(0,0,0,0.55), transparent)",
          textAlign:"left",color:"#fff",
          pointerEvents:"none",
        }}>
          {b.subtitle&&<div style={{fontSize:10,letterSpacing:"0.18em",opacity:0.85,fontFamily:FF,marginBottom:4}}>{b.subtitle}</div>}
          {b.title&&<div style={{fontFamily:SERIF,fontSize:18,lineHeight:1.3,fontWeight:600}}>{b.title}</div>}
        </div>
      )}
    </div>
  );

  // 后端有 banner 用后端,否则 fallback 硬编码
  const useBackendBanners = banners.length > 0;
  const carouselItems = useBackendBanners ? banners : SLIDERS;
  const carouselRender = useBackendBanners ? renderBackendBanner : renderSlide;

  return(
    <div style={{height:"100%",position:"relative",background:"#fff"}}>
      {/* ── Floating animated header ── */}
      <div style={{
        position:"absolute",top:0,left:0,right:0,zIndex:50,
        background:`rgba(255,255,255,${bgAlpha})`,
        backdropFilter:blurPx>0?`blur(${blurPx}px)`:"none",
        WebkitBackdropFilter:blurPx>0?`blur(${blurPx}px)`:"none",
        borderBottom:p1>0.6?"1px solid rgba(220,220,220,0.6)":"none",
        paddingTop:8,paddingLeft:14,paddingRight:14,paddingBottom:10,
      }}>
        {/* Logo + zipcode — collapses as p2 goes 0→1 */}
        <div style={{
          height:(1-p2)*52,overflow:"hidden",opacity:1-p2,
          display:"flex",alignItems:"center",justifyContent:"space-between",
          marginBottom:(1-p2)*8,
        }}>
          {/* 主页左上 LOGO — 优先用图片;加载失败 fallback 到朱红文字 */}
          {logoFailed ? (
            <span style={{fontFamily:SERIF,fontSize:26,letterSpacing:"0.04em",color:"#c8102e",lineHeight:1,flexShrink:0,fontWeight:700}}>十一家</span>
          ) : (
            <img
              src={`${process.env.PUBLIC_URL||""}/logo-syj.png`}
              alt="十一家"
              onError={()=>setLogoFailed(true)}
              style={{height:46,width:"auto",display:"block",flexShrink:0,objectFit:"contain"}}
            />
          )}
          <button
            onClick={()=>detectZipcode(true)}
            disabled={zipDetecting}
            title="点击重新定位"
            style={{display:"flex",alignItems:"center",gap:5,background:"rgba(0,0,0,0.05)",border:"none",borderRadius:20,padding:"5px 11px",cursor:zipDetecting?"wait":"pointer",fontFamily:FF}}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2.5"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
            <span style={{fontSize:12,color:"#444",fontFamily:FF,fontWeight:500,letterSpacing:"0.04em"}}>{zipcode}</span>
            {zipDetecting
              ?<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2.5" strokeLinecap="round" style={{animation:"spin 0.9s linear infinite"}}><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>
              :<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg>}
          </button>
        </div>
        {/* Search + cart */}
        <div style={{display:"flex",alignItems:"center",gap:10,position:"relative"}}>
          <div style={{flex:1,display:"flex",alignItems:"center",gap:9,background:"linear-gradient(90deg,#f0f0ee,#ebebea)",borderRadius:22,padding:"9px 14px",boxShadow:"inset 0 1px 3px rgba(0,0,0,0.04)"}}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              onFocus={()=>setSearchFocused(true)}
              onBlur={()=>setTimeout(()=>setSearchFocused(false),150)}
              onKeyDown={e=>{if(e.key==="Enter"&&search.trim()){pushRecentSearch(search.trim());e.target.blur();}}}
              placeholder="搜索商品名称…"
              style={{border:"none",background:"none",outline:"none",fontSize:13.5,color:"#333",flex:1,fontFamily:FF}}/>
            {search&&<button onClick={()=>setSearch("")} style={{background:"none",border:"none",cursor:"pointer",padding:0,color:"#aaa",fontSize:14}}>✕</button>}
          </div>
          <button onClick={()=>setTab("cart")} style={{background:"none",border:"none",cursor:"pointer",padding:0,position:"relative",flexShrink:0}}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="20" r="1.6"/><circle cx="18" cy="20" r="1.6"/><path d="M2 3h2.5l2.7 12.2a1.6 1.6 0 001.6 1.3h9.1a1.6 1.6 0 001.55-1.2L21 7H6"/></svg>
            {cartCount>0&&<span style={{position:"absolute",top:-4,right:-4,background:"#e03030",color:"#fff",borderRadius:"50%",width:16,height:16,fontSize:9,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:FF}}>{cartCount>9?"9+":cartCount}</span>}
          </button>

          {/* 搜索下拉 — 聚焦时显示 */}
          {searchFocused&&(
            <div style={{position:"absolute",top:"100%",left:0,right:42,marginTop:6,background:"#fff",borderRadius:14,boxShadow:"0 8px 24px rgba(0,0,0,0.12)",padding:"12px 0",zIndex:50,maxHeight:380,overflowY:"auto"}}>
              {/* 输入联想 */}
              {search.trim()&&suggestions.length>0&&(
                <>
                  <div style={{padding:"4px 14px 8px",fontSize:11,color:"#888",fontFamily:FF,fontWeight:600,letterSpacing:"0.06em"}}>商品建议</div>
                  {suggestions.map(s=>(
                    <button key={s.id} onMouseDown={e=>{e.preventDefault();pushRecentSearch(search);onOpenProduct(s);setSearchFocused(false);}}
                      style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"8px 14px",background:"none",border:"none",textAlign:"left",cursor:"pointer"}}>
                      <div style={{width:32,height:32,borderRadius:6,background:"#f5f5f4",overflow:"hidden",flexShrink:0}}>
                        {s.imgUrl&&<img src={s.imgUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>}
                      </div>
                      <div style={{flex:1,minWidth:0,fontSize:12.5,color:"#1a1a1a",fontFamily:FF,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{s.name}</div>
                      <span style={{fontSize:12,color:"#e03030",fontFamily:FF,fontWeight:600,whiteSpace:"nowrap"}}>${s.price?.toFixed(2)}</span>
                    </button>
                  ))}
                  <div style={{height:1,background:"#f5f5f4",margin:"6px 0"}}/>
                </>
              )}
              {/* 最近搜索 */}
              {!search.trim()&&recentSearches.length>0&&(
                <>
                  <div style={{padding:"4px 14px 6px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <span style={{fontSize:11,color:"#888",fontFamily:FF,fontWeight:600,letterSpacing:"0.06em"}}>最近搜索</span>
                    <button onMouseDown={e=>{e.preventDefault();clearRecentSearches();}} style={{background:"none",border:"none",fontSize:11,color:"#aaa",cursor:"pointer",fontFamily:FF}}>清除</button>
                  </div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:6,padding:"4px 14px 10px"}}>
                    {recentSearches.map((kw,i)=>(
                      <button key={i} onMouseDown={e=>{e.preventDefault();setSearch(kw);pushRecentSearch(kw);}}
                        style={{padding:"5px 12px",fontSize:12,background:"#f5f5f4",border:"none",borderRadius:14,color:"#444",fontFamily:FF,cursor:"pointer"}}>
                        {kw}
                      </button>
                    ))}
                  </div>
                  <div style={{height:1,background:"#f5f5f4",margin:"6px 0"}}/>
                </>
              )}
              {/* 热门搜索 */}
              {!search.trim()&&hotSearches.length>0&&(
                <>
                  <div style={{padding:"4px 14px 6px",fontSize:11,color:"#888",fontFamily:FF,fontWeight:600,letterSpacing:"0.06em"}}>🔥 热门搜索</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:6,padding:"4px 14px 10px"}}>
                    {hotSearches.map((kw,i)=>(
                      <button key={i} onMouseDown={e=>{e.preventDefault();setSearch(kw);pushRecentSearch(kw);}}
                        style={{padding:"5px 12px",fontSize:12,background:"linear-gradient(135deg,#fff5f0,#ffe8de)",border:"1px solid #ffcab0",borderRadius:14,color:"#c42020",fontFamily:FF,cursor:"pointer"}}>
                        {kw}
                      </button>
                    ))}
                  </div>
                </>
              )}
              {/* 空状态 */}
              {!search.trim()&&recentSearches.length===0&&hotSearches.length===0&&(
                <div style={{padding:"24px 14px",textAlign:"center",fontSize:12,color:"#bbb",fontFamily:FF}}>开始输入搜索…</div>
              )}
              {search.trim()&&suggestions.length===0&&(
                <div style={{padding:"20px 14px",textAlign:"center",fontSize:12,color:"#bbb",fontFamily:FF}}>没找到匹配的商品</div>
              )}
            </div>
          )}
        </div>
        {/* L2 sub-category pills — auto-scrolling, collapses with logo (p2) */}
        <div style={{
          height:(1-p2)*66, marginTop:(1-p2)*10, opacity:1-p2,
          overflow:"hidden", pointerEvents:p2>0.5?"none":"auto",
        }}>
          <AutoScrollPills rows={subRows} onSelect={id=>setActiveCat(activeCat===id?null:id)}/>
        </div>
      </div>

      {/* ── Scrollable content: top padding = HEADER_TALL so slider never hides under header ── */}
      <div ref={scrollRef} style={{overflowY:"auto",height:"100%",paddingBottom:72}}>
        <div style={{height:HEADER_TALL}}/>
        {/* Slider sits exactly below header */}
        <InfiniteCarousel items={carouselItems} renderItem={carouselRender} cardWidth={SLIDE_W} gap={12} peekLeft={48} autoMs={4000}/>

        {/* 未登录引导 已移到「我的」tab 登录页 */}
        <div style={{paddingTop:18,paddingBottom:4}}>
          <InfiniteCatRow cats={quickCats} activeCat={activeCat} onSelect={id=>setActiveCat(activeCat===id?null:id)}/>
        </div>
        <div style={{padding:"16px 14px 10px",display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontFamily:SERIF,fontSize:15,color:"#1a1a1a"}}>
            {isSearching?`搜索结果${searchLoading?" …":` (${prodList.length})`}`:(activeCat?([...quickCats,...subWithTint].find(c=>c.id===activeCat)?.label??"为你推荐"):"为你推荐")}
          </span>
          <div style={{flex:1,height:1,background:"#e8e8e8"}}/>
          {searchLoading&&<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2.4" strokeLinecap="round" style={{animation:"spin 0.9s linear infinite"}}><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>}
          {activeCat&&!isSearching&&<button onClick={()=>setActiveCat(null)} style={{background:"none",border:"none",fontSize:11,color:"#aaa",cursor:"pointer",fontFamily:FF}}>清除</button>}
        </div>
        {error&&(
          <div style={{padding:"50px 24px 30px",textAlign:"center",fontFamily:FF}}>
            <div style={{fontSize:38,marginBottom:10,opacity:0.7}}>📦</div>
            <div style={{fontSize:14,color:"#1a1a1a",fontWeight:500,marginBottom:4}}>暂时加载不到商品</div>
            <div style={{fontSize:11.5,color:"#999",lineHeight:1.6,marginBottom:18}}>请检查网络后重试,或稍后再试一次</div>
            {onRetry&&(
              <button
                onClick={onRetry}
                style={{
                  background:"#1a1a1a",color:"#fff",border:"none",
                  borderRadius:22,padding:"10px 28px",fontSize:13,
                  fontFamily:FF,fontWeight:500,cursor:"pointer",
                  boxShadow:"0 2px 8px rgba(0,0,0,0.12)",
                }}
              >重新加载</button>
            )}
          </div>
        )}
        {!error&&loading&&prodList.length===0&&<ProductGridSkeleton count={6}/>}
        {!error&&!loading&&prodList.length===0&&<div style={{padding:"60px 0",textAlign:"center",color:"#bbb",fontSize:13,fontFamily:FF}}>暂无商品</div>}
        {!error&&<MasonryGrid products={prodList} wishlist={wishlist} onWish={handleWish} onAdd={addToCart} onOpen={onOpenProduct} keyPrefix="home-"/>}
        <div ref={loadingRef} style={{height:60,display:"flex",alignItems:"center",justifyContent:"center"}}>
          {prodList.length>0&&<div style={{display:"flex",gap:6}}>{[0,1,2].map(i=><div key={i} style={{width:6,height:6,borderRadius:"50%",background:"#d8d8d8",animation:`dp 1.2s ease-in-out ${i*0.2}s infinite`}}/>)}</div>}
        </div>
        {/*
          底部推荐栏 — 标签必须和顶部"为你推荐"区分,否则用户会看到两个一模一样的标题。
          统一的命名:
            personalized → "猜你喜欢"
            trending     → "近期热销"
            local        → "更多发现"
        */}
        {recoProds.length>0&&!error&&(
          <div style={{paddingBottom:16}}>
            <div style={{padding:"4px 14px 12px",display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontFamily:SERIF,fontSize:15,color:"#1a1a1a"}}>
                {recoSource==="personalized"?"猜你喜欢":recoSource==="trending"?"近期热销":"更多发现"}
              </span>
              <div style={{flex:1,height:1,background:"#e8e8e8"}}/>
              <span style={{fontSize:10.5,color:"#c0c0c0",fontFamily:FF}}>
                {recoSource==="personalized"?"基于你的浏览和购买":recoSource==="trending"?"销量飙升":"为您精选"}
              </span>
            </div>
            <MasonryGrid products={recoProds} wishlist={wishlist} onWish={handleWish} onAdd={addToCart} onOpen={onOpenProduct} keyPrefix="reco-"/>
          </div>
        )}
      </div>
      {/* 定位失败 toast — 用户主动点定位才显示,自动定位失败保持沉默 */}
      {zipError&&(
        <div style={{position:"absolute",top:"calc(env(safe-area-inset-top, 0px) + 60px)",left:14,right:14,zIndex:80,background:"#1a1a1a",color:"#fff",padding:"10px 14px",borderRadius:10,fontSize:12.5,fontFamily:FF,lineHeight:1.5,boxShadow:"0 4px 14px rgba(0,0,0,0.18)",display:"flex",alignItems:"center",gap:9,animation:"toastFade 5.5s ease-out forwards"}}>
          <span style={{fontSize:14}}>📍</span>
          <span style={{flex:1}}>{zipError}</span>
        </div>
      )}
      <button onClick={scrollToTop} aria-label="回到顶部" style={{position:"absolute",bottom:90,right:18,width:54,height:54,borderRadius:"50%",background:"rgba(26,26,26,0.92)",backdropFilter:"blur(10px)",WebkitBackdropFilter:"blur(10px)",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 6px 20px rgba(0,0,0,0.28)",opacity:showFab?1:0,transform:showFab?"translateY(0) scale(1)":"translateY(10px) scale(0.85)",transition:"opacity 0.25s,transform 0.25s",pointerEvents:showFab?"auto":"none",zIndex:40}}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
      </button>
      <style>{`@keyframes dp{0%,100%{opacity:.3;transform:scale(1)}50%{opacity:1;transform:scale(1.3)}}@keyframes pillScroll0{from{transform:translateX(0)}to{transform:translateX(-33.3333%)}}@keyframes pillScroll1{from{transform:translateX(-33.3333%)}to{transform:translateX(0)}}`}</style>
      {StockToast}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CATEGORIES  — WEEE style: search / horizontal L1 icon row / L2 pills / grid
// ─────────────────────────────────────────────────────────────────────────────
const CAT_COLORS={
  new:     ["#fff4e6","#ffb347"],
  hot:     ["#fff0f0","#ff6b6b"],
  clothing:["#f0f4ff","#7c9eff"],
  intimate:["#fff0f8","#ff7eb3"],
  beauty:  ["#f0fff8","#5ecb8f"],
  daily:   ["#f5f0ff","#a07cff"],
  hidden:  ["#fffbe6","#ffd166"],
};

// 默认头像 — 可爱女孩 SVG(扁平插画风)
function CuteGirlAvatar({size=64,bg=true}){
  // 软萌 chibi 风:大眼 + 双马尾 + 蝴蝶结 + 心形腮红
  return(
    <svg width={size} height={size} viewBox="0 0 100 100" style={{display:"block"}}>
      <defs>
        <linearGradient id="hairGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#5a3a25"/>
          <stop offset="100%" stopColor="#3a2415"/>
        </linearGradient>
        <radialGradient id="cheekGrad" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#ffb3c0" stopOpacity="0.85"/>
          <stop offset="100%" stopColor="#ffb3c0" stopOpacity="0"/>
        </radialGradient>
      </defs>
      {bg&&<circle cx="50" cy="50" r="50" fill="#ffeae0"/>}

      {/* 双马尾(后层,贴在脑后,微微飘出脸侧) */}
      <ellipse cx="14" cy="62" rx="9" ry="16" fill="url(#hairGrad)" transform="rotate(-15 14 62)"/>
      <ellipse cx="86" cy="62" rx="9" ry="16" fill="url(#hairGrad)" transform="rotate(15 86 62)"/>
      {/* 头发后半(整圆顶) */}
      <path d="M16 52 Q 16 18, 50 16 Q 84 18, 84 52 Q 84 42, 78 32 Q 50 10, 22 32 Q 16 42, 16 52 Z" fill="url(#hairGrad)"/>

      {/* 脸 */}
      <ellipse cx="50" cy="58" rx="24" ry="26" fill="#ffe6cc"/>

      {/* 刘海(三缕,中间略短露额头) */}
      <path d="M27 36 Q 34 28, 44 32 Q 38 42, 30 44 Q 26 42, 27 36 Z" fill="url(#hairGrad)"/>
      <path d="M73 36 Q 66 28, 56 32 Q 62 42, 70 44 Q 74 42, 73 36 Z" fill="url(#hairGrad)"/>
      <path d="M44 30 Q 50 26, 56 30 Q 53 38, 50 38 Q 47 38, 44 30 Z" fill="url(#hairGrad)"/>

      {/* 蝴蝶结(头顶左侧) */}
      <g transform="translate(28 18)">
        <path d="M0 5 L 6 0 L 6 10 Z" fill="#ff6b8a"/>
        <path d="M12 5 L 6 0 L 6 10 Z" fill="#ff8aa3"/>
        <circle cx="6" cy="5" r="2" fill="#ffd0dd"/>
      </g>

      {/* 大眼睛 — 圆润 + 高光 */}
      <ellipse cx="38" cy="60" rx="4.5" ry="6" fill="#1a1a1a"/>
      <ellipse cx="62" cy="60" rx="4.5" ry="6" fill="#1a1a1a"/>
      {/* 主高光 */}
      <circle cx="39.5" cy="57.5" r="1.6" fill="#fff"/>
      <circle cx="63.5" cy="57.5" r="1.6" fill="#fff"/>
      {/* 副高光 — 让眼睛更水汪汪 */}
      <circle cx="36.5" cy="62.5" r="0.9" fill="#fff" opacity="0.7"/>
      <circle cx="60.5" cy="62.5" r="0.9" fill="#fff" opacity="0.7"/>

      {/* 眉毛 — 弯弯小弧 */}
      <path d="M33 50 Q 38 48, 43 50" stroke="#5a3a25" strokeWidth="1.4" fill="none" strokeLinecap="round"/>
      <path d="M57 50 Q 62 48, 67 50" stroke="#5a3a25" strokeWidth="1.4" fill="none" strokeLinecap="round"/>

      {/* 心形腮红 */}
      <g fill="url(#cheekGrad)" opacity="0.95">
        <ellipse cx="32" cy="69" rx="5" ry="3.2"/>
        <ellipse cx="68" cy="69" rx="5" ry="3.2"/>
      </g>

      {/* 小樱桃嘴 */}
      <path d="M47 73 Q 50 76, 53 73 Q 50 75, 47 73 Z" fill="#ff7a8a"/>
      <path d="M50 73 Q 50 71, 50 73" stroke="#c44560" strokeWidth="0.6" fill="none"/>
    </svg>
  );
}

// VIP 等级配置(默认 VIP3,每消费 $1000 升一级,封顶 VIP9)
function vipLevel(totalSpent){
  const lvl = 3 + Math.floor((totalSpent||0) / 1000);
  return Math.min(9, Math.max(3, lvl));
}
function vipNextThreshold(totalSpent){
  const lvl = vipLevel(totalSpent);
  if(lvl >= 9) return null;
  return (lvl - 2) * 1000; // 升 VIPn 需要的累计消费
}
function vipBadgeColors(level){
  if(level>=8) return ["#1f5fb8","#5a9aff","#fff"]; // 蓝(钻石)
  if(level>=6) return ["#b87333","#e8a060","#fff"]; // 橙金
  if(level>=4) return ["#ffd700","#ffb300","#1a1a1a"]; // 金
  return ["#d4d4d4","#a0a0a0","#1a1a1a"]; // 银(VIP3)
}

// 地址输入 + Google Places 自动补全
// onPick({line1, apt, city, state, zip}) 选中建议时回调,自动填入其他字段
function AddressAutocompleteInput({value,onChange,onPick,placeholder="请输入街道地址",style,suppressToken=0}){
  const [suggestions,setSuggestions]=useState([]);
  const [open,setOpen]=useState(false);
  const [loading,setLoading]=useState(false);
  const [active,setActive]=useState(-1);
  const [pos,setPos]=useState(null); // {top,left,width} for fixed dropdown
  const inputRef=useRef(null);
  const debouncer=useRef(null);
  const skipNextSearch=useRef(false);
  const lastSuppressToken=useRef(suppressToken);

  // 计算 dropdown 位置(避免被父级 overflow 裁剪)
  const updatePos=useCallback(()=>{
    if(!inputRef.current)return;
    const r=inputRef.current.getBoundingClientRect();
    setPos({top:r.bottom+4,left:r.left,width:r.width});
  },[]);

  useEffect(()=>{
    // 父组件 suppressToken 变了 → 这次 value 变是程序化的(比如从定位回填),
    // 不要触发 autocomplete + 关掉已展开的下拉
    if(suppressToken!==lastSuppressToken.current){
      lastSuppressToken.current=suppressToken;
      clearTimeout(debouncer.current);
      setSuggestions([]);
      setOpen(false);
      return;
    }
    if(skipNextSearch.current){skipNextSearch.current=false;return;}
    clearTimeout(debouncer.current);
    if(!value||value.trim().length<3){setSuggestions([]);setOpen(false);return;}
    debouncer.current=setTimeout(async()=>{
      setLoading(true);
      try{
        const list=await mapsAutocomplete(value);
        setSuggestions(list||[]);
        if((list||[]).length>0){updatePos();setOpen(true);}
      }catch(err){console.warn("autocomplete error:",err);setSuggestions([]);}
      setLoading(false);
    },280);
    return()=>clearTimeout(debouncer.current);
  },[value,updatePos,suppressToken]);

  // 滚动 / 窗口尺寸变化时跟新位置(若关掉就不需要)
  useEffect(()=>{
    if(!open)return;
    const fn=()=>updatePos();
    window.addEventListener("scroll",fn,true);
    window.addEventListener("resize",fn);
    return()=>{
      window.removeEventListener("scroll",fn,true);
      window.removeEventListener("resize",fn);
    };
  },[open,updatePos]);

  const pick=async(s)=>{
    setOpen(false);setSuggestions([]);
    try{
      const detail=await mapsPlaceDetails(s.place_id);
      skipNextSearch.current=true;
      onChange?.(detail.line1||s.main_text||"");
      onPick?.(detail);
    }catch(err){
      console.warn("place-details failed:",err);
    }
  };

  return(
    <div style={{position:"relative"}}>
      <input
        ref={inputRef}
        value={value}
        onChange={e=>{onChange?.(e.target.value);setActive(-1);}}
        onFocus={()=>{if(suggestions.length>0){updatePos();setOpen(true);}}}
        onBlur={()=>setTimeout(()=>setOpen(false),200)}
        onKeyDown={e=>{
          if(e.key==="ArrowDown"){e.preventDefault();setActive(i=>Math.min(suggestions.length-1,i+1));}
          else if(e.key==="ArrowUp"){e.preventDefault();setActive(i=>Math.max(-1,i-1));}
          else if(e.key==="Enter"&&active>=0){e.preventDefault();pick(suggestions[active]);}
          else if(e.key==="Escape"){setOpen(false);}
        }}
        placeholder={placeholder}
        style={style}
        autoComplete="off"
      />
      {loading&&<div style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",width:14,height:14,border:"2px solid #ddd",borderTopColor:"#888",borderRadius:"50%",animation:"spin 0.8s linear infinite",pointerEvents:"none"}}/>}
      {open&&suggestions.length>0&&pos&&(
        <div style={{position:"fixed",top:pos.top,left:pos.left,width:pos.width,background:"#fff",border:"1px solid #e8e8e8",borderRadius:10,boxShadow:"0 8px 24px rgba(0,0,0,0.18)",zIndex:9999,maxHeight:240,overflowY:"auto"}}>
          {suggestions.map((s,i)=>(
            <button
              key={s.place_id}
              onMouseDown={e=>e.preventDefault()}
              onClick={()=>pick(s)}
              onMouseEnter={()=>setActive(i)}
              style={{width:"100%",display:"flex",alignItems:"flex-start",gap:8,padding:"10px 12px",background:active===i?"#f7f6f4":"#fff",border:"none",borderBottom:i<suggestions.length-1?"1px solid #f5f5f4":"none",cursor:"pointer",textAlign:"left"}}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" style={{marginTop:2,flexShrink:0}}><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{s.main_text}</div>
                <div style={{fontSize:11,color:"#888",fontFamily:FF,marginTop:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{s.secondary_text}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// 商品卡 skeleton — 灰底 + 闪光动画
function ProductCardSkeleton(){
  return(
    <div style={{background:"#fff",borderRadius:14,overflow:"hidden",boxShadow:"0 1px 6px rgba(0,0,0,0.05)"}}>
      <div style={{paddingBottom:"100%",background:"linear-gradient(90deg,#f0f0f0 0%,#f8f8f8 50%,#f0f0f0 100%)",backgroundSize:"200% 100%",animation:"shimmer 1.4s linear infinite"}}/>
      <div style={{padding:"10px 10px 12px"}}>
        <div style={{height:18,width:"60%",borderRadius:4,background:"linear-gradient(90deg,#f0f0f0 0%,#f8f8f8 50%,#f0f0f0 100%)",backgroundSize:"200% 100%",animation:"shimmer 1.4s linear infinite",marginBottom:8}}/>
        <div style={{height:12,width:"90%",borderRadius:4,background:"linear-gradient(90deg,#f0f0f0 0%,#f8f8f8 50%,#f0f0f0 100%)",backgroundSize:"200% 100%",animation:"shimmer 1.4s linear infinite",marginBottom:5}}/>
        <div style={{height:12,width:"40%",borderRadius:4,background:"linear-gradient(90deg,#f0f0f0 0%,#f8f8f8 50%,#f0f0f0 100%)",backgroundSize:"200% 100%",animation:"shimmer 1.4s linear infinite"}}/>
      </div>
    </div>
  );
}
// 商品 grid skeleton(双列布局)
function ProductGridSkeleton({count=6}){
  return(
    <div style={{display:"flex",gap:10,padding:"0 12px",alignItems:"flex-start"}}>
      {[0,1].map(col=>(
        <div key={col} style={{flex:1,display:"flex",flexDirection:"column",gap:10}}>
          {Array.from({length:Math.ceil(count/2)}).map((_,i)=><ProductCardSkeleton key={i}/>)}
        </div>
      ))}
    </div>
  );
}
// 单行 skeleton(订单列表用)
function ListSkeleton({count=4}){
  return(
    <div style={{padding:"12px 12px 0"}}>
      {Array.from({length:count}).map((_,i)=>(
        <div key={i} style={{background:"#fff",borderRadius:14,padding:"14px 16px",marginBottom:10,boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
          <div style={{height:14,width:"35%",borderRadius:4,background:"linear-gradient(90deg,#f0f0f0 0%,#f8f8f8 50%,#f0f0f0 100%)",backgroundSize:"200% 100%",animation:"shimmer 1.4s linear infinite",marginBottom:10}}/>
          <div style={{height:14,width:"75%",borderRadius:4,background:"linear-gradient(90deg,#f0f0f0 0%,#f8f8f8 50%,#f0f0f0 100%)",backgroundSize:"200% 100%",animation:"shimmer 1.4s linear infinite",marginBottom:6}}/>
          <div style={{height:12,width:"50%",borderRadius:4,background:"linear-gradient(90deg,#f0f0f0 0%,#f8f8f8 50%,#f0f0f0 100%)",backgroundSize:"200% 100%",animation:"shimmer 1.4s linear infinite"}}/>
        </div>
      ))}
    </div>
  );
}

// 浏览历史 — 最多保留最近 30 个商品 ID,详情页 mount 时写入
const HISTORY_KEY = "syj_history_pids";
const HISTORY_MAX = 30;
function pushHistory(pid){
  if(!pid)return;
  try{
    const raw=localStorage.getItem(HISTORY_KEY);
    const arr=raw?JSON.parse(raw):[];
    const next=[pid,...arr.filter(x=>x!==pid)].slice(0,HISTORY_MAX);
    localStorage.setItem(HISTORY_KEY,JSON.stringify(next));
  }catch{}
}
function loadHistory(){
  try{
    const raw=localStorage.getItem(HISTORY_KEY);
    return raw?JSON.parse(raw):[];
  }catch{return [];}
}
function useHistory(){
  const [ids,setIds]=useState(()=>loadHistory());
  // 监听 storage 事件 + 提供刷新
  const refresh=()=>setIds(loadHistory());
  return [ids,refresh];
}

// useState 的持久化版本 —— 自动 localStorage 读写
function usePersistedState(key,initial){
  const [v,setV]=useState(()=>{
    try{
      const raw=localStorage.getItem(key);
      return raw?JSON.parse(raw):initial;
    }catch{return initial;}
  });
  useEffect(()=>{
    try{localStorage.setItem(key,JSON.stringify(v));}catch{}
  },[key,v]);
  return [v,setV];
}

// 当天稳定的随机排序 — 新一天才重洗,期间无论页面切换多少次顺序都一样
//   localStorage 缓存当天的 product id 顺序,新增商品(没在缓存里)放最后
const DAILY_SHUFFLE_KEY = "syj_daily_shuffle";
function getDailyShuffle(products) {
  if (!Array.isArray(products) || products.length === 0) return products;
  const todayKey = new Date().toISOString().slice(0, 10);
  let cached = null;
  try {
    const raw = localStorage.getItem(DAILY_SHUFFLE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.date === todayKey && Array.isArray(parsed.ids)) cached = parsed.ids;
    }
  } catch {}

  if (cached) {
    // 用缓存的顺序排;新加的商品(不在缓存)放最后,按它们自身随机
    const idx = new Map(cached.map((id, i) => [id, i]));
    const known = [], unknown = [];
    for (const p of products) {
      if (idx.has(p.id)) known.push(p);
      else unknown.push(p);
    }
    known.sort((a, b) => idx.get(a.id) - idx.get(b.id));
    return [...known, ...shuffle(unknown)];
  }

  // 新一天 → 重洗 + 缓存
  const shuffled = shuffle(products);
  try {
    localStorage.setItem(DAILY_SHUFFLE_KEY, JSON.stringify({
      date: todayKey,
      ids: shuffled.map(p => p.id),
    }));
  } catch {}
  return shuffled;
}

function shuffle(arr){
  const a=[...arr];
  for(let i=a.length-1;i>0;i--){
    const j=Math.floor(Math.random()*(i+1));
    [a[i],a[j]]=[a[j],a[i]];
  }
  return a;
}

// 根据品类名称给个 emoji 占位(后端没图标字段)
function catEmoji(name){
  if(!name)return"📦";
  if(name.includes("衣")||name.includes("鞋"))return"👗";
  if(name.includes("化妆")||name.includes("护肤")||name.includes("彩妆")||name.includes("洗护"))return"💄";
  if(name.includes("套")||name.includes("KY")||name.includes("情趣"))return"💝";
  if(name.includes("药"))return"💊";
  if(name.includes("日用")||name.includes("家"))return"🏠";
  if(name.includes("隐藏"))return"🎁";
  if(name.includes("食"))return"🍱";
  if(name.includes("饮"))return"🥤";
  return"📦";
}

// 收集分类节点本身 + 所有后代 id(用于按品类筛选商品)
function descendantIds(cat){
  const ids=[cat.id];
  (cat.children||[]).forEach(c=>ids.push(...descendantIds(c)));
  return ids;
}

function CategoriesPage({cart,setCart,wishlist,setWishlist,loggedIn,setTab,onOpenProduct,products,categories}){
  // 一级 tab 列表:虚拟「新品 / 热销」+ 后端真实分类
  const VIRTUAL_TABS=[
    {id:"__new",label:"新品",emoji:"✨",virtual:"new"},
    {id:"__hot",label:"热销",emoji:"🔥",virtual:"hot"},
  ];
  const tabs=[...VIRTUAL_TABS,...categories.map(c=>({id:c.id,label:c.name,emoji:catEmoji(c.name),children:c.children||[]}))];
  const [activeCat,setActiveCat]=useState("__new");
  const [activeSub,setActiveSub]=useState(null);
  const [search,setSearch]=useState("");
  const cartCount=cart.reduce((a,i)=>a+i.qty,0);
  const cur=tabs.find(c=>c.id===activeCat)||tabs[0];
  useEffect(()=>setActiveSub(null),[activeCat]);
  // 当后端数据加载完默认选第一个真实分类(如果当前还停在 __new 但没有商品)
  useEffect(()=>{if(categories.length>0&&!tabs.find(t=>t.id===activeCat)){setActiveCat(tabs[0].id);}},[categories]);// eslint-disable-line

  const {addToCart, increment: _inc, StockToast} = useStockGuardedCart(cart, setCart);
  const handleWish=(id)=>{if(!loggedIn){setTab("profile");return;}setWishlist(w=>w.includes(id)?w.filter(x=>x!==id):[...w,id]);};

  // 商品默认随机 — 每天 0 点重洗,期间稳定
  const shuffledAll=useMemo(()=>getDailyShuffle(products),[products]);
  // 新品 / 热销规则
  const newestPool=useMemo(()=>(
    [...products]
      .sort((a,b)=>new Date(b.createdAt||0)-new Date(a.createdAt||0))
      .slice(0,50)
  ),[products]);
  const hottestPool=useMemo(()=>(
    [...products].sort((a,b)=>{
      const diff=(b.sold||0)-(a.sold||0);
      return diff!==0?diff:Math.random()-0.5;
    })
  ),[products]);

  let pool;
  if(cur?.virtual==="new")pool=newestPool;
  else if(cur?.virtual==="hot")pool=hottestPool;
  else if(cur){
    const ids=descendantIds(cur);
    pool=activeSub
      ?shuffledAll.filter(p=>p.cat===activeSub)
      :shuffledAll.filter(p=>ids.includes(p.cat));
  }else pool=shuffledAll;
  const filteredProducts=search?pool.filter(p=>p.name.includes(search)):pool;

  const subPill=(a)=>({
    flexShrink:0,padding:"6px 15px",
    background:a?"#1a1a1a":"#fff",
    border:`1.5px solid ${a?"#1a1a1a":"#e8e8e8"}`,
    borderRadius:20,cursor:"pointer",fontSize:12.5,
    fontFamily:FF,color:a?"#fff":"#555",
    whiteSpace:"nowrap",transition:"all 0.15s",
  });

  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#fff"}}>

      {/* ── Fixed header block (App container 已处理 safe-area-top)── */}
      <div style={{background:"#fff",paddingTop:12,borderBottom:"1px solid #f0f0f0",flexShrink:0}}>

        {/* Search + cart */}
        <div style={{display:"flex",alignItems:"center",gap:10,padding:"0 14px 12px"}}>
          <div style={{flex:1,display:"flex",alignItems:"center",gap:9,background:"#f5f5f4",borderRadius:22,padding:"9px 14px"}}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="搜索商品名称…"
              style={{border:"none",background:"none",outline:"none",fontSize:13.5,color:"#333",flex:1,fontFamily:FF}}/>
            {search&&<button onClick={()=>setSearch("")} style={{background:"none",border:"none",cursor:"pointer",padding:0,color:"#aaa",fontSize:14}}>✕</button>}
          </div>
          <button onClick={()=>setTab("cart")} style={{background:"none",border:"none",cursor:"pointer",padding:0,position:"relative",flexShrink:0}}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="20" r="1.6"/><circle cx="18" cy="20" r="1.6"/><path d="M2 3h2.5l2.7 12.2a1.6 1.6 0 001.6 1.3h9.1a1.6 1.6 0 001.55-1.2L21 7H6"/></svg>
            {cartCount>0&&<span style={{position:"absolute",top:-4,right:-4,background:"#e03030",color:"#fff",borderRadius:"50%",width:16,height:16,fontSize:9,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:FF}}>{cartCount>9?"9+":cartCount}</span>}
          </button>
        </div>

        {/* L1 — horizontally scrollable big icon row */}
        <div style={{display:"flex",overflowX:"auto",scrollbarWidth:"none",gap:4,padding:"0 10px 14px"}}>
          {tabs.map(c=>{
            const a=activeCat===c.id;
            const [bg,accent]=CAT_COLORS[c.id]||["#f5f5f4","#888"];
            return(
              <button key={c.id} onClick={()=>setActiveCat(c.id)}
                style={{flexShrink:0,display:"flex",flexDirection:"column",alignItems:"center",gap:6,
                  background:"none",border:"none",cursor:"pointer",padding:"4px 6px",minWidth:68}}>
                <div style={{
                  width:58,height:58,borderRadius:18,
                  background:a?"#1a1a1a":bg,
                  display:"flex",alignItems:"center",justifyContent:"center",
                  fontSize:26,
                  boxShadow:a?"0 4px 14px rgba(0,0,0,0.22)":`0 2px 8px ${accent}44`,
                  transition:"all 0.18s",
                  transform:a?"scale(1.08)":"scale(1)",
                  border:a?"none":`1.5px solid ${accent}33`,
                }}>{c.emoji}</div>
                <span style={{
                  fontSize:11,fontFamily:FF,
                  color:a?"#1a1a1a":"#555",
                  fontWeight:a?700:400,
                  whiteSpace:"nowrap",
                  lineHeight:1.2,
                }}>{c.label}</span>
              </button>
            );
          })}
        </div>

        {/* L2 — pill filter row */}
        {cur&&cur.children&&cur.children.length>0&&(
          <div style={{display:"flex",overflowX:"auto",scrollbarWidth:"none",gap:7,padding:"0 14px 12px"}}>
            <button onClick={()=>setActiveSub(null)} style={subPill(!activeSub)}>全部</button>
            {cur.children.map(s=>(
              <button key={s.id} onClick={()=>setActiveSub(activeSub===s.id?null:s.id)} style={subPill(activeSub===s.id)}>{s.name}</button>
            ))}
          </div>
        )}
      </div>

      {/* ── Product grid ── */}
      <div style={{flex:1,overflowY:"auto",padding:"14px 0 72px"}}>
        {filteredProducts.length===0
          ?<div style={{textAlign:"center",padding:"60px 0",color:"#ccc",fontFamily:FF,fontSize:14}}>暂无商品</div>
          :<MasonryGrid products={filteredProducts} wishlist={wishlist} onWish={handleWish} onAdd={addToCart} onOpen={onOpenProduct} keyPrefix="cat-"/>
        }
      </div>

      {StockToast}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// NAVBAR
// ─────────────────────────────────────────────────────────────────────────────
function NavBar({tab,setTab,cartCount,onScan}){
  const cartBtnRef=useRef(null);
  // 把购物车图标位置注册到全局,商品卡飞入动画用
  useEffect(()=>{
    window.__syjCartIconEl=cartBtnRef.current;
    return()=>{if(window.__syjCartIconEl===cartBtnRef.current)delete window.__syjCartIconEl;};
  },[]);
  const tabs=[
    {id:"home",label:"主页",icon:a=><svg width="22" height="22" viewBox="0 0 24 24" fill={a?"#1a1a1a":"none"} stroke={a?"#1a1a1a":"#aaa"} strokeWidth="1.8"><path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z"/></svg>},
    {id:"categories",label:"分类",icon:a=><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={a?"#1a1a1a":"#aaa"} strokeWidth="1.8"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>},
    // 中间特殊按钮 — 扫一扫识别同款(以图搜图)
    {id:"__scan",label:"扫一扫",isScan:true,icon:()=>(
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>
        <circle cx="12" cy="13" r="4"/>
      </svg>
    )},
    {id:"cart",label:"购物车",badge:cartCount,icon:a=><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={a?"#1a1a1a":"#aaa"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="20" r="1.6"/><circle cx="18" cy="20" r="1.6"/><path d="M2 3h2.5l2.7 12.2a1.6 1.6 0 001.6 1.3h9.1a1.6 1.6 0 001.55-1.2L21 7H6"/></svg>},
    {id:"profile",label:"用户",icon:a=><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={a?"#1a1a1a":"#aaa"} strokeWidth="1.8"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>},
  ];
  return(
    <div style={{
      // iOS 原生 tab bar 半透明 + 背后高斯模糊
      background:"rgba(255,255,255,0.88)",
      backdropFilter:"blur(20px) saturate(180%)",
      WebkitBackdropFilter:"blur(20px) saturate(180%)",
      borderTop:"1px solid rgba(0,0,0,0.08)",
      display:"flex",flexShrink:0,
      // 提高图层防止内容滚到底下露出来
      position:"relative",zIndex:10,
      // NavBar 自己负责 home 指示条留白,白色背景延伸到屏幕底部。
      // 减去 16px 让图标更靠近屏幕底部 — iPhone home 指示条本身只占视觉 5px,
      // 不需要预留全部 34px,这样更像 TikTok / Instagram 的紧凑感
      paddingBottom:"max(4px, calc(env(safe-area-inset-bottom) - 16px))",
    }}>
      {tabs.map(t=>{
        if(t.isScan){
          // 中间扫一扫按钮 — 凸起的圆形,深色背景,白色 icon
          return(
            <button key={t.id} onClick={()=>onScan?.()} style={{
              flex:1,padding:"4px 0 6px",border:"none",background:"none",
              display:"flex",flexDirection:"column",alignItems:"center",gap:3,cursor:"pointer",position:"relative",
            }}>
              <div style={{
                width:48,height:48,borderRadius:14,
                background:"linear-gradient(135deg,#1a1a1a,#3a3a3a)",
                display:"flex",alignItems:"center",justifyContent:"center",
                marginTop:-14,
                boxShadow:"0 4px 14px rgba(0,0,0,0.25)",
              }}>{t.icon()}</div>
              <span style={{
                fontSize:10,letterSpacing:"0.02em",
                color:"#1a1a1a",fontFamily:FF,fontWeight:600,
                marginTop:-2,
              }}>{t.label}</span>
            </button>
          );
        }
        const active=tab===t.id;
        return(
        <button key={t.id} ref={t.id==="cart"?cartBtnRef:undefined} onClick={()=>setTab(t.id)} style={{
          flex:1,padding:"8px 0 6px",border:"none",background:"none",
          display:"flex",flexDirection:"column",alignItems:"center",gap:3,cursor:"pointer",position:"relative",
        }}>
          <div style={{
            transition:"transform 0.18s cubic-bezier(0.4,0,0.2,1)",
            transform:active?"scale(1.08)":"scale(1)",
          }}>{t.icon(active)}</div>
          {t.badge>0&&(
            <span style={{
              position:"absolute",top:4,left:"50%",marginLeft:6,
              background:"#ff3b30",color:"#fff",borderRadius:9,
              minWidth:18,height:18,padding:"0 5px",
              fontSize:10,fontWeight:700,
              display:"flex",alignItems:"center",justifyContent:"center",
              fontFamily:"-apple-system,BlinkMacSystemFont,sans-serif",
              border:"1.5px solid rgba(255,255,255,0.95)",
              boxShadow:"0 1px 2px rgba(0,0,0,0.15)",
            }}>{t.badge>99?"99+":t.badge}</span>
          )}
          <span style={{
            fontSize:10,letterSpacing:"0.02em",
            color:active?"#1a1a1a":"#9a9a9a",
            fontFamily:FF,fontWeight:active?600:500,
            transition:"color 0.18s",
          }}>{t.label}</span>
        </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CART PAGE
// ─────────────────────────────────────────────────────────────────────────────
const qBtn={width:27,height:27,borderRadius:7,border:"1.5px solid #eee",background:"#fff",cursor:"pointer",fontSize:14,color:"#555",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:FF};

function CartPage({cart,setCart,setCheckout,wishlist,setWishlist,loggedIn,setTab,onOpenProduct,products=[]}){
  const [recoCount,setRecoCount]=useState(10);
  const sentinelRef=useRef(null);
  // 按 (productId + variantId) 匹配,确保多变体购物车 +/- / 删除互不串
  const update=(id,vId,d)=>setCart(c=>c.map(i=>(i.id===id&&(i.variantId||null)===(vId||null))?{...i,qty:i.qty+d}:i).filter(i=>i.qty>0));
  const remove=(id,vId)=>setCart(c=>c.filter(i=>!(i.id===id&&(i.variantId||null)===(vId||null))));
  const totalQty=cart.reduce((a,i)=>a+i.qty,0);
  const total=cart.reduce((a,i)=>a+i.price*i.qty,0);
  const totalOrig=cart.reduce((a,i)=>a+(i.origPrice||i.price)*i.qty,0);
  const savings=totalOrig-total;
  const {addToCart, increment, StockToast} = useStockGuardedCart(cart, setCart);
  const handleWish=(id)=>{if(!loggedIn){setTab("profile");return;}setWishlist(w=>w.includes(id)?w.filter(x=>x!==id):[...w,id]);};
  useEffect(()=>{
    const s=sentinelRef.current;if(!s)return;
    const obs=new IntersectionObserver(es=>{if(es[0].isIntersecting)setRecoCount(c=>c+8);},{threshold:0.1});
    obs.observe(s);return()=>obs.disconnect();
  },[]);
  // 推荐:优先用后端个性化推荐,失败回退本地;过滤掉购物车里已有的
  const [apiRecos,setApiRecos]=useState([]);
  useEffect(()=>{
    let alive=true;
    getRecommendations({limit:12}).then(({products:rec})=>{
      if(!alive)return;
      setApiRecos((rec||[]).filter(p=>!cart.find(c=>c.id===p.id)));
    }).catch(()=>{});
    return()=>{alive=false;};
  },[loggedIn,cart.length]); // eslint-disable-line
  const baseReco=apiRecos.length>0?apiRecos:products.filter(p=>!cart.find(c=>c.id===p.id));
  const recoList=baseReco.length>0?Array.from({length:Math.min(recoCount,baseReco.length*4)},(_,i)=>({...baseReco[i%baseReco.length],_key:`r${i}`})):[];

  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#f6f7f8"}}>
      {/* Header — App container 已处理 safe-area-top */}
      <div style={{background:"#fff",padding:"14px 16px 14px",flexShrink:0,display:"flex",alignItems:"baseline",gap:8}}>
        <span style={{fontFamily:SERIF,fontSize:19,fontWeight:600,color:"#1a1a1a"}}>购物车</span>
        {totalQty>0&&<span style={{fontSize:12,color:"#888",fontFamily:FF}}>共 {totalQty} 件</span>}
      </div>

      <div style={{flex:1,overflowY:"auto",paddingBottom:cart.length>0?12:0}}>
        {cart.length===0?(
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",paddingTop:80,gap:14,paddingLeft:32,paddingRight:32}}>
            <div style={{width:88,height:88,borderRadius:"50%",background:"#f0f0ee",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="20" r="1.6"/><circle cx="18" cy="20" r="1.6"/><path d="M2 3h2.5l2.7 12.2a1.6 1.6 0 001.6 1.3h9.1a1.6 1.6 0 001.55-1.2L21 7H6"/></svg>
            </div>
            <div style={{fontSize:14,color:"#888",fontFamily:FF}}>购物车空空的</div>
            <button onClick={()=>setTab("home")} style={{marginTop:6,background:"#1a1a1a",color:"#fff",border:"none",borderRadius:24,padding:"11px 32px",fontSize:13,cursor:"pointer",fontFamily:FF,fontWeight:500}}>去逛逛</button>
          </div>
        ):(
          <div style={{padding:"12px 12px 0"}}>
            {cart.map((item,idx)=>{
              const discount=item.origPrice&&item.origPrice>item.price?Math.round((1-item.price/item.origPrice)*100):0;
              return(
                <div key={`${item.id}-${item.variantId||""}-${idx}`} style={{background:"#fff",borderRadius:14,padding:12,display:"flex",gap:12,marginBottom:10,boxShadow:"0 1px 3px rgba(0,0,0,0.04)",position:"relative"}}>
                  <div style={{width:84,height:84,flexShrink:0,borderRadius:10,overflow:"hidden",position:"relative",cursor:onOpenProduct?"pointer":"default"}} onClick={()=>onOpenProduct?.(item)}>
                    <ProductImage img={item.img} imgUrl={item.imgUrl} name={item.name} aspect={1}/>
                    {discount>0&&<span style={{position:"absolute",top:4,left:4,background:"#e03030",color:"#fff",fontSize:9.5,fontWeight:600,padding:"1px 5px",borderRadius:3,fontFamily:FF}}>{discount}% off</span>}
                  </div>
                  <div style={{flex:1,display:"flex",flexDirection:"column",justifyContent:"space-between",minWidth:0}}>
                    <div>
                      <div style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,lineHeight:1.4,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{item.name}</div>
                      {item.sub&&<div style={{fontSize:11,color:"#aaa",fontFamily:FF,marginTop:3,background:"#f5f5f4",display:"inline-block",padding:"2px 7px",borderRadius:3}}>{item.sub}</div>}
                    </div>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:4}}>
                      <div style={{display:"flex",alignItems:"baseline",gap:5}}>
                        <span style={{fontSize:16,fontWeight:700,color:"#e03030",fontFamily:FF,letterSpacing:"-0.01em"}}>${item.price}</span>
                        {item.origPrice&&<span style={{fontSize:11,color:"#bbb",textDecoration:"line-through",fontFamily:FF}}>${item.origPrice}</span>}
                      </div>
                      <div style={{display:"flex",alignItems:"center",background:"#f5f5f4",borderRadius:14,overflow:"hidden"}}>
                        <button onClick={()=>update(item.id,item.variantId,-1)} style={{width:26,height:26,background:"none",border:"none",cursor:"pointer",fontSize:14,color:"#666",fontFamily:FF,display:"flex",alignItems:"center",justifyContent:"center"}}>−</button>
                        <span style={{minWidth:22,textAlign:"center",fontSize:13,fontFamily:FF,color:"#1a1a1a",fontWeight:500}}>{item.qty}</span>
                        <button onClick={()=>increment(item.id,item.variantId)} style={{width:26,height:26,background:"none",border:"none",cursor:"pointer",fontSize:14,color:"#666",fontFamily:FF,display:"flex",alignItems:"center",justifyContent:"center"}}>+</button>
                      </div>
                    </div>
                  </div>
                  <button onClick={()=>remove(item.id,item.variantId)} style={{position:"absolute",top:8,right:8,background:"none",border:"none",cursor:"pointer",padding:4,color:"#ccc"}} aria-label="删除">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* 推荐 */}
        <div style={{padding:"4px 0 0"}}>
          <div style={{padding:"14px 14px 8px",display:"flex",alignItems:"center",gap:10}}>
            <div style={{flex:1,height:1,background:"#e8e8e8"}}/>
            <span style={{fontFamily:FF,fontSize:13,color:"#888",fontWeight:500}}>{cart.length>0?"猜你喜欢":"为你推荐"}</span>
            <div style={{flex:1,height:1,background:"#e8e8e8"}}/>
          </div>
          <MasonryGrid products={recoList} wishlist={wishlist} onWish={handleWish} onAdd={addToCart} onOpen={onOpenProduct} keyPrefix="cart-reco-"/>
          <div ref={sentinelRef} style={{height:50,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <div style={{display:"flex",gap:6}}>{[0,1,2].map(i=><div key={i} style={{width:5,height:5,borderRadius:"50%",background:"#d8d8d8",animation:`dp 1.2s ease-in-out ${i*0.2}s infinite`}}/>)}</div>
          </div>
        </div>
      </div>

      {cart.length>0&&(
        <div style={{background:"#fff",padding:"12px 14px 18px",borderTop:"1px solid #f0f0f0",flexShrink:0,display:"flex",alignItems:"center",gap:12,boxShadow:"0 -2px 8px rgba(0,0,0,0.04)"}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:"flex",alignItems:"baseline",gap:5}}>
              <span style={{fontSize:11,color:"#888",fontFamily:FF}}>合计</span>
              <span style={{fontSize:20,fontWeight:700,color:"#e03030",fontFamily:FF,letterSpacing:"-0.01em"}}>${total.toFixed(2)}</span>
            </div>
            {savings>0&&<div style={{fontSize:11,color:"#3a9e3a",fontFamily:FF,marginTop:1}}>已省 ${savings.toFixed(2)}</div>}
          </div>
          <button onClick={()=>setCheckout(true)} style={{background:"linear-gradient(135deg,#e03030,#c42020)",color:"#fff",border:"none",borderRadius:24,padding:"13px 32px",fontSize:14,fontFamily:FF,fontWeight:600,cursor:"pointer",boxShadow:"0 4px 12px rgba(224,48,48,0.3)",letterSpacing:"0.04em"}}>去结账 ({totalQty})</button>
        </div>
      )}
      <style>{`@keyframes dp{0%,100%{opacity:.3;transform:scale(1)}50%{opacity:1;transform:scale(1.3)}}@keyframes pillScroll0{from{transform:translateX(0)}to{transform:translateX(-33.3333%)}}@keyframes pillScroll1{from{transform:translateX(-33.3333%)}to{transform:translateX(0)}}`}</style>
      {StockToast}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CHECKOUT  — full multi-step with address list, payment list, smart defaults
// ─────────────────────────────────────────────────────────────────────────────
function CheckoutPage({cart,setCart,setCheckout,setTab}){
  // 已保存地址 — 登录用户进结算时拉云端
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [savedPayments,  setSavedPayments]  = useState([]);
  // 没登录的话云端接口都会 401 — 直接跳过,避免控制台一片红字
  const isAuthed = (() => { try { return !!localStorage.getItem("syj_token"); } catch { return false; } })();
  useEffect(()=>{
    if(!isAuthed)return;
    let alive=true;
    getAddresses().then(list=>{
      if(!alive)return;
      setSavedAddresses(list||[]);
      // 自动选默认地址,有就当前选中
      const def=(list||[]).find(a=>a.isDefault)||(list||[])[0];
      if(def)setSelAddr(s=>s||def.id);
    }).catch(()=>{}); // 已经 isAuthed 检查过,这里再 catch 兜底
    return()=>{alive=false;};
  },[isAuthed]);

  // Checkout state
  const [step,     setStep]    = useState("main"); // main|addrList|addrNew|payList|payNew|addCardForm
  // 游客用户在 addCardForm 输完的卡 — 没保存到 Stripe Customer(因为没登录),
  // 只是用 createPaymentMethod 拿到一次性 pm_id,提交订单时立刻消费
  const [inputCard, setInputCard] = useState(null); // {pm_id, brand, last4, exp_month, exp_year}
  const [delivery, setDelivery]= useState("pickup");
  const [selAddr,  setSelAddr] = useState(null);
  const [newAddr,  setNewAddr] = useState({name:"",phone:"",line1:"",apt:"",city:"",state:"CA",zip:""});
  const [editingAddrId, setEditingAddrId] = useState(null); // null = 新建,id = 编辑这条
  const [selPay,   setSelPay]  = useState(null);
  const [smsConsent, setSmsConsent] = useState(false);   // SMS 合规：必须勾选才能下单
  const [coupon,    setCoupon]    = useState("");
  const [couponOk,  setCouponOk]  = useState(false); // 校验通过且已应用
  const [couponData,setCouponData]= useState(null);  // 后端返回的 { coupon, discount, eligible_subtotal }
  const [couponErr, setCouponErr] = useState("");
  const [couponBusy,setCouponBusy]= useState(false);
  // Stripe 支付状态 — 创建好 PI 后弹卡片输入框
  const [stripeFlow,setStripeFlow]= useState(null); // { orderId, clientSecret, payMethodId } | null
  const [myCoupons, setMyCoupons] = useState([]);
  const [showCouponPicker, setShowCouponPicker] = useState(false);
  // 已保存的卡 — 登录后自动拉
  const [savedCards, setSavedCards] = useState([]);
  // 选中要用的已存卡 id;null = 用新卡(走 PaymentElement)
  const [selectedSavedPmId, setSelectedSavedPmId] = useState(null);
  // 卡选择条是否展开(savedCards > 1 时点「更换」展开)
  const [showCardPicker, setShowCardPicker] = useState(false);
  // 一键支付中(用已存卡)
  const [oneTapBusy, setOneTapBusy] = useState(false);
  // Apple Pay — 用 Stripe paymentRequest API,只在浏览器+设备+Stripe 都支持时按钮才出现
  const [applePay, setApplePay] = useState(null); // {stripe, pr} | null

  // 进入结账时拉钱包 + 已保存的卡 — 仅登录用户(没 token 必然 401)
  useEffect(()=>{
    if(!isAuthed)return;
    let alive=true;
    getMyCoupons()
      .then(d=>{ if(alive)setMyCoupons(d||[]); })
      .catch(()=>{});
    getSavedPaymentMethods()
      .then(d=>{
        if(!alive)return;
        const list = d || [];
        setSavedCards(list);
        // 自动选中默认卡(或第一张)+ 默认进入「银行卡」支付模式 — 这样
        // 用户进结算页银行卡 pill 直接是激活状态,显示卡号尾号
        const def = list.find(c=>c.is_default) || list[0];
        if(def){
          setSelectedSavedPmId(def.id);
          setSelPay("credit");
        }
      })
      .catch(()=>{});
    return()=>{alive=false;};
  },[isAuthed]);
  const [tip,      setTip]     = useState(0);
  const [done,     setDone]    = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr,  setSubmitErr]  = useState(null);
  const [doneOrder,  setDoneOrder]  = useState(null);
  // pickup 模式联系信息(到店自取没收货地址,但要联系方式以便备货完成通知)
  const storedUser = (() => { try { return loadStoredUser(); } catch { return null; } })();
  const [pickupName,  setPickupName]  = useState(storedUser?.name || "");
  const [pickupPhone, setPickupPhone] = useState(storedUser?.phone || "");

  // submitErr 5 秒后自动淡化
  useEffect(()=>{
    if(!submitErr)return;
    const t=setTimeout(()=>setSubmitErr(null),5000);
    return()=>clearTimeout(t);
  },[submitErr]);

  // 切换配送方式时,如果当前选的是 COD 但配送不再是 delivery,自动 fallback
  useEffect(()=>{
    if(delivery!=="delivery"&&selPay==="cod"){
      // 优先 fallback 到默认 / 第一张保存的卡
      const defCard=savedCards.find(c=>c.is_default)||savedCards[0];
      if(defCard){
        setSelPay(null);
        setSelectedSavedPmId(defCard.id);
        return;
      }
      // 没保存卡 → 看是否有非 deliveryOnly 的本地支付
      const fallback=savedPayments.find(sp=>{
        const m=PAYMENT_METHODS.find(x=>x.id===sp.id);
        return m&&!m.deliveryOnly;
      });
      setSelPay(fallback?.id||null);
    }
  },[delivery,selPay,savedPayments,savedCards]);
  const [geoLoad,  setGeoLoad] = useState(false);
  const [geoMsg,   setGeoMsg]  = useState(null);
  const [taxRate,  setTaxRate] = useState(0);
  // 自动定位回填后用这个 token 让 AddressAutocompleteInput 跳过下次 autocomplete
  const [addrSuppressToken,setAddrSuppressToken] = useState(0);

  const subtotal  = cart.reduce((a,i)=>a+i.price*i.qty,0);
  // 仅非特价商品计入包邮门槛
  const nonSpecialSubtotal = cart.reduce((a,i)=>a+(i.isSpecial?0:i.price*i.qty),0);
  const eligibleFreeShip = nonSpecialSubtotal >= FREE_SHIP_THRESHOLD;
  const remainingForFreeShip = Math.max(0, FREE_SHIP_THRESHOLD - nonSpecialSubtotal);

  // 当前选中地址(后面会再次定义,这里先取一次以便算运费)
  const _curAddr = selAddr ? savedAddresses.find(a=>a.id===selAddr) : savedAddresses[0]??null;

  // 运费 + 是否阻塞结账
  const computeShipping = ()=>{
    if(delivery==="pickup")return{fee:0,note:"免运费",error:null};
    if(!_curAddr)return{fee:0,note:"请添加地址",error:null};
    if(delivery==="delivery"){
      // 送货上门必须 ≥ $100,否则强制改自取或邮寄
      if(!eligibleFreeShip){
        return{fee:0,note:`满$100起送`,error:`满 $100 可送货上门,还差 $${remainingForFreeShip.toFixed(2)},请继续选购`};
      }
      // 用统一的判断:邮编归一化(去除 zip+4 后缀)→ 不命中再查城市名
      if(!isDeliverableAddress(_curAddr)){
        return{fee:0,note:"超出配送范围",error:"您所在区域暂不支持送货上门(支持 Monterey Park / Rosemead / San Gabriel)"};
      }
      return{fee:0,note:"满 $100 免费送货",error:null};
    }
    // mail
    if(eligibleFreeShip)return{fee:0,note:"满 $100 包邮",error:null};
    const isCA=(_curAddr.state||"").toUpperCase()==="CA";
    const fee=isCA?SHIP_CA:SHIP_OTHER;
    return{fee,note:isCA?`本州 (CA) $${fee}`:`外州 $${fee}`,error:null};
  };
  const ship = computeShipping();
  const shipping = ship.fee;
  const shipError = ship.error;
  const shipNote = ship.note;

  const discountAmt = couponOk && couponData ? parseFloat(couponData.discount) || 0 : 0;
  const tax = subtotal * taxRate;
  // 小费仅送货上门生效;自取/邮寄不计
  const effectiveTip = delivery==="delivery"?tip:0;
  const grandTotal = Math.max(0, subtotal + shipping + tax - discountAmt + effectiveTip);

  // 地址或配送方式变化时,异步重新拉取销售税率
  useEffect(()=>{
    let cancelled=false;
    let zip,state;
    if(delivery==="pickup"){zip="91754";state="CA";} // 自取按门店地址
    else if(_curAddr){zip=_curAddr.zip;state=_curAddr.state;}
    else{setTaxRate(0);return;}
    getTaxRate({zip,state}).then(r=>{if(!cancelled)setTaxRate(r);});
    return()=>{cancelled=true;};
  },[delivery,_curAddr?.zip,_curAddr?.state]);// eslint-disable-line

  // ── Apple Pay setup ─────────────────────────────────────────────────────
  // 优先用原生 SyjApplePay 插件(STPApplePayContext + PassKit,真原生)
  // 没有原生插件(web 浏览器)→ 回退到 Stripe paymentRequest API(Safari only)
  useEffect(()=>{
    let alive = true;
    const native = window.__syjApplePay;
    if (native) {
      native.canMakePayments().then(r => {
        if (alive && r?.canMakePayments && r?.hasCard) setApplePay({ native: true });
      }).catch(()=>{});
      return () => { alive = false; };
    }
    if (!stripePromise) return;
    stripePromise.then(stripe => {
      if (!stripe || !alive) return;
      const pr = stripe.paymentRequest({
        country: 'US', currency: 'usd',
        total: { label: '十一家', amount: 100 },
        requestPayerName: false, requestPayerEmail: false, requestShipping: false,
      });
      pr.canMakePayment().then(result => {
        if (alive && result?.applePay) setApplePay({ stripe, pr });
      }).catch(()=>{});
    });
    return () => { alive = false; };
  },[]);

  // grandTotal 变化时同步给 paymentRequest
  useEffect(()=>{
    if(!applePay||grandTotal<=0)return;
    try{
      applePay.pr.update({total:{label:"十一家",amount:Math.round(grandTotal*100)}});
    }catch{}
  },[grandTotal,applePay]);

  // 兼容 a.line1(标准)和 a.street(旧)两种字段命名,避免显示 "undefined"
  const addrLabel = (a)=>{
    const line=a.line1||a.street||"";
    const parts=[
      `${line}${a.apt?` #${a.apt}`:""}`,
      a.city||"",
      `${a.state||""} ${a.zip||""}`.trim(),
    ].filter(Boolean);
    return parts.join(", ");
  };
  const payMethod = (id)=>PAYMENT_METHODS.find(m=>m.id===id);

  // 构造下单 payload — 给 submitOrder 和 Apple Pay 共用
  const buildOrderPayload = (payMethodId)=>{
    const isPickup = delivery==="pickup";
    return {
      customer_name: isPickup ? (pickupName||"").trim() : (_curAddr?.name || ""),
      customer_phone: isPickup ? (pickupPhone||"").trim() : (_curAddr?.phone || ""),
      customer_email: "",
      delivery_method: delivery,
      delivery_address: !isPickup && _curAddr ? {
        name: _curAddr.name,
        phone: _curAddr.phone,
        line1: _curAddr.line1,
        apt: _curAddr.apt || "",
        city: _curAddr.city,
        state: _curAddr.state,
        zip: _curAddr.zip,
      } : null,
      pay_method: payMethodId,
      items: cart.map(it=>({
        product_id: it.variantId ? null : it.id,
        variant_id: it.variantId || null,
        qty: it.qty,
        unit_price: it.price,
        name: it.name,
      })),
      subtotal,
      shipping_fee: shipping,
      tax_amount: tax,
      tip_amount: effectiveTip,
      discount_amount: discountAmt,
      coupon_code: couponOk && couponData ? couponData.coupon.code : null,
      total: grandTotal,
      note: "",
    };
  };

  // Apple Pay 支付流程 — paymentRequest event handler
  const handleApplePay = async ()=>{
    if(!applePay||submitting||cart.length===0)return;
    if(delivery!=="pickup"&&!_curAddr){setSubmitErr("请先选择收货地址");return;}
    if(delivery==="pickup"&&!pickupPhone?.trim()){setSubmitErr("请填写联系电话");return;}
    if(shipError){setSubmitErr(shipError);return;}

    // 模式 A:原生 Apple Pay 插件(STPApplePayContext via Swift)
    if (applePay.native && window.__syjApplePay) {
      setSubmitting(true);setSubmitErr(null);
      // 关键:用 outer-scope 变量保存订单 id,catch 块要用它取消孤儿订单
      // 不然用户取消 Apple Pay 表单后,订单留在 pending_payment → "待付款"+1 bug
      let createdOrderId = null;
      let paymentSucceeded = false;
      try {
        const pk = await awaitStripePk(8000);
        if (!pk) throw new Error("Stripe 配置加载失败,请检查网络后重试");
        const payload = buildOrderPayload("apple_pay");
        const order = await createOnlineOrder(payload);
        createdOrderId = order.id;
        const pi = await createStripePaymentIntent(order.id);
        await window.__syjApplePay.present({
          publishableKey: pk,
          clientSecret: pi.clientSecret,
          amount: grandTotal,
          label: '十一家订单',
          merchantIdentifier: 'merchant.com.us198.app',
        });
        const paid = await confirmOrderPayment(order.id, {
          payment_intent_id: pi.paymentIntentId || '',
          payment_method: 'apple_pay',
        });
        paymentSucceeded = true;
        setCart([]);
        setDoneOrder({...paid, pay_method: 'apple_pay'});
        setDone(true);
      } catch (err) {
        const msg = err?.message || '';
        if (!/USER_CANCELLED|用户取消/.test(msg)) {
          setSubmitErr(msg || 'Apple Pay 失败,请重试');
        }
      } finally {
        // 失败 / 取消 → 取消订单,不让它留在"待付款"里污染数据
        if (createdOrderId && !paymentSucceeded) {
          cancelOrder(createdOrderId).catch(()=>{});
        }
        setSubmitting(false);
      }
      return;
    }

    // 模式 B:web Stripe paymentRequest(Safari only)
    try{
      applePay.pr.update({total:{label:"十一家",amount:Math.round(grandTotal*100)}});
    }catch{}

    const handler = async (ev)=>{
      applePay.pr.off("paymentmethod",handler);
      setSubmitting(true);setSubmitErr(null);
      let createdOrderId = null;
      let paymentSucceeded = false;
      try{
        const payload = buildOrderPayload("apple_pay");
        const order = await createOnlineOrder(payload);
        createdOrderId = order.id;
        const pi = await createStripePaymentIntent(order.id);
        const {error,paymentIntent} = await applePay.stripe.confirmCardPayment(
          pi.clientSecret,
          {payment_method: ev.paymentMethod.id},
          {handleActions: false}
        );
        if(error){
          ev.complete("fail");
          throw new Error(error.message||"支付失败");
        }
        ev.complete("success");
        const paid = await confirmOrderPayment(order.id,{
          payment_intent_id: paymentIntent.id,
          payment_method: "apple_pay",
        });
        paymentSucceeded = true;
        setCart([]);
        setDoneOrder({...paid, pay_method:"apple_pay"});
        setDone(true);
      }catch(err){
        try{ev.complete("fail");}catch{}
        setSubmitErr(err.message||"Apple Pay 失败,请重试或选其他支付方式");
      }finally{
        if (createdOrderId && !paymentSucceeded) {
          cancelOrder(createdOrderId).catch(()=>{});
        }
        setSubmitting(false);
      }
    };
    applePay.pr.on("paymentmethod",handler);
    applePay.pr.show();
  };

  // 提交订单 — 真实调后端
  const submitOrder = async ()=>{
    if(submitting||cart.length===0)return;
    setSubmitting(true);setSubmitErr(null);
    // 跟踪本次创建的订单和是否最终成功 — 失败时要取消孤儿订单
    let createdOrderId = null;
    let paymentSucceeded = false;
    let openedStripeFlow = false;
    try{
      // 1. 构造订单 payload
      const _curAddrSnapshot = curAddr; // 锁定 closure
      const _curPaySnapshot = curPay;
      // pickup 用 pickupName/pickupPhone;送货/邮寄用收货地址里的信息
      const isPickup = delivery==="pickup";
      const payload = {
        customer_name: isPickup ? (pickupName||"").trim() : (_curAddrSnapshot?.name || ""),
        customer_phone: isPickup ? (pickupPhone||"").trim() : (_curAddrSnapshot?.phone || ""),
        customer_email: "",
        delivery_method: delivery, // pickup | delivery | mail
        delivery_address: !isPickup && _curAddrSnapshot ? {
          name: _curAddrSnapshot.name,
          phone: _curAddrSnapshot.phone,
          line1: _curAddrSnapshot.line1,
          apt: _curAddrSnapshot.apt || "",
          city: _curAddrSnapshot.city,
          state: _curAddrSnapshot.state,
          zip: _curAddrSnapshot.zip,
        } : null,
        pay_method: _curPaySnapshot?.id || "card",
        items: cart.map(it=>({
          product_id: it.variantId ? null : it.id,
          variant_id: it.variantId || null,
          qty: it.qty,
          unit_price: it.price,
          name: it.name,
        })),
        subtotal,
        shipping_fee: shipping,
        tax_amount: tax,
        tip_amount: effectiveTip,
        discount_amount: discountAmt,
        coupon_code: couponOk && couponData ? couponData.coupon.code : null,
        total: grandTotal,
        note: "",
      };

      // 2. 创建订单(pending_payment,不扣库存)
      const order = await createOnlineOrder(payload);
      createdOrderId = order.id;
      const payMethodId = _curPaySnapshot?.id || "card";

      // 3a. COD(送货上门 + 现金当面付款)→ 不走支付,直接 confirm
      if (payMethodId === "cod") {
        const paid = await confirmOrderPayment(order.id, { payment_method: "cod" });
        paymentSucceeded = true;
        setCart([]);
        setDoneOrder({...paid, pay_method: payMethodId});
        setDone(true);
        return;
      }

      // 3b-0. 游客本次输入的新卡(inputCard.pm_id)→ 创建 PI 用 pm_id 直接 confirm
      if (inputCard?.pm_id && stripePromise) {
        const pi = await createStripePaymentIntent(order.id);
        const stripe = await stripePromise;
        const { error: payErr, paymentIntent } = await stripe.confirmCardPayment(pi.clientSecret, {
          payment_method: inputCard.pm_id,
        });
        if (payErr) throw new Error(payErr.message || "支付失败");
        if (paymentIntent?.status !== "succeeded") {
          throw new Error(`支付状态异常: ${paymentIntent?.status || "unknown"}`);
        }
        const paid = await confirmOrderPayment(order.id, {
          payment_intent_id: paymentIntent.id,
          payment_method: payMethodId,
        });
        paymentSucceeded = true;
        setCart([]);
        setInputCard(null);
        setDoneOrder({ ...paid, pay_method: payMethodId });
        setDone(true);
        return;
      }

      // 3b-1. 选了已保存的卡 → 一键 off-session 支付
      if (selectedSavedPmId && stripePromise) {
        const r = await chargeSavedCard(order.id, selectedSavedPmId);
        if (r.ok) {
          // 一键成功 → confirm 订单
          const paid = await confirmOrderPayment(order.id, {
            payment_intent_id: r.data.paymentIntentId,
            payment_method: payMethodId,
          });
          paymentSucceeded = true;
          setCart([]);
          setDoneOrder({...paid, pay_method: payMethodId});
          setDone(true);
          return;
        }
        if (r.requires_action && r.clientSecret) {
          // 3DS 验证 → 退回 PaymentElement 流程让用户输验证码
          setStripeFlow({
            orderId: order.id,
            clientSecret: r.clientSecret,
            payMethodId,
            from3ds: true,
          });
          openedStripeFlow = true;
          return;
        }
        // 一键失败(卡过期/余额不足等)→ 提示后退回新卡输入
        throw new Error(r.error || "已存卡支付失败,请尝试其他卡");
      }

      // 3b-2. 没选已存卡 → 创建新 PaymentIntent + 弹 PaymentElement
      if (stripePromise) {
        try {
          const pi = await createStripePaymentIntent(order.id);
          setStripeFlow({
            orderId: order.id,
            clientSecret: pi.clientSecret,
            payMethodId,
          });
          openedStripeFlow = true;
          return;
        } catch (e) {
          throw new Error("支付初始化失败:" + (e.message || "请稍后重试"));
        }
      }

      // 3c. 没配 Stripe → 不允许跳过支付(防止白嫖)。提示用户重启或联系客服
      throw new Error(`支付服务暂不可用,请重启 APP 或联系客服(${CONTACT_PHONE_DISPLAY})`);
    }catch(err){
      setSubmitErr(err.message || "下单失败,请稍后重试");
    }finally{
      // 失败且没进入 Stripe 弹窗(那个有自己的 onClose cancelOrder) → 直接取消订单
      // 防止留在 "待付款" 列表里污染数据
      if (createdOrderId && !paymentSucceeded && !openedStripeFlow) {
        cancelOrder(createdOrderId).catch(()=>{});
      }
      setSubmitting(false);
    }
  };

  const saveNewAddr = async()=>{
    if(!newAddr.name||!newAddr.phone||!newAddr.line1||!newAddr.city||!newAddr.zip)return;
    if(editingAddrId){
      // 编辑模式 — 调 updateAddress
      let entry={...newAddr,id:editingAddrId};
      try{
        const updated=await updateAddress(editingAddrId,{...newAddr,street:newAddr.line1});
        if(updated)entry={...updated,line1:updated.street||updated.line1};
      }catch{}
      setSavedAddresses(prev=>prev.map(a=>a.id===editingAddrId?entry:a));
      setSelAddr(entry.id);
    }else{
      // 新建模式
      let entry={...newAddr,id:Date.now()};
      try{
        const created=await createAddress({...newAddr,street:newAddr.line1});
        if(created)entry={...created,line1:created.street||created.line1};
      }catch{}
      setSavedAddresses(prev=>[...prev,entry]);
      setSelAddr(entry.id);
    }
    setNewAddr({name:"",phone:"",line1:"",apt:"",city:"",state:"CA",zip:""});
    setEditingAddrId(null);
    setStep(savedAddresses.length>0||editingAddrId?"addrList":"main");
  };

  // 获取当前位置 → Google reverse-geocode 自动填入地址
  const useMyLocation = ()=>{
    const hasGeo=("geolocation" in navigator)||window.__syjGetLocation;
    if(!hasGeo){setGeoMsg("当前设备不支持定位");return;}
    setGeoLoad(true);setGeoMsg(null);
    const onSuccess=async pos=>{
      try{
        const detail=await mapsReverseGeocode(pos.coords.latitude,pos.coords.longitude);
        if(detail&&(detail.line1||detail.zip||detail.city)){
          setNewAddr(a=>({
            ...a,
            line1: detail.line1 || a.line1,
            city:  detail.city  || a.city,
            state: detail.state || a.state || "CA",
            zip:   detail.zip   || a.zip,
          }));
          setAddrSuppressToken(t=>t+1);
          setGeoMsg("已自动填入,请检查并补充门牌号(若有)");
        }else{
          setGeoMsg("无法识别您的位置,请手动输入");
        }
      }catch{
        setGeoMsg("地址解析失败,请手动输入");
      }
      setGeoLoad(false);
      setTimeout(()=>setGeoMsg(null),5000);
    };
    const onError=err=>{
      setGeoLoad(false);
      setGeoMsg(err?.code===1?"定位权限未开启,请在系统设置中允许":"无法获取定位,请手动输入");
      setTimeout(()=>setGeoMsg(null),5000);
    };
    const fn=navigator.geolocation?.getCurrentPosition?.bind(navigator.geolocation)||window.__syjGetLocation;
    fn(onSuccess,onError,{timeout:12000,enableHighAccuracy:false});
  };

  // ── Done screen ───────────────────────────────────────────────────────────
  if(done){
    const isCod = doneOrder?.pay_method==="cod";
    const isPickup = doneOrder?.delivery_method==="pickup";
    const isDelivery = doneOrder?.delivery_method==="delivery";
    // 温暖 / 个性化的副标题
    const heroSub = isCod
      ? "我们将尽快为您备货,送达时见 ♡"
      : isPickup
      ? "我们已收到您的订单,稍后会通知您到店取货"
      : isDelivery
      ? "我们将尽快为您安排配送,期待与您相见"
      : "我们已收到您的订单,正在为您备货 ♡";

    return(
      <div style={{height:"100%",overflowY:"auto",background:"linear-gradient(180deg,#fff5f0 0%,#fff 35%,#fff 100%)"}}>
        <div style={{minHeight:"100%",display:"flex",flexDirection:"column",alignItems:"center",padding:"60px 24px 40px"}}>

          {/* 庆祝图标 — 礼盒配星星 */}
          <div style={{position:"relative",marginBottom:22}}>
            <div style={{
              width:96,height:96,borderRadius:"50%",
              background:"linear-gradient(135deg,#ff9d6c,#e03030)",
              display:"flex",alignItems:"center",justifyContent:"center",
              boxShadow:"0 12px 32px rgba(224,48,48,0.28)",
              animation:"successPop 0.6s cubic-bezier(0.34,1.56,0.64,1)",
            }}>
              <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            {/* 装饰星 */}
            <div style={{position:"absolute",top:-8,right:-12,fontSize:18,opacity:0.85}}>✨</div>
            <div style={{position:"absolute",bottom:0,left:-10,fontSize:14,opacity:0.7}}>✨</div>
            <div style={{position:"absolute",top:14,left:-18,fontSize:11,opacity:0.6}}>✦</div>
          </div>

          {/* 主标题 */}
          <div style={{fontFamily:SERIF,fontSize:24,color:"#1a1a1a",fontWeight:700,letterSpacing:"0.02em",marginBottom:6}}>下单成功</div>
          <div style={{fontFamily:FF,fontSize:13.5,color:"#666",marginBottom:6,lineHeight:1.6,textAlign:"center"}}>感谢您光临十一家</div>
          <div style={{fontFamily:FF,fontSize:12.5,color:"#999",lineHeight:1.7,textAlign:"center",marginBottom:24,maxWidth:300}}>{heroSub}</div>

          {/* 订单号卡片 */}
          {doneOrder?.order_num&&(
            <div style={{
              width:"100%",maxWidth:340,
              background:"#fff",
              border:"1px solid #fde8d8",
              borderRadius:14,
              padding:"16px 18px",
              marginBottom:14,
              boxShadow:"0 2px 12px rgba(255,180,140,0.08)",
              display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,
            }}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:10.5,color:"#aaa",fontFamily:FF,letterSpacing:"0.06em",marginBottom:5}}>订单编号</div>
                <div style={{fontSize:14,color:"#1a1a1a",fontFamily:"'SF Mono','Menlo','Consolas',monospace",fontWeight:600,letterSpacing:"0.04em",wordBreak:"break-all"}}>{doneOrder.order_num}</div>
              </div>
              <button
                onClick={()=>{
                  if(navigator.clipboard?.writeText){
                    navigator.clipboard.writeText(doneOrder.order_num).catch(()=>{});
                  }
                }}
                style={{
                  flexShrink:0,
                  background:"#fff5f0",border:"1px solid #ffd0b8",
                  color:"#e03030",fontSize:11.5,fontFamily:FF,fontWeight:500,
                  padding:"6px 12px",borderRadius:8,cursor:"pointer",
                }}
                aria-label="复制订单号"
              >复制</button>
            </div>
          )}

          {/* 金额 */}
          {doneOrder?.total!=null&&(
            <div style={{
              width:"100%",maxWidth:340,
              background:"linear-gradient(135deg,#1a1a1a,#333)",
              borderRadius:14,
              padding:"16px 18px",
              marginBottom:14,
              color:"#fff",
              display:"flex",alignItems:"center",justifyContent:"space-between",
              boxShadow:"0 4px 14px rgba(0,0,0,0.12)",
            }}>
              <div>
                <div style={{fontSize:10.5,color:"rgba(255,255,255,0.6)",fontFamily:FF,letterSpacing:"0.06em",marginBottom:4}}>{isCod?"待支付":"已支付"}</div>
                <div style={{fontFamily:SERIF,fontSize:22,fontWeight:700}}>${parseFloat(doneOrder.total||0).toFixed(2)}</div>
              </div>
              <div style={{
                background:"rgba(255,255,255,0.12)",
                padding:"5px 12px",borderRadius:14,
                fontSize:11,fontFamily:FF,
              }}>
                {isCod?"💵 现金":"💳 已结款"}
              </div>
            </div>
          )}

          {/* COD 提示 */}
          {isCod&&(
            <div style={{
              width:"100%",maxWidth:340,
              background:"#fff8e1",border:"1px solid #ffe082",
              borderRadius:12,padding:"12px 14px",
              fontSize:11.5,color:"#7a5a10",fontFamily:FF,
              lineHeight:1.7,marginBottom:14,
            }}>
              💵 <span style={{fontWeight:600}}>请准备好现金 ${parseFloat(doneOrder.total||0).toFixed(2)}</span>
              <br/><span style={{color:"#a07a3a",fontSize:11}}>送达时配送员会向您收取</span>
            </div>
          )}

          {/* 游客提示 — 没登录的用户在订单完成页提示注册/登录 */}
          {!isAuthed && (
            <div style={{
              width:"100%",maxWidth:340,
              background:"linear-gradient(135deg,#fff8e1,#fef3c7)",
              border:"1px solid #fde68a",
              borderRadius:12,padding:"14px 16px",marginBottom:14,
              display:"flex",alignItems:"flex-start",gap:10,
            }}>
              <span style={{fontSize:18,flexShrink:0}}>💡</span>
              <div style={{flex:1,fontSize:12,color:"#7a5a10",fontFamily:FF,lineHeight:1.6}}>
                <div style={{fontWeight:600,marginBottom:3}}>注册账号 · 一键查单</div>
                <div>登录后可保存订单到您的账户,随时查询物流状态、获取消费积分,以及在「我的订单」里申请退换货。</div>
              </div>
            </div>
          )}

          {/* 下一步操作 */}
          <div style={{width:"100%",maxWidth:340,marginTop:isAuthed?20:0,display:"flex",flexDirection:"column",gap:10}}>
            {!isAuthed && (
              <button
                onClick={()=>{
                  setCheckout(false);
                  setTab?.("profile");  // profile tab 自动展示登录界面
                }}
                style={{
                  width:"100%",
                  background:"linear-gradient(135deg,#e03030,#c42020)",
                  color:"#fff",border:"none",
                  padding:"14px 0",borderRadius:12,
                  fontSize:14,fontFamily:FF,fontWeight:600,letterSpacing:"0.04em",
                  cursor:"pointer",
                  boxShadow:"0 4px 12px rgba(224,48,48,0.25)",
                }}
              >立即注册 / 登录</button>
            )}
            <button
              onClick={()=>{setCheckout(false);}}
              style={{
                width:"100%",
                background:isAuthed?"linear-gradient(135deg,#1a1a1a,#333)":"#fff",
                color:isAuthed?"#fff":"#1a1a1a",
                border:isAuthed?"none":"1.5px solid #1a1a1a",
                padding:isAuthed?"14px 0":"13px 0",borderRadius:12,
                fontSize:isAuthed?14:13.5,fontFamily:FF,fontWeight:isAuthed?600:500,letterSpacing:"0.04em",
                cursor:"pointer",
                boxShadow:isAuthed?"0 4px 12px rgba(0,0,0,0.15)":"none",
              }}
            >继续逛逛</button>
            {isAuthed && (
              <button
                onClick={()=>{
                  setCheckout(false);
                  setTab?.("profile");
                }}
                style={{
                  width:"100%",
                  background:"#fff",
                  color:"#1a1a1a",border:"1.5px solid #1a1a1a",
                  padding:"13px 0",borderRadius:12,
                  fontSize:13.5,fontFamily:FF,fontWeight:500,
                  cursor:"pointer",
                }}
              >查看我的订单</button>
            )}
          </div>

          {/* 底部小温馨提示 */}
          <div style={{
            marginTop:32,padding:"12px 16px",
            background:"#f9f9f7",borderRadius:10,
            fontSize:11,color:"#999",fontFamily:FF,
            lineHeight:1.7,textAlign:"center",maxWidth:300,
          }}>
            如有疑问,请到「我的 → 联系客服」<br/>
            十一家客服{CONTACT_HOURS}在线
          </div>
        </div>

        <style>{`
          @keyframes successPop {
            0% { transform: scale(0); opacity: 0; }
            60% { transform: scale(1.1); opacity: 1; }
            100% { transform: scale(1); opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  // ── Address list ──────────────────────────────────────────────────────────
  if(step==="addrList")return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#f6f7f8"}}>
      <BackHeader title="选择地址" onBack={()=>setStep("main")}/>
      <div style={{flex:1,overflowY:"auto",padding:"14px 14px 0"}}>
        {savedAddresses.map(a=>{
          const sel=(selAddr??savedAddresses[0]?.id)===a.id;
          return(
            // 用 div 包整行,内部分两块:左侧选择(整行点击),右侧编辑铅笔按钮
            <div key={a.id} style={{display:"flex",alignItems:"stretch",gap:0,marginBottom:10,background:"#fff",border:`1.5px solid ${sel?"#e03030":"#ececec"}`,borderRadius:14,boxShadow:sel?"0 2px 8px rgba(224,48,48,0.08)":"none",overflow:"hidden"}}>
              <button onClick={()=>{setSelAddr(a.id);setStep("main");}} style={{flex:1,display:"flex",alignItems:"flex-start",gap:12,padding:"14px 14px 14px 16px",background:"none",border:"none",cursor:"pointer",textAlign:"left"}}>
                <svg style={{flexShrink:0,marginTop:3}} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={sel?"#e03030":"#ccc"} strokeWidth="2"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontFamily:FF,color:"#1a1a1a",fontWeight:500,marginBottom:2}}>{a.name}&nbsp;&nbsp;<span style={{fontWeight:400,color:"#888"}}>{a.phone}</span></div>
                  <div style={{fontSize:12,fontFamily:FF,color:"#666",lineHeight:1.5}}>{addrLabel(a)}</div>
                </div>
                {sel&&<svg width="18" height="18" viewBox="0 0 24 24" fill="#e03030" stroke="none" style={{flexShrink:0}}><path d="M12 2L9.19 8.62 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.62L12 2z"/></svg>}
              </button>
              {/* 编辑按钮 — 进入 addrNew 表单的"编辑模式",载入这条地址的数据 */}
              <button
                onClick={()=>{
                  setEditingAddrId(a.id);
                  setNewAddr({
                    name:a.name||"",
                    phone:a.phone||"",
                    line1:a.line1||a.street||"",
                    apt:a.apt||"",
                    city:a.city||"",
                    state:a.state||"CA",
                    zip:a.zip||"",
                  });
                  setStep("addrNew");
                }}
                style={{
                  display:"flex",alignItems:"center",justifyContent:"center",
                  width:48,background:"#fafafa",border:"none",borderLeft:"1px solid #f0f0f0",
                  cursor:"pointer",color:"#666",
                }}
                aria-label="编辑地址"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </button>
            </div>
          );
        })}
      </div>
      <div style={{padding:"12px 14px 24px",background:"#f6f7f8",flexShrink:0}}>
        <button onClick={()=>{setEditingAddrId(null);setNewAddr({name:"",phone:"",line1:"",apt:"",city:"",state:"CA",zip:""});setStep("addrNew");}} style={{...primaryBtn,background:"#1a1a1a"}}>＋ 添加新地址</button>
      </div>
    </div>
  );

  // ── New address form / Edit existing ───────────────────────────────────────
  if(step==="addrNew")return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#f6f7f8"}}>
      <BackHeader title={editingAddrId?"编辑地址":"添加地址"} onBack={()=>{setEditingAddrId(null);setNewAddr({name:"",phone:"",line1:"",apt:"",city:"",state:"CA",zip:""});setStep(savedAddresses.length>0?"addrList":"main");}}/>
      <div style={{flex:1,overflowY:"auto",padding:"16px 14px 24px"}}>
        {/* 定位按钮 */}
        <button onClick={useMyLocation} disabled={geoLoad} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"12px 16px",background:"linear-gradient(135deg,#fff5f5,#ffeaea)",border:"1.5px solid #ffb8b8",borderRadius:12,cursor:geoLoad?"wait":"pointer",marginBottom:12,fontFamily:FF,opacity:geoLoad?0.6:1}}>
          {geoLoad?(
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e03030" strokeWidth="2.4" strokeLinecap="round" style={{animation:"spin 1s linear infinite"}}><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>
          ):(
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e03030" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="22" y1="12" x2="18" y2="12"/><line x1="6" y1="12" x2="2" y2="12"/><line x1="12" y1="6" x2="12" y2="2"/><line x1="12" y1="22" x2="12" y2="18"/></svg>
          )}
          <span style={{fontSize:13,color:"#e03030",fontWeight:600}}>{geoLoad?"正在获取位置...":"使用我的当前位置"}</span>
        </button>
        {geoMsg&&<div style={{fontSize:11.5,color:"#888",fontFamily:FF,marginBottom:14,padding:"8px 12px",background:"#fffbf0",borderRadius:8,lineHeight:1.5}}>{geoMsg}</div>}

        <div style={{background:"#fff",borderRadius:14,padding:"4px 16px",marginBottom:0}}>
          {/* 收件人 + 电话 */}
          {[
            {k:"name",label:"收件人",placeholder:"请输入姓名"},
            {k:"phone",label:"电话",placeholder:"+1 626-xxx-xxxx",type:"tel"},
          ].map(f=>(
            <div key={f.k} style={{display:"flex",alignItems:"center",padding:"13px 0",borderBottom:"1px solid #f5f5f4"}}>
              <span style={{flex:"0 0 70px",fontSize:13,color:"#888",fontFamily:FF}}>{f.label}</span>
              <input value={newAddr[f.k]} onChange={e=>setNewAddr(a=>({...a,[f.k]:e.target.value}))} placeholder={f.placeholder} type={f.type||"text"} style={{flex:1,border:"none",outline:"none",fontSize:13.5,color:"#1a1a1a",fontFamily:FF,background:"none",textAlign:"right"}}/>
            </div>
          ))}
          {/* 街道 — 带 Google Places 自动补全 */}
          <div style={{display:"flex",alignItems:"center",padding:"13px 0",borderBottom:"1px solid #f5f5f4"}}>
            <span style={{flex:"0 0 70px",fontSize:13,color:"#888",fontFamily:FF}}>街道</span>
            <div style={{flex:1}}>
              <AddressAutocompleteInput
                value={newAddr.line1}
                onChange={v=>setNewAddr(a=>({...a,line1:v}))}
                onPick={d=>setNewAddr(a=>({...a,line1:d.line1||a.line1,city:d.city||a.city,state:d.state||a.state||"CA",zip:d.zip||a.zip,apt:d.apt||a.apt}))}
                placeholder="开始输入,选地址自动填城市/邮编"
                style={{width:"100%",border:"none",outline:"none",fontSize:13.5,color:"#1a1a1a",fontFamily:FF,background:"none",textAlign:"right",padding:0}}
                suppressToken={addrSuppressToken}
              />
            </div>
          </div>
          {/* 门牌号 */}
          <div style={{display:"flex",alignItems:"center",padding:"13px 0",borderBottom:"1px solid #f5f5f4"}}>
            <span style={{flex:"0 0 70px",fontSize:13,color:"#888",fontFamily:FF}}>门牌号</span>
            <input value={newAddr.apt} onChange={e=>setNewAddr(a=>({...a,apt:e.target.value}))} placeholder="Apt / Suite(选填)" style={{flex:1,border:"none",outline:"none",fontSize:13.5,color:"#1a1a1a",fontFamily:FF,background:"none",textAlign:"right"}}/>
          </div>
          {/* 城市 */}
          <div style={{display:"flex",alignItems:"center",padding:"13px 0",borderBottom:"1px solid #f5f5f4"}}>
            <span style={{flex:"0 0 70px",fontSize:13,color:"#888",fontFamily:FF}}>城市</span>
            <input value={newAddr.city} onChange={e=>setNewAddr(a=>({...a,city:e.target.value}))} placeholder="Monterey Park" style={{flex:1,border:"none",outline:"none",fontSize:13.5,color:"#1a1a1a",fontFamily:FF,background:"none",textAlign:"right"}}/>
          </div>
          <div style={{display:"flex",alignItems:"center",padding:"13px 0"}}>
            <span style={{flex:"0 0 70px",fontSize:13,color:"#888",fontFamily:FF}}>州 / 邮编</span>
            <input value={newAddr.state} onChange={e=>setNewAddr(a=>({...a,state:e.target.value.toUpperCase().slice(0,2)}))} placeholder="CA" style={{width:50,border:"none",outline:"none",fontSize:13.5,color:"#1a1a1a",fontFamily:FF,background:"none",textAlign:"right"}}/>
            <input value={newAddr.zip} onChange={e=>setNewAddr(a=>({...a,zip:e.target.value.replace(/\D/g,"").slice(0,5)}))} placeholder="91754" inputMode="numeric" style={{width:80,border:"none",outline:"none",fontSize:13.5,color:"#1a1a1a",fontFamily:FF,background:"none",textAlign:"right"}}/>
          </div>
        </div>
        <button onClick={saveNewAddr} style={{...primaryBtn,marginTop:24,background:"#e03030",opacity:(newAddr.name&&newAddr.phone&&newAddr.line1&&newAddr.city&&newAddr.zip)?1:0.4,borderRadius:24}}>{editingAddrId?"保存修改":"保存地址"}</button>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  // ── 银行卡管理 — 直接复用 Profile 的 PaymentMethodsPanel(设默认/删除/添加) ──
  // 返回时自动重拉 savedCards 并同步默认选中
  if(step==="payList")return(
    <PaymentMethodsPanel
      onBack={async()=>{
        try{
          const list = await getSavedPaymentMethods();
          setSavedCards(list || []);
          const def = (list||[]).find(c=>c.is_default) || (list||[])[0];
          if(def){ setSelectedSavedPmId(def.id); setSelPay("credit"); }
          else{ setSelectedSavedPmId(null); }
        }catch{}
        setStep("main");
      }}
    />
  );

  // ── Add new payment ───────────────────────────────────────────────────────
  if(step==="payNew")return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#f6f7f8"}}>
      <BackHeader title="添加支付方式" onBack={()=>setStep(savedPayments.length>0?"payList":"main")}/>
      <div style={{flex:1,overflowY:"auto",padding:"16px 14px"}}>
        <div style={{fontSize:12,color:"#aaa",fontFamily:FF,marginBottom:14,lineHeight:1.6}}>选择后将调用第三方支付 SDK 完成绑定</div>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {/* deliveryOnly 的方式只在「送货上门」时显示 */}
          {PAYMENT_METHODS.filter(m=>!m.deliveryOnly||delivery==="delivery").map(m=>(
            <button key={m.id} onClick={()=>{
              setSavedPayments(p=>{if(!p.find(x=>x.id===m.id))return[...p,{id:m.id}];return p;});
              setSelPay(m.id);
              setStep("main");
            }} style={{display:"flex",alignItems:"center",gap:14,padding:"16px 16px",background:"#fff",border:m.id==="cod"?"1.5px solid #f5a623":"1.5px solid #ececec",borderRadius:14,cursor:"pointer",textAlign:"left"}}>
              <span style={{fontSize:22,width:28,textAlign:"center"}}>{m.icon||"💳"}</span>
              <div style={{flex:1}}>
                <div style={{fontSize:14,fontFamily:FF,color:"#1a1a1a",fontWeight:500,display:"flex",alignItems:"center",gap:6}}>
                  {m.label}
                  {m.id==="cod"&&<span style={{fontSize:9.5,color:"#f5a623",background:"#fff8e1",padding:"1px 6px",borderRadius:3,fontWeight:600,letterSpacing:"0.04em"}}>COD</span>}
                </div>
                <div style={{fontSize:11,fontFamily:FF,color:"#aaa",marginTop:2}}>{m.note}</div>
              </div>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  // ── 添加新卡 ─────────────────────────────────────────────────────────────
  //   登录用户 → 复用 Profile 的 AddCardPanel(SetupIntent,真存到 Stripe Customer)
  //   游客 → 走 GuestCardForm(createPaymentMethod,一次性 pm_id 用完即弃)
  if(step==="addCardForm") {
    if (isAuthed) {
      return (
        <AddCardPanel
          onBack={async()=>{
            try{
              const list = await getSavedPaymentMethods();
              setSavedCards(list || []);
              const def = (list||[]).find(c=>c.is_default) || (list||[])[0];
              if(def){ setSelectedSavedPmId(def.id); setSelPay("credit"); }
            }catch{}
            setStep("main");
          }}
        />
      );
    }
    // 游客分支 — 用 createPaymentMethod 拿一次性 pm_id
    return stripePromise ? (
      <Elements
        stripe={stripePromise}
        options={{
          mode: 'payment',
          amount: Math.max(50, Math.round(grandTotal * 100)),
          currency: 'usd',
          locale: 'zh',
          paymentMethodCreation: 'manual',
          appearance: { theme: 'stripe', variables: { colorPrimary: '#1a1a1a', borderRadius: '10px' } },
        }}
      >
        <GuestCardForm
          onBack={()=>setStep("main")}
          onSaved={(card)=>{
            setInputCard(card);
            setSelectedSavedPmId(null);
            setSelPay("credit");
            setStep("main");
          }}
          onGoLogin={()=>{
            // 注册 / 登录都跳到 Profile tab(同一个 OTP flow,不区分)— 关闭结算
            setCheckout(false);
            setTab("profile");
          }}
        />
      </Elements>
    ) : null;
  }

  // ── Main checkout ────────────────────────────────────────────────────────
  const curAddr = selAddr?savedAddresses.find(a=>a.id===selAddr):savedAddresses[0]??null;
  // curPay 优先从 savedPayments 找,找不到 fallback 到 PAYMENT_METHODS 直查
  // 这样 COD / Apple Pay / PayPal 等直接 setSelPay('cod') 也能识别
  const curPay  = selPay
    ? (savedPayments.find(p=>p.id===selPay) || PAYMENT_METHODS.find(m=>m.id===selPay) || null)
    : (savedPayments[0] ?? null);
  const needAddr= delivery!=="pickup";
  // pickup 至少要联系电话(10 位数字);其他模式靠地址里的电话
  const pickupPhoneOk = !needAddr ? (pickupPhone||"").replace(/\D/g,"").length>=10 : true;
  // 用户必须明确选择了支付方式才允许提交:
  //   - 选了 Apple Pay
  //   - 选了银行卡(selPay="credit",有保存卡或会被引导到全屏输卡页)
  //   - 选了 cod(送货上门时)
  const hasPaymentChoice =
    selPay === "apple_pay" ||
    selPay === "credit" ||
    !!curPay ||
    !!selectedSavedPmId;
  const canPay = (!needAddr||(curAddr&&!shipError)) && hasPaymentChoice && pickupPhoneOk;
  const TIPS=[0,1,2,5];

  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#f6f7f8"}}>
      {/* Header — App container 已处理 safe-area-top,这里只留小空隙 */}
      <div style={{background:"#fff",padding:"14px 16px 14px",display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
        <button onClick={()=>setCheckout(false)} style={{background:"none",border:"none",cursor:"pointer",padding:0}}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <span style={{fontFamily:SERIF,fontSize:18,fontWeight:600}}>结账</span>
      </div>

      <div style={{flex:1,overflowY:"auto",padding:"12px 12px 100px"}}>

        {/* 配送方式 */}
        <div style={{background:"#fff",borderRadius:16,padding:"16px 16px 14px",marginBottom:10,boxShadow:"0 1px 3px rgba(0,0,0,0.03)"}}>
          <div style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600,marginBottom:11}}>配送方式</div>
          <div style={{display:"flex",gap:8}}>
            {[{id:"pickup",label:"到店自取",icon:"🏪",sub:"免运费"},{id:"delivery",label:"送货上门",icon:"🛵",sub:"满$100"},{id:"mail",label:"快递邮寄",icon:"📦",sub:"+$8"}].map(o=>{
              const sel=delivery===o.id;
              return(
                <button key={o.id} onClick={()=>setDelivery(o.id)} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4,padding:"11px 4px 9px",background:sel?"#fff5f5":"#f8f8f7",border:`1.5px solid ${sel?"#e03030":"transparent"}`,borderRadius:12,cursor:"pointer",transition:"all 0.15s"}}>
                  <span style={{fontSize:20,marginBottom:1}}>{o.icon}</span>
                  <span style={{fontSize:11.5,fontFamily:FF,color:sel?"#e03030":"#444",fontWeight:sel?600:500}}>{o.label}</span>
                  <span style={{fontSize:10,color:sel?"#e03030":"#aaa",fontFamily:FF}}>{o.sub}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* 地址 */}
        {delivery==="pickup"?(
          <>
            <div style={{background:"#fff",borderRadius:16,padding:"14px 16px",marginBottom:10,display:"flex",alignItems:"flex-start",gap:11,boxShadow:"0 1px 3px rgba(0,0,0,0.03)"}}>
              <div style={{width:32,height:32,borderRadius:10,background:"#e8f7ee",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:15}}>📍</div>
              <div style={{flex:1}}>
                <div style={{fontSize:12,color:"#888",fontFamily:FF,marginBottom:2}}>取货地址</div>
                <div style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:500,lineHeight:1.5}}>{PICKUP_ADDRESS}</div>
                <div style={{fontSize:11,color:"#3a9e3a",fontFamily:FF,marginTop:4}}>营业时间{PICKUP_HOURS}</div>
              </div>
            </div>
            {/* 取货联系信息 — 输入框右侧加铅笔图标,清晰标识可编辑 */}
            <div style={{background:"#fff",borderRadius:16,padding:"4px 16px",marginBottom:10,boxShadow:"0 1px 3px rgba(0,0,0,0.03)"}}>
              <div style={{display:"flex",alignItems:"center",padding:"13px 0",borderBottom:"1px solid #f5f5f4"}}>
                <span style={{flex:"0 0 70px",fontSize:13,color:"#888",fontFamily:FF}}>取货人</span>
                <input value={pickupName} onChange={e=>setPickupName(e.target.value)} placeholder="您的姓名" style={{flex:1,border:"none",outline:"none",fontSize:13.5,color:"#1a1a1a",fontFamily:FF,background:"none",textAlign:"right"}}/>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginLeft:8,flexShrink:0}}>
                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </div>
              <div style={{display:"flex",alignItems:"center",padding:"13px 0"}}>
                <span style={{flex:"0 0 70px",fontSize:13,color:"#888",fontFamily:FF}}>联系电话 <span style={{color:"#e03030"}}>*</span></span>
                <input value={pickupPhone} onChange={e=>setPickupPhone(e.target.value)} placeholder="备货完成通知" type="tel" inputMode="tel" style={{flex:1,border:"none",outline:"none",fontSize:13.5,color:"#1a1a1a",fontFamily:FF,background:"none",textAlign:"right"}}/>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginLeft:8,flexShrink:0}}>
                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </div>
            </div>
          </>
        ):(
          <button onClick={()=>{
            if(savedAddresses.length===0) setStep("addrNew");
            else setStep("addrList");
          }} style={{width:"100%",display:"flex",alignItems:"center",gap:11,padding:"14px 16px",background:"#fff",border:"none",borderRadius:16,cursor:"pointer",marginBottom:10,textAlign:"left",boxShadow:"0 1px 3px rgba(0,0,0,0.03)"}}>
            <div style={{width:32,height:32,borderRadius:10,background:curAddr?"#fff5f5":"#f5f5f4",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={curAddr?"#e03030":"#aaa"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:12,color:"#888",fontFamily:FF,marginBottom:2}}>配送地址</div>
              {curAddr?(
                <>
                  <div style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:500}}>{curAddr.name} <span style={{fontWeight:400,color:"#888",fontSize:12}}>{curAddr.phone}</span></div>
                  <div style={{fontSize:12,color:"#666",fontFamily:FF,marginTop:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{addrLabel(curAddr)}</div>
                </>
              ):<div style={{fontSize:13,color:"#bbb",fontFamily:FF}}>请添加配送地址</div>}
            </div>
            {/* 已有地址时显示"修改",未填则显示"添加",更明确 */}
            <span style={{fontSize:12,color:"#888",fontFamily:FF,fontWeight:500,marginRight:2,whiteSpace:"nowrap"}}>{curAddr?"修改":"添加"}</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        )}

        {/* 支付方式 — 始终显示「银行卡 | Apple Pay」两个并列按钮
            银行卡 pill 显示当前激活的卡(默认/选中的保存卡),没卡时显示"信用卡 / 储蓄卡" */}
        {(() => {
          // 当前激活的银行卡(显示在 pill 里):
          //   优先 inputCard(游客本次输入的) > selectedSavedPmId > 默认卡 > 第一张
          const activeCard = inputCard
            ? { brand: inputCard.brand, last4: inputCard.last4 }
            : (savedCards.find(c => c.id === selectedSavedPmId)
              || savedCards.find(c => c.is_default)
              || savedCards[0]
              || null);

          const cardModeActive = selPay === "credit" || (!selPay && activeCard);
          const applePayActive = selPay === "apple_pay";
          const codActive = selPay === "cod";
          const showCod = delivery === "delivery"; // 货到付款仅在送货上门时显示
          return (
            <div style={{background:"#fff",borderRadius:16,padding:"14px 16px",marginBottom:10,boxShadow:"0 1px 3px rgba(0,0,0,0.03)"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:11}}>
                <div style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600}}>支付方式</div>
                {/* 已登录用户显示"管理"链接 → 跳转到我的付款方式管理页 */}
                {savedCards.length>0&&(
                  <button
                    onClick={()=>setStep(savedCards.length>0?"payList":"addCardForm")}
                    style={{background:"none",border:"none",cursor:"pointer",fontSize:11.5,color:"#888",fontFamily:FF,padding:"2px 4px",display:"flex",alignItems:"center",gap:2}}
                  >
                    管理 ›
                  </button>
                )}
              </div>
              <div style={{display:"flex",gap:10}}>
                {/* 左:银行卡 */}
                <button
                  onClick={()=>{
                    setSelPay("credit");
                    // 已经有 inputCard 或保存卡 → 卡管理页;什么都没有 → 直接添加
                    if(inputCard || savedCards.length>0){
                      setStep(savedCards.length>0 ? "payList" : "addCardForm");
                    }else{
                      setStep("addCardForm");
                    }
                  }}
                  style={{
                    flex:1,
                    display:"flex",flexDirection:"column",alignItems:"center",gap:6,
                    padding:"14px 8px",
                    background:cardModeActive?"#fff5f5":"#fafafa",
                    border:`1.5px solid ${cardModeActive?"#e03030":"transparent"}`,
                    borderRadius:12,cursor:"pointer",
                    transition:"all 0.15s",
                  }}
                >
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={cardModeActive?"#e03030":"#888"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="6" width="20" height="13" rx="2"/>
                    <line x1="2" y1="11" x2="22" y2="11"/>
                    <line x1="6" y1="15" x2="10" y2="15"/>
                  </svg>
                  <div style={{fontSize:12.5,fontFamily:FF,color:cardModeActive?"#e03030":"#444",fontWeight:600}}>
                    {activeCard
                      ? `${(activeCard.brand||"卡").toUpperCase()} •••• ${activeCard.last4}`
                      : "银行卡"}
                  </div>
                  <div style={{fontSize:9.5,fontFamily:FF,color:"#aaa",letterSpacing:"0.02em"}}>
                    {activeCard
                      ? (savedCards.length>1?"点击切换 / 管理":"点击管理")
                      : "信用卡 / 储蓄卡"}
                  </div>
                </button>

                {/* 右:Apple Pay */}
                <button
                  onClick={()=>{
                    if(!applePay){
                      setSubmitErr("Apple Pay 不可用,请在 iPhone 钱包添加银行卡");
                      return;
                    }
                    setSelPay("apple_pay");
                  }}
                  disabled={!applePay}
                  style={{
                    flex:1,
                    display:"flex",flexDirection:"column",alignItems:"center",gap:6,
                    padding:"14px 8px",
                    background:applePayActive?"#1a1a1a":"#fafafa",
                    border:`1.5px solid ${applePayActive?"#000":"transparent"}`,
                    borderRadius:12,cursor:applePay?"pointer":"not-allowed",
                    transition:"all 0.15s",opacity:applePay?1:0.55,
                  }}
                >
                  <svg width="26" height="26" viewBox="0 0 24 28" fill={applePayActive?"#fff":"#1a1a1a"}>
                    <path d="M17.05 14.96c-.03-2.83 2.31-4.18 2.42-4.25-1.32-1.93-3.38-2.19-4.11-2.22-1.74-.18-3.4 1.03-4.28 1.03-.9 0-2.25-1.01-3.71-.98-1.9.03-3.66 1.11-4.64 2.81-1.99 3.45-.51 8.55 1.43 11.34.95 1.37 2.07 2.91 3.55 2.85 1.43-.06 1.97-.93 3.7-.93 1.72 0 2.21.93 3.71.9 1.54-.03 2.51-1.39 3.45-2.77 1.09-1.59 1.54-3.13 1.56-3.21-.03-.01-2.99-1.15-3.02-4.57zm-2.81-8.39c.79-.96 1.32-2.29 1.18-3.61-1.14.05-2.51.76-3.33 1.71-.73.85-1.37 2.21-1.2 3.5 1.27.1 2.56-.65 3.35-1.6z"/>
                  </svg>
                  <div style={{fontSize:12.5,fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",color:applePayActive?"#fff":"#1a1a1a",fontWeight:600}}>Apple Pay</div>
                  <div style={{fontSize:9.5,fontFamily:FF,color:applePayActive?"rgba(255,255,255,0.7)":"#aaa",letterSpacing:"0.02em"}}>
                    {applePay?"无需输卡号":"需要 iPhone"}
                  </div>
                </button>
              </div>

              {/* 第二行:货到付款 — 仅送货上门时显示,占满整行 */}
              {showCod && (
                <button
                  onClick={()=>{
                    setSelPay("cod");
                    setSelectedSavedPmId(null);
                  }}
                  style={{
                    width:"100%",marginTop:10,
                    display:"flex",alignItems:"center",gap:11,
                    padding:"12px 14px",
                    background:codActive?"linear-gradient(135deg,#fffbeb,#fef3c7)":"#fafafa",
                    border:`1.5px solid ${codActive?"#d97706":"transparent"}`,
                    borderRadius:12,cursor:"pointer",
                    transition:"all 0.15s",
                  }}
                >
                  <div style={{width:36,height:36,borderRadius:10,background:codActive?"#fef3c7":"#f5f5f4",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:18}}>💵</div>
                  <div style={{flex:1,textAlign:"left"}}>
                    <div style={{fontSize:13,color:codActive?"#a16207":"#1a1a1a",fontFamily:FF,fontWeight:600}}>货到付款</div>
                    <div style={{fontSize:11,color:codActive?"#a16207":"#888",fontFamily:FF,marginTop:1}}>送达时给配送员现金</div>
                  </div>
                  {codActive && <span style={{fontSize:14,color:"#d97706",fontWeight:700}}>✓</span>}
                </button>
              )}
            </div>
          );
        })()}




        {/* 商品清单 */}
        <div style={{background:"#fff",borderRadius:16,padding:"14px 16px",marginBottom:10,boxShadow:"0 1px 3px rgba(0,0,0,0.03)"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:11}}>
            <span style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600}}>商品清单</span>
            <span style={{fontSize:11,color:"#888",fontFamily:FF}}>共 {cart.reduce((a,i)=>a+i.qty,0)} 件</span>
          </div>
          <div style={{display:"flex",gap:8,overflowX:"auto",paddingBottom:2,scrollbarWidth:"none"}}>
            {cart.map((item,idx)=>(
              <div key={`${item.id}-${item.variantId||""}-${idx}`} style={{position:"relative",flexShrink:0}}>
                <div style={{width:54,height:54,borderRadius:10,overflow:"hidden",border:"1px solid #f0f0f0"}}>
                  <ProductImage img={item.img} imgUrl={item.imgUrl} name={item.name} aspect={1}/>
                </div>
                {item.qty>1&&<span style={{position:"absolute",top:-5,right:-5,background:"#e03030",color:"#fff",borderRadius:"50%",minWidth:18,height:18,padding:"0 5px",fontSize:9.5,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:FF,fontWeight:600}}>{item.qty}</span>}
              </div>
            ))}
          </div>
        </div>

        {/* 优惠券 */}
        <div style={{background:"#fff",borderRadius:16,padding:"14px 16px",marginBottom:10,boxShadow:"0 1px 3px rgba(0,0,0,0.03)"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <span style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600}}>优惠券</span>
            {couponOk && couponData
              ? <span style={{fontSize:11,color:"#3a9e3a",fontFamily:FF,fontWeight:500}}>✓ 已应用 -${parseFloat(couponData.discount).toFixed(2)}</span>
              : myCoupons.length > 0
                ? <button onClick={()=>setShowCouponPicker(s=>!s)} style={{background:"none",border:"none",fontSize:11,color:"#e03030",fontFamily:FF,fontWeight:600,cursor:"pointer",padding:0}}>我有 {myCoupons.length} 张可用 ▾</button>
                : null}
          </div>

          {/* 钱包选择 */}
          {showCouponPicker && !couponOk && (
            <div style={{background:"#f9fafb",borderRadius:10,padding:"6px 8px",marginBottom:10,maxHeight:180,overflowY:"auto"}}>
              {myCoupons.map(mc=>(
                <button
                  key={mc.id}
                  onClick={()=>{setCoupon(mc.code);setShowCouponPicker(false);}}
                  style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"8px 10px",background:"#fff",border:"1px solid #ececec",borderRadius:8,marginBottom:6,cursor:"pointer",textAlign:"left"}}
                >
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:700,color:"#1a1a1a",fontFamily:FF}}>{mc.name}</div>
                    <div style={{fontSize:10.5,color:"#888",fontFamily:FF,marginTop:1}}>{mc.description||(mc.type==="fixed"?`满 $${mc.min_subtotal} 减 $${mc.value}`:`${mc.value}% off`)}</div>
                  </div>
                  <span style={{fontSize:10.5,fontFamily:"monospace",color:"#e03030",fontWeight:700}}>{mc.code}</span>
                </button>
              ))}
            </div>
          )}

          <div style={{display:"flex",gap:8}}>
            <input
              value={coupon}
              onChange={e=>{setCoupon(e.target.value.toUpperCase());setCouponErr("");}}
              placeholder="输入优惠码"
              style={{flex:1,padding:"10px 14px",borderRadius:10,border:`1.5px solid ${couponErr?"#e03030":"#ececec"}`,outline:"none",fontSize:13,color:"#1a1a1a",fontFamily:FF,background:"#fafaf9",boxSizing:"border-box",fontFamily:"monospace",textTransform:"uppercase"}}
              disabled={couponOk||couponBusy}
            />
            <button
              onClick={async()=>{
                if(couponOk){
                  // 取消应用
                  setCouponOk(false);setCouponData(null);setCoupon("");setCouponErr("");
                  return;
                }
                if(!coupon.trim())return;
                setCouponBusy(true);setCouponErr("");
                try{
                  const r = await previewCoupon({
                    code: coupon.trim(),
                    items: cart.map(i=>({
                      product_id: i.id,
                      qty: i.qty,
                      unit_price: i.price,
                      is_special: !!i.isSpecial,
                    })),
                    subtotal,
                  });
                  if(r.ok){
                    setCouponOk(true);
                    setCouponData(r);
                  }else{
                    setCouponErr(r.error||"优惠码无效");
                  }
                }catch(e){
                  setCouponErr(e.message||"校验失败");
                }finally{
                  setCouponBusy(false);
                }
              }}
              disabled={couponBusy||(!couponOk&&!coupon.trim())}
              style={{flexShrink:0,background:couponOk?"#888":"#1a1a1a",color:"#fff",border:"none",borderRadius:10,padding:"0 18px",cursor:"pointer",fontSize:13,fontFamily:FF,opacity:couponBusy?0.5:1}}
            >
              {couponBusy?"…":couponOk?"取消":"应用"}
            </button>
          </div>
          {couponErr && <div style={{marginTop:8,fontSize:11,color:"#e03030",fontFamily:FF}}>{couponErr}</div>}
        </div>

        {/* 配送小费 — 仅送货上门时显示 */}
        {delivery==="delivery"&&(
          <div style={{background:"#fff",borderRadius:16,padding:"14px 16px",marginBottom:10,boxShadow:"0 1px 3px rgba(0,0,0,0.03)"}}>
            <div style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600,marginBottom:10}}>配送小费 <span style={{fontSize:11,color:"#aaa",fontWeight:400}}>(可选)</span></div>
            <div style={{display:"flex",gap:8}}>
              {TIPS.map(t=>(
                <button key={t} onClick={()=>setTip(t)} style={{flex:1,padding:"9px 0",background:tip===t?"#1a1a1a":"#f5f5f4",border:"none",borderRadius:10,cursor:"pointer",fontSize:12.5,fontFamily:FF,color:tip===t?"#fff":"#666",fontWeight:tip===t?600:500,transition:"all 0.15s"}}>{t===0?"不加":"$"+t}</button>
              ))}
            </div>
          </div>
        )}

        {/* 配送范围错误提示 — 不满 $100 / 超出配送范围 都用同一行红色提示,不放电话 */}
        {shipError&&(
          <div style={{background:"#fff5f5",border:"1px solid #ffc8c8",borderRadius:12,padding:"10px 14px",marginBottom:10,display:"flex",alignItems:"center",gap:9}}>
            <span style={{fontSize:14,flexShrink:0}}>⚠️</span>
            <span style={{flex:1,fontSize:12.5,color:"#e03030",fontFamily:FF,fontWeight:500,lineHeight:1.4,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{shipError}</span>
          </div>
        )}

        {/* 包邮门槛提示 — 邮寄方式且未满 $100 时显示,文案温和(蓝色标签风) */}
        {!eligibleFreeShip&&delivery==="mail"&&!shipError&&cart.length>0&&(
          <div style={{background:"#fff8e1",border:"1px solid #ffe082",borderRadius:12,padding:"10px 14px",marginBottom:10,display:"flex",alignItems:"center",gap:9,fontSize:12,color:"#7a5a10",fontFamily:FF}}>
            <span style={{fontSize:14}}>🎁</span>
            <span>再买 <span style={{fontWeight:700,color:"#e03030"}}>${remainingForFreeShip.toFixed(2)}</span> 即可包邮{cart.some(i=>i.isSpecial)?"(特价商品不计入)":""}</span>
          </div>
        )}

        {/* 费用明细 */}
        <div style={{background:"#fff",borderRadius:16,padding:"14px 16px",marginBottom:10,boxShadow:"0 1px 3px rgba(0,0,0,0.03)"}}>
          <div style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600,marginBottom:11}}>费用明细</div>
          <SumRow label={`商品 (${cart.reduce((a,i)=>a+i.qty,0)} 件)`} val={`$${subtotal.toFixed(2)}`}/>
          {/* 运费行 — 自取不显示;送货/邮寄都显示。包邮时显示 $0.00 + 绿色"已包邮"提示 */}
          {delivery!=="pickup"&&(
            <>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:shipping>0?2:7,alignItems:"baseline"}}>
                <span style={{fontSize:13,color:"#888",fontFamily:FF}}>运费</span>
                {shipping>0?(
                  <span style={{fontSize:13,fontFamily:FF,color:"#1a1a1a"}}>${shipping.toFixed(2)}</span>
                ):(
                  <span style={{fontSize:13,fontFamily:FF,color:"#3a9e3a",fontWeight:500}}>$0.00 <span style={{fontSize:10.5,color:"#888",fontWeight:400,marginLeft:3}}>已享免邮</span></span>
                )}
              </div>
              {shipping>0&&(
                <div style={{fontSize:10.5,color:"#a07060",fontFamily:FF,marginBottom:7,lineHeight:1.4}}>
                  💡 联系客服可咨询运费折扣
                </div>
              )}
            </>
          )}
          <SumRow label="销售税" val={`$${tax.toFixed(2)}`}/>
          {couponOk&&couponData&&<SumRow label={`优惠券 (${couponData.coupon?.code||""})`} val={`-$${parseFloat(couponData.discount).toFixed(2)}`} accent/>}
          {delivery==="delivery"&&tip>0&&<SumRow label="配送小费" val={`$${tip.toFixed(2)}`}/>}
          <div style={{height:1,background:"#f0f0f0",margin:"8px 0 6px"}}/>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginTop:4}}>
            <span style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600}}>总计</span>
            <span style={{fontSize:18,fontFamily:FF,fontWeight:700,color:"#e03030"}}>${grandTotal.toFixed(2)}</span>
          </div>
        </div>

        {/* 短信通知同意 — 可选 (Twilio TCPA 合规, 不勾也能下单) */}
        <div style={{background:"#fff",borderRadius:16,padding:"14px 16px",marginBottom:10,boxShadow:"0 1px 3px rgba(0,0,0,0.03)"}}>
          <label style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer",margin:0}}>
            <input
              type="checkbox"
              checked={smsConsent}
              onChange={e => setSmsConsent(e.target.checked)}
              style={{marginTop:2,width:16,height:16,accentColor:"#e03030",flexShrink:0,cursor:"pointer"}}
            />
            <div style={{fontSize:12.5,color:"#444",fontFamily:FF,lineHeight:1.55}}>
              <div>同意接收订单短信通知 · I agree to receive SMS updates from ShiYi.</div>
              <div style={{color:"#888",marginTop:3,fontSize:11.5}}>
                发送 STOP 退订 · 可能产生短信费 ·{" "}
                <a href="/privacy" target="_blank" rel="noreferrer"
                   style={{color:"#1a1a1a",textDecoration:"underline"}}>隐私政策</a>
                {" · "}
                <a href="/terms" target="_blank" rel="noreferrer"
                   style={{color:"#1a1a1a",textDecoration:"underline"}}>服务条款</a>
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* 固定底部操作栏 — 单按钮,根据已选支付方式分发 */}
      <div style={{position:"absolute",bottom:0,left:0,right:0,background:"#fff",borderTop:"1px solid #f0f0f0",padding:"12px 14px 18px",display:"flex",alignItems:"center",gap:12,boxShadow:"0 -2px 8px rgba(0,0,0,0.04)"}}>
        <div style={{flex:1}}>
          <div style={{fontSize:10.5,color:"#888",fontFamily:FF}}>合计</div>
          <div style={{fontSize:20,fontWeight:700,color:"#e03030",fontFamily:FF,letterSpacing:"-0.01em"}}>${grandTotal.toFixed(2)}</div>
        </div>
        <button
          disabled={submitting||!!stripeFlow||!canPay}
          onClick={()=>{
            // 关键防重入:Stripe 弹窗开着的时候,这个按钮一定要 disabled —
            // 上一单 pending_payment 还没付,再点一次会再创建一单(就是"待付款里
            // 多一单一样的订单"那个 bug)。
            if(stripeFlow)return;
            if(shipError){setSubmitErr(shipError);return;}
            if(needAddr&&!curAddr){if(savedAddresses.length===0)setStep("addrNew");else setStep("addrList");return;}
            if(!pickupPhoneOk){setSubmitErr("请填写联系电话");return;}
            // Apple Pay 走专用 handler;其他都走 submitOrder
            if(curPay?.id==="apple_pay"||selPay==="apple_pay"){
              if(!applePay){setSubmitErr("请在 iPhone 钱包添加银行卡后使用 Apple Pay");return;}
              handleApplePay();
              return;
            }
            // 没选支付方式 → 引导用户去选
            if(!curPay&&!selectedSavedPmId){setSubmitErr("请先选择支付方式");return;}
            submitOrder();
          }}
          style={{background:(canPay&&!submitting&&!stripeFlow)?"linear-gradient(135deg,#e03030,#c42020)":"#ccc",color:"#fff",border:"none",borderRadius:24,padding:"13px 28px",fontSize:14,fontFamily:FF,fontWeight:600,cursor:(canPay&&!submitting&&!stripeFlow)?"pointer":"not-allowed",boxShadow:(canPay&&!submitting&&!stripeFlow)?"0 4px 12px rgba(224,48,48,0.3)":"none",letterSpacing:"0.02em",minWidth:120}}
        >
          {submitting?"提交中…":stripeFlow?"等待支付…":shipError?"超出配送范围":needAddr&&!curAddr?"先填写地址":!pickupPhoneOk?"先填写联系电话":(curPay?.id==="apple_pay"||selPay==="apple_pay")?`Apple Pay $${grandTotal.toFixed(2)}`:selPay==="cod"?`货到付款 $${grandTotal.toFixed(2)}`:"提交订单"}
        </button>
      </div>
      {/* 错误提示 toast — 5 秒后自动淡化消失 */}
      {submitErr&&(
        <div style={{position:"absolute",bottom:88,left:14,right:14,background:"#ffe5e5",border:"1px solid #ffb8b8",color:"#c42020",padding:"10px 14px",borderRadius:10,fontSize:12.5,fontFamily:FF,zIndex:60,boxShadow:"0 4px 12px rgba(0,0,0,0.08)",display:"flex",alignItems:"center",gap:8,animation:"toastFade 5s ease-out forwards"}}>
          <span style={{fontSize:14}}>⚠️</span>
          <span style={{flex:1}}>{submitErr}</span>
          <button onClick={()=>setSubmitErr(null)} style={{background:"none",border:"none",color:"#c42020",cursor:"pointer",fontSize:14,padding:0}}>✕</button>
        </div>
      )}

      {/* Stripe 支付弹窗 — 用 CardElement (无 Link / 无 wallets / 纯卡号输入) */}
      {stripeFlow && stripePromise && (
        <Elements
          stripe={stripePromise}
          options={{
            // 不传 clientSecret — 用 CardElement 模式时不该传(传了 PaymentElement
            // 会被默认载入,Stripe.js 偶发性也会注入 Link autofill)
            locale: 'zh',
            appearance: { theme: 'stripe', variables: { colorPrimary: '#1a1a1a', borderRadius: '10px' } },
          }}
        >
          <StripePaymentModal
            total={grandTotal}
            payMethodId={stripeFlow.payMethodId}
            clientSecret={stripeFlow.clientSecret}
            onClose={() => {
              // 用户关掉了 Stripe 弹窗但没付款 → 把对应的 pending_payment
              // 订单取消掉,避免在"待付款"里堆积幽灵订单。
              // 失败也不影响 UI(订单还会在那,用户可手动取消)。
              const orphanId = stripeFlow.orderId;
              setStripeFlow(null);
              if (orphanId) {
                cancelOrder(orphanId).catch(() => {});
              }
            }}
            onPaid={async (pi) => {
              try {
                const paid = await confirmOrderPayment(stripeFlow.orderId, {
                  payment_intent_id: pi.id,
                  payment_method: stripeFlow.payMethodId,
                });
                setCart([]);
                setStripeFlow(null);
                setDoneOrder({ ...paid, pay_method: stripeFlow.payMethodId });
                setDone(true);
              } catch (e) {
                setSubmitErr("支付完成但订单确认失败,客服将联系您:" + (e.message || ""));
              }
            }}
          />
        </Elements>
      )}
    </div>
  );
}

// ── 游客添加卡(不登录场景)— createPaymentMethod 拿一次性 pm_id ───────────
function GuestCardForm({ onBack, onSaved, onGoLogin }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [numC, setNumC] = useState(false);
  const [expC, setExpC] = useState(false);
  const [cvcC, setCvcC] = useState(false);
  const [zip, setZip] = useState("");
  const ok = numC && expC && cvcC && /^\d{5}$/.test(zip);

  const handleSave = async () => {
    if (!stripe || !elements || !ok) {
      setError("请填写完整的卡信息和邮编"); return;
    }
    setSubmitting(true); setError("");
    try {
      const cardNumber = elements.getElement(CardNumberElement);
      const { paymentMethod, error: pmErr } = await stripe.createPaymentMethod({
        type: 'card', card: cardNumber,
        billing_details: { address: { country: "US", postal_code: zip } },
      });
      if (pmErr) { setError(pmErr.message || "卡号验证失败"); setSubmitting(false); return; }
      onSaved({
        pm_id: paymentMethod.id,
        brand: paymentMethod.card.brand,
        last4: paymentMethod.card.last4,
        exp_month: paymentMethod.card.exp_month,
        exp_year: paymentMethod.card.exp_year,
      });
    } catch (e) {
      setError(e.message || "卡号验证失败"); setSubmitting(false);
    }
  };

  const elStyle = { style: { base: { fontSize: "16px", fontFamily: FF, color: "#1a1a1a", "::placeholder": { color: "#bbb" } }, invalid: { color: "#e03030", iconColor: "#e03030" } } };
  const fieldShell = { background: "#fafafa", border: "1.5px solid #ececec", borderRadius: 10, padding: "13px 14px", minHeight: 22 };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", background: "#f6f7f8" }}>
      <BackHeader title="输入银行卡" onBack={onBack} />
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 14px 24px" }}>
        {/* ── 卡片表单包 — 白底 + 圆角 + 微阴影,提升整体质感 ── */}
        <div style={{
          background: "#fff",
          borderRadius: 16,
          padding: "18px 16px 14px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
          marginBottom: 14,
        }}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: "#888", marginBottom: 7, marginLeft: 2 }}>卡号</div>
            <div style={fieldShell}><CardNumberElement options={{ ...elStyle, placeholder: "1234 1234 1234 1234", showIcon: true }} onChange={(e)=>{setNumC(e.complete);if(e.error)setError(e.error.message);else setError("");}}/></div>
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, color: "#888", marginBottom: 7, marginLeft: 2 }}>有效期</div>
              <div style={fieldShell}><CardExpiryElement options={{ ...elStyle, placeholder: "MM / YY" }} onChange={(e)=>setExpC(e.complete)}/></div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, color: "#888", marginBottom: 7, marginLeft: 2 }}>安全码</div>
              <div style={fieldShell}><CardCvcElement options={{ ...elStyle, placeholder: "CVC" }} onChange={(e)=>setCvcC(e.complete)}/></div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, color: "#888", marginBottom: 7, marginLeft: 2 }}>邮编</div>
              <input type="text" inputMode="numeric" value={zip} onChange={(e)=>setZip(e.target.value.replace(/\D/g,"").slice(0,5))} placeholder="91754" style={{...fieldShell, width:"100%", outline:"none", fontSize:"16px", fontFamily:FF, color:"#1a1a1a", boxSizing:"border-box"}}/>
            </div>
          </div>
          {error && <div style={{padding:"10px 12px",background:"#fee2e2",color:"#c42020",borderRadius:8,fontSize:12,fontFamily:FF,marginBottom:10}}>⚠️ {error}</div>}

          {/* 卡片底部 — 小锁图标 + Stripe 加密提示,增加信任感 */}
          <div style={{
            paddingTop: 10,
            borderTop: "1px dashed #f0f0f0",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
            fontSize: 10.5, color: "#aaa", fontFamily: FF, letterSpacing: "0.02em",
          }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
            </svg>
            <span>卡号由 Stripe 端到端加密传输</span>
          </div>
        </div>

        {/* 注册 / 登录区 — 都跳到 Profile tab(我们的注册和登录是同一个 OTP flow) */}
        {onGoLogin && (
          <div style={{
            background: "#fff",
            borderRadius: 16,
            padding: "16px 14px 14px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
          }}>
            <div style={{ fontSize: 12, color: "#666", textAlign: "center", marginBottom: 12, fontFamily: FF, letterSpacing: "0.02em", lineHeight: 1.5 }}>
              <span style={{ color: "#999" }}>已是十一家会员? </span>登录后可保存付款方式,下次一键结账
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={onGoLogin}
                disabled={submitting}
                style={{
                  flex: 1, padding: "11px 0",
                  background: "#fff", color: "#1a1a1a",
                  border: "1.5px solid #1a1a1a", borderRadius: 10,
                  fontSize: 13.5, fontFamily: FF, fontWeight: 500,
                  cursor: submitting ? "not-allowed" : "pointer",
                  letterSpacing: "0.02em",
                }}
              >注册</button>
              <button
                onClick={onGoLogin}
                disabled={submitting}
                style={{
                  flex: 1, padding: "11px 0",
                  background: "#1a1a1a", color: "#fff",
                  border: "1.5px solid #1a1a1a", borderRadius: 10,
                  fontSize: 13.5, fontFamily: FF, fontWeight: 500,
                  cursor: submitting ? "not-allowed" : "pointer",
                  letterSpacing: "0.02em",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                }}
              >登录</button>
            </div>
          </div>
        )}
      </div>
      <div style={{ background:"#fff",borderTop:"1px solid #f0f0f0",padding:"12px 14px",paddingBottom:"calc(14px + env(safe-area-inset-bottom))",flexShrink:0 }}>
        <button onClick={handleSave} disabled={!stripe||submitting||!ok} style={{ width:"100%",background:ok&&!submitting?"linear-gradient(135deg,#e03030,#c42020)":"#bbb",color:"#fff",border:"none",borderRadius:24,padding:"14px 0",fontSize:15,fontFamily:FF,fontWeight:600,letterSpacing:"0.02em",cursor:!stripe||submitting||!ok?"not-allowed":"pointer",boxShadow:ok&&!submitting?"0 4px 12px rgba(224,48,48,0.3)":"none" }}>
          {submitting?"验证中…":"保存并使用此卡"}
        </button>
      </div>
    </div>
  );
}

// ── Stripe 支付收款弹窗 ─────────────────────────────────────────────────────
// 重要:用 CardNumberElement / CardExpiryElement / CardCvcElement 三个独立元素,
// 而不是合并的 CardElement —— CardElement 在最新 Stripe.js 里集成了 Link 自动填充
// (会显示「自动填充 link」绿色按钮),三个独立元素是纯净的,绝不会有 Link UI。
function StripePaymentModal({ total, payMethodId, clientSecret, onClose, onPaid }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [numComplete, setNumComplete] = useState(false);
  const [expComplete, setExpComplete] = useState(false);
  const [cvcComplete, setCvcComplete] = useState(false);
  const [postalCode, setPostalCode] = useState("");

  const allComplete = numComplete && expComplete && cvcComplete && /^\d{5}$/.test(postalCode);

  const handlePay = async () => {
    if (!stripe || !elements) return;
    if (!allComplete) {
      setError("请填写完整的卡号信息和邮编");
      return;
    }
    setSubmitting(true); setError("");
    try {
      const cardNumber = elements.getElement(CardNumberElement);
      const { error: confirmErr, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardNumber,  // CardNumberElement 是支付方式的"主元素",其他两个会自动关联
          billing_details: {
            address: { country: "US", postal_code: postalCode },
          },
        },
      });
      if (confirmErr) {
        setError(confirmErr.message || "支付失败");
        setSubmitting(false);
        return;
      }
      if (paymentIntent?.status === "succeeded") {
        await onPaid(paymentIntent);
      } else {
        setError(`支付状态异常: ${paymentIntent?.status || "unknown"}`);
        setSubmitting(false);
      }
    } catch (e) {
      setError(e.message || "支付失败,请重试");
      setSubmitting(false);
    }
  };

  // 三个独立元素的统一样式
  const elementStyle = {
    style: {
      base: {
        fontSize: "16px",
        fontFamily: "-apple-system,BlinkMacSystemFont,'PingFang SC','Helvetica Neue',sans-serif",
        color: "#1a1a1a",
        "::placeholder": { color: "#bbb" },
      },
      invalid: { color: "#e03030", iconColor: "#e03030" },
    },
  };

  // 单个字段的容器样式
  const fieldShell = {
    background: "#fafafa",
    border: "1.5px solid #ececec",
    borderRadius: 10,
    padding: "13px 14px",
    minHeight: 22,
  };

  return (
    <div style={{
      // 全屏页面 — 不再是底部弹窗,跟其他全屏 step 一致
      position: "fixed", inset: 0, zIndex: 200,
      background: "#f6f7f8",
      paddingTop: "env(safe-area-inset-top)",
      display: "flex", flexDirection: "column",
    }}>
      {/* Header — 跟其他子页面相同的样式 */}
      <div style={{ background: "#fff", padding: "14px 16px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
        <button
          onClick={!submitting ? onClose : undefined}
          disabled={submitting}
          style={{ background: "none", border: "none", cursor: submitting ? "not-allowed" : "pointer", padding: 0 }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={submitting ? "#bbb" : "#1a1a1a"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <span style={{ fontFamily: SERIF, fontSize: 18, fontWeight: 600, color: "#1a1a1a" }}>输入卡号</span>
      </div>

      {/* Body — 可滚动表单区 */}
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 16px 24px" }}>
        <div style={{ textAlign: "center", marginBottom: 22 }}>
          <div style={{ fontSize: 11, color: "#888", fontFamily: FF, marginBottom: 4, letterSpacing: "0.04em" }}>需支付</div>
          <div style={{ fontSize: 32, color: "#e03030", fontWeight: 700, fontFamily: FF, lineHeight: 1.1, letterSpacing: "-0.02em" }}>${total.toFixed(2)}</div>
        </div>

        {/* 卡号 — 独立 element,无 Link autofill */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: "#888", fontFamily: FF, marginBottom: 7, marginLeft: 2 }}>卡号</div>
          <div style={fieldShell}>
            <CardNumberElement
              options={{ ...elementStyle, placeholder: "1234 1234 1234 1234", showIcon: true }}
              onChange={(e) => { setNumComplete(e.complete); if (e.error) setError(e.error.message); else setError(""); }}
            />
          </div>
        </div>

        {/* 有效期 + 安全码 + 邮编 — 一行三栏 */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: "#888", fontFamily: FF, marginBottom: 7, marginLeft: 2 }}>有效期</div>
            <div style={fieldShell}>
              <CardExpiryElement
                options={{ ...elementStyle, placeholder: "MM / YY" }}
                onChange={(e) => setExpComplete(e.complete)}
              />
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: "#888", fontFamily: FF, marginBottom: 7, marginLeft: 2 }}>安全码</div>
            <div style={fieldShell}>
              <CardCvcElement
                options={{ ...elementStyle, placeholder: "CVC" }}
                onChange={(e) => setCvcComplete(e.complete)}
              />
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: "#888", fontFamily: FF, marginBottom: 7, marginLeft: 2 }}>邮编</div>
            <input
              type="text"
              inputMode="numeric"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, "").slice(0, 5))}
              placeholder="91754"
              style={{
                ...fieldShell,
                width: "100%",
                outline: "none",
                fontSize: "16px",
                fontFamily: "-apple-system,BlinkMacSystemFont,'PingFang SC','Helvetica Neue',sans-serif",
                color: "#1a1a1a",
                boxSizing: "border-box",
              }}
            />
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 11.5, color: "#888", fontFamily: FF, lineHeight: 1.6, padding: "0 2px" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
          <span>卡片信息由 Stripe 端到端加密。十一家不会看到完整卡号或 CVC。</span>
        </div>

        {error && (
          <div style={{
            marginTop: 14,
            padding: "10px 12px", background: "#fee2e2", color: "#c42020",
            borderRadius: 8, fontSize: 12, fontFamily: FF,
          }}>⚠️ {error}</div>
        )}
      </div>

      {/* Footer — 固定底部支付按钮 */}
      <div style={{ background: "#fff", borderTop: "1px solid #f0f0f0", padding: "12px 14px", paddingBottom: "calc(14px + env(safe-area-inset-bottom))", flexShrink: 0 }}>
        <button
          onClick={handlePay}
          disabled={!stripe || submitting || !allComplete}
          style={{
            width: "100%",
            background: allComplete && !submitting ? "linear-gradient(135deg,#e03030,#c42020)" : "#bbb",
            color: "#fff", border: "none", borderRadius: 24,
            padding: "14px 0",
            fontSize: 15, fontFamily: FF, fontWeight: 600, letterSpacing: "0.02em",
            cursor: !stripe || submitting || !allComplete ? "not-allowed" : "pointer",
            boxShadow: allComplete && !submitting ? "0 4px 12px rgba(224,48,48,0.3)" : "none",
          }}
        >
          {submitting ? "处理中…" : `确认支付 $${total.toFixed(2)}`}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PROFILE — ORDER ICONS / FAVORITES / ADDRESS / ABOUT / CONTACT / FEEDBACK
// ─────────────────────────────────────────────────────────────────────────────
function FeedbackPanel({onBack}){
  const [rating,setRating]=useState(0);
  const [hov,setHov]=useState(0);
  const [type,setType]=useState(null);
  const [msg,setMsg]=useState("");
  const [sent,setSent]=useState(false);
  const [submitting,setSubmitting]=useState(false);
  const [err,setErr]=useState("");
  const TYPES=["商品问题","配送问题","支付问题","App体验","其他"];

  const handleSubmit=async()=>{
    if(rating<=0)return;
    setSubmitting(true);setErr("");
    try{
      await submitFeedback({
        rating,
        type:type||"其他",
        message:msg||"",
        app_version:APP_VERSION,
        device_info:typeof navigator!=="undefined"?navigator.userAgent.slice(0,200):"",
      });
      setSent(true);
    }catch(e){
      setErr(e.message||"提交失败,请稍后再试");
    }
    setSubmitting(false);
  };
  if(sent)return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#fff"}}>
      <BackHeader title="反馈意见" onBack={onBack}/>
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14}}>
        <div style={{width:60,height:60,borderRadius:"50%",background:"#1a1a1a",display:"flex",alignItems:"center",justifyContent:"center"}}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <div style={{fontFamily:SERIF,fontSize:17}}>感谢您的反馈</div>
        <div style={{fontSize:13,color:"#aaa",fontFamily:FF,textAlign:"center",maxWidth:220,lineHeight:1.6}}>我们会认真阅读每一条建议，持续改进</div>
        <button onClick={onBack} style={{marginTop:8,background:"none",border:"1.5px solid #1a1a1a",borderRadius:10,padding:"10px 28px",fontSize:13,cursor:"pointer",fontFamily:FF}}>返回</button>
      </div>
    </div>
  );
  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#f7f6f4"}}>
      <BackHeader title="反馈意见" onBack={onBack}/>
      <div style={{flex:1,overflowY:"auto",padding:"20px 16px 30px"}}>
        {/* Star rating */}
        <div style={{background:"#fff",borderRadius:14,padding:"20px 16px",marginBottom:14}}>
          <div style={{fontSize:13,fontFamily:FF,color:"#555",marginBottom:14,fontWeight:500}}>您对本次购物体验的评分</div>
          <div style={{display:"flex",gap:10,justifyContent:"center",marginBottom:10}}>
            {[1,2,3,4,5].map(s=>(
              <button key={s} onMouseEnter={()=>setHov(s)} onMouseLeave={()=>setHov(0)} onClick={()=>setRating(s)}
                style={{background:"none",border:"none",cursor:"pointer",padding:0,fontSize:32,
                  color:(hov||rating)>=s?"#1a1a1a":"#e0e0e0",transition:"color 0.15s,transform 0.1s",
                  transform:(hov||rating)>=s?"scale(1.15)":"scale(1)"}}>★</button>
            ))}
          </div>
          <div style={{textAlign:"center",fontSize:12,color:"#aaa",fontFamily:FF,minHeight:18}}>
            {["","非常不满意","不满意","一般","满意","非常满意"][hov||rating]||""}
          </div>
        </div>
        {/* Feedback type */}
        <div style={{background:"#fff",borderRadius:14,padding:"16px",marginBottom:14}}>
          <div style={{fontSize:13,fontFamily:FF,color:"#555",marginBottom:12,fontWeight:500}}>反馈类型</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
            {TYPES.map(t=>{
              const sel=type===t;
              return(
                <button key={t} onClick={()=>setType(sel?null:t)} style={{padding:"7px 14px",background:sel?"#1a1a1a":"#f5f5f4",border:`1.5px solid ${sel?"#1a1a1a":"transparent"}`,borderRadius:20,cursor:"pointer",fontSize:12.5,fontFamily:FF,color:sel?"#fff":"#555",transition:"all 0.15s"}}>
                  {t}
                </button>
              );
            })}
          </div>
        </div>
        {/* Text */}
        <div style={{background:"#fff",borderRadius:14,padding:"16px",marginBottom:20}}>
          <div style={{fontSize:13,fontFamily:FF,color:"#555",marginBottom:10,fontWeight:500}}>详细描述（选填）</div>
          <textarea value={msg} onChange={e=>setMsg(e.target.value)} placeholder="请描述您遇到的问题或建议…"
            style={{width:"100%",minHeight:110,border:"1.5px solid #f0f0f0",borderRadius:10,padding:"12px",fontSize:13,fontFamily:FF,color:"#333",background:"#fafaf9",outline:"none",resize:"none",boxSizing:"border-box",lineHeight:1.6}}/>
          <div style={{textAlign:"right",fontSize:11,color:"#ccc",fontFamily:FF,marginTop:4}}>{msg.length}/300</div>
        </div>
        {err&&<div style={{padding:"10px 12px",background:"#fee2e2",color:"#c42020",borderRadius:8,fontSize:12,fontFamily:FF,marginBottom:12}}>⚠️ {err}</div>}
        <button onClick={handleSubmit} disabled={rating<=0||submitting}
          style={{...primaryBtn,opacity:(rating>0&&!submitting)?1:0.35,cursor:submitting?"wait":"pointer"}}>
          {submitting?"提交中…":"提交反馈"}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 地址管理
// ─────────────────────────────────────────────────────────────────────────────
function AddressPanel({onBack}){
  const [addrs,setAddrs]=useState([]);
  const [loading,setLoading]=useState(true);
  const [err,setErr]=useState("");
  const [saving,setSaving]=useState(false);
  const [editing,setEditing]=useState(null);
  const [form,setForm]=useState({name:"",phone:"",street:"",city:"",state:"CA",zip:""});

  // 拉云端地址
  useEffect(()=>{
    let alive=true;
    (async()=>{
      try{
        const list=await getAddresses();
        if(alive)setAddrs(list);
      }catch(e){
        if(alive)setErr(e.message||"加载失败");
      }finally{
        if(alive)setLoading(false);
      }
    })();
    return()=>{alive=false;};
  },[]);

  // 错误 5s 自动消失
  useEffect(()=>{
    if(!err)return;
    const t=setTimeout(()=>setErr(""),5000);
    return()=>clearTimeout(t);
  },[err]);

  const startEdit=(a)=>{setEditing(a||{id:null});setForm(a?{...a}:{name:"",phone:"",street:"",city:"",state:"CA",zip:""});};

  const save=async()=>{
    if(!form.name||!form.phone||!form.street||!form.city||!form.state||!form.zip){
      setErr("必填字段不完整");
      return;
    }
    setSaving(true);
    try{
      if(editing.id){
        const updated=await updateAddress(editing.id,form);
        setAddrs(list=>list.map(x=>x.id===editing.id?updated:x));
      }else{
        const created=await createAddress({...form,isDefault:addrs.length===0});
        // 服务端会确保第一条自动设为默认 + 同账号其他默认被清掉
        setAddrs(list=>{
          const cleared=created.isDefault?list.map(x=>({...x,isDefault:false})):list;
          return [...cleared,created];
        });
      }
      setEditing(null);
    }catch(e){
      setErr(e.message||"保存失败");
    }finally{
      setSaving(false);
    }
  };

  const remove=async(id)=>{
    try{
      await deleteAddress(id);
      setAddrs(list=>list.filter(x=>x.id!==id));
    }catch(e){
      setErr(e.message||"删除失败");
    }
  };

  const setDefault=async(id)=>{
    // 乐观更新
    const prev=addrs;
    setAddrs(list=>list.map(x=>({...x,isDefault:x.id===id})));
    try{
      await setDefaultAddress(id);
    }catch(e){
      setAddrs(prev);
      setErr(e.message||"设置失败");
    }
  };

  // 获取当前定位 → 反查 Google Maps → 自动填充地址栏
  const [locating,setLocating]=useState(false);
  // 自动定位回填后用这个 token 让 AddressAutocompleteInput 跳过下次 autocomplete
  const [addrSuppressToken,setAddrSuppressToken]=useState(0);
  const useCurrentLocation=()=>{
    const hasGeo=("geolocation" in navigator)||window.__syjGetLocation;
    if(!hasGeo){
      setErr("当前设备不支持定位");
      return;
    }
    setLocating(true);setErr("");
    const onSuccess=async(pos)=>{
      try{
        const{latitude:lat,longitude:lng}=pos.coords;
        const d=await mapsReverseGeocode(lat,lng);
        if(d&&(d.line1||d.zip||d.city)){
          setForm(f=>({
            ...f,
            street:d.line1||f.street,
            city:d.city||f.city,
            state:(d.state||f.state||"CA").toUpperCase().slice(0,2),
            zip:(d.zip||f.zip||"").slice(0,5),
          }));
          // 让街道 autocomplete 这次跳过 — 不弹建议
          setAddrSuppressToken(t=>t+1);
        }else{
          // 定位 OK 但 Google 没解析出来 — 把坐标显示出来便于排查
          setErr(`定位成功(${pos.coords.latitude.toFixed(3)}, ${pos.coords.longitude.toFixed(3)}),但 Google 没返回地址,请手动输入`);
        }
      }catch(e){
        // 后端 Maps API 故障 / Key 未配置 / 配额超限 — 把真实错误透出来
        setErr(`地址解析失败:${e?.message||"未知错误"}`);
      }finally{
        setLocating(false);
      }
    };
    const onError=(geoErr)=>{
      setLocating(false);
      setErr(geoErr?.message || (geoErr?.code===1?"定位权限未开启":"无法获取定位,请手动输入"));
    };
    const fn=navigator.geolocation?.getCurrentPosition?.bind(navigator.geolocation)||window.__syjGetLocation;
    fn(onSuccess,onError,{enableHighAccuracy:true,timeout:12000,maximumAge:60_000});
  };

  if(editing){
    return(
      <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#fff"}}>
        <BackHeader title={editing.id?"编辑地址":"添加地址"} onBack={()=>setEditing(null)}/>
        <div style={{flex:1,overflowY:"auto",padding:"18px 16px"}}>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <input placeholder="收件人姓名" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} style={inStyle}/>
            <input placeholder="联系电话" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} style={inStyle}/>

            {/* 使用当前定位 — 一键反查 GPS → 街道/城市/州/邮编 */}
            <button
              type="button"
              onClick={useCurrentLocation}
              disabled={locating}
              style={{
                display:"flex",alignItems:"center",justifyContent:"center",gap:8,
                padding:"10px 14px",borderRadius:10,
                border:"1.5px dashed #c7c7c2",background:"#f9f9f7",
                color:locating?"#888":"#1a1a1a",fontFamily:FF,fontSize:13,fontWeight:500,
                cursor:locating?"wait":"pointer",
              }}
            >
              {locating?(
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{animation:"spin 0.9s linear infinite"}}>
                    <path d="M21 12a9 9 0 11-6.219-8.56"/>
                  </svg>
                  正在定位…
                </>
              ):(
                <>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="10" r="3"/>
                    <path d="M12 2a8 8 0 00-8 8c0 5.4 8 12 8 12s8-6.6 8-12a8 8 0 00-8-8z"/>
                  </svg>
                  使用当前定位
                </>
              )}
            </button>

            <AddressAutocompleteInput
              value={form.street}
              onChange={v=>setForm(f=>({...f,street:v}))}
              onPick={d=>setForm(f=>({...f,street:d.line1||f.street,city:d.city||f.city,state:d.state||f.state||"CA",zip:d.zip||f.zip}))}
              placeholder="街道地址(开始输入会自动建议)"
              style={inStyle}
              suppressToken={addrSuppressToken}
            />
            <div style={{display:"flex",gap:10}}>
              <input placeholder="城市" value={form.city} onChange={e=>setForm(f=>({...f,city:e.target.value}))} style={{...inStyle,flex:2}}/>
              <input placeholder="州" value={form.state} onChange={e=>setForm(f=>({...f,state:e.target.value.toUpperCase().slice(0,2)}))} style={{...inStyle,flex:1}}/>
              <input placeholder="邮编" value={form.zip} onChange={e=>setForm(f=>({...f,zip:e.target.value.replace(/\D/g,"").slice(0,5)}))} style={{...inStyle,flex:1.4}}/>
            </div>
          </div>
          {err&&<div style={{marginTop:14,fontSize:12,color:"#e03030",fontFamily:FF,textAlign:"center"}}>{err}</div>}
          <button onClick={save} disabled={saving} style={{...primaryBtn,marginTop:24,opacity:saving?0.6:1}}>{saving?"保存中…":"保存"}</button>
          {editing.id&&<button onClick={()=>{remove(editing.id);setEditing(null);}} style={{...ghostBtn,marginTop:10,color:"#e03030",borderColor:"#e03030"}}>删除地址</button>}
        </div>
      </div>
    );
  }

  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#f7f6f4"}}>
      <BackHeader title="地址管理" onBack={onBack}/>
      <div style={{flex:1,overflowY:"auto",padding:"14px 14px 80px"}}>
        {err&&!loading&&(
          <div style={{textAlign:"center",padding:"10px",color:"#e03030",fontSize:12.5,fontFamily:FF,background:"#fff5f5",borderRadius:8,marginBottom:10}}>{err}</div>
        )}
        {loading?(
          <div style={{textAlign:"center",padding:"60px 0",color:"#bbb",fontSize:13,fontFamily:FF}}>加载中…</div>
        ):addrs.length===0?(
          <div style={{textAlign:"center",padding:"60px 0",color:"#bbb",fontSize:13,fontFamily:FF}}>暂无收货地址</div>
        ):addrs.map(a=>(
          <div key={a.id} style={{background:"#fff",borderRadius:12,padding:"14px 16px",marginBottom:10,boxShadow:"0 1px 4px rgba(0,0,0,0.04)"}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
              <span style={{fontSize:14,color:"#1a1a1a",fontFamily:FF,fontWeight:600}}>{a.name}</span>
              <span style={{fontSize:12,color:"#888",fontFamily:FF}}>{a.phone}</span>
              {a.isDefault&&<span style={{fontSize:10,color:"#e03030",border:"1px solid #e03030",borderRadius:3,padding:"1px 5px",fontFamily:FF}}>默认</span>}
            </div>
            <div style={{fontSize:12.5,color:"#555",fontFamily:FF,lineHeight:1.5,marginBottom:10}}>{a.street},{a.city},{a.state} {a.zip}</div>
            <div style={{display:"flex",gap:14,alignItems:"center",borderTop:"1px solid #f5f5f4",paddingTop:9}}>
              {!a.isDefault&&<button onClick={()=>setDefault(a.id)} style={{background:"none",border:"none",fontSize:11.5,color:"#666",fontFamily:FF,cursor:"pointer",padding:0}}>设为默认</button>}
              <span style={{flex:1}}/>
              <button onClick={()=>startEdit(a)} style={{background:"none",border:"none",fontSize:11.5,color:"#666",fontFamily:FF,cursor:"pointer",padding:0}}>编辑</button>
              <button onClick={()=>remove(a.id)} style={{background:"none",border:"none",fontSize:11.5,color:"#e03030",fontFamily:FF,cursor:"pointer",padding:0}}>删除</button>
            </div>
          </div>
        ))}
      </div>
      <div style={{padding:"12px 14px 24px",background:"#fff",borderTop:"1px solid #f0f0f0"}}>
        <button onClick={()=>startEdit(null)} style={primaryBtn}>+ 添加新地址</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 关于我们
// ─────────────────────────────────────────────────────────────────────────────
function AboutPanel({onBack}){
  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#fff"}}>
      <BackHeader title="关于我们" onBack={onBack}/>
      <div style={{flex:1,overflowY:"auto",padding:"32px 24px 40px"}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{width:80,height:80,borderRadius:20,background:"linear-gradient(135deg,#1a1a1a,#444)",margin:"0 auto 14px",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:30,fontFamily:SERIF,fontWeight:600,boxShadow:"0 8px 24px rgba(0,0,0,0.15)"}}>十一</div>
          <div style={{fontSize:20,fontFamily:SERIF,color:"#1a1a1a",fontWeight:600,marginBottom:5}}>十一家</div>
          <div style={{fontSize:11.5,color:"#aaa",fontFamily:FF,letterSpacing:"0.04em"}}>v1.0.0</div>
        </div>
        <div style={{fontSize:13.5,color:"#444",fontFamily:FF,lineHeight:1.8,marginBottom:24}}>
          十一家是面向北美华人社区的精选生活方式品牌,提供高品质的服饰、美妆护肤、日用百货等商品。我们坚持「正品保障、快速送达、贴心服务」三大承诺,让每一位客户都能享受愉快、便捷的购物体验。
        </div>
        <div style={{background:"#f7f6f4",borderRadius:12,padding:"18px 18px",marginBottom:18}}>
          <div style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600,marginBottom:10}}>我们的使命</div>
          <div style={{fontSize:12.5,color:"#666",fontFamily:FF,lineHeight:1.7}}>让北美华人随时随地买到家的味道、家的便利。</div>
        </div>
        <div style={{background:"#f7f6f4",borderRadius:12,padding:"18px 18px",marginBottom:18}}>
          <div style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600,marginBottom:10}}>核心价值</div>
          {["品质优先 — 每一件商品严选把关","隐私保护 — 包装匿名,贴心配送","用心服务 — 真人客服每日在线"].map((t,i)=>(
            <div key={i} style={{display:"flex",alignItems:"flex-start",gap:8,marginBottom:i<2?8:0,fontSize:12.5,color:"#666",fontFamily:FF,lineHeight:1.6}}>
              <span style={{color:"#3a9e3a",marginTop:2}}>✓</span>
              <span>{t}</span>
            </div>
          ))}
        </div>
        <div style={{textAlign:"center",fontSize:11,color:"#bbb",fontFamily:FF,marginTop:24,lineHeight:1.7}}>
          © 2023-2026 十一家 SYJ Inc.<br/>
          All rights reserved.
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 联系客服
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// 政策类页面(隐私政策 / 服务条款)— 通用渲染器
// ─────────────────────────────────────────────────────────────────────────────
// 政策类页面默认更新日期 — 改一次同时改下面 PRIVACY_UPDATED_AT / TERMS_UPDATED_AT
const PRIVACY_UPDATED_AT = "2026 年 5 月 7 日";
const TERMS_UPDATED_AT   = "2026 年 5 月 7 日";
function PolicyPanel({title,sections,onBack,updatedAt=PRIVACY_UPDATED_AT}){
  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#fff"}}>
      <BackHeader title={title} onBack={onBack}/>
      <div style={{flex:1,overflowY:"auto",padding:"24px 20px 40px"}}>
        <div style={{fontSize:11.5,color:"#aaa",fontFamily:FF,marginBottom:18,letterSpacing:"0.04em"}}>最后更新:{updatedAt}</div>
        {sections.map((s,i)=>(
          <div key={i} style={{marginBottom:22}}>
            {s.title&&<div style={{fontSize:14,color:"#1a1a1a",fontFamily:FF,fontWeight:600,marginBottom:8}}>{s.title}</div>}
            {(Array.isArray(s.body)?s.body:[s.body]).map((p,j)=>(
              <div key={j} style={{fontSize:13,color:"#444",fontFamily:FF,lineHeight:1.85,marginBottom:8,whiteSpace:"pre-wrap"}}>{p}</div>
            ))}
          </div>
        ))}
        <div style={{marginTop:32,padding:"14px 16px",background:"#f7f6f4",borderRadius:10,fontSize:11.5,color:"#888",fontFamily:FF,lineHeight:1.7}}>
          如有疑问,请通过「联系客服」与我们沟通,或发送邮件至 <span style={{color:"#1a1a1a",fontWeight:500}}>shiyi@shiyigt.com</span>
        </div>
      </div>
    </div>
  );
}

function PrivacyPolicyPanel({onBack}){
  return <PolicyPanel onBack={onBack} title="隐私政策" sections={[
    {body:"十一家(以下简称「我们」)非常重视您的隐私。本隐私政策说明了我们如何收集、使用、共享和保护您在使用十一家 APP 时提供的个人信息。使用本服务即表示您同意本政策的条款。"},
    {title:"1. 我们收集的信息",body:[
      "• 账号信息:手机号、昵称、邮箱(选填)、头像",
      "• 订单信息:收货地址、联系电话、购买的商品、支付凭据(经第三方加密处理)",
      "• 设备信息:设备型号、操作系统、IP 地址、APP 版本",
      "• 使用数据:浏览记录、搜索关键词、收藏夹、购物车",
      "• 位置信息:仅在您主动使用「使用当前位置」功能时获取,用于自动填充收货地址",
    ]},
    {title:"2. 我们如何使用信息",body:[
      "• 提供商品展示、订单处理、配送、售后等核心服务",
      "• 通过短信发送验证码、订单状态通知",
      "• 改善产品体验、推荐您可能感兴趣的商品",
      "• 防止欺诈、维护账号安全",
      "• 履行法律法规要求",
    ]},
    {title:"3. 第三方服务",body:[
      "我们使用以下第三方服务,他们仅在为我们提供服务的范围内访问您的相关信息:",
      "• Apple Sign In:可选登录方式,Apple 仅返回稳定的匿名用户标识(Apple ID),您可选择是否分享真实邮箱",
      "• Twilio:发送短信验证码、在线客服消息",
      "• Stripe:支付处理(您的卡号不会经过我们服务器)",
      "• EasyPost / USPS / UPS:运费计算和包裹追踪",
      "• Google Maps:地址自动补全和邮编反查",
      "• Cloudflare R2:商品图片存储",
      "• Sentry:错误日志收集(已开启 PII 脱敏)",
      "他们各自有独立的隐私政策,我们建议您查阅。",
    ]},
    {title:"4. App Tracking Transparency (iOS)",body:"在 iOS 14.5+ 设备上,首次启动时我们可能会弹出系统的「跟踪权限」对话框。我们 **不会** 跨 APP/网站跟踪您的活动用于广告投放。我们仅在内部用您的浏览/购买/收藏数据为您推荐更相关的商品(数据保留在我们自己的服务器,不与第三方共享)。如果您选择「请 APP 不要跟踪」,APP 仍可正常使用,所有功能不受影响。"},
    {title:"5. 信息共享",body:"除非:(a) 您同意分享、(b) 履行您的订单(例如向快递公司提供地址)、(c) 法律法规要求,我们不会将您的个人信息出售或共享给任何第三方。"},
    {title:"6. 数据安全",body:"我们采用 HTTPS 传输加密、密码哈希存储、严格的访问控制等措施保护您的信息。但请注意,互联网传输无法保证 100% 安全。"},
    {title:"7. 您的权利",body:[
      "您有权:",
      "• 随时查看、修改个人资料(在「我的 → 编辑个人资料」中)",
      "• 在 APP 内一键注销账号:进入「我的 → 编辑个人资料」,滚动到页面底部点击「注销账号」即可。注销后个人资料(姓名、邮箱、地址、收藏、购物车、积分、优惠券等)将立即从我们的活跃数据库中清除;订单记录因税务及售后需要保留 30 天,之后转入归档库,仅保留法律强制要求的最少字段。",
      "• 拒绝接收营销短信(回复 STOP 退订);邮件营销可在邮件底部点退订",
      "• 加州居民(CCPA / CPRA):您可要求获取我们持有的关于您的数据副本、要求更正或删除您的个人信息、限制销售/共享您的信息。请发送邮件至 shiyi@shiyigt.com,我们将在 45 天内响应。",
    ]},
    {title:"8. 儿童隐私",body:"本服务面向 18 岁以上成年用户。我们不会有意收集 18 岁以下未成年人的信息。如发现未成年人提交了信息,我们将立即删除。"},
    {title:"9. 政策变更",body:"我们可能不时更新本政策,重大变更会通过 APP 内通知或邮件告知您。继续使用本服务即视为您接受更新后的政策。"},
    {title:"10. 联系我们",body:[
      "运营主体:Shiyi Global Trading LLC",
      `地址:${PICKUP_ADDRESS}`,
      "邮箱:shiyi@shiyigt.com",
      `电话:${CONTACT_PHONE_DISPLAY}`,
    ]},
  ]}/>;
}

function TermsPanel({onBack}){
  return <PolicyPanel onBack={onBack} title="用户协议" sections={[
    {body:"欢迎使用十一家 APP。本协议是您与十一家(运营主体:Shiyi Global Trading LLC)就 APP 服务所订立的法律协议。注册或使用本服务即表示您已阅读、理解并同意接受本协议的全部条款。"},
    {title:"1. 用户资格",body:"您必须年满 18 周岁,且具有独立民事行为能力,方可注册和使用本服务。"},
    {title:"2. 账号管理",body:[
      "• 您应妥善保管手机号和验证码,不得将账号转让、出借给他人",
      "• 因您未妥善保管造成的账号损失,由您自行承担",
      "• 如发现账号被盗用,请立即联系我们",
      "• 我们有权对涉嫌违规、欺诈的账号采取封禁等措施",
    ]},
    {title:"3. 商品与订单",body:[
      "• 商品展示信息(图片、价格、描述)我们已尽力准确,但仍可能存在误差,实际以发货为准",
      "• 订单提交后即视为采购意愿确认,我们有权根据库存等情况选择是否接受订单",
      "• 价格如有变动以下单时为准,极少数情况下因系统原因导致明显错价的订单,我们有权取消并退款",
      "• 您应在下单时如实填写收货信息,因信息错误导致的损失由您承担",
    ]},
    {title:"4. 支付与配送",body:[
      "• 支持信用卡、Apple Pay、PayPal 等支付方式,具体以结账页为准",
      "• 配送方式包括到店自取、送货上门(限指定区域)、快递邮寄",
      "• 满 $100 (非特价商品)享受免运费,具体规则以结账页为准",
      "• 销售税按当地税率自动计算",
    ]},
    {title:"5. 退换货政策",body:[
      "• 签收后 7 天内,在不影响二次销售的前提下(商品完好、吊牌/包装齐全、未使用未洗涤、附件齐全),可在 APP「我的订单 → 订单详情 → 申请退换货」中提交申请,并请第一时间寄回。",
      "• 因商品质量问题(发错货、包装破损、瑕疵等)的退换货由我们承担运费;",
      "• 因个人原因(尺码不合、颜色偏差、不喜欢等)的退换货,运费由您承担;",
      "• 出于卫生及安全考虑,部分品类不支持无理由退换:贴身衣物、化妆品(已开封)、食品、营养品、定制商品等;",
      "• 退款将在审核通过后 3-7 个工作日内原路退回原支付方式;",
      "• 超过 7 天未联系或拒绝寄回的,我们将视为放弃退换货权利。",
    ]},
    {title:"6. 隐私政策",body:"我们如何收集、使用、保护您的信息,请参阅《隐私政策》。"},
    {title:"7. 用户行为规范",body:[
      "您不得:",
      "• 利用本服务从事违法犯罪活动",
      "• 上传/发送虚假、骚扰、辱骂、淫秽信息",
      "• 干扰我们正常运营、攻击系统、爬取数据",
      "• 转售商品(批发请联系商务合作)",
      "违反规定的,我们有权封号并追究法律责任。",
    ]},
    {title:"8. 知识产权",body:"本 APP 内的商标、Logo、文字、图片、设计等所有知识产权归我们或合法权利人所有。未经书面许可,不得复制、转载、改编。"},
    {title:"9. 免责声明",body:[
      "在法律允许的最大范围内,我们对以下情况不承担责任:",
      "• 因不可抗力(自然灾害、战争、政府行为等)导致的服务中断或损失",
      "• 因网络通信、第三方支付、第三方物流等原因造成的延误或损失",
      "• 您未妥善保管账号、密码导致的损失",
    ]},
    {title:"10. 适用法律与争议解决",body:"本协议的订立、生效、解释、执行均适用美国加利福尼亚州法律。因本协议产生的争议,双方应友好协商;协商不成的,提交至加州洛杉矶县有管辖权的法院诉讼解决。"},
    {title:"11. 协议修改",body:"我们有权根据业务发展不时修改本协议,修改后的协议将通过 APP 通知或在本页面发布,继续使用即视为您接受修改后的协议。"},
    {title:"12. 联系我们",body:"如对本协议有任何疑问,请通过 APP 内「联系客服」或邮件 shiyi@shiyigt.com 与我们联系。"},
  ]}/>;
}

// ─────────────────────────────────────────────────────────────────────────────
// 在线客服(Twilio Conversations)
//   prefillProduct: 从商品详情点"客服"过来时,挂一个待咨询商品在输入框上方
//                   发消息时自动把商品信息 prepend 到消息体(图片+名+价格+链接)
//   onPrefillConsumed: 商品发送后或用户主动 ✕ 关掉时调用,清掉父级 state
//   loggedIn=false 也允许发消息 — 后端按 guestId(localStorage UUID)区分会话
// ─────────────────────────────────────────────────────────────────────────────
// 游客 ID:每个浏览器/设备一个长期 UUID,存 localStorage,用于把游客发的消息
//   归到同一个 Twilio conversation。登录后会切到 customer-{id} 会话,游客
//   会话里的历史保留(以后实在需要可做迁移)
const GUEST_CHAT_ID_KEY = "syj_chat_guest_id";
function getOrCreateGuestId(){
  try{
    let id=localStorage.getItem(GUEST_CHAT_ID_KEY);
    if(!id){
      // 生成 24 字符 base36 UUID-ish — 字母数字,符合后端正则
      id=Math.random().toString(36).slice(2,14)+Math.random().toString(36).slice(2,14);
      localStorage.setItem(GUEST_CHAT_ID_KEY,id);
    }
    return id;
  }catch{return null;}
}

// 商品咨询消息体格式(发送时拼成)→ 渲染时解析回 card:
//   📦 商品咨询
//   🖼️ {imgUrl}
//   🏷️ {name}
//   💰 ${price}
//   🔗 {productUrl}
//
//   {可选的用户提问文字}
function parseProductCard(body){
  if(!body||!body.startsWith("📦 商品咨询"))return null;
  const lines=body.split("\n");
  const card={};
  let blankIdx=-1;
  for(let i=1;i<lines.length;i++){
    const line=lines[i];
    if(line.startsWith("🖼️ "))card.imgUrl=line.slice(3).trim();
    else if(line.startsWith("🏷️ "))card.name=line.slice(3).trim();
    else if(line.startsWith("💰 "))card.price=line.slice(3).trim();
    else if(line.startsWith("🔗 "))card.url=line.slice(3).trim();
    else if(line.trim()===""){blankIdx=i;break;}
  }
  card.text=blankIdx>=0?lines.slice(blankIdx+1).join("\n").trim():"";
  return card;
}

// 聊天气泡 — 普通消息直接渲染文字;商品咨询消息渲染富文本卡片
function ChatBubble({body,isSelf}){
  const card=parseProductCard(body);
  const baseStyle={maxWidth:"72%",background:isSelf?"linear-gradient(135deg,#e03030,#c42020)":"#fff",color:isSelf?"#fff":"#1a1a1a",padding:"9px 13px",borderRadius:isSelf?"14px 14px 4px 14px":"14px 14px 14px 4px",fontSize:13.5,fontFamily:FF,lineHeight:1.5,wordBreak:"break-word",boxShadow:"0 1px 3px rgba(0,0,0,0.05)",whiteSpace:"pre-wrap"};
  if(!card){
    return <div style={baseStyle}>{body}</div>;
  }
  // 商品咨询卡片
  return(
    <div style={{...baseStyle,padding:0,overflow:"hidden",maxWidth:"80%"}}>
      <div style={{padding:"7px 12px",fontSize:11,opacity:0.85,borderBottom:`1px solid ${isSelf?"rgba(255,255,255,0.18)":"#f0f0f0"}`,letterSpacing:"0.04em"}}>
        📦 商品咨询
      </div>
      {card.url?(
        <a href={card.url} target="_blank" rel="noreferrer" style={{display:"flex",gap:10,padding:"10px 12px",textDecoration:"none",color:"inherit",alignItems:"center"}}>
          {card.imgUrl&&(
            <div style={{width:56,height:56,borderRadius:8,background:"#f5f5f4",overflow:"hidden",flexShrink:0}}>
              <img src={card.imgUrl} alt={card.name||""} loading="lazy" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
            </div>
          )}
          <div style={{flex:1,minWidth:0}}>
            {card.name&&<div style={{fontSize:13,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{card.name}</div>}
            {card.price&&<div style={{fontSize:13.5,fontWeight:700,color:isSelf?"#fff":"#e03030",marginTop:3}}>{card.price}</div>}
            <div style={{fontSize:10.5,opacity:0.7,marginTop:4}}>点击查看商品 ›</div>
          </div>
        </a>
      ):(
        <div style={{display:"flex",gap:10,padding:"10px 12px",alignItems:"center"}}>
          {card.imgUrl&&(
            <div style={{width:56,height:56,borderRadius:8,background:"#f5f5f4",overflow:"hidden",flexShrink:0}}>
              <img src={card.imgUrl} alt={card.name||""} loading="lazy" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
            </div>
          )}
          <div style={{flex:1,minWidth:0}}>
            {card.name&&<div style={{fontSize:13,fontWeight:600}}>{card.name}</div>}
            {card.price&&<div style={{fontSize:13.5,fontWeight:700,color:isSelf?"#fff":"#e03030",marginTop:3}}>{card.price}</div>}
          </div>
        </div>
      )}
      {card.text&&(
        <div style={{padding:"8px 12px",borderTop:`1px solid ${isSelf?"rgba(255,255,255,0.18)":"#f0f0f0"}`,fontSize:13,lineHeight:1.5,whiteSpace:"pre-wrap"}}>{card.text}</div>
      )}
    </div>
  );
}

function ChatPanel({onBack,loggedIn,onNeedLogin,prefillProduct,onPrefillConsumed}){
  const [messages,setMessages]=useState([]);
  const [input,setInput]=useState("");
  const [sending,setSending]=useState(false);
  const [loading,setLoading]=useState(true);
  const [err,setErr]=useState(null);
  const [pendingProduct,setPendingProduct]=useState(prefillProduct||null);
  const scrollRef=useRef(null);
  // 游客 chat ID — 登录用户也保留,用于历史无缝(优先 customer JWT,backend 自动识别)
  const guestIdRef=useRef(loggedIn?null:getOrCreateGuestId());

  // prefillProduct 变化(用户从另一个商品再来咨询)→ 替换 pending
  useEffect(()=>{
    if(prefillProduct)setPendingProduct(prefillProduct);
  },[prefillProduct]);

  const fetchMsgs=useCallback(async()=>{
    try{
      const data=await chatList(loggedIn?null:guestIdRef.current);
      setMessages(data||[]);
      setErr(null);
    }catch(e){
      if(e.status===401){setMessages([]);setErr(null);return;}
      setErr(e.message||"加载消息失败");
    }finally{setLoading(false);}
  },[loggedIn]);

  useEffect(()=>{
    fetchMsgs();
    // 每 5s 拉新消息;标签页隐藏时跳过(节省请求 + 电量)
    const t=setInterval(()=>{
      if(document.visibilityState!=="visible")return;
      fetchMsgs();
    },5000);
    const onVis=()=>{if(document.visibilityState==="visible")fetchMsgs();};
    document.addEventListener("visibilitychange",onVis);
    return()=>{clearInterval(t);document.removeEventListener("visibilitychange",onVis);};
  },[loggedIn,fetchMsgs]);

  // 滚动到底部
  useEffect(()=>{
    const el=scrollRef.current;
    if(el)el.scrollTop=el.scrollHeight;
  },[messages.length]);

  const dismissProductCard=()=>{
    setPendingProduct(null);
    onPrefillConsumed?.();
  };

  const handleSend=async()=>{
    const text=input.trim();
    if((!text&&!pendingProduct)||sending)return;
    setSending(true);
    try{
      // 商品咨询 → 用富文本卡片格式,客服一眼看到图、名、价、链接
      let body=text;
      if(pendingProduct){
        const p=pendingProduct;
        const lines=["📦 商品咨询"];
        if(p.imgUrl)lines.push(`🖼️ ${p.imgUrl}`);
        if(p.name)lines.push(`🏷️ ${p.name}`);
        if(p.price)lines.push(`💰 $${parseFloat(p.price).toFixed(2)}`);
        if(p.id)lines.push(`🔗 https://us198.com/?p=${p.id}`);
        const productCard=lines.join("\n");
        body=text?`${productCard}\n\n${text}`:productCard;
      }
      await chatSend(body, loggedIn?null:guestIdRef.current);
      setInput("");
      if(pendingProduct){
        setPendingProduct(null);
        onPrefillConsumed?.();
      }
      await fetchMsgs();
    }catch(e){
      setErr(e.message||"发送失败");
    }finally{setSending(false);}
  };

  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#f6f7f8"}}>
      <BackHeader title="在线客服" onBack={onBack}/>

      {/* 消息列表 */}
      <div ref={scrollRef} style={{flex:1,overflowY:"auto",padding:"14px 14px 14px"}}>
        {loading&&<div style={{textAlign:"center",padding:"40px 0",color:"#bbb",fontSize:12,fontFamily:FF}}>加载中…</div>}

        {!loading&&messages.length===0&&(
          <div style={{textAlign:"center",padding:"40px 16px"}}>
            <div style={{fontSize:38,opacity:0.4,marginBottom:10}}>👋</div>
            <div style={{fontSize:13,color:"#666",fontFamily:FF,fontWeight:500,marginBottom:4}}>欢迎咨询十一家客服</div>
            <div style={{fontSize:11.5,color:"#aaa",fontFamily:FF,lineHeight:1.6}}>{CONTACT_HOURS}<br/>有任何问题请直接发送消息</div>
          </div>
        )}

        {messages.map(m=>(
          <div key={m.sid} style={{display:"flex",justifyContent:m.is_self?"flex-end":"flex-start",marginBottom:10}}>
            {!m.is_self&&(
              <div style={{width:30,height:30,borderRadius:"50%",background:"linear-gradient(135deg,#1a1a1a,#3a3a3a)",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontFamily:SERIF,fontWeight:600,marginRight:8,flexShrink:0,alignSelf:"flex-end"}}>客</div>
            )}
            <ChatBubble body={m.body} isSelf={m.is_self}/>
          </div>
        ))}
      </div>

      {/* 错误提示 */}
      {err&&<div style={{background:"#fff5f5",color:"#e03030",padding:"8px 14px",fontSize:12,fontFamily:FF,borderTop:"1px solid #ffd0d0"}}>⚠ {err}</div>}

      {/* 待咨询商品卡 — 跟淘宝/拼多多一样,商品自动挂在输入框上方 */}
      {pendingProduct&&(
        <div style={{background:"#fff",borderTop:"1px solid #f0f0f0",padding:"10px 12px",display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:42,height:42,borderRadius:8,background:"#f5f5f4",overflow:"hidden",flexShrink:0}}>
            {pendingProduct.imgUrl?(
              <img src={pendingProduct.imgUrl} alt={pendingProduct.name||""} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
            ):pendingProduct.img?(
              <div style={{width:"100%",height:"100%",background:pendingProduct.img,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>🛍️</div>
            ):(
              <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>🛍️</div>
            )}
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:11,color:"#888",fontFamily:FF,marginBottom:2}}>正在咨询</div>
            <div style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:500,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{pendingProduct.name||"商品"}</div>
            {pendingProduct.price&&<div style={{fontSize:12,color:"#e03030",fontFamily:FF,fontWeight:600,marginTop:1}}>${parseFloat(pendingProduct.price).toFixed(2)}</div>}
          </div>
          <button onClick={dismissProductCard} style={{background:"none",border:"none",cursor:"pointer",padding:6,color:"#bbb",flexShrink:0}} aria-label="移除">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      )}

      {/* 输入区 */}
      <div style={{background:"#fff",borderTop:pendingProduct?"none":"1px solid #f0f0f0",padding:"10px 12px 18px",display:"flex",alignItems:"flex-end",gap:10}}>
        <textarea
          value={input}
          onChange={e=>setInput(e.target.value)}
          onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();handleSend();}}}
          placeholder={pendingProduct?"对这件商品有什么问题?":"输入消息…"}
          rows={1}
          style={{flex:1,padding:"10px 14px",border:"1.5px solid #ececec",borderRadius:18,outline:"none",fontSize:13.5,color:"#1a1a1a",fontFamily:FF,background:"#fafaf9",resize:"none",maxHeight:100,lineHeight:1.5}}
        />
        <button onClick={handleSend} disabled={(!input.trim()&&!pendingProduct)||sending} style={{background:((input.trim()||pendingProduct)&&!sending)?"linear-gradient(135deg,#e03030,#c42020)":"#ccc",color:"#fff",border:"none",borderRadius:"50%",width:38,height:38,cursor:((input.trim()||pendingProduct)&&!sending)?"pointer":"not-allowed",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:((input.trim()||pendingProduct)&&!sending)?"0 2px 6px rgba(224,48,48,0.3)":"none"}}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{marginLeft:2}}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
        </button>
      </div>
    </div>
  );
}

function ContactPanel({onBack,onOpenChat}){
  const [openFaq,setOpenFaq]=useState(null);
  const faqs=[
    {q:"配送时间多久?",a:"美西时间下午 3:00 前下单当天发货,过点顺延次日。本地(加州)1-2 个工作日送达,跨州 2-5 个工作日,偏远地区可能略长。"},
    {q:"如何申请退换货?",a:"签收后 7 天内,在不影响二次销售的前提下(商品完好、包装/吊牌齐全、未使用未洗涤),可在「我的订单 → 订单详情 → 申请退换货」提交申请,并请第一时间寄回。客服 1 个工作日内审核,通过后 3-7 个工作日原路退款。"},
    {q:"包装是否隐私?",a:"我们使用纯色匿名外包装,不会显示商品内容,保护您的隐私。"},
    {q:"是否支持自提?",a:`支持。门店地址 ${PICKUP_ADDRESS},营业时间${PICKUP_HOURS}(美西时间 PT)。`},
    {q:"支持哪些支付方式?",a:"支持信用卡 / 借记卡(Visa / Mastercard / Amex / Discover)、Apple Pay、Google Pay。门店自取另支持现金和 Zelle。"},
  ];
  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#f7f6f4"}}>
      <BackHeader title="联系客服" onBack={onBack}/>
      <div style={{flex:1,overflowY:"auto",padding:"14px 14px 24px"}}>
        <div style={{background:"#fff",borderRadius:14,padding:"18px 16px",marginBottom:12}}>
          <div style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600,marginBottom:4}}>客服在线时间</div>
          <div style={{fontSize:12,color:"#666",fontFamily:FF}}>{CONTACT_HOURS}</div>
          <div style={{fontSize:11,color:"#aaa",fontFamily:FF,marginTop:4}}>非工作时间留言会在次日上班后回复</div>
        </div>
        <div style={{background:"#fff",borderRadius:14,overflow:"hidden",marginBottom:12}}>
          {[
            {ico:"💬",label:"在线客服",sub:"实时聊天,响应最快",action:onOpenChat},
            {ico:"📞",label:"电话客服",sub:CONTACT_PHONE_DISPLAY,action:()=>{window.location.href=`tel:${CONTACT_PHONE}`;}},
            {ico:"✉",label:"邮件咨询",sub:"shiyi@shiyigt.com",action:()=>{window.location.href="mailto:shiyi@shiyigt.com";}},
          ].map((c,i,arr)=>(
            <button key={i} onClick={c.action||undefined} style={{width:"100%",display:"flex",alignItems:"center",gap:14,padding:"15px 16px",background:"none",border:"none",borderBottom:i<arr.length-1?"1px solid #f5f5f4":"none",cursor:c.action?"pointer":"default",textAlign:"left"}}>
              <span style={{width:36,height:36,borderRadius:10,background:"#f5f5f4",display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,flexShrink:0}}>{c.ico}</span>
              <div style={{flex:1}}>
                <div style={{fontSize:13.5,color:"#1a1a1a",fontFamily:FF,fontWeight:500}}>{c.label}</div>
                <div style={{fontSize:11.5,color:"#888",fontFamily:FF,marginTop:2}}>{c.sub}</div>
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          ))}
        </div>
        <div style={{background:"#fff",borderRadius:14,padding:"4px 0",overflow:"hidden"}}>
          <div style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600,padding:"12px 16px 8px"}}>常见问题</div>
          {faqs.map((f,i)=>{
            const open=openFaq===i;
            return(
              <div key={i} style={{borderTop:"1px solid #f5f5f4"}}>
                <button onClick={()=>setOpenFaq(open?null:i)} style={{width:"100%",display:"flex",alignItems:"center",gap:8,padding:"13px 16px",background:"none",border:"none",cursor:"pointer",textAlign:"left"}}>
                  <span style={{flex:1,fontSize:13,color:"#222",fontFamily:FF}}>{f.q}</span>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2" style={{transform:open?"rotate(180deg)":"none",transition:"transform 0.18s"}}><polyline points="6 9 12 15 18 9"/></svg>
                </button>
                {open&&<div style={{padding:"0 16px 14px",fontSize:12,color:"#666",fontFamily:FF,lineHeight:1.7}}>{f.a}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 我的收藏
// ─────────────────────────────────────────────────────────────────────────────
function FavoritesPanel({onBack,wishlist,setWishlist,setCart,onOpenProduct,products=[]}){
  const items=products.filter(p=>wishlist.includes(p.id));
  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#fff"}}>
      <BackHeader title={`我的收藏 ${items.length>0?`(${items.length})`:""}`} onBack={onBack}/>
      <div style={{flex:1,overflowY:"auto",padding:"12px 0 24px"}}>
        {items.length===0?(
          <div style={{textAlign:"center",padding:"100px 0",color:"#bbb",fontSize:13,fontFamily:FF}}>
            <div style={{fontSize:48,opacity:0.3,marginBottom:12}}>♡</div>
            <div>还没有收藏的商品</div>
            <button onClick={onBack} style={{marginTop:16,padding:"9px 22px",background:"none",border:"1.5px solid #1a1a1a",borderRadius:20,fontSize:12,color:"#1a1a1a",cursor:"pointer",fontFamily:FF}}>去逛逛</button>
          </div>
        ):(
          <MasonryGrid products={items} wishlist={wishlist} onWish={id=>setWishlist(w=>w.includes(id)?w.filter(x=>x!==id):[...w,id])} onAdd={p=>setCart(c=>{
            const vId=p.variantId||null;
            const ex=c.find(i=>i.id===p.id&&(i.variantId||null)===vId);
            return ex?c.map(i=>(i.id===p.id&&(i.variantId||null)===vId)?{...i,qty:i.qty+1}:i):[...c,{...p,qty:1}];
          })} onOpen={onOpenProduct} keyPrefix="fav-"/>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 我的订单
// ─────────────────────────────────────────────────────────────────────────────
// 订单状态 → 显示文案 + 颜色 + 内部 tab 分组
const ORDER_STATUS = {
  pending_payment: { label:"待付款",   color:"#f5a623", tab:"unpaid"   },
  paid:            { label:"待发货",   color:"#1f5fb8", tab:"unship"   },
  shipped:         { label:"已发货",   color:"#3a9e3a", tab:"shipping" },
  delivered:       { label:"已送达",   color:"#3a9e3a", tab:"shipping" },
  completed:       { label:"已完成",   color:"#888",    tab:"completed"},
  cancelled:       { label:"已取消",   color:"#aaa",    tab:"cancelled"},
  refunded:        { label:"已退款",   color:"#e03030", tab:"refund"   },
};

function OrdersPanel({onBack,initTab="all",onOpenOrder}){
  const [tab,setTab]=useState(initTab);
  const [orders,setOrders]=useState([]);
  const [loading,setLoading]=useState(true);
  const [refreshing,setRefreshing]=useState(false);
  const [err,setErr]=useState(null);
  const TABS=[
    {id:"all",label:"全部"},
    {id:"unpaid",label:"待付款"},
    {id:"unship",label:"待发货"},
    {id:"pickup",label:"待取货"},
    {id:"shipping",label:"已发货"},
    {id:"refund",label:"退换/售后"},
  ];

  // 抽出 fetch 函数,首次/刷新/可见性切换都用
  const fetchOrders=useCallback(async(silent=false)=>{
    if(!silent)setRefreshing(true);
    try{
      const d=await getMyOrders();
      setOrders(d||[]);
      setErr(null);
    }catch(e){
      setErr(e.message||"加载失败");
    }finally{
      if(!silent)setRefreshing(false);
      setLoading(false);
    }
  },[]);

  // 首次挂载 + 标签页切回前台 → 都重新拉一次
  // (用户刚下单后回到订单页,这里能保证看到最新数据)
  useEffect(()=>{
    fetchOrders();
    const onVis=()=>{ if(document.visibilityState==="visible")fetchOrders(true); };
    document.addEventListener("visibilitychange",onVis);
    return()=>document.removeEventListener("visibilitychange",onVis);
  },[fetchOrders]);

  // 智能轮询:有非终态订单(pending_payment / paid / shipped)时,每 25s 拉一次
  // 防止用户停留在订单页看不到状态变化
  useEffect(()=>{
    const hasActive=orders.some(o=>
      o.status==="pending_payment"||o.status==="paid"||o.status==="shipped"
    );
    if(!hasActive)return;
    const id=setInterval(()=>{
      if(document.visibilityState!=="visible")return;
      getMyOrders().then(d=>{
        const list=d||[];
        const dirty=list.length!==orders.length||
          list.some((o,i)=>!orders[i]||orders[i].status!==o.status||orders[i].tracking_number!==o.tracking_number);
        if(dirty)setOrders(list);
      }).catch(()=>{});
    },25_000);
    return()=>clearInterval(id);
  },[orders]);

  const filtered=tab==="all"?orders:orders.filter(o=>{
    const s=ORDER_STATUS[o.status];
    if(!s)return false;
    // "退换/售后" tab 特殊:除了已完全退款的订单(status='refunded'),
    // 还包括有 refund_request 的订单(待审核 / 已通过 / 已驳回 / 已退款都算)
    if(tab==="refund"){
      return o.status==="refunded"||(o.refund_requests&&o.refund_requests.length>0);
    }
    // 自取订单 status='paid' 时归到「待取货」而非「待发货」
    if(o.delivery_method==="pickup"&&(o.status==="paid"||o.status==="shipped")){
      return tab==="pickup";
    }
    // 非自取订单走原有 tab 映射
    return s.tab===tab;
  });

  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#f6f7f8"}}>
      <BackHeader title="我的订单" onBack={onBack} right={
        <button onClick={()=>fetchOrders(false)} disabled={refreshing} style={{background:"none",border:"none",cursor:refreshing?"wait":"pointer",padding:"6px 8px",color:refreshing?"#bbb":"#666",display:"flex",alignItems:"center",gap:4,fontSize:12,fontFamily:FF}}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{animation:refreshing?"spin 0.9s linear infinite":"none"}}>
            <polyline points="23 4 23 10 17 10"/>
            <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
          </svg>
          {refreshing?"刷新中…":"刷新"}
        </button>
      }/>
      <div style={{background:"#fff",display:"flex",borderBottom:"1px solid #f0f0f0",overflowX:"auto"}}>
        {TABS.map(t=>{
          const a=tab===t.id;
          return(
            <button key={t.id} onClick={()=>setTab(t.id)} style={{flex:"0 0 auto",padding:"14px 18px",background:"none",border:"none",borderBottom:`2px solid ${a?"#1a1a1a":"transparent"}`,cursor:"pointer",fontSize:13,color:a?"#1a1a1a":"#888",fontFamily:FF,fontWeight:a?600:400,marginBottom:-1,whiteSpace:"nowrap"}}>{t.label}</button>
          );
        })}
      </div>
      <div style={{flex:1,overflowY:"auto",padding:"12px 12px 24px"}}>
        {loading&&<ListSkeleton count={3}/>}
        {err&&<div style={{textAlign:"center",padding:"40px 16px",color:"#e03030",fontSize:13,fontFamily:FF}}>{err}<br/><span style={{fontSize:11,color:"#888"}}>登录后才能查看订单历史</span></div>}
        {!loading&&!err&&filtered.length===0&&(
          <div style={{textAlign:"center",padding:"60px 24px"}}>
            <div style={{fontSize:48,opacity:0.18,marginBottom:14}}>📦</div>
            <div style={{fontSize:13,color:"#bbb",fontFamily:FF,marginBottom:6}}>{tab==="all"?"暂无订单":`暂无${TABS.find(t=>t.id===tab)?.label}订单`}</div>
            <div style={{fontSize:11,color:"#ccc",fontFamily:FF}}>下单后将在此查看订单状态</div>
          </div>
        )}
        {filtered.map(o=>{
          let s=ORDER_STATUS[o.status]||{label:o.status,color:"#888"};
          // 自取订单的 paid/shipped 状态显示成「待取货」更直观
          if(o.delivery_method==="pickup"&&(o.status==="paid"||o.status==="shipped")){
            s={...s,label:"待取货",color:"#7c3aed"};
          }
          const itemCount=(o.items||[]).reduce((a,i)=>a+i.qty,0);
          const firstName=o.items?.[0]?.product_name||"商品";
          const moreCount=(o.items?.length||0)-1;
          const dt=new Date(o.created_at).toLocaleDateString("zh-CN");
          return(
            <button key={o.id} onClick={()=>onOpenOrder?.(o)} style={{width:"100%",background:"#fff",border:"none",borderRadius:14,padding:"14px 16px",marginBottom:10,boxShadow:"0 1px 3px rgba(0,0,0,0.04)",cursor:"pointer",textAlign:"left"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <span style={{fontSize:11.5,color:"#888",fontFamily:"monospace"}}>{o.order_num}</span>
                <span style={{fontSize:11.5,color:s.color,fontFamily:FF,fontWeight:600}}>{s.label}</span>
              </div>
              <div style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,lineHeight:1.5,marginBottom:6}}>
                {firstName}{moreCount>0&&` 等 ${moreCount+1} 件`}
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",fontSize:11,color:"#888",fontFamily:FF}}>
                <span>{dt} · 共 {itemCount} 件</span>
                <span><span style={{color:"#e03030",fontWeight:600,fontSize:14,fontFamily:FF}}>${parseFloat(o.total).toFixed(2)}</span></span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// 订单详情 + 物流轨迹
function OrderDetailPanel({order:initialOrder,onBack,onReorder,setTab,onApplyRefund}){
  const [order,setOrder]=useState(initialOrder);
  const [tracking,setTracking]=useState(null);
  const [trackLoading,setTrackLoading]=useState(false);
  const [actionBusy,setActionBusy]=useState(null); // 'cancel' | 'reorder'
  const [actionMsg,setActionMsg]=useState(null);
  let s=ORDER_STATUS[order.status]||{label:order.status,color:"#888"};
  // 自取订单 paid/shipped 实际是「待取货」
  if(order.delivery_method==="pickup"&&(order.status==="paid"||order.status==="shipped")){
    s={label:"待取货",color:"#7c3aed"};
  }
  const addr=order.delivery_address;
  const canCancel=order.status==="pending_payment";
  // 自取订单 paid 状态 → 可以确认已取货
  const canConfirmPickup=order.delivery_method==="pickup"&&(order.status==="paid"||order.status==="shipped");
  // 已完成 / 已发货 → 「再次购买」(整单复制);取消的订单 → 「重新加入购物车」(让用户挑挑减减)
  const canReorder=["paid","shipped","delivered","completed","refunded","cancelled"].includes(order.status);
  const reorderLabel = order.status==="cancelled" ? "重新加入购物车" : "再次购买";
  // 已支付未全额退款的订单 → 可申请退换货(30 天内)
  const daysAgo=(Date.now()-new Date(order.created_at||Date.now()).getTime())/86400000;
  // 已存在 pending / approved 退换货申请 → 不允许重复提交
  const hasActiveRefund=!!order.has_active_refund;
  const canApplyRefund=["paid","shipped","delivered","completed"].includes(order.status)&&daysAgo<=30&&parseFloat(order.refunded_amount||0)<parseFloat(order.total||0)&&!hasActiveRefund;
  // 退换货申请状态摘要(给用户看)
  const latestRefund=(order.refund_requests||[])[0];

  const handleCancel=async()=>{
    if(!window.confirm("确认取消此订单?未付款订单取消后不可恢复"))return;
    setActionBusy("cancel");setActionMsg(null);
    try{
      const updated=await cancelOrder(order.id);
      setOrder(updated);
      setActionMsg("订单已取消");
    }catch(err){
      setActionMsg(err.message||"取消失败");
    }finally{setActionBusy(null);}
  };

  const handleReorder=async()=>{
    setActionBusy("reorder");setActionMsg(null);
    try{
      const items=order.items||[];
      onReorder?.(items);
      setActionMsg("已加入购物车,正在跳转…");
      setTimeout(()=>setTab?.("cart"),700);
    }catch(err){
      setActionMsg(err.message||"操作失败");
    }finally{setActionBusy(null);}
  };

  const handleConfirmPickup=async()=>{
    if(!window.confirm("确认已到店取货?\n确认后订单状态将变为「已完成」,无法撤销"))return;
    setActionBusy("pickup");setActionMsg(null);
    try{
      const updated=await confirmPickup(order.id);
      setOrder(updated);
      setActionMsg("✓ 已确认取货,感谢您的购物!");
    }catch(err){
      setActionMsg(err.message||"操作失败");
    }finally{setActionBusy(null);}
  };

  useEffect(()=>{
    if(!order.tracking_number){return;}
    setTrackLoading(true);
    getOrderTracking(order.id).then(d=>{setTracking(d);setTrackLoading(false);})
      .catch(()=>setTrackLoading(false));
  },[order.id,order.tracking_number]);

  // 智能轮询:非终态订单每 25s 拉一次最新状态(状态/运单号变化时同步刷新轨迹)
  useEffect(()=>{
    const isFinal=["delivered","completed","cancelled","refunded","shipping_failed"].includes(order.status);
    if(isFinal)return;
    const id=setInterval(()=>{
      if(document.visibilityState!=="visible")return;
      // 用 getOrderById 只拉这一单(比 getMyOrders 拉全量节省多个数量级)
      getOrderById(order.id).then(fresh=>{
        if(!fresh)return;
        if(fresh.status!==order.status||fresh.tracking_number!==order.tracking_number){
          setOrder(fresh);
          // 物流单号变了 → 立刻拉一次轨迹
          if(fresh.tracking_number&&fresh.tracking_number!==order.tracking_number){
            getOrderTracking(fresh.id).then(t=>setTracking(t)).catch(()=>{});
          }
        }
      }).catch(()=>{});
    },25_000);
    return()=>clearInterval(id);
  },[order.id,order.status,order.tracking_number]);

  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#f6f7f8"}}>
      <BackHeader title="订单详情" onBack={onBack}/>
      <div style={{flex:1,overflowY:"auto",padding:"12px 12px 24px"}}>
        {/* 状态卡 */}
        <div style={{background:`linear-gradient(135deg,${s.color},${s.color}dd)`,borderRadius:14,padding:"18px 18px 16px",marginBottom:10,color:"#fff"}}>
          <div style={{fontSize:16,fontFamily:SERIF,fontWeight:600,marginBottom:4}}>{s.label}</div>
          <div style={{fontSize:11.5,color:"rgba(255,255,255,0.8)",fontFamily:FF}}>订单号 <span style={{fontFamily:"monospace"}}>{order.order_num}</span></div>
        </div>

        {/* 物流轨迹 */}
        {order.tracking_number&&(
          <div style={{background:"#fff",borderRadius:14,padding:"14px 16px",marginBottom:10,boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
              <span style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600}}>物流追踪</span>
              <span style={{fontSize:11,color:"#888",fontFamily:FF}}>{order.tracking_carrier} · {order.tracking_number}</span>
            </div>
            {trackLoading&&<div style={{fontSize:12,color:"#888",fontFamily:FF,padding:"8px 0"}}>加载中…</div>}
            {tracking&&tracking.events?.length>0?(
              <div>
                {/* 高层状态摘要 — 顶部一行,绿色显示中文状态 */}
                {tracking.status&&(
                  <div style={{fontSize:12.5,color:"#1a7d1a",fontFamily:FF,fontWeight:600,marginBottom:10,padding:"6px 10px",background:"#ecfdf3",borderRadius:6,display:"inline-block"}}>
                    {translateTrackingStatus(tracking.status)}
                    {tracking.est_delivery_date&&<span style={{color:"#666",fontWeight:400,marginLeft:8}}>· 预计 {new Date(tracking.est_delivery_date).toLocaleDateString("zh-CN")}</span>}
                  </div>
                )}
                {tracking.events.map((e,i)=>(
                  <div key={i} style={{display:"flex",gap:10,paddingBottom:10,position:"relative"}}>
                    <div style={{width:8,height:8,borderRadius:"50%",background:i===0?"#3a9e3a":"#ccc",marginTop:5,flexShrink:0,boxShadow:i===0?"0 0 0 3px rgba(58,158,58,0.2)":"none"}}/>
                    {i<tracking.events.length-1&&<div style={{position:"absolute",left:3.5,top:13,bottom:0,width:1,background:"#eee"}}/>}
                    <div style={{flex:1,marginBottom:4}}>
                      <div style={{fontSize:12.5,color:"#1a1a1a",fontFamily:FF,lineHeight:1.5}}>{translateTrackingMessage(e.message)}</div>
                      <div style={{fontSize:10.5,color:"#aaa",fontFamily:FF,marginTop:2}}>{new Date(e.datetime).toLocaleString("zh-CN")}{e.location&&` · ${e.location}`}</div>
                    </div>
                  </div>
                ))}
              </div>
            ):!trackLoading&&(
              <div style={{fontSize:12,color:"#888",fontFamily:FF,padding:"8px 0"}}>暂无物流信息,请稍后再查</div>
            )}
          </div>
        )}

        {/* 收货地址 */}
        {addr&&(
          <div style={{background:"#fff",borderRadius:14,padding:"14px 16px",marginBottom:10,boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
            <div style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600,marginBottom:8}}>收货地址</div>
            <div style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,marginBottom:3}}>{addr.name} <span style={{color:"#888",marginLeft:6}}>{addr.phone}</span></div>
            <div style={{fontSize:12,color:"#666",fontFamily:FF,lineHeight:1.5}}>{addr.line1}{addr.apt?` #${addr.apt}`:""}, {addr.city}, {addr.state} {addr.zip}</div>
          </div>
        )}

        {/* 商品列表 */}
        <div style={{background:"#fff",borderRadius:14,padding:"14px 16px",marginBottom:10,boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
          <div style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600,marginBottom:10}}>商品</div>
          {(order.items||[]).map((it,i)=>(
            <div key={i} style={{display:"flex",gap:10,paddingBottom:10,marginBottom:i<order.items.length-1?10:0,borderBottom:i<order.items.length-1?"1px solid #f5f5f4":"none"}}>
              <div style={{width:50,height:50,borderRadius:8,background:"#f5f5f4",overflow:"hidden",flexShrink:0}}>
                {(it.product_images||it.variant_images)?.[0]&&<img src={(it.variant_images||it.product_images)[0]} alt={it.product_name||"商品"} loading="lazy" decoding="async" style={{width:"100%",height:"100%",objectFit:"cover"}}/>}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,lineHeight:1.4,marginBottom:3,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{it.product_name||"商品"}</div>
                {(it.color_name||it.size)&&<div style={{fontSize:11,color:"#888",fontFamily:FF}}>{[it.color_name,it.size].filter(Boolean).join(" / ")}</div>}
              </div>
              <div style={{textAlign:"right",fontSize:12,color:"#666",fontFamily:FF,whiteSpace:"nowrap"}}>
                <div>×{it.qty}</div>
                <div style={{color:"#e03030",fontWeight:600,marginTop:3}}>${parseFloat(it.unit_price).toFixed(2)}</div>
              </div>
            </div>
          ))}
        </div>

        {/* 费用明细 */}
        <div style={{background:"#fff",borderRadius:14,padding:"14px 16px",boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
          <div style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600,marginBottom:11}}>费用明细</div>
          <SumRow label="商品" val={`$${parseFloat(order.subtotal||0).toFixed(2)}`}/>
          {parseFloat(order.shipping_fee||0)>0&&<SumRow label="运费" val={`$${parseFloat(order.shipping_fee).toFixed(2)}`}/>}
          {parseFloat(order.tax_amount||0)>0&&<SumRow label="销售税" val={`$${parseFloat(order.tax_amount).toFixed(2)}`}/>}
          {parseFloat(order.tip_amount||0)>0&&<SumRow label="配送小费" val={`$${parseFloat(order.tip_amount).toFixed(2)}`}/>}
          {parseFloat(order.discount||0)>0&&<SumRow label="优惠" val={`-$${parseFloat(order.discount).toFixed(2)}`} accent/>}
          {parseFloat(order.refunded_amount||0)>0&&<SumRow label="已退款" val={`-$${parseFloat(order.refunded_amount).toFixed(2)}`} accent/>}
          <div style={{height:1,background:"#f0f0f0",margin:"8px 0 6px"}}/>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginTop:4}}>
            <span style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600}}>实付</span>
            <span style={{fontSize:18,fontFamily:FF,fontWeight:700,color:"#e03030"}}>${parseFloat(order.total).toFixed(2)}</span>
          </div>
          {/* 已完成订单 → 显示获得的积分(消费 $1 = 1 分,向下取整;退款扣相应比例) */}
          {(()=>{
            if(order.status!=="completed")return null;
            const total=parseFloat(order.total)||0;
            const refunded=parseFloat(order.refunded_amount||0);
            const baseEarn=Math.floor(total);
            // 部分退款按比例扣回积分;全额退款积分清零
            const netEarn=refunded>=total?0:Math.floor(baseEarn*(1-refunded/total));
            const reversed=baseEarn-netEarn;
            if(baseEarn<=0)return null;
            return(
              <div style={{marginTop:10,padding:"9px 12px",background:"linear-gradient(135deg,#fff7e6,#fff3d6)",border:"1px solid #ffd591",borderRadius:9,display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,fontFamily:FF}}>
                <div style={{display:"flex",alignItems:"center",gap:7}}>
                  <span style={{fontSize:14}}>💎</span>
                  <span style={{fontSize:12,color:"#7a5a10",fontWeight:500}}>
                    {reversed>0?`已获得 ${netEarn} 分`:`已获得 ${baseEarn} 分`}
                  </span>
                </div>
                {reversed>0&&(
                  <span style={{fontSize:10.5,color:"#a07060",fontWeight:400}}>退款扣回 -{reversed}</span>
                )}
              </div>
            );
          })()}
        </div>

        {/* 自取订单专用 — 确认已取货按钮(放在最显眼位置) */}
        {canConfirmPickup && (
          <button
            onClick={handleConfirmPickup}
            disabled={actionBusy==="pickup"}
            style={{
              width:"100%",marginTop:14,
              background:actionBusy==="pickup"?"#bbb":"linear-gradient(135deg,#7c3aed,#6d28d9)",
              color:"#fff",border:"none",borderRadius:12,padding:"14px 0",
              fontSize:14,fontFamily:FF,fontWeight:600,cursor:actionBusy?"not-allowed":"pointer",
              boxShadow:actionBusy?"none":"0 4px 12px rgba(124,58,237,0.3)",
              display:"flex",alignItems:"center",justifyContent:"center",gap:8,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            {actionBusy==="pickup"?"确认中…":"确认已到店取货"}
          </button>
        )}

        {/* 操作按钮 */}
        {(canCancel||canReorder)&&(
          <div style={{display:"flex",gap:10,marginTop:14}}>
            {canCancel&&(
              <button
                onClick={handleCancel}
                disabled={actionBusy==="cancel"}
                style={{flex:1,background:"#fff",border:"1.5px solid #e03030",color:"#e03030",borderRadius:11,padding:"12px 0",fontSize:13,fontFamily:FF,fontWeight:600,cursor:actionBusy?"not-allowed":"pointer",opacity:actionBusy?0.6:1}}
              >
                {actionBusy==="cancel"?"取消中…":"取消订单"}
              </button>
            )}
            {canReorder&&(
              <button
                onClick={handleReorder}
                disabled={actionBusy==="reorder"}
                style={{flex:1,background:"linear-gradient(135deg,#1a1a1a,#333)",color:"#fff",border:"none",borderRadius:11,padding:"12px 0",fontSize:13,fontFamily:FF,fontWeight:600,cursor:actionBusy?"not-allowed":"pointer",boxShadow:actionBusy?"none":"0 4px 12px rgba(0,0,0,0.15)",opacity:actionBusy?0.6:1}}
              >
                {actionBusy==="reorder"?"加入中…":reorderLabel}
              </button>
            )}
          </div>
        )}

        {actionMsg&&(
          <div style={{marginTop:10,padding:"8px 12px",background:"#f7f6f4",borderRadius:8,fontSize:12,color:"#666",fontFamily:FF,textAlign:"center"}}>{actionMsg}</div>
        )}

        {/* 申请退换货 — 没有进行中的申请才显示 */}
        {canApplyRefund&&onApplyRefund&&(
          <button onClick={()=>onApplyRefund(order)}
            style={{width:"100%",marginTop:10,background:"#fff",border:"1px solid #e5e5e5",color:"#1a1a1a",borderRadius:11,padding:"12px 0",fontSize:13,fontFamily:FF,fontWeight:600,cursor:"pointer"}}>
            申请退换货
          </button>
        )}

        {/* 已有退换货申请 → 显示状态卡,代替"申请"按钮 */}
        {hasActiveRefund&&latestRefund&&(()=>{
          const STATUS_TXT={pending:{txt:"审核中",color:"#f59e0b",bg:"#fff7ed",bd:"#fed7aa"},approved:{txt:"已通过 · 退款处理中",color:"#16a34a",bg:"#f0fdf4",bd:"#bbf7d0"}};
          const s=STATUS_TXT[latestRefund.status]||{txt:latestRefund.status,color:"#888",bg:"#f5f5f4",bd:"#e5e5e5"};
          return(
            <div style={{marginTop:14,padding:"12px 14px",background:s.bg,border:`1px solid ${s.bd}`,borderRadius:10,fontSize:12,color:"#1a1a1a",fontFamily:FF,lineHeight:1.6}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                <span style={{fontWeight:600}}>{latestRefund.type==="exchange"?"换货":"退款"}申请</span>
                <span style={{fontSize:11,color:s.color,fontWeight:600,padding:"2px 8px",background:"#fff",border:`1px solid ${s.bd}`,borderRadius:10}}>{s.txt}</span>
              </div>
              <div style={{fontSize:11,color:"#666"}}>
                金额 ${parseFloat(latestRefund.refund_amount||0).toFixed(2)} · 提交于 {new Date(latestRefund.created_at).toLocaleDateString("zh-CN")}
              </div>
              {latestRefund.admin_note&&(
                <div style={{fontSize:11,color:"#666",marginTop:4,padding:"6px 8px",background:"#fff",borderRadius:6}}>
                  客服备注:{latestRefund.admin_note}
                </div>
              )}
            </div>
          );
        })()}

        {/* 已无法操作的订单提示 */}
        {!canApplyRefund&&!hasActiveRefund&&order.status!=="pending_payment"&&order.status!=="cancelled"&&order.status!=="refunded"&&daysAgo>30&&(
          <div style={{marginTop:14,padding:"12px 14px",background:"#fff8e1",border:"1px solid #ffe082",borderRadius:10,fontSize:11.5,color:"#7a5a10",fontFamily:FF,lineHeight:1.6}}>
            💡 该订单已超过 30 天退换期限,如有问题请联系客服。
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 我的优惠券钱包
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// 我的积分(余额 + 流水 + 入口去积分商城)
// ─────────────────────────────────────────────────────────────────────────────
function PointsPanel({onBack,onGoShop}){
  const [bal,setBal]=useState(null);
  const [history,setHistory]=useState([]);
  const [loading,setLoading]=useState(true);

  useEffect(()=>{
    let alive=true;
    Promise.all([getPointBalance().catch(()=>({points:0})),getPointHistory(50).catch(()=>[])])
      .then(([b,h])=>{if(alive){setBal(b?.points??0);setHistory(h||[]);setLoading(false);}});
    return()=>{alive=false;};
  },[]);

  const fmtType=(t)=>({earn:"消费奖励",redeem:"兑换",admin_adjust:"管理员调整",refund:"退款返还"}[t]||t);
  const fmtDate=(iso)=>{
    const d=new Date(iso);
    return d.toLocaleString("zh-CN",{month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"});
  };

  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#f6f7f8"}}>
      <BackHeader title="我的积分" onBack={onBack}/>

      {/* 余额卡 */}
      <div style={{padding:"14px 14px 0"}}>
        <div style={{
          background:"linear-gradient(135deg,#7c3aed,#5b21b6)",
          borderRadius:14,padding:"20px 18px",
          color:"#fff",
          boxShadow:"0 8px 24px rgba(124,58,237,0.25)",
        }}>
          <div style={{fontSize:11,opacity:0.85,fontFamily:FF,letterSpacing:"0.05em",marginBottom:6}}>当前积分余额</div>
          <div style={{fontSize:34,fontFamily:SERIF,fontWeight:700,lineHeight:1,marginBottom:14}}>
            {loading?"…":(bal??0).toLocaleString()}
          </div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={{fontSize:11,opacity:0.85,fontFamily:FF,lineHeight:1.6}}>
              消费 $1 = 1 积分<br/>
              积分可在积分商城兑换好物
            </div>
            <button
              onClick={onGoShop}
              style={{
                background:"#fff",color:"#5b21b6",
                border:"none",padding:"8px 16px",borderRadius:18,
                fontSize:12,fontFamily:FF,fontWeight:600,cursor:"pointer",
                whiteSpace:"nowrap",
              }}
            >去兑换 →</button>
          </div>
        </div>
      </div>

      {/* 流水 */}
      <div style={{flex:1,overflowY:"auto",padding:"14px 14px 24px"}}>
        <div style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600,marginBottom:10}}>积分明细</div>
        {loading&&<div style={{textAlign:"center",padding:"30px 0",color:"#bbb",fontSize:13,fontFamily:FF}}>加载中…</div>}
        {!loading&&history.length===0&&(
          <div style={{textAlign:"center",padding:"40px 0",color:"#bbb",fontSize:13,fontFamily:FF}}>暂无积分记录</div>
        )}
        {history.map(h=>{
          const positive=h.points>0;
          return(
            <div key={h.id} style={{background:"#fff",borderRadius:10,padding:"12px 14px",marginBottom:8,boxShadow:"0 1px 3px rgba(0,0,0,0.04)",display:"flex",alignItems:"center",gap:10}}>
              <div style={{
                flexShrink:0,width:32,height:32,borderRadius:"50%",
                background:positive?"#dcfce7":"#fee2e2",
                color:positive?"#16a34a":"#dc2626",
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:14,fontWeight:700,
              }}>
                {positive?"+":"-"}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:500}}>
                  {fmtType(h.type)}
                  {h.point_product_name&&<span style={{color:"#888",fontWeight:400}}> · {h.point_product_name}</span>}
                </div>
                <div style={{fontSize:10.5,color:"#aaa",fontFamily:FF,marginTop:2}}>
                  {fmtDate(h.created_at)}
                  {h.order_num&&<span style={{marginLeft:6,fontFamily:"monospace"}}>订单 {h.order_num}</span>}
                </div>
                {h.note&&<div style={{fontSize:10.5,color:"#888",fontFamily:FF,marginTop:1}}>{h.note}</div>}
              </div>
              <div style={{
                fontSize:14,fontFamily:FF,fontWeight:700,
                color:positive?"#16a34a":"#dc2626",
              }}>
                {positive?"+":""}{h.points}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 积分商城
// ─────────────────────────────────────────────────────────────────────────────
function PointShopPanel({onBack,onRedeemed}){
  const [products,setProducts]=useState([]);
  const [bal,setBal]=useState(0);
  const [loading,setLoading]=useState(true);
  const [selected,setSelected]=useState(null);
  const [addresses,setAddresses]=useState([]);
  const [delivery,setDelivery]=useState("pickup");
  const [selAddrId,setSelAddrId]=useState(null);
  const [pickupName,setPickupName]=useState("");
  const [pickupPhone,setPickupPhone]=useState("");
  const [submitting,setSubmitting]=useState(false);
  const [msg,setMsg]=useState({type:null,text:""});
  const stored=loadStoredUser();

  useEffect(()=>{
    let alive=true;
    Promise.all([
      getPointProducts().catch(()=>[]),
      getPointBalance().catch(()=>({points:0})),
      getAddresses().catch(()=>[]),
    ]).then(([p,b,a])=>{
      if(!alive)return;
      setProducts(p||[]);
      setBal(b?.points||0);
      setAddresses(a||[]);
      const def=a?.find(x=>x.isDefault)||a?.[0];
      if(def)setSelAddrId(def.id);
      setPickupName(stored?.name||"");
      setPickupPhone(stored?.phone||"");
      setLoading(false);
    });
    return()=>{alive=false;};
  },[]);

  useEffect(()=>{
    if(!msg.text)return;
    const t=setTimeout(()=>setMsg({type:null,text:""}),5000);
    return()=>clearTimeout(t);
  },[msg]);

  const handleRedeem=async()=>{
    if(!selected)return;
    if(submitting)return;
    if(bal<selected.points_required){
      setMsg({type:"err",text:`积分不足,还差 ${selected.points_required-bal}`});
      return;
    }
    if(delivery!=="pickup"){
      const addr=addresses.find(a=>a.id===selAddrId);
      if(!addr){setMsg({type:"err",text:"请先添加收货地址"});return;}
    }
    if(delivery==="pickup"&&!pickupPhone.trim()){
      setMsg({type:"err",text:"取货请填写联系电话"});
      return;
    }

    setSubmitting(true);
    try{
      const addr=addresses.find(a=>a.id===selAddrId);
      const payload={
        point_product_id:selected.id,
        delivery_method:delivery,
        ...(delivery==="pickup"?{
          customer_name:pickupName.trim(),
          customer_phone:pickupPhone.trim(),
        }:{
          customer_name:addr?.name||"",
          customer_phone:addr?.phone||"",
          delivery_address:addr?{
            name:addr.name,phone:addr.phone,
            line1:addr.street,apt:addr.apt||"",
            city:addr.city,state:addr.state,zip:addr.zip,
          }:null,
        }),
      };
      const r=await redeemPointProduct(payload);
      setBal(r.balance);
      setSelected(null);
      setMsg({type:"ok",text:`✓ 兑换成功!订单号 ${r.order?.order_num}`});
      onRedeemed?.();
      // 刷新商品列表(库存变化)
      getPointProducts().then(p=>setProducts(p||[])).catch(()=>{});
    }catch(e){
      setMsg({type:"err",text:e.message||"兑换失败"});
    }finally{
      setSubmitting(false);
    }
  };

  // 商城列表
  if(!selected){
    return(
      <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#f6f7f8"}}>
        <BackHeader title="积分商城" onBack={onBack}/>
        {/* 余额条 */}
        <div style={{padding:"10px 14px",background:"linear-gradient(135deg,#7c3aed,#5b21b6)",color:"#fff",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <span style={{fontSize:12,fontFamily:FF,opacity:0.85}}>当前积分</span>
          <span style={{fontSize:18,fontFamily:SERIF,fontWeight:700}}>{(bal||0).toLocaleString()}</span>
        </div>

        <div style={{flex:1,overflowY:"auto",padding:"14px 14px 24px"}}>
          {msg.text&&(
            <div style={{
              padding:"10px 14px",borderRadius:10,marginBottom:10,
              fontSize:12,fontFamily:FF,
              background:msg.type==="ok"?"#dcfce7":"#fee2e2",
              color:msg.type==="ok"?"#166534":"#c42020",
            }}>{msg.text}</div>
          )}
          {loading&&<div style={{textAlign:"center",padding:"40px 0",color:"#bbb",fontSize:13,fontFamily:FF}}>加载中…</div>}
          {!loading&&products.length===0&&(
            <div style={{textAlign:"center",padding:"60px 0"}}>
              <div style={{fontSize:40,opacity:0.18,marginBottom:14}}>🎁</div>
              <div style={{fontSize:13,color:"#bbb",fontFamily:FF}}>暂无积分商品</div>
            </div>
          )}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            {products.map(p=>{
              const affordable=bal>=p.points_required;
              const oos=p.stock<=0;
              const disabled=!affordable||oos;
              return(
                <button
                  key={p.id}
                  onClick={()=>!oos&&setSelected(p)}
                  disabled={oos}
                  style={{
                    background:"#fff",borderRadius:14,
                    border:"none",padding:0,cursor:oos?"not-allowed":"pointer",
                    overflow:"hidden",textAlign:"left",
                    boxShadow:"0 1px 3px rgba(0,0,0,0.04)",
                    opacity:oos?0.5:1,
                    display:"flex",flexDirection:"column",
                  }}
                >
                  <div style={{position:"relative",width:"100%",aspectRatio:"1/1",background:"#f3f4f6"}}>
                    <img src={p.image_url} alt={p.name} loading="lazy" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                    {oos&&(
                      <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.45)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                        <span style={{color:"#fff",fontSize:13,fontFamily:FF,fontWeight:600}}>已兑完</span>
                      </div>
                    )}
                  </div>
                  <div style={{padding:"10px 12px 12px",display:"flex",flexDirection:"column",gap:4,flex:1}}>
                    <div style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:500,lineHeight:1.4,minHeight:36,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{p.name}</div>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:"auto"}}>
                      <span style={{
                        fontSize:14,fontFamily:SERIF,fontWeight:700,
                        color:affordable?"#7c3aed":"#bbb",
                      }}>{p.points_required.toLocaleString()} 积分</span>
                      <span style={{fontSize:10,color:"#aaa",fontFamily:FF}}>剩 {p.stock}</span>
                    </div>
                    {!affordable&&!oos&&<div style={{fontSize:10,color:"#dc2626",fontFamily:FF}}>还差 {(p.points_required-bal).toLocaleString()} 积分</div>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // 详情 + 兑换
  const affordable=bal>=selected.points_required;
  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#f6f7f8"}}>
      <BackHeader title="确认兑换" onBack={()=>setSelected(null)}/>
      <div style={{flex:1,overflowY:"auto",padding:"14px 14px 100px"}}>
        {/* 商品 */}
        <div style={{background:"#fff",borderRadius:14,padding:"14px",display:"flex",gap:12,boxShadow:"0 1px 3px rgba(0,0,0,0.04)",marginBottom:10}}>
          <div style={{width:96,height:96,borderRadius:10,overflow:"hidden",flexShrink:0,background:"#f3f4f6"}}>
            <img src={selected.image_url} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:14,fontFamily:FF,fontWeight:600,color:"#1a1a1a",marginBottom:4}}>{selected.name}</div>
            {selected.description&&<div style={{fontSize:11.5,color:"#888",fontFamily:FF,lineHeight:1.5,marginBottom:8}}>{selected.description}</div>}
            <div style={{display:"flex",alignItems:"baseline",gap:6}}>
              <span style={{fontSize:18,fontFamily:SERIF,fontWeight:700,color:"#7c3aed"}}>{selected.points_required.toLocaleString()}</span>
              <span style={{fontSize:12,color:"#888",fontFamily:FF}}>积分</span>
            </div>
          </div>
        </div>

        {/* 配送方式 */}
        <div style={{background:"#fff",borderRadius:14,padding:"14px 14px",marginBottom:10}}>
          <div style={{fontSize:13,fontFamily:FF,fontWeight:600,color:"#1a1a1a",marginBottom:10}}>配送方式</div>
          <div style={{display:"flex",gap:8}}>
            {[["pickup","到店自取"],["mail","邮寄"]].map(([id,lbl])=>{
              const sel=delivery===id;
              return(
                <button key={id} onClick={()=>setDelivery(id)} style={{flex:1,padding:"10px 0",borderRadius:10,border:`1.5px solid ${sel?"#7c3aed":"#ececec"}`,background:sel?"#f5f3ff":"#fafafa",color:sel?"#7c3aed":"#666",fontSize:12.5,fontFamily:FF,fontWeight:sel?600:400,cursor:"pointer"}}>{lbl}</button>
              );
            })}
          </div>
        </div>

        {/* 自取 → 联系信息;邮寄 → 选地址 */}
        {delivery==="pickup"?(
          <div style={{background:"#fff",borderRadius:14,padding:"14px 14px",marginBottom:10}}>
            <div style={{fontSize:13,fontFamily:FF,fontWeight:600,color:"#1a1a1a",marginBottom:10}}>取货联系</div>
            <input value={pickupName} onChange={e=>setPickupName(e.target.value)} placeholder="姓名" style={{...inStyle,marginBottom:8}}/>
            <input value={pickupPhone} onChange={e=>setPickupPhone(e.target.value)} placeholder="电话" style={inStyle}/>
            <div style={{fontSize:11,color:"#888",fontFamily:FF,marginTop:8,lineHeight:1.6}}>📍 {PICKUP_ADDRESS} · 营业时间{PICKUP_HOURS}</div>
          </div>
        ):(
          <div style={{background:"#fff",borderRadius:14,padding:"14px 14px",marginBottom:10}}>
            <div style={{fontSize:13,fontFamily:FF,fontWeight:600,color:"#1a1a1a",marginBottom:10}}>收货地址</div>
            {addresses.length===0?(
              <div style={{fontSize:12,color:"#888",fontFamily:FF,padding:"10px 0"}}>请先去「地址管理」添加地址</div>
            ):addresses.map(a=>{
              const sel=selAddrId===a.id;
              return(
                <button key={a.id} onClick={()=>setSelAddrId(a.id)} style={{width:"100%",display:"flex",alignItems:"flex-start",gap:10,padding:"10px 0",background:"none",border:"none",cursor:"pointer",borderBottom:"1px solid #f5f5f4",textAlign:"left"}}>
                  <div style={{width:18,height:18,borderRadius:"50%",border:`2px solid ${sel?"#7c3aed":"#ccc"}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:2}}>
                    {sel&&<div style={{width:8,height:8,borderRadius:"50%",background:"#7c3aed"}}/>}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontFamily:FF,color:"#1a1a1a",fontWeight:500}}>{a.name} <span style={{color:"#888",fontWeight:400,marginLeft:6}}>{a.phone}</span></div>
                    <div style={{fontSize:11.5,color:"#666",fontFamily:FF,lineHeight:1.5,marginTop:2}}>{a.street}{a.apt?` #${a.apt}`:""}, {a.city}, {a.state} {a.zip}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* 余额对比 */}
        <div style={{background:"#fff",borderRadius:14,padding:"14px 14px",marginBottom:10}}>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:12.5,fontFamily:FF,padding:"4px 0"}}>
            <span style={{color:"#666"}}>需消耗</span>
            <span style={{color:"#7c3aed",fontWeight:600}}>-{selected.points_required.toLocaleString()} 积分</span>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:12.5,fontFamily:FF,padding:"4px 0"}}>
            <span style={{color:"#666"}}>当前余额</span>
            <span style={{color:"#1a1a1a"}}>{bal.toLocaleString()}</span>
          </div>
          <div style={{height:1,background:"#f0f0f0",margin:"6px 0"}}/>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:13,fontFamily:FF,padding:"4px 0"}}>
            <span style={{color:"#1a1a1a",fontWeight:600}}>兑换后剩余</span>
            <span style={{color:affordable?"#16a34a":"#dc2626",fontWeight:700,fontFamily:SERIF}}>{(bal-selected.points_required).toLocaleString()}</span>
          </div>
        </div>

        {msg.text&&(
          <div style={{padding:"10px 14px",borderRadius:10,marginBottom:10,fontSize:12,fontFamily:FF,background:msg.type==="ok"?"#dcfce7":"#fee2e2",color:msg.type==="ok"?"#166534":"#c42020"}}>{msg.text}</div>
        )}
      </div>

      {/* 底部 sticky 按钮 */}
      <div style={{padding:"12px 14px",background:"#fff",borderTop:"1px solid #ececec",flexShrink:0}}>
        <button
          onClick={handleRedeem}
          disabled={!affordable||submitting}
          style={{
            width:"100%",
            background:affordable?"linear-gradient(135deg,#7c3aed,#5b21b6)":"#ccc",
            color:"#fff",border:"none",borderRadius:11,
            padding:"14px 0",fontSize:14,fontFamily:FF,fontWeight:600,
            cursor:affordable&&!submitting?"pointer":"not-allowed",
            boxShadow:affordable?"0 4px 12px rgba(124,58,237,0.25)":"none",
          }}
        >{submitting?"兑换中…":affordable?`确认兑换 (${selected.points_required.toLocaleString()} 积分)`:`积分不足 还差 ${(selected.points_required-bal).toLocaleString()}`}</button>
      </div>
    </div>
  );
}

function CouponsPanel({onBack}){
  const [coupons,setCoupons]=useState([]);
  const [loading,setLoading]=useState(true);
  const [redeemCode,setRedeemCode]=useState("");
  const [redeeming,setRedeeming]=useState(false);
  const [msg,setMsg]=useState({type:null,text:""});

  const load=async()=>{
    setLoading(true);
    try{
      const list=await getMyCoupons();
      setCoupons(list||[]);
    }catch(e){
      setMsg({type:"err",text:e.message||"加载失败"});
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{load();},[]);

  const handleRedeem=async()=>{
    if(!redeemCode.trim())return;
    setRedeeming(true);setMsg({type:null,text:""});
    try{
      const c=await redeemCoupon(redeemCode);
      setMsg({type:"ok",text:`✓ 「${c.name}」已加入您的钱包`});
      setRedeemCode("");
      await load();
    }catch(e){
      setMsg({type:"err",text:e.message||"兑换失败"});
    }finally{
      setRedeeming(false);
    }
  };

  // 5s 自动消失
  useEffect(()=>{
    if(!msg.text)return;
    const t=setTimeout(()=>setMsg({type:null,text:""}),5000);
    return()=>clearTimeout(t);
  },[msg]);

  const fmtValue=(c)=>c.type==="fixed"?`$${parseFloat(c.value).toFixed(2)}`:`${parseFloat(c.value).toFixed(0)}%`;
  const fmtCondition=(c)=>{
    const parts=[];
    if(parseFloat(c.min_subtotal)>0)parts.push(`满 $${parseFloat(c.min_subtotal).toFixed(0)}`);
    if(c.exclude_special)parts.push("不含特价");
    return parts.join(" · ")||"无门槛";
  };
  const fmtExpire=(c)=>{
    if(!c.expires_at)return "永久有效";
    const d=new Date(c.expires_at);
    const now=new Date();
    const days=Math.ceil((d-now)/86400000);
    if(days<0)return "已过期";
    if(days<=3)return <span style={{color:"#e03030",fontWeight:600}}>{days===0?"今天到期":`${days} 天后到期`}</span>;
    return d.toLocaleDateString("zh-CN")+" 到期";
  };

  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#f6f7f8"}}>
      <BackHeader title="我的优惠券" onBack={onBack}/>

      {/* 兑换码输入 */}
      <div style={{padding:"14px 14px 0"}}>
        <div style={{background:"#fff",borderRadius:14,padding:"14px 14px",boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
          <div style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600,marginBottom:10}}>兑换优惠码</div>
          <div style={{display:"flex",gap:8}}>
            <input
              value={redeemCode}
              onChange={e=>setRedeemCode(e.target.value.toUpperCase())}
              placeholder="输入优惠码"
              style={{flex:1,padding:"10px 14px",borderRadius:10,border:"1.5px solid #ececec",outline:"none",fontSize:13,color:"#1a1a1a",fontFamily:"monospace",background:"#fafaf9",boxSizing:"border-box",textTransform:"uppercase"}}
              disabled={redeeming}
            />
            <button
              onClick={handleRedeem}
              disabled={redeeming||!redeemCode.trim()}
              style={{flexShrink:0,background:"#1a1a1a",color:"#fff",border:"none",borderRadius:10,padding:"0 20px",fontSize:13,fontFamily:FF,cursor:"pointer",opacity:redeeming||!redeemCode.trim()?0.4:1}}
            >{redeeming?"…":"兑换"}</button>
          </div>
          {msg.text&&<div style={{marginTop:10,fontSize:12,color:msg.type==="ok"?"#16a34a":"#e03030",fontFamily:FF}}>{msg.text}</div>}
        </div>
      </div>

      {/* 列表 */}
      <div style={{flex:1,overflowY:"auto",padding:"14px 14px 24px"}}>
        {loading&&<div style={{textAlign:"center",padding:"40px 0",color:"#bbb",fontSize:13,fontFamily:FF}}>加载中…</div>}
        {!loading&&coupons.length===0&&(
          <div style={{textAlign:"center",padding:"50px 24px"}}>
            <div style={{fontSize:46,opacity:0.18,marginBottom:14}}>🎟️</div>
            <div style={{fontSize:13,color:"#bbb",fontFamily:FF,marginBottom:6}}>暂无可用优惠券</div>
            <div style={{fontSize:11,color:"#ccc",fontFamily:FF,lineHeight:1.6}}>关注公众号或活动页获取兑换码</div>
          </div>
        )}
        {!loading&&coupons.map(c=>(
          <div
            key={c.id}
            style={{
              display:"flex",
              background:"#fff",
              borderRadius:12,
              marginBottom:10,
              overflow:"hidden",
              boxShadow:"0 1px 3px rgba(0,0,0,0.04)",
            }}
          >
            {/* 左侧金额 */}
            <div style={{
              flexShrink:0,
              width:110,
              background:"linear-gradient(135deg,#e03030,#c42020)",
              color:"#fff",
              display:"flex",
              flexDirection:"column",
              alignItems:"center",
              justifyContent:"center",
              padding:"14px 8px",
              position:"relative",
            }}>
              <div style={{fontSize:11,fontFamily:FF,opacity:0.85}}>{c.type==="fixed"?"立减":"折扣"}</div>
              <div style={{fontSize:28,fontFamily:SERIF,fontWeight:700,lineHeight:1,margin:"4px 0"}}>{fmtValue(c)}</div>
              {c.max_discount&&c.type==="percent"&&<div style={{fontSize:9.5,fontFamily:FF,opacity:0.85}}>最高 ${parseFloat(c.max_discount).toFixed(0)}</div>}
              {/* 锯齿 */}
              <div style={{position:"absolute",right:-6,top:0,bottom:0,width:12,background:"radial-gradient(circle at 0 50%,transparent 5px,#e03030 5px) repeat-y",backgroundSize:"12px 12px"}}/>
            </div>

            {/* 右侧内容 */}
            <div style={{flex:1,padding:"12px 14px",minWidth:0}}>
              <div style={{fontSize:14,color:"#1a1a1a",fontFamily:FF,fontWeight:600,marginBottom:3,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{c.name}</div>
              <div style={{fontSize:11,color:"#888",fontFamily:FF,marginBottom:6,lineHeight:1.5}}>{c.description||fmtCondition(c)}</div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                <span style={{fontSize:10.5,fontFamily:"monospace",color:"#e03030",fontWeight:700,background:"#fff5f5",padding:"2px 7px",borderRadius:4}}>{c.code}</span>
                <span style={{fontSize:10,color:"#888",fontFamily:FF}}>{fmtExpire(c)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 推荐有礼 — 我的推荐码 + 战绩 + 分享
// ─────────────────────────────────────────────────────────────────────────────
function ReferralPanel({onBack}){
  const [data,setData]=useState(null);
  const [loading,setLoading]=useState(true);
  const [err,setErr]=useState("");
  const [copied,setCopied]=useState(false);

  const load=async()=>{
    setLoading(true);setErr("");
    try{
      const d=await getMyReferral();
      setData(d);
    }catch(e){setErr(e.message||"加载失败");}
    setLoading(false);
  };
  useEffect(()=>{load();},[]);

  const code=data?.referral_code||"";
  const shareLink=code?`https://us198.com/?ref=${code}`:"";
  const shareText=`我在十一家发现好多精选好物 ❤ 用我的推荐码 ${code} 注册,我们各得 $5 优惠券!\n${shareLink}`;

  const copyAll=async()=>{
    try{
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(()=>setCopied(false),2000);
    }catch{}
  };

  const nativeShare=async()=>{
    if(!navigator.share){copyAll();return;}
    try{
      await navigator.share({title:"十一家",text:shareText,url:shareLink});
    }catch{}
  };

  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#f6f7f8"}}>
      <BackHeader title="推荐有礼" onBack={onBack}/>
      <div style={{flex:1,overflowY:"auto",padding:"12px 14px 24px"}}>
        {loading&&<div style={{padding:30,color:"#888",fontSize:13,fontFamily:FF,textAlign:"center"}}>加载中…</div>}
        {err&&<div style={{padding:14,background:"#fee2e2",color:"#c42020",borderRadius:10,fontSize:12.5,fontFamily:FF,marginBottom:10}}>⚠️ {err}</div>}

        {data&&(
          <>
            {/* Hero */}
            <div style={{background:"linear-gradient(135deg,#fff5f0,#ffe0d5)",borderRadius:16,padding:"22px 18px",textAlign:"center",marginBottom:14,boxShadow:"0 1px 6px rgba(0,0,0,0.04)"}}>
              <div style={{fontSize:34,marginBottom:8}}>🎁</div>
              <div style={{fontSize:16,fontFamily:SERIF,fontWeight:700,color:"#1a1a1a",marginBottom:6}}>邀请好友各得 $5</div>
              <div style={{fontSize:12,color:"#7a5a48",fontFamily:FF,lineHeight:1.6}}>
                分享您的专属推荐码,好友注册并完成首单<br/>
                您和好友各得 <span style={{color:"#e03030",fontWeight:700}}>$5 优惠券</span>
              </div>
            </div>

            {/* 我的推荐码 */}
            <div style={{background:"#fff",borderRadius:14,padding:"18px 16px",marginBottom:12,boxShadow:"0 1px 6px rgba(0,0,0,0.04)",textAlign:"center"}}>
              <div style={{fontSize:11,color:"#888",fontFamily:FF,marginBottom:8}}>我的推荐码</div>
              <div style={{fontSize:28,fontFamily:"monospace",fontWeight:700,color:"#1a1a1a",letterSpacing:"0.18em",marginBottom:14}}>
                {code}
              </div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={copyAll}
                  style={{flex:1,padding:"11px 0",background:copied?"#dcfce7":"#fff",border:"1.5px solid "+(copied?"#16a34a":"#e5e5e5"),color:copied?"#16a34a":"#1a1a1a",borderRadius:10,fontSize:13,fontFamily:FF,fontWeight:600,cursor:"pointer"}}>
                  {copied?"✓ 已复制":"📋 复制邀请文案"}
                </button>
                <button onClick={nativeShare}
                  style={{flex:1,padding:"11px 0",background:"linear-gradient(135deg,#1a1a1a,#333)",color:"#fff",border:"none",borderRadius:10,fontSize:13,fontFamily:FF,fontWeight:600,cursor:"pointer",boxShadow:"0 4px 12px rgba(0,0,0,0.15)"}}>
                  📤 立即分享
                </button>
              </div>
            </div>

            {/* 战绩 */}
            <div style={{display:"flex",gap:8,marginBottom:12}}>
              <div style={{flex:1,background:"#fff",borderRadius:12,padding:"14px 0",textAlign:"center",boxShadow:"0 1px 6px rgba(0,0,0,0.04)"}}>
                <div style={{fontSize:22,fontWeight:700,color:"#1a1a1a",fontFamily:FF}}>{data.total_invites}</div>
                <div style={{fontSize:11,color:"#888",fontFamily:FF,marginTop:2}}>累计推荐</div>
              </div>
              <div style={{flex:1,background:"#fff",borderRadius:12,padding:"14px 0",textAlign:"center",boxShadow:"0 1px 6px rgba(0,0,0,0.04)"}}>
                <div style={{fontSize:22,fontWeight:700,color:"#16a34a",fontFamily:FF}}>{data.rewarded_count}</div>
                <div style={{fontSize:11,color:"#888",fontFamily:FF,marginTop:2}}>已奖励</div>
              </div>
              <div style={{flex:1,background:"#fff",borderRadius:12,padding:"14px 0",textAlign:"center",boxShadow:"0 1px 6px rgba(0,0,0,0.04)"}}>
                <div style={{fontSize:22,fontWeight:700,color:"#f59e0b",fontFamily:FF}}>{data.pending_count}</div>
                <div style={{fontSize:11,color:"#888",fontFamily:FF,marginTop:2}}>待首单</div>
              </div>
            </div>

            {/* 推荐记录 */}
            {data.recent?.length>0&&(
              <div style={{background:"#fff",borderRadius:14,overflow:"hidden",boxShadow:"0 1px 6px rgba(0,0,0,0.04)"}}>
                <div style={{padding:"12px 16px",borderBottom:"1px solid #f5f5f4",fontSize:13,fontFamily:FF,fontWeight:600,color:"#1a1a1a"}}>
                  推荐记录
                </div>
                {data.recent.map((r,i)=>(
                  <div key={i} style={{padding:"12px 16px",borderBottom:i<data.recent.length-1?"1px solid #f5f5f4":"none",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontFamily:FF,color:"#1a1a1a",fontWeight:500}}>{r.referee_name}</div>
                      <div style={{fontSize:11,fontFamily:FF,color:"#888",marginTop:2}}>
                        {r.referee_phone||"—"} · {new Date(r.created_at).toLocaleDateString("zh-CN")}
                      </div>
                    </div>
                    <span style={{padding:"3px 9px",fontSize:11,borderRadius:5,fontFamily:FF,fontWeight:600,
                      background:r.status==="rewarded"?"#dcfce7":"#fef9c3",
                      color:  r.status==="rewarded"?"#16a34a":"#a16207"}}>
                      {r.status==="rewarded"?"✓ 已奖励":"等待首单"}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {data.recent?.length===0&&(
              <div style={{padding:"30px 20px",textAlign:"center",color:"#aaa",fontSize:12,fontFamily:FF}}>
                还没有推荐记录,赶紧把推荐码分享给好友吧 ❤
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 我的支付方式 — 列表 / 设默认 / 删除 / 添加新卡(SetupIntent)
// ─────────────────────────────────────────────────────────────────────────────
function PaymentMethodsPanel({onBack}){
  const [list,setList]=useState([]);
  const [loading,setLoading]=useState(true);
  const [busy,setBusy]=useState(null); // pmId 正在操作
  const [err,setErr]=useState("");
  const [adding,setAdding]=useState(false);

  const load=async()=>{
    setLoading(true);setErr("");
    try{
      const data=await getSavedPaymentMethods();
      setList(data||[]);
    }catch(e){
      setErr(e.message||"加载失败");
    }
    setLoading(false);
  };
  useEffect(()=>{load();},[]);

  const setDefault=async(id)=>{
    setBusy(id);
    try{
      await setDefaultPaymentMethod(id);
      setList(ls=>ls.map(c=>({...c,is_default:c.id===id})));
    }catch(e){
      alert(e.message||"操作失败");
    }
    setBusy(null);
  };

  const removeCard=async(id,last4)=>{
    if(!window.confirm(`确认删除尾号 ${last4} 的卡?`))return;
    setBusy(id);
    try{
      await deletePaymentMethod(id);
      setList(ls=>ls.filter(c=>c.id!==id));
    }catch(e){
      alert(e.message||"删除失败");
    }
    setBusy(null);
  };

  if(adding){
    return <AddCardPanel onBack={()=>{setAdding(false);load();}}/>;
  }

  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#f6f7f8"}}>
      <BackHeader title="我的支付方式" onBack={onBack}/>
      <div style={{flex:1,overflowY:"auto",padding:"12px 14px 24px"}}>
        {loading&&<div style={{padding:20,color:"#888",fontSize:13,fontFamily:FF,textAlign:"center"}}>加载中…</div>}
        {err&&<div style={{padding:14,background:"#fee2e2",color:"#c42020",borderRadius:10,fontSize:12.5,fontFamily:FF,marginBottom:10}}>⚠️ {err}</div>}

        {!loading&&list.length===0&&(
          <div style={{padding:"40px 20px",textAlign:"center",color:"#999",fontSize:13,fontFamily:FF,background:"#fff",borderRadius:14,marginBottom:14}}>
            <div style={{fontSize:36,marginBottom:10}}>💳</div>
            <div style={{marginBottom:6,color:"#1a1a1a",fontWeight:600}}>尚未保存任何卡片</div>
            <div style={{fontSize:11.5,color:"#999"}}>添加后,下次结账可一键支付</div>
          </div>
        )}

        {list.map(c=>(
          <div key={c.id} style={{background:"#fff",borderRadius:14,padding:"14px 16px",marginBottom:10,boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:46,height:32,borderRadius:6,background:"linear-gradient(135deg,#1a1a1a,#444)",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:700,letterSpacing:"0.05em",fontFamily:FF,textTransform:"uppercase"}}>
                {c.brand||"CARD"}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:14,fontFamily:FF,fontWeight:600,color:"#1a1a1a"}}>•••• {c.last4}</div>
                <div style={{fontSize:11,fontFamily:FF,color:"#888",marginTop:2}}>有效期 {String(c.exp_month).padStart(2,"0")}/{String(c.exp_year).slice(-2)}</div>
              </div>
              {c.is_default&&(
                <span style={{padding:"2px 8px",fontSize:10,background:"#fef3c7",color:"#a16207",borderRadius:4,fontFamily:FF,fontWeight:600}}>默认</span>
              )}
            </div>
            <div style={{display:"flex",gap:8,marginTop:10,paddingTop:10,borderTop:"1px solid #f5f5f4"}}>
              {!c.is_default&&(
                <button onClick={()=>setDefault(c.id)} disabled={busy===c.id}
                  style={{flex:1,background:"none",border:"1px solid #e5e5e5",color:"#1a1a1a",borderRadius:8,padding:"7px 0",fontSize:12,fontFamily:FF,cursor:busy?"wait":"pointer"}}>
                  {busy===c.id?"…":"设为默认"}
                </button>
              )}
              <button onClick={()=>removeCard(c.id,c.last4)} disabled={busy===c.id}
                style={{flex:c.is_default?1:undefined,padding:c.is_default?"7px 0":"7px 14px",background:"none",border:"1px solid #fca5a5",color:"#dc2626",borderRadius:8,fontSize:12,fontFamily:FF,cursor:busy?"wait":"pointer"}}>
                {busy===c.id?"…":"删除"}
              </button>
            </div>
          </div>
        ))}

        <button onClick={()=>setAdding(true)}
          style={{width:"100%",marginTop:8,padding:"14px 0",background:"linear-gradient(135deg,#1a1a1a,#333)",color:"#fff",border:"none",borderRadius:12,fontSize:13.5,fontFamily:FF,fontWeight:600,cursor:"pointer",boxShadow:"0 4px 12px rgba(0,0,0,0.12)"}}>
          + 添加新卡
        </button>

        <div style={{marginTop:14,padding:"10px 12px",background:"#f0f9ff",border:"1px solid #bae6fd",borderRadius:10,fontSize:11,color:"#075985",fontFamily:FF,lineHeight:1.6}}>
          🔒 卡片信息由 Stripe 加密保管,十一家不会看到完整卡号
        </div>
      </div>
    </div>
  );
}

// 添加新卡 — SetupIntent + 三个独立 Card Element
//
// 注意:这里**不能**用 PaymentElement(layout:"tabs"),原因:
//   1. PaymentElement 启动时会去 Stripe "elements store" 拉取账号配置
//   2. 当 Stripe Dashboard 关闭 Link / 调整支付方式时,该 store 偶发返回
//      "Could not retrieve elements store due to unexpected error"
//   3. 三个独立 Element(CardNumber/CardExpiry/CardCvc)走 confirmCardSetup,
//      完全不依赖 elements store,稳定可靠 — 跟 CheckoutPanel 同样模式
function AddCardPanel({onBack}){
  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#f6f7f8"}}>
      <BackHeader title="添加新卡" onBack={onBack}/>
      <div style={{flex:1,overflowY:"auto",padding:"12px 14px 32px"}}>
        {stripePromise ? (
          <Elements stripe={stripePromise}>
            <SetupForm onDone={onBack}/>
          </Elements>
        ) : (
          <div style={{padding:30,color:"#888",fontSize:13,fontFamily:FF,textAlign:"center"}}>初始化中…</div>
        )}
      </div>
    </div>
  );
}

function SetupForm({onDone}){
  const stripe=useStripe();
  const elements=useElements();
  const [submitting,setSubmitting]=useState(false);
  const [error,setError]=useState("");
  const [numComplete,setNumComplete]=useState(false);
  const [expComplete,setExpComplete]=useState(false);
  const [cvcComplete,setCvcComplete]=useState(false);
  const [postalCode,setPostalCode]=useState("");

  const allComplete = numComplete && expComplete && cvcComplete && /^\d{5}$/.test(postalCode);

  const handleSave=async()=>{
    if(!stripe||!elements)return;
    if(!allComplete){setError("请填写完整的卡片信息和邮编");return;}
    setSubmitting(true);setError("");
    try{
      // 1. 创建 SetupIntent(只在按"保存"时创建,避免 stale clientSecret)
      const si = await createSetupIntent();
      const clientSecret = si?.clientSecret;
      if(!clientSecret) throw new Error("初始化失败,请重试");
      // 2. 用 CardNumberElement 作为主元素 confirmCardSetup
      const cardNumber = elements.getElement(CardNumberElement);
      const {error:confErr,setupIntent} = await stripe.confirmCardSetup(clientSecret,{
        payment_method:{
          card:cardNumber,
          billing_details:{address:{country:"US",postal_code:postalCode}},
        },
      });
      if(confErr){setError(confErr.message||"保存失败");setSubmitting(false);return;}
      if(setupIntent?.status==="succeeded"){
        onDone?.();
      }else{
        setError(`状态异常: ${setupIntent?.status||"unknown"}`);
        setSubmitting(false);
      }
    }catch(e){
      setError(e.message||"保存失败,请重试");
      setSubmitting(false);
    }
  };

  // Element 统一样式 — 跟 CheckoutPanel 保持一致
  const elementStyle = {
    style:{
      base:{
        fontSize:"16px",
        fontFamily:"-apple-system,BlinkMacSystemFont,'PingFang SC','Helvetica Neue',sans-serif",
        color:"#1a1a1a",
        "::placeholder":{color:"#bbb"},
      },
      invalid:{color:"#e03030",iconColor:"#e03030"},
    },
  };
  const fieldShell = {
    background:"#fafafa",
    border:"1.5px solid #ececec",
    borderRadius:10,
    padding:"13px 14px",
    minHeight:22,
  };

  return(
    <div style={{background:"#fff",borderRadius:14,padding:"18px 16px 16px",boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
      {/* 卡号 */}
      <div style={{marginBottom:12}}>
        <div style={{fontSize:12,color:"#888",fontFamily:FF,marginBottom:7,marginLeft:2}}>卡号</div>
        <div style={fieldShell}>
          <CardNumberElement
            options={{...elementStyle,placeholder:"1234 1234 1234 1234",showIcon:true}}
            onChange={(e)=>{setNumComplete(e.complete);if(e.error)setError(e.error.message);else setError("");}}
          />
        </div>
      </div>
      {/* 有效期 + CVC + 邮编 一行三栏 */}
      <div style={{display:"flex",gap:8,marginBottom:14}}>
        <div style={{flex:1}}>
          <div style={{fontSize:12,color:"#888",fontFamily:FF,marginBottom:7,marginLeft:2}}>有效期</div>
          <div style={fieldShell}>
            <CardExpiryElement options={{...elementStyle,placeholder:"MM / YY"}} onChange={(e)=>setExpComplete(e.complete)}/>
          </div>
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:12,color:"#888",fontFamily:FF,marginBottom:7,marginLeft:2}}>安全码</div>
          <div style={fieldShell}>
            <CardCvcElement options={{...elementStyle,placeholder:"CVC"}} onChange={(e)=>setCvcComplete(e.complete)}/>
          </div>
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:12,color:"#888",fontFamily:FF,marginBottom:7,marginLeft:2}}>邮编</div>
          <input
            type="text"
            inputMode="numeric"
            maxLength={5}
            value={postalCode}
            onChange={(e)=>setPostalCode(e.target.value.replace(/\D/g,"").slice(0,5))}
            placeholder="00000"
            style={{...fieldShell,width:"100%",border:fieldShell.border,fontSize:16,fontFamily:FF,color:"#1a1a1a",outline:"none",boxSizing:"border-box"}}
          />
        </div>
      </div>
      {error&&<div style={{padding:"10px 12px",background:"#fee2e2",color:"#c42020",borderRadius:8,fontSize:12,fontFamily:FF,marginBottom:12}}>⚠️ {error}</div>}
      <button onClick={handleSave} disabled={!stripe||submitting||!allComplete}
        style={{width:"100%",padding:"13px 0",background:(!stripe||submitting||!allComplete)?"#ccc":"linear-gradient(135deg,#1a1a1a,#333)",color:"#fff",border:"none",borderRadius:11,fontSize:13.5,fontFamily:FF,fontWeight:600,cursor:(!stripe||submitting||!allComplete)?"not-allowed":"pointer",boxShadow:(!stripe||submitting||!allComplete)?"none":"0 4px 12px rgba(0,0,0,0.15)"}}>
        {submitting?"保存中…":"保存卡片"}
      </button>
      <div style={{marginTop:12,fontSize:10.5,color:"#aaa",fontFamily:FF,textAlign:"center",lineHeight:1.5}}>
        🔒 卡片信息由 Stripe 加密保管 · 不存储在本机
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 退换货自助
// ─────────────────────────────────────────────────────────────────────────────
const REFUND_STATUS={
  pending:  {txt:"审核中",color:"#f59e0b"},
  approved: {txt:"已通过",color:"#22c55e"},
  refunded: {txt:"已退款",color:"#22c55e"},
  rejected: {txt:"已驳回",color:"#ef4444"},
  cancelled:{txt:"已撤回",color:"#9ca3af"},
};

// ────────────────────────────────────────────────────────────────────
// 再次购买面板:展示客户购买过的商品(去重),不是订单列表
// 顶部:已购商品网格;底部:为你推荐
// ────────────────────────────────────────────────────────────────────
function BuyAgainPanel({onBack,wishlist,onWish,onAdd,onOpenProduct}){
  const [purchased,setPurchased]=useState(null); // null=loading, []=empty
  const [reco,setReco]=useState([]);
  const [err,setErr]=useState(null);

  useEffect(()=>{
    let alive=true;
    setErr(null);
    Promise.all([
      getPurchasedProducts().catch(e=>{ if(alive) setErr(e.message||"加载失败"); return []; }),
      getRecommendations({limit:8}).then(r=>r?.products||[]).catch(()=>[]),
    ]).then(([p,r])=>{
      if(!alive)return;
      // 转成 mapProduct 兼容的格式(MasonryGrid 期望:id, name, price, imgUrl, images, isSpecial)
      const norm=(arr)=>arr.map(x=>({
        id:x.id,
        name:x.name||"",
        price:parseFloat(x.price)||0,
        origPrice:x.orig_price||x.origPrice||null,
        imgUrl:(x.images&&x.images[0])||x.imgUrl||null,
        images:x.images||[],
        cat:x.category_id||x.cat,
        isSpecial:!!x.is_special||!!x.isSpecial,
        sub:x.sub||"",
        labels:x.labels||[],
        sold:0,
      }));
      setPurchased(norm(p));
      setReco(norm(r));
    });
    return()=>{alive=false;};
  },[]);

  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#f6f7f8"}}>
      <BackHeader title="再次购买" onBack={onBack}/>
      <div style={{flex:1,overflowY:"auto",padding:"6px 0 24px"}}>
        {/* 已购商品 */}
        {purchased===null&&(
          <div style={{padding:"40px 0",textAlign:"center",color:"#999",fontSize:13,fontFamily:FF}}>
            <div style={{display:"inline-block",width:24,height:24}}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2.4" strokeLinecap="round" style={{animation:"spin 0.9s linear infinite"}}>
                <path d="M21 12a9 9 0 11-6.219-8.56"/>
              </svg>
            </div>
            <div style={{marginTop:6}}>加载中…</div>
          </div>
        )}

        {purchased&&purchased.length===0&&!err&&(
          <div style={{padding:"60px 24px",textAlign:"center",fontFamily:FF}}>
            <div style={{fontSize:42,marginBottom:12}}>🛍️</div>
            <div style={{fontSize:14,color:"#1a1a1a",fontWeight:600,marginBottom:6}}>还没有购买记录</div>
            <div style={{fontSize:11.5,color:"#999",lineHeight:1.6}}>下方为你推荐了一些热门商品</div>
          </div>
        )}

        {err&&(
          <div style={{padding:"40px 24px",textAlign:"center",fontFamily:FF}}>
            <div style={{fontSize:32,marginBottom:8}}>⚠️</div>
            <div style={{fontSize:13,color:"#666"}}>{err}</div>
          </div>
        )}

        {purchased&&purchased.length>0&&(
          <>
            <div style={{padding:"14px 14px 8px",display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontFamily:SERIF,fontSize:15,color:"#1a1a1a"}}>购买过的商品</span>
              <span style={{fontSize:11.5,color:"#aaa",fontFamily:FF}}>{purchased.length} 件</span>
              <div style={{flex:1,height:1,background:"#e8e8e8"}}/>
            </div>
            <MasonryGrid
              products={purchased}
              wishlist={wishlist}
              onWish={onWish}
              onAdd={onAdd}
              onOpen={onOpenProduct}
              keyPrefix="buyagain-"
            />
          </>
        )}

        {/* 为你推荐 */}
        {reco.length>0&&(
          <div style={{paddingTop:16}}>
            <div style={{padding:"4px 14px 12px",display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontFamily:SERIF,fontSize:15,color:"#1a1a1a"}}>为你推荐</span>
              <div style={{flex:1,height:1,background:"#e8e8e8"}}/>
              <span style={{fontSize:10.5,color:"#c0c0c0",fontFamily:FF}}>猜你也喜欢</span>
            </div>
            <MasonryGrid
              products={reco}
              wishlist={wishlist}
              onWish={onWish}
              onAdd={onAdd}
              onOpen={onOpenProduct}
              keyPrefix="buyagain-reco-"
            />
          </div>
        )}
      </div>
    </div>
  );
}

function RefundsPanel({onBack,onApply}){
  const [list,setList]=useState([]);
  const [loading,setLoading]=useState(true);
  const [err,setErr]=useState("");

  const load=async()=>{
    setLoading(true);setErr("");
    try{
      const d=await getMyRefunds();
      setList(d||[]);
    }catch(e){
      setErr(e.message||"加载失败");
    }
    setLoading(false);
  };
  useEffect(()=>{load();},[]);

  const handleCancel=async(id)=>{
    if(!window.confirm("确认撤回此申请?"))return;
    try{
      await cancelRefundRequest(id);
      load();
    }catch(e){
      alert(e.message||"撤回失败");
    }
  };

  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#f6f7f8"}}>
      <BackHeader title="退换货" onBack={onBack}/>
      <div style={{flex:1,overflowY:"auto",padding:"12px 14px 24px"}}>
        {loading&&<div style={{padding:20,color:"#888",fontSize:13,fontFamily:FF,textAlign:"center"}}>加载中…</div>}
        {err&&<div style={{padding:14,background:"#fee2e2",color:"#c42020",borderRadius:10,fontSize:12.5,fontFamily:FF,marginBottom:10}}>⚠️ {err}</div>}

        {!loading&&list.length===0&&(
          <div style={{padding:"40px 20px",textAlign:"center",color:"#999",fontSize:13,fontFamily:FF,background:"#fff",borderRadius:14,marginBottom:14}}>
            <div style={{fontSize:36,marginBottom:10}}>📦</div>
            <div style={{marginBottom:6,color:"#1a1a1a",fontWeight:600}}>暂无退换货记录</div>
            <div style={{fontSize:11.5,color:"#999"}}>需要退换的订单可在「订单详情」内发起申请</div>
          </div>
        )}

        {list.map(r=>{
          const st=REFUND_STATUS[r.status]||{txt:r.status,color:"#888"};
          return(
            <div key={r.id} style={{background:"#fff",borderRadius:14,padding:"14px 16px",marginBottom:10,boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                <span style={{fontSize:13,fontFamily:FF,fontWeight:600,color:"#1a1a1a"}}>
                  {r.type==="refund"?"🔄 退款":"↔️ 换货"} · {r.order_num||""}
                </span>
                <span style={{padding:"2px 8px",fontSize:11,borderRadius:5,background:st.color+"22",color:st.color,fontFamily:FF,fontWeight:600}}>
                  {st.txt}
                </span>
              </div>
              <div style={{fontSize:12,color:"#666",fontFamily:FF,lineHeight:1.6,marginBottom:6}}>
                {r.reason}
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:11.5,color:"#999",fontFamily:FF}}>
                <span>提交于 {new Date(r.created_at).toLocaleString("zh-CN",{month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"})}</span>
                <span style={{color:"#e03030",fontWeight:600}}>${parseFloat(r.refund_amount||0).toFixed(2)}</span>
              </div>
              {r.admin_note&&r.status==="rejected"&&(
                <div style={{marginTop:8,padding:"8px 10px",background:"#fef2f2",borderRadius:8,fontSize:11.5,color:"#7f1d1d",fontFamily:FF,lineHeight:1.5}}>
                  驳回原因: {r.admin_note}
                </div>
              )}
              {r.status==="pending"&&(
                <button onClick={()=>handleCancel(r.id)}
                  style={{marginTop:10,width:"100%",background:"none",border:"1px solid #e5e5e5",color:"#666",borderRadius:8,padding:"7px 0",fontSize:12,fontFamily:FF,cursor:"pointer"}}>
                  撤回申请
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// 提交单个订单的退换货申请
function RefundApplyPanel({order,onBack,onSubmitted}){
  const [type,setType]=useState("refund");
  const [reason,setReason]=useState("");
  // 每件商品申请退的数量 { [order_item_id]: qty }
  const [qtyMap,setQtyMap]=useState({});
  const [photos,setPhotos]=useState([]); // 已上传成功的 R2 URL 数组
  const [uploading,setUploading]=useState(false);
  const fileInputRef=useRef(null);
  const [submitting,setSubmitting]=useState(false);
  const [err,setErr]=useState("");

  const items=order.items||[];

  const totalEst=items.reduce((s,it)=>{
    const q=qtyMap[it.id]||0;
    return s+(parseFloat(it.unit_price)||0)*q;
  },0);

  // 用户从相册/相机选了文件 → 立刻上传 R2,成功后追加到 photos
  const onFilePicked=async(e)=>{
    const files=Array.from(e.target.files||[]);
    e.target.value=""; // 允许下次选同一张
    if(!files.length)return;
    if(photos.length+files.length>9){
      setErr(`最多 9 张,当前 ${photos.length} 张`);return;
    }
    setErr("");
    setUploading(true);
    try{
      // 串行上传,避免并发把 R2 打挂
      const newUrls=[];
      for(const file of files){
        if(!file.type?.startsWith("image/")){
          throw new Error(`${file.name} 不是图片`);
        }
        const {url}=await uploadImage(file);
        newUrls.push(url);
      }
      setPhotos(p=>[...p,...newUrls]);
    }catch(e){
      setErr(e.message||"上传失败,请重试");
    }finally{
      setUploading(false);
    }
  };

  const removePhoto=(i)=>setPhotos(p=>p.filter((_,j)=>j!==i));

  const submit=async()=>{
    setErr("");
    const itemsPayload=items
      .filter(it=>(qtyMap[it.id]||0)>0)
      .map(it=>({order_item_id:it.id,qty:qtyMap[it.id]}));
    if(itemsPayload.length===0){setErr("请选择至少一件需要退换的商品");return;}
    if(reason.trim().length<2){setErr("请填写退换原因");return;}
    setSubmitting(true);
    try{
      await createRefundRequest({
        order_id:order.id,
        type,
        items:itemsPayload,
        reason:reason.trim(),
        photos,
      });
      onSubmitted?.();
    }catch(e){
      setErr(e.message||"提交失败");
      setSubmitting(false);
    }
  };

  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#f6f7f8"}}>
      <BackHeader title="申请退换货" onBack={onBack}/>
      <div style={{flex:1,overflowY:"auto",padding:"12px 14px 24px"}}>
        <div style={{background:"#fff",borderRadius:14,padding:"14px 16px",marginBottom:10,boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
          <div style={{fontSize:13,fontFamily:FF,fontWeight:600,marginBottom:10,color:"#1a1a1a"}}>申请类型</div>
          <div style={{display:"flex",gap:10}}>
            {[{v:"refund",l:"🔄 退款"},{v:"exchange",l:"↔️ 换货"}].map(o=>(
              <button key={o.v} onClick={()=>setType(o.v)}
                style={{flex:1,padding:"10px 0",background:type===o.v?"#1a1a1a":"#fff",color:type===o.v?"#fff":"#1a1a1a",border:"1px solid "+(type===o.v?"#1a1a1a":"#e5e5e5"),borderRadius:10,fontSize:13,fontFamily:FF,fontWeight:600,cursor:"pointer"}}>
                {o.l}
              </button>
            ))}
          </div>
        </div>

        <div style={{background:"#fff",borderRadius:14,padding:"14px 16px",marginBottom:10,boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
          <div style={{fontSize:13,fontFamily:FF,fontWeight:600,marginBottom:10,color:"#1a1a1a"}}>选择商品</div>
          {items.map(it=>{
            const remain=it.qty-(it.returned_qty||0);
            const q=qtyMap[it.id]||0;
            if(remain<=0)return null;
            return(
              <div key={it.id} style={{display:"flex",alignItems:"center",gap:10,paddingBottom:10,marginBottom:10,borderBottom:"1px solid #f5f5f4"}}>
                <div style={{width:46,height:46,borderRadius:8,background:"#f5f5f4",overflow:"hidden",flexShrink:0}}>
                  {(it.product_images||it.variant_images)?.[0]&&<img src={(it.variant_images||it.product_images)[0]} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12.5,fontFamily:FF,color:"#1a1a1a",lineHeight:1.4,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{it.product_name||"商品"}</div>
                  <div style={{fontSize:11,fontFamily:FF,color:"#888",marginTop:2}}>
                    {[it.color_name,it.size].filter(Boolean).join(" / ")||"—"} · 可退 {remain}
                  </div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <button onClick={()=>setQtyMap(m=>({...m,[it.id]:Math.max(0,(m[it.id]||0)-1)}))}
                    style={{width:26,height:26,border:"1px solid #e5e5e5",background:"#fff",borderRadius:6,fontSize:14,cursor:"pointer"}}>−</button>
                  <span style={{fontSize:13,fontFamily:FF,fontWeight:600,minWidth:18,textAlign:"center"}}>{q}</span>
                  <button onClick={()=>setQtyMap(m=>({...m,[it.id]:Math.min(remain,(m[it.id]||0)+1)}))}
                    style={{width:26,height:26,border:"1px solid #e5e5e5",background:"#fff",borderRadius:6,fontSize:14,cursor:"pointer"}}>+</button>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{background:"#fff",borderRadius:14,padding:"14px 16px",marginBottom:10,boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
          <div style={{fontSize:13,fontFamily:FF,fontWeight:600,marginBottom:8,color:"#1a1a1a"}}>退换原因 *</div>
          <textarea value={reason} onChange={e=>setReason(e.target.value)}
            placeholder="请描述退换原因(尺码不合适 / 质量问题 / 与描述不符 等)"
            rows={4}
            style={{width:"100%",padding:"10px 12px",border:"1px solid #e5e5e5",borderRadius:10,fontSize:13,fontFamily:FF,resize:"vertical",outline:"none",boxSizing:"border-box"}}/>
        </div>

        <div style={{background:"#fff",borderRadius:14,padding:"14px 16px",marginBottom:10,boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
          <div style={{fontSize:13,fontFamily:FF,fontWeight:600,marginBottom:8,color:"#1a1a1a"}}>凭证图片 (选填,最多 9 张)</div>
          {/* 隐藏的 file input — 用 ref 触发原生选择器(相册/拍照) */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={onFilePicked}
            style={{display:"none"}}
          />
          <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
            {photos.map((url,i)=>(
              <div key={i} style={{position:"relative",width:80,height:80,borderRadius:10,overflow:"hidden",border:"1px solid #e5e5e5",background:"#f5f5f4"}}>
                <img src={url} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                <button onClick={()=>removePhoto(i)}
                  style={{position:"absolute",top:3,right:3,width:20,height:20,padding:0,background:"rgba(0,0,0,0.65)",color:"#fff",border:"none",borderRadius:"50%",fontSize:11,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>✕</button>
              </div>
            ))}
            {photos.length<9&&(
              <button
                onClick={()=>fileInputRef.current?.click()}
                disabled={uploading}
                style={{
                  width:80,height:80,borderRadius:10,
                  border:"1.5px dashed #c8c8c8",background:uploading?"#f5f5f4":"#fafaf9",
                  cursor:uploading?"wait":"pointer",
                  display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                  gap:4,fontFamily:FF,color:"#888",fontSize:11,
                  transition:"all 0.15s",
                }}>
                {uploading?(
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2.5" strokeLinecap="round" style={{animation:"spin 0.8s linear infinite"}}>
                      <path d="M21 12a9 9 0 11-6.219-8.56"/>
                    </svg>
                    <span>上传中…</span>
                  </>
                ):(
                  <>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21 15 16 10 5 21"/>
                    </svg>
                    <span>添加图片</span>
                  </>
                )}
              </button>
            )}
          </div>
          <div style={{marginTop:8,fontSize:11,color:"#aaa",fontFamily:FF}}>
            支持从相册选择或现场拍照,单张不超过 5MB
          </div>
        </div>

        <div style={{padding:"10px 14px",background:"#fff8e1",border:"1px solid #ffe082",borderRadius:10,fontSize:11.5,color:"#7a5a10",fontFamily:FF,lineHeight:1.6,marginBottom:14}}>
          预估{type==="refund"?"退款":"换货差额"} <span style={{color:"#e03030",fontWeight:700}}>${totalEst.toFixed(2)}</span> · 实际金额以审核后为准
        </div>

        {err&&<div style={{padding:"10px 12px",background:"#fee2e2",color:"#c42020",borderRadius:8,fontSize:12,fontFamily:FF,marginBottom:12}}>⚠️ {err}</div>}

        <button onClick={submit} disabled={submitting}
          style={{width:"100%",padding:"14px 0",background:submitting?"#ccc":"linear-gradient(135deg,#1a1a1a,#333)",color:"#fff",border:"none",borderRadius:12,fontSize:14,fontFamily:FF,fontWeight:600,cursor:submitting?"wait":"pointer",boxShadow:submitting?"none":"0 4px 12px rgba(0,0,0,0.15)"}}>
          {submitting?"提交中…":"提交申请"}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 编辑用户信息
// ─────────────────────────────────────────────────────────────────────────────
function EditProfilePanel({name,email,phone,loggedIn,onSave,onBack}){
  const [nm,setNm]=useState(name||"");
  const [em,setEm]=useState(email||"");
  // Apple Sign In 用户的 phone 被后端用 'apple:xxx' 占位 → 视为未绑定,允许用户填真实手机号
  const isApplePlaceholder=!!(phone&&/^apple:/i.test(phone));
  const realPhone=isApplePlaceholder?"":(phone||"");
  // 手机号:已绑定的不可改;未绑定(Apple 登录 / 邮箱登录)的可补全
  const [ph,setPh]=useState(realPhone);
  const phoneLocked=!!(realPhone&&realPhone.trim());
  const [confirmDelete,setConfirmDelete]=useState(false);
  const [deleting,setDeleting]=useState(false);
  const [saving,setSaving]=useState(false);
  // 至少昵称必填;邮箱选填;新填的手机号校验 10 位+
  const phValid=phoneLocked||!ph.trim()||/^\+?\d{10,15}$/.test(ph.replace(/\D/g,""));
  const valid=nm.trim().length>0&&phValid;
  const dirty=nm!==(name||"")||em!==(email||"")||(!phoneLocked&&ph!==realPhone);
  const handleDelete=async()=>{
    if(!confirmDelete){setConfirmDelete(true);return;}
    setDeleting(true);
    try{
      await deleteMe();
      clearAuth();
      window.location.reload();
    }catch(err){
      alert(err.message||"注销失败");
      setDeleting(false);
    }
  };
  const handleSave=async()=>{
    if(!valid||saving)return;
    setSaving(true);
    try{
      // 手机号已绑定的不传(后端 COALESCE 保护,但避免误覆盖)
      const phoneOut=phoneLocked?phone:(ph.trim()||"");
      await onSave(nm.trim(),em.trim(),phoneOut);
    }finally{setSaving(false);}
  };
  return(
    <div style={{height:"100%",display:"flex",flexDirection:"column",background:"#f6f7f8",position:"relative"}}>
      {/* 顶部 hero — 浅色渐变 + 充足底部 padding 给头像留位置(避免被遮挡) */}
      <div style={{background:"linear-gradient(135deg,#fff5f0 0%,#ffe0d5 60%,#ffd0c0 100%)",padding:"14px 16px 80px",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:-40,right:-40,width:140,height:140,borderRadius:"50%",background:"radial-gradient(circle,rgba(255,255,255,0.65) 0%,rgba(255,255,255,0) 70%)",pointerEvents:"none"}}/>
        <button onClick={onBack} style={{background:"rgba(255,255,255,0.7)",border:"none",borderRadius:"50%",width:34,height:34,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",backdropFilter:"blur(8px)",position:"relative",boxShadow:"0 2px 6px rgba(0,0,0,0.06)"}}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <div style={{textAlign:"center",marginTop:8,position:"relative"}}>
          <div style={{fontSize:17,color:"#1a1a1a",fontFamily:SERIF,fontWeight:600,letterSpacing:"0.04em"}}>编辑个人资料</div>
          <div style={{fontSize:11.5,color:"#a07060",fontFamily:FF,marginTop:4}}>完善信息后享受更精准的推荐</div>
        </div>
      </div>

      {/* 头像区 — 漂浮在 hero 和卡片交界处。marginTop:-50 + hero padding-bottom:80 → 头像下半在白底上,上半略浮入 hero 但不会遮挡标题(标题已上移到 marginTop:8) */}
      <div style={{padding:"0 16px",marginTop:-50,marginBottom:14,display:"flex",justifyContent:"center"}}>
        <div style={{width:96,height:96,borderRadius:"50%",border:"4px solid #fff",overflow:"hidden",boxShadow:"0 10px 28px rgba(180,90,80,0.22)",background:"#ffeae0",position:"relative",zIndex:2}}>
          <CuteGirlAvatar size={88}/>
        </div>
      </div>

      <div style={{flex:1,overflowY:"auto",padding:"0 14px 110px"}}>
        {/* 资料卡片 */}
        <div style={{background:"#fff",borderRadius:16,overflow:"hidden",boxShadow:"0 1px 4px rgba(0,0,0,0.04)"}}>
          <div style={{padding:"14px 16px 6px",fontSize:11,color:"#aaa",fontFamily:FF,letterSpacing:"0.08em",textTransform:"uppercase"}}>基本信息</div>
          {/* 昵称 */}
          <div style={{padding:"4px 16px 12px",borderBottom:"1px solid #f5f5f4"}}>
            <div style={{fontSize:11,color:"#888",fontFamily:FF,marginBottom:4}}>昵称 <span style={{color:"#e03030"}}>*</span></div>
            <input value={nm} onChange={e=>setNm(e.target.value)} placeholder="请输入昵称" maxLength={20} style={{width:"100%",border:"none",outline:"none",fontSize:15,color:"#1a1a1a",fontFamily:FF,background:"none",padding:"4px 0"}}/>
          </div>
          {/* 邮箱 */}
          <div style={{padding:"12px 16px",borderBottom:"1px solid #f5f5f4"}}>
            <div style={{fontSize:11,color:"#888",fontFamily:FF,marginBottom:4}}>邮箱<span style={{color:"#bbb",marginLeft:5,fontSize:10}}>选填,用于接收订单状态</span></div>
            <input value={em} onChange={e=>setEm(e.target.value)} placeholder="name@example.com" type="email" style={{width:"100%",border:"none",outline:"none",fontSize:15,color:"#1a1a1a",fontFamily:FF,background:"none",padding:"4px 0"}}/>
          </div>
          {/* 手机号 — 已绑定的只读 + "已验证"标识;未绑定(Apple 登录)可补全 */}
          <div style={{padding:"12px 16px",display:"flex",alignItems:"center",gap:8}}>
            <div style={{flex:1}}>
              <div style={{fontSize:11,color:"#888",fontFamily:FF,marginBottom:4}}>
                手机号
                {!phoneLocked&&<span style={{color:"#bbb",marginLeft:5,fontSize:10}}>选填,用于接收订单短信</span>}
              </div>
              {phoneLocked?(
                <div style={{fontSize:15,color:"#1a1a1a",fontFamily:FF,padding:"4px 0",fontWeight:500}}>{realPhone}</div>
              ):(
                <input
                  value={ph}
                  onChange={e=>setPh(e.target.value.replace(/[^\d+]/g,"").slice(0,15))}
                  placeholder="如 3231234567(已注册可关联)"
                  type="tel"
                  inputMode="tel"
                  style={{width:"100%",border:"none",outline:"none",fontSize:15,color:"#1a1a1a",fontFamily:FF,background:"none",padding:"4px 0"}}
                />
              )}
            </div>
            {phoneLocked&&<span style={{fontSize:10,color:"#3a9e3a",background:"#e8f7ee",padding:"3px 8px",borderRadius:4,fontFamily:FF,fontWeight:500}}>已验证</span>}
          </div>
        </div>

        {/* 注销账号 */}
        {loggedIn&&(
          <div style={{marginTop:14}}>
            {!confirmDelete?(
              <button onClick={handleDelete} style={{width:"100%",background:"#fff",border:"none",borderRadius:14,padding:"14px 0",fontSize:13,color:"#e03030",cursor:"pointer",fontFamily:FF,fontWeight:500,boxShadow:"0 1px 4px rgba(0,0,0,0.04)"}}>注销账号</button>
            ):(
              <div style={{background:"#fff",borderRadius:14,padding:"16px",boxShadow:"0 1px 4px rgba(0,0,0,0.04)",borderLeft:"3px solid #e03030"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                  <span style={{fontSize:16}}>⚠️</span>
                  <span style={{fontSize:13.5,color:"#1a1a1a",fontFamily:FF,fontWeight:600}}>确认注销账号?</span>
                </div>
                <div style={{fontSize:12,color:"#666",fontFamily:FF,lineHeight:1.7,marginBottom:14,paddingLeft:24}}>
                  注销后:<br/>
                  • 个人资料、购物车、收藏将清除<br/>
                  • 订单记录保留 30 天用于退换货<br/>
                  • 此操作<span style={{color:"#e03030",fontWeight:600}}>不可撤销</span>
                </div>
                <div style={{display:"flex",gap:10}}>
                  <button onClick={()=>setConfirmDelete(false)} disabled={deleting} style={{flex:1,background:"#f5f5f4",border:"none",borderRadius:10,padding:"11px 0",fontSize:13,color:"#666",cursor:"pointer",fontFamily:FF,fontWeight:500}}>取消</button>
                  <button onClick={handleDelete} disabled={deleting} style={{flex:1,background:"#e03030",color:"#fff",border:"none",borderRadius:10,padding:"11px 0",fontSize:13,cursor:deleting?"not-allowed":"pointer",fontFamily:FF,fontWeight:500,opacity:deleting?0.6:1}}>{deleting?"处理中…":"确认注销"}</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 固定底部保存按钮 */}
      <div style={{position:"absolute",bottom:0,left:0,right:0,background:"#fff",borderTop:"1px solid #f0f0f0",padding:"12px 16px 22px",boxShadow:"0 -2px 8px rgba(0,0,0,0.04)"}}>
        <button onClick={handleSave} disabled={!valid||!dirty||saving} style={{width:"100%",background:(valid&&dirty&&!saving)?"linear-gradient(135deg,#1a1a1a,#3a3a3a)":"#ccc",color:"#fff",border:"none",borderRadius:12,padding:"14px 0",fontSize:14,fontFamily:FF,fontWeight:600,cursor:(valid&&dirty&&!saving)?"pointer":"not-allowed",letterSpacing:"0.06em",boxShadow:(valid&&dirty&&!saving)?"0 4px 12px rgba(0,0,0,0.15)":"none"}}>
          {saving?"保存中…":dirty?"保存":"无修改"}
        </button>
      </div>
    </div>
  );
}

function ProfilePage({onLoginChange,wishlist,setWishlist,setCart,onOpenProduct,products,setTab,initialPanel,onInitialPanelConsumed,chatPrefillProduct,onChatPrefillConsumed}){
  // 从 localStorage 恢复登录态
  const stored = loadStoredUser();
  const [loggedIn,setLoggedIn]=useState(!!stored);
  const [userId,setUserId]=useState(stored?.id||null);
  const [email,setEmail]=useState(stored?.email||"");
  const [name,setName]=useState(stored?.name||"");
  const [phone,setPhone]=useState(stored?.phone||"");
  const [panel,setPanel]=useState(null); // null | "feedback" | "address" | "about" | "contact" | "favorites" | "edit" | "chat" | {type:"orders",tab}

  // 外部跳转钩子 — 比如商品详情页点"客服"直接打开 chat panel
  useEffect(()=>{
    if(initialPanel){
      setPanel(initialPanel);
      onInitialPanelConsumed?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[initialPanel]);

  // OTP 登录状态
  const [otpStep,setOtpStep]=useState("phone"); // 'phone' | 'code'
  const [otpPhone,setOtpPhone]=useState("");
  const [otpCode,setOtpCode]=useState("");
  const [otpRefCode,setOtpRefCode]=useState(()=>{
    // 优先取 URL ?ref=XXX,其次 localStorage(防止用户从分享链接进来后又走别的路)
    try{
      const url=new URL(window.location.href);
      const fromUrl=url.searchParams.get("ref");
      if(fromUrl&&/^[A-Z0-9]{4,10}$/i.test(fromUrl)){
        localStorage.setItem("syj_pending_ref",fromUrl.toUpperCase());
        return fromUrl.toUpperCase();
      }
      const cached=localStorage.getItem("syj_pending_ref");
      if(cached&&/^[A-Z0-9]{4,10}$/i.test(cached))return cached;
    }catch{}
    return "";
  });
  const [showRefField,setShowRefField]=useState(false); // 「填写推荐码」展开/收起
  const [otpSending,setOtpSending]=useState(false);
  const [otpErr,setOtpErr]=useState(null);
  const [resendIn,setResendIn]=useState(0); // 重发倒计时

  // 顶部数据条 — 实时拉(用聚合接口一次拿全)
  const [profileStats,setProfileStats]=useState({
    orders:0,coupons:0,points:0,expiring:[],
    // 4 个订单 tab 的数量徽章
    pendingPayment:0, pendingShip:0, inTransit:0, refundActive:0,
  });
  const [expiryToastShown,setExpiryToastShown]=useState(false);
  const [expiryToast,setExpiryToast]=useState(null); // {coupons:[...]}
  // 标签页切回前台 → 强制刷新一次。覆盖"刚自取确认完积分到账,回 Profile 没更新"的场景
  useEffect(()=>{
    if(!loggedIn)return;
    const onVis=()=>{
      if(document.visibilityState!=="visible")return;
      getProfileStats().then(s=>{
        setProfileStats(prev=>({
          orders:(s.pending_ship_count||0)+(s.in_transit_count||0),
          coupons:s.coupon_count||0,
          points:s.points||0,
          expiring:s.expiring_coupons||prev.expiring,
          pendingPayment:s.pending_payment_count||0,
          pendingShip:s.pending_ship_count||0,
          inTransit:s.in_transit_count||0,
          refundActive:s.refund_active_count||0,
        }));
      }).catch(()=>{});
    };
    document.addEventListener("visibilitychange",onVis);
    return()=>document.removeEventListener("visibilitychange",onVis);
  },[loggedIn]);
  useEffect(()=>{
    if(!loggedIn)return;
    let alive=true;
    // 优先用聚合接口,失败时降级单独拉
    getProfileStats().then(s=>{
      if(!alive)return;
      const orders=(s.pending_ship_count||0)+(s.in_transit_count||0); // 待发货 + 运输中
      const newStats={
        orders,
        coupons:s.coupon_count||0,
        points:s.points||0,
        expiring:s.expiring_coupons||[],
        pendingPayment:s.pending_payment_count||0,
        pendingShip:s.pending_ship_count||0,
        inTransit:s.in_transit_count||0,
        refundActive:s.refund_active_count||0,
      };
      setProfileStats(newStats);
      // 进 APP 第一次,如果有即将到期的券 → 弹 toast 一次
      if(!expiryToastShown&&newStats.expiring.length>0&&!panel){
        setExpiryToast({coupons:newStats.expiring});
        setExpiryToastShown(true);
      }
    }).catch(()=>{
      // 降级:并行拉旧接口
      Promise.all([
        getMyOrders().then(d=>(d||[]).filter(o=>o.status!=="cancelled").length).catch(()=>0),
        getMyCoupons().then(d=>(d||[]).length).catch(()=>0),
        getPointBalance().then(b=>b?.points||0).catch(()=>0),
      ]).then(([orders,coupons,points])=>{
        if(alive)setProfileStats({orders,coupons,points,expiring:[]});
      });
    });
    return()=>{alive=false;};
  },[loggedIn,panel]); // 切换 panel 后回到主页时刷新

  // 启动时通知 App 当前登录态(刷新页面后)
  useEffect(()=>{onLoginChange(loggedIn);},[]); // eslint-disable-line

  // 重发倒计时
  useEffect(()=>{
    if(resendIn<=0)return;
    const t=setTimeout(()=>setResendIn(s=>s-1),1000);
    return()=>clearTimeout(t);
  },[resendIn]);

  const doSendOtp=async()=>{
    setOtpErr(null);
    const cleaned=otpPhone.replace(/\D/g,"");
    if(cleaned.length<10){setOtpErr("请输入有效的手机号");return;}
    setOtpSending(true);
    try{
      await sendOtp(otpPhone);
      setOtpStep("code");
      setResendIn(60);
    }catch(err){
      setOtpErr(err.message||"发送失败");
    }
    setOtpSending(false);
  };

  const doVerifyOtp=async()=>{
    setOtpErr(null);
    if(!/^\d{4,8}$/.test(otpCode)){setOtpErr("请输入验证码");return;}
    setOtpSending(true);
    try{
      const res=await verifyOtp(otpPhone,otpCode,otpRefCode);
      saveAuth(res.token,res.user);
      setUserId(res.user.id);
      setName(res.user.name||"");
      setEmail(res.user.email||"");
      setPhone(res.user.phone||"");
      setLoggedIn(true);
      onLoginChange(true);
      setOtpPhone("");setOtpCode("");setOtpStep("phone");
      // 推荐码已用,清掉缓存
      try{localStorage.removeItem("syj_pending_ref");}catch{}
      setOtpRefCode("");
    }catch(err){
      setOtpErr(err.message||"登录失败");
    }
    setOtpSending(false);
  };

  const doLogout=()=>{
    clearAuth();
    setLoggedIn(false);
    setUserId(null);setEmail("");setName("");setPhone("");
    setOtpStep("phone");setOtpPhone("");setOtpCode("");
    onLoginChange(false);
    setPanel(null);
  };

  // Sign in with Apple — 双模式:native(iOS APP)优先 / web(浏览器)兜底
  // - Native:src/native.js 装好 window.__syjAppleSignIn 用 @capacitor-community/apple-sign-in
  // - Web:用 Apple JS SDK,要 REACT_APP_APPLE_SERVICE_ID + REDIRECT_URI 环境变量
  const doAppleSignIn=async()=>{
    setOtpErr(null);
    const SERVICE_ID  = process.env.REACT_APP_APPLE_SERVICE_ID;
    const REDIRECT_URI= process.env.REACT_APP_APPLE_REDIRECT_URI||window.location.origin;
    setOtpSending(true);
    try{
      let identityToken, fullName, email;
      if(typeof window.__syjAppleSignIn==="function"){
        // Native iOS — 调原生 plugin
        const data=await window.__syjAppleSignIn();
        identityToken=data.identityToken;
        fullName=data.fullName;
        email=data.email;
      }else if(window.AppleID?.auth&&SERVICE_ID){
        // Web — 用 Apple JS SDK
        window.AppleID.auth.init({
          clientId:SERVICE_ID,
          scope:"name email",
          redirectURI:REDIRECT_URI,
          usePopup:true,
        });
        const data=await window.AppleID.auth.signIn();
        identityToken=data.authorization?.id_token;
        fullName=data.user?.name||null;
        email=data.user?.email||null;
      }else{
        setOtpErr("Apple 登录暂不可用");
        setOtpSending(false);
        return;
      }
      const res=await signInWithApple({identityToken,fullName,email});
      saveAuth(res.token,res.user);
      setUserId(res.user.id);
      setName(res.user.name||"");
      setEmail(res.user.email||"");
      setPhone(res.user.phone||"");
      setLoggedIn(true);
      onLoginChange(true);
    }catch(err){
      // 用户主动取消不算错
      if(err?.error==="popup_closed_by_user"||err?.code==="1001"||/canceled/i.test(err?.message||""))return;
      setOtpErr(err?.message||"Apple 登录失败");
    }finally{
      setOtpSending(false);
    }
  };

  const back=()=>setPanel(null);

  // Sub-panels
  if(panel==="feedback")  return <FeedbackPanel onBack={back}/>;
  if(panel==="address")   return <AddressPanel  onBack={back}/>;
  if(panel==="about")     return <AboutPanel    onBack={back}/>;
  if(panel==="contact")   return <ContactPanel  onBack={back} onOpenChat={()=>setPanel("chat")}/>;
  if(panel==="chat")      return <ChatPanel     onBack={()=>setPanel(null)} loggedIn={loggedIn} onNeedLogin={()=>setPanel(null)} prefillProduct={chatPrefillProduct} onPrefillConsumed={onChatPrefillConsumed}/>;
  if(panel==="privacy")   return <PrivacyPolicyPanel onBack={back}/>;
  if(panel==="terms")     return <TermsPanel        onBack={back}/>;
  if(panel==="favorites") return <FavoritesPanel onBack={back} wishlist={wishlist} setWishlist={setWishlist} setCart={setCart} onOpenProduct={onOpenProduct} products={products}/>;
  if(panel==="edit")      return <EditProfilePanel onBack={back} name={name} email={email} phone={phone} loggedIn={loggedIn} onSave={async(n,e,p)=>{
    if(loggedIn){
      const payload={name:n,email:e};
      if(p&&p!==(phone||""))payload.phone=p;
      let resp;
      try{resp=await updateMe(payload);}catch(err){alert(err.message||"保存失败");return;}
      // 后端做了合并(Apple 用户绑定旧手机号 OTP 账号)→ 返回新 JWT + 合并后数据
      if(resp.merged&&resp.newToken){
        saveAuth(resp.newToken,resp.data);
        setUserId(resp.data.id);
        setName(resp.data.name||"");
        setEmail(resp.data.email||"");
        setPhone(resp.data.phone||"");
        // 立即刷新 profileStats(积分/订单数等),避免显示旧账号数据
        try{
          const s=await getProfileStats();
          setProfileStats({
            orders:(s.pending_ship_count||0)+(s.in_transit_count||0),
            coupons:s.coupon_count||0,
            points:s.points||0,
            expiring:s.expiring_coupons||[],
          });
        }catch{}
        alert(resp.message||"已成功关联到您之前的账号 — 订单和积分已合并");
        setPanel(null);
        return;
      }
      // 普通更新 — 同步 localStorage
      const u=loadStoredUser()||{};
      saveAuth(localStorage.getItem("syj_token"),{...u,name:n,email:e,phone:p||u.phone});
    }
    setName(n);setEmail(e);setPhone(p);setPanel(null);
  }}/>;
  if(panel==="coupons")   return <CouponsPanel  onBack={back}/>;
  if(panel==="points")    return <PointsPanel   onBack={back} onGoShop={()=>setPanel("pointshop")}/>;
  if(panel==="pointshop") return <PointShopPanel onBack={()=>setPanel("points")} onRedeemed={()=>{}}/>;
  if(panel==="payments")  return <PaymentMethodsPanel onBack={back}/>;
  if(panel==="refunds")   return <RefundsPanel onBack={back}/>;
  if(panel==="referral")  return <ReferralPanel onBack={back}/>;
  if(panel==="buyAgain")  return <BuyAgainPanel onBack={back} wishlist={wishlist} onWish={(id)=>{const n=new Set(wishlist||[]);n.has(id)?n.delete(id):n.add(id);setWishlist?.(Array.from(n));}} onAdd={(p)=>setCart?.(c=>{const ex=c.find(x=>x.id===p.id&&!x.variantId);if(ex){return c.map(x=>x===ex?{...x,qty:x.qty+1}:x);}return [...c,{...p,qty:1,variantId:null}];})} onOpenProduct={(p)=>onOpenProduct?.(p)}/>;
  if(panel&&panel.type==="refundApply") return <RefundApplyPanel order={panel.order} onBack={()=>setPanel({type:"orderDetail",order:panel.order})} onSubmitted={()=>{setPanel("refunds");}}/>;
  if(panel&&panel.type==="orders") return <OrdersPanel onBack={back} initTab={panel.tab} onOpenOrder={o=>setPanel({type:"orderDetail",order:o})}/>;
  if(panel&&panel.type==="orderDetail") return <OrderDetailPanel order={panel.order} onBack={()=>setPanel({type:"orders",tab:"all"})} setTab={t=>{setPanel(null);setTab?.(t);}} onApplyRefund={o=>setPanel({type:"refundApply",order:o})} onReorder={items=>{
    if(!setCart||!Array.isArray(items))return;
    setCart(c=>{
      const next=[...c];
      for(const it of items){
        const productId=it.product_id;
        if(!productId)continue;
        // 从当前商品列表里查实时商品(避免用过期的 unit_price/库存)
        const fresh=(products||[]).find(p=>p.id===productId);
        if(!fresh)continue; // 商品已下架
        const ex=next.find(x=>x.id===productId&&x.variantId===(it.variant_id||null));
        if(ex){ex.qty+=it.qty;}
        else{next.push({...fresh,qty:it.qty,variantId:it.variant_id||null,variantInfo:(it.color_name||it.size)?{color:it.color_name,size:it.size}:null});}
      }
      return next;
    });
  }}/>;

  // ── Logged-in profile ────────────────────────────────────────────────────
  if(loggedIn){
    const ORDER_ICONS=[
      {label:"待付款",tab:"unpaid",count:profileStats.pendingPayment||0,icon:(
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
          <circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 15"/>
        </svg>
      )},
      {label:"待发货",tab:"unship",count:profileStats.pendingShip||0,icon:(
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
          <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
        </svg>
      )},
      {label:"已发货",tab:"shipping",count:profileStats.inTransit||0,icon:(
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
          <rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 5v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
        </svg>
      )},
      {label:"退换/售后",tab:"refund",count:profileStats.refundActive||0,icon:(
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
          <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/>
        </svg>
      )},
    ];

    const MENU_ROWS=[
      {
        items:[
          {label:"我的收藏",sub:`${wishlist?.length||0} 件宝贝`,bg:"linear-gradient(135deg,#ffeaea,#fff5f5)",iconBg:"#ffd5d5",iconColor:"#e03030",icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="#e03030" stroke="#e03030" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>,action:()=>setPanel("favorites")},
          {label:"再次购买",sub:"快速回购心仪商品",bg:"linear-gradient(135deg,#e8f0ff,#f5f9ff)",iconBg:"#d0e0fa",iconColor:"#2461c6",icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2461c6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><polyline points="23 20 23 14 17 14"/><path d="M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15"/></svg>,action:()=>setPanel("buyAgain")},
        ]
      },
    ];

    const SOLO_ROWS=[
      {label:"我的优惠券",icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>,action:()=>setPanel("coupons")},
      {label:"我的积分",icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>,action:()=>setPanel("points")},
      {label:"地址管理",icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="1.8"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>,action:()=>setPanel("address")},
      {label:"我的支付方式",icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>,action:()=>setPanel("payments")},
      {label:"推荐有礼",sub:"邀请好友各得 $5",icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#e03030" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>,action:()=>setPanel("referral")},
      {label:"关于我们",icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,action:()=>setPanel("about")},
      {label:"联系客服",icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="1.8"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>,action:()=>setPanel("contact")},
      {label:"反馈意见",icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="1.8"><path d="M11 4H4a2 2 0 00-2 2v14l4-4h14a2 2 0 002-2V9"/><path d="M18 2l4 4-8 8H10v-4l8-8z"/></svg>,action:()=>setPanel("feedback")},
      {label:"用户协议",icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/></svg>,action:()=>setPanel("terms")},
      {label:"隐私政策",icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,action:()=>setPanel("privacy")},
    ];

    const displayName=name?.trim()||(phone?`用户${phone.replace(/\D/g,"").slice(-4)}`:"用户");
    const subInfo=email?.trim()||phone||"";
    const totalSpent=stored?.total_spent||0;
    const vip=stored?.vip_level||vipLevel(totalSpent);
    const nextTh=vipNextThreshold(totalSpent);
    const remainingForNext=nextTh!==null?Math.max(0,nextTh-totalSpent):0;
    const [vipC1,vipC2,vipText]=vipBadgeColors(vip);
    return(
      <div style={{height:"100%",background:"#f6f7f8",overflowY:"auto",paddingBottom:80}}>
        {/* Hero — 浅色渐变(粉米色),女性化、轻盈 (App container 已处理 safe-area-top) */}
        <div style={{background:"linear-gradient(135deg,#fff5f0 0%,#ffe0d5 55%,#ffd0c0 100%)",padding:"16px 18px 60px",position:"relative",overflow:"hidden"}}>
          {/* 装饰光斑(白色柔光)*/}
          <div style={{position:"absolute",top:-50,right:-50,width:180,height:180,borderRadius:"50%",background:"radial-gradient(circle,rgba(255,255,255,0.7) 0%,rgba(255,255,255,0) 70%)",pointerEvents:"none"}}/>
          <div style={{position:"absolute",bottom:-40,left:-40,width:140,height:140,borderRadius:"50%",background:"radial-gradient(circle,rgba(255,170,150,0.35) 0%,rgba(255,170,150,0) 70%)",pointerEvents:"none"}}/>

          {/* 居中堆叠布局:头像在最上方居中,下面是 名字+VIP 徽章,再下面是手机号
              edit icon 浮在右上角(absolute) */}
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",position:"relative",paddingTop:6}}>
            {/* Avatar — 可爱女孩 SVG */}
            <div style={{width:78,height:78,borderRadius:"50%",border:"3px solid #fff",overflow:"hidden",boxShadow:"0 6px 16px rgba(180,90,80,0.25)",background:"#ffeae0"}}>
              <CuteGirlAvatar size={72}/>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8,marginTop:12,flexWrap:"wrap",justifyContent:"center"}}>
              <span style={{fontFamily:SERIF,fontSize:19,color:"#1a1a1a",fontWeight:600,letterSpacing:"0.02em"}}>{displayName}</span>
              {/* 动态 VIP 徽章 */}
              <span style={{display:"inline-flex",alignItems:"center",gap:3,fontSize:10,color:vipText,background:`linear-gradient(135deg,${vipC1},${vipC2})`,padding:"2px 8px",borderRadius:10,fontFamily:FF,fontWeight:700,letterSpacing:"0.06em",boxShadow:`0 2px 6px ${vipC1}55`}}>
                ✦ VIP{vip}
              </span>
            </div>
            {subInfo&&(
              <div style={{fontSize:12,color:"#7a5a48",fontFamily:FF,marginTop:4,maxWidth:"80%",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",textAlign:"center"}}>
                {subInfo}
              </div>
            )}
            {/* Edit icon — 白色磨砂按钮,浮在右上角不占布局空间 */}
            <button onClick={()=>setPanel("edit")} style={{position:"absolute",top:0,right:0,background:"rgba(255,255,255,0.65)",border:"none",borderRadius:"50%",width:34,height:34,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",backdropFilter:"blur(8px)",boxShadow:"0 2px 6px rgba(0,0,0,0.06)"}}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            </button>
          </div>

          {/* 数据条:订单/收藏/优惠券/积分 */}
          <div style={{position:"relative",marginTop:24,display:"flex",justifyContent:"space-around",background:"rgba(255,255,255,0.55)",borderRadius:14,padding:"12px 0",backdropFilter:"blur(8px)",border:"1px solid rgba(255,255,255,0.7)"}}>
            {[
              {label:"订单",val:String(profileStats.orders),onClick:()=>setPanel({type:"orders",tab:"all"})},
              {label:"收藏",val:String(wishlist?.length||0),onClick:()=>setPanel("favorites")},
              {label:"优惠券",val:String(profileStats.coupons),onClick:()=>setPanel("coupons")},
              {label:"积分",val:(profileStats.points||0).toLocaleString(),onClick:()=>setPanel("points")},
            ].map((s,i)=>(
              <button key={i} onClick={s.onClick} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2,background:"none",border:"none",cursor:"pointer",padding:"4px 0"}}>
                <span style={{fontSize:18,fontWeight:700,color:"#1a1a1a",fontFamily:FF,letterSpacing:"-0.01em"}}>{s.val}</span>
                <span style={{fontSize:10.5,color:"#a07060",fontFamily:FF}}>{s.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* 内容区 — 上提覆盖 hero 底部 */}
        <div style={{padding:"0 14px",marginTop:-28,display:"flex",flexDirection:"column",gap:12,position:"relative"}}>
          {/* ── Apple Sign In 用户绑定旧手机号账号的提示 ── */}
          {/^apple:/i.test(phone||"")&&(
            <button
              onClick={()=>setPanel("edit")}
              style={{
                background:"linear-gradient(135deg,#fff5e1,#ffefd5)",
                border:"1px solid #ffd591",
                borderRadius:12,
                padding:"12px 14px",
                cursor:"pointer",
                textAlign:"left",
                display:"flex",
                alignItems:"center",
                gap:10,
                boxShadow:"0 2px 6px rgba(255,180,90,0.15)",
              }}
            >
              <span style={{fontSize:18}}>🔗</span>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,color:"#7a5a10",fontFamily:FF,fontWeight:600}}>关联您之前的账号</div>
                <div style={{fontSize:11,color:"#a07a30",fontFamily:FF,marginTop:2,lineHeight:1.4}}>
                  之前用过手机号注册?在这里绑定手机号即可自动合并 — 订单、积分、地址全部保留
                </div>
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a07a30" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          )}
          {/* ── My Orders card ── */}
          <div style={{background:"#fff",borderRadius:16,overflow:"hidden",boxShadow:"0 1px 6px rgba(0,0,0,0.04)"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"15px 16px 12px"}}>
              <span style={{fontSize:14,fontFamily:FF,fontWeight:600,color:"#1a1a1a"}}>我的订单</span>
              <button onClick={()=>setPanel({type:"orders",tab:"all"})} style={{display:"flex",alignItems:"center",gap:4,cursor:"pointer",background:"none",border:"none",padding:0}}>
                <span style={{fontSize:12,color:"#aaa",fontFamily:FF}}>查看全部</span>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </div>
            <div style={{display:"flex",borderTop:"1px solid #f7f6f4"}}>
              {ORDER_ICONS.map((o,i)=>(
                <button key={i} onClick={()=>{
                  if(o.tab==="refund"){setPanel("refunds");}
                  else{setPanel({type:"orders",tab:o.tab});}
                }} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:7,padding:"16px 4px 14px",background:"none",border:"none",cursor:"pointer",borderRight:i<ORDER_ICONS.length-1?"1px solid #f7f6f4":"none",position:"relative"}}>
                  <div style={{color:"#1a1a1a",position:"relative"}}>
                    {o.icon}
                    {/* 数量徽章 — 红圈白字,贴在图标右上角;>99 显示 99+ */}
                    {o.count>0&&(
                      <span style={{
                        position:"absolute",
                        top:-6,right:-10,
                        minWidth:o.count>9?20:16,height:16,
                        padding:o.count>9?"0 4px":0,
                        background:"#e03030",
                        color:"#fff",
                        borderRadius:8,
                        border:"1.5px solid #fff",
                        fontSize:10,fontWeight:700,
                        fontFamily:FF,
                        display:"flex",alignItems:"center",justifyContent:"center",
                        lineHeight:1,
                        boxShadow:"0 1px 3px rgba(224,48,48,0.35)",
                        boxSizing:"border-box",
                      }}>
                        {o.count>99?"99+":o.count}
                      </span>
                    )}
                  </div>
                  <span style={{fontSize:11,fontFamily:FF,color:"#555",whiteSpace:"nowrap"}}>{o.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* ── Favorites + Rebuy row ── */}
          <div style={{display:"flex",gap:10}}>
            {MENU_ROWS[0].items.map((item,i)=>(
              <button key={i} onClick={item.action} style={{flex:1,background:item.bg,border:"none",borderRadius:16,padding:"16px 14px",display:"flex",alignItems:"center",gap:11,cursor:"pointer",textAlign:"left",position:"relative",overflow:"hidden",boxShadow:"0 1px 6px rgba(0,0,0,0.04)"}}>
                <div style={{width:42,height:42,borderRadius:12,background:item.iconBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:`0 2px 8px ${item.iconBg}`}}>
                  {item.icon}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13.5,fontFamily:FF,color:"#1a1a1a",fontWeight:600,marginBottom:2}}>{item.label}</div>
                  <div style={{fontSize:10.5,fontFamily:FF,color:"#888",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{item.sub}</div>
                </div>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={item.iconColor} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0,opacity:0.6}}><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            ))}
          </div>

          {/* ── Solo menu rows ── */}
          <div style={{background:"#fff",borderRadius:16,overflow:"hidden",boxShadow:"0 1px 6px rgba(0,0,0,0.04)"}}>
            {SOLO_ROWS.map((row,i)=>(
              <button key={i} onClick={row.action||undefined} style={{width:"100%",display:"flex",alignItems:"center",gap:14,padding:"15px 16px",background:"none",border:"none",borderBottom:i<SOLO_ROWS.length-1?"1px solid #f7f6f4":"none",cursor:"pointer",textAlign:"left"}}>
                <div style={{width:34,height:34,borderRadius:10,background:"#f5f5f4",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  {row.icon}
                </div>
                <span style={{flex:1,fontSize:14,fontFamily:FF,color:"#222"}}>{row.label}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            ))}
          </div>

          {/* ── Logout ── */}
          <button onClick={doLogout} style={{width:"100%",background:"#fff",border:"none",borderRadius:14,padding:"15px 0",fontSize:13,color:"#e03030",cursor:"pointer",fontFamily:FF,fontWeight:500,boxShadow:"0 1px 6px rgba(0,0,0,0.04)"}}>
            退出登录
          </button>
        </div>

        {/* 优惠券到期 toast — 进 APP 后弹一次 */}
        {expiryToast&&(
          <div onClick={()=>setExpiryToast(null)}
            style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:20,animation:"fadeIn 0.2s"}}>
            <div onClick={e=>e.stopPropagation()}
              style={{background:"#fff",borderRadius:18,padding:"22px 22px 18px",maxWidth:340,width:"100%",boxShadow:"0 12px 40px rgba(0,0,0,0.3)"}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
                <div style={{width:40,height:40,borderRadius:"50%",background:"linear-gradient(135deg,#e03030,#c42020)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,boxShadow:"0 4px 12px rgba(224,48,48,0.25)"}}>⏰</div>
                <div>
                  <div style={{fontSize:15,fontFamily:SERIF,fontWeight:700,color:"#1a1a1a"}}>优惠券即将到期</div>
                  <div style={{fontSize:11.5,fontFamily:FF,color:"#888",marginTop:1}}>{expiryToast.coupons.length} 张券将在 3 天内到期</div>
                </div>
              </div>
              <div style={{maxHeight:200,overflowY:"auto",margin:"0 -4px"}}>
                {expiryToast.coupons.map(c=>{
                  const days=Math.ceil((new Date(c.expires_at)-new Date())/86400000);
                  return(
                    <div key={c.id} style={{padding:"10px 4px",borderBottom:"1px solid #f5f5f4",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontFamily:FF,color:"#1a1a1a",fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{c.name}</div>
                        <div style={{fontSize:11,fontFamily:FF,color:"#999",marginTop:2}}>
                          {c.discount_type==="fixed"?`立减 $${parseFloat(c.discount_value||0).toFixed(0)}`:`${parseFloat(c.discount_value||0).toFixed(0)}% 折扣`}
                        </div>
                      </div>
                      <span style={{fontSize:11,padding:"3px 8px",background:"#fee2e2",color:"#c42020",borderRadius:4,fontFamily:FF,fontWeight:600}}>
                        {days===0?"今天到期":`${days} 天`}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div style={{display:"flex",gap:8,marginTop:14}}>
                <button onClick={()=>setExpiryToast(null)}
                  style={{flex:1,padding:"11px 0",background:"none",border:"1px solid #e5e5e5",color:"#666",borderRadius:10,fontSize:13,fontFamily:FF,cursor:"pointer"}}>
                  知道了
                </button>
                <button onClick={()=>{setExpiryToast(null);setPanel("coupons");}}
                  style={{flex:1,padding:"11px 0",background:"linear-gradient(135deg,#1a1a1a,#333)",color:"#fff",border:"none",borderRadius:10,fontSize:13,fontFamily:FF,fontWeight:600,cursor:"pointer",boxShadow:"0 4px 10px rgba(0,0,0,0.15)"}}>
                  立即使用
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── 手机号 OTP 登录 ────────────────────────────────────────────────────
  return(
    <div style={{height:"100%",overflow:"auto",background:"linear-gradient(180deg,#fff5f5 0%,#fff 35%,#fff 100%)",display:"flex",flexDirection:"column"}}>
      {/* 顶部 brand 区 — App container 已处理 safe-area-top */}
      <div style={{padding:"24px 28px 24px",textAlign:"center"}}>
        <div style={{width:64,height:64,borderRadius:18,background:"linear-gradient(135deg,#1a1a1a,#3a3a3a)",margin:"0 auto 14px",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:24,fontFamily:SERIF,fontWeight:700,boxShadow:"0 8px 24px rgba(0,0,0,0.18)",letterSpacing:"-0.02em"}}>十一</div>
        <div style={{fontSize:18,fontFamily:SERIF,color:"#1a1a1a",fontWeight:600,marginBottom:5}}>欢迎来到十一家</div>
        <div style={{fontSize:12,color:"#999",fontFamily:FF,letterSpacing:"0.05em"}}>SYJ · 您身边的精选好物</div>
      </div>

      <div style={{padding:"4px 24px 30px",flex:1}}>
        {otpStep==="phone"?(
          <>
            <div style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600,marginBottom:8}}>手机号</div>
            <div style={{display:"flex",alignItems:"center",border:"1.5px solid #ececec",borderRadius:12,padding:"3px 4px 3px 14px",background:"#fff",boxShadow:"0 1px 4px rgba(0,0,0,0.03)"}}>
              <span style={{display:"flex",alignItems:"center",gap:4,padding:"8px 8px 8px 0",fontFamily:FF,color:"#1a1a1a",fontSize:14,borderRight:"1px solid #ececec",marginRight:10,userSelect:"none"}}>
                🇺🇸 +1
              </span>
              <input
                type="tel"
                inputMode="numeric"
                autoFocus
                placeholder="请输入手机号"
                value={otpPhone}
                onChange={e=>setOtpPhone(e.target.value.replace(/[^\d+]/g,"").slice(0,15))}
                onKeyDown={e=>{if(e.key==="Enter")doSendOtp();}}
                style={{flex:1,padding:"13px 0",border:"none",outline:"none",fontSize:15,color:"#1a1a1a",fontFamily:FF,background:"none"}}
              />
              {otpPhone&&<button onClick={()=>setOtpPhone("")} style={{background:"#e8e8e8",border:"none",borderRadius:"50%",width:18,height:18,cursor:"pointer",color:"#888",fontSize:11,marginRight:8,padding:0,display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>}
            </div>
            {otpErr&&<div style={{fontSize:12,color:"#e03030",fontFamily:FF,marginTop:10,display:"flex",alignItems:"center",gap:5}}>⚠️ {otpErr}</div>}

            {/* 推荐码 - 折叠显示;有 URL 带过来的或本地缓存就直接展开 */}
            {(showRefField||otpRefCode)?(
              <div style={{marginTop:14}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                  <span style={{fontSize:12,color:"#1a1a1a",fontFamily:FF,fontWeight:500}}>推荐码 <span style={{color:"#aaa",fontWeight:400}}>(选填)</span></span>
                  {!otpRefCode&&<button onClick={()=>setShowRefField(false)} style={{background:"none",border:"none",color:"#888",fontSize:11,cursor:"pointer",fontFamily:FF}}>取消</button>}
                </div>
                <input type="text" value={otpRefCode}
                  onChange={e=>setOtpRefCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g,"").slice(0,10))}
                  placeholder="朋友给您的推荐码"
                  style={{width:"100%",padding:"11px 14px",border:"1.5px solid #ececec",borderRadius:10,outline:"none",fontSize:14,fontFamily:"monospace",letterSpacing:"0.1em",color:"#1a1a1a",background:"#fff",boxSizing:"border-box",textTransform:"uppercase"}}/>
                {otpRefCode&&(
                  <div style={{fontSize:11,color:"#16a34a",fontFamily:FF,marginTop:4}}>✓ 注册后您和好友各得 $5 优惠券</div>
                )}
              </div>
            ):(
              <button onClick={()=>setShowRefField(true)}
                style={{marginTop:12,background:"none",border:"none",color:"#888",fontSize:12,fontFamily:FF,cursor:"pointer",padding:"4px 0",textDecoration:"underline"}}>
                有推荐码?点这里填写
              </button>
            )}

            <button
              onClick={doSendOtp}
              disabled={otpSending||otpPhone.replace(/\D/g,"").length<10}
              style={{width:"100%",background:(otpSending||otpPhone.replace(/\D/g,"").length<10)?"#ccc":"linear-gradient(135deg,#1a1a1a,#333)",color:"#fff",border:"none",borderRadius:12,padding:"14px 0",fontSize:14,fontFamily:FF,fontWeight:600,cursor:(otpSending||otpPhone.replace(/\D/g,"").length<10)?"not-allowed":"pointer",marginTop:24,letterSpacing:"0.05em",boxShadow:(otpSending||otpPhone.replace(/\D/g,"").length<10)?"none":"0 4px 12px rgba(0,0,0,0.15)"}}
            >
              {otpSending?"发送中…":"获取验证码"}
            </button>

            {/* Sign in with Apple — 显示条件:
                · 配置了 REACT_APP_APPLE_SERVICE_ID(web 端线上)
                · 或者运行在 Capacitor 原生(iOS APP,SIWA 强制可用)
                这样本地 dev 不会看到点了报错的按钮 */}
            {(process.env.REACT_APP_APPLE_SERVICE_ID||(typeof window!=="undefined"&&window.__syjAppleSignIn))&&(
              <>
                <div style={{display:"flex",alignItems:"center",gap:10,margin:"22px 0 16px"}}>
                  <div style={{flex:1,height:1,background:"#ececec"}}/>
                  <span style={{fontSize:11,color:"#aaa",fontFamily:FF,letterSpacing:"0.05em"}}>或</span>
                  <div style={{flex:1,height:1,background:"#ececec"}}/>
                </div>
                <button onClick={doAppleSignIn} disabled={otpSending}
                  style={{
                    width:"100%",background:"#000",color:"#fff",border:"none",borderRadius:12,
                    padding:"13px 0",fontSize:14,fontFamily:FF,fontWeight:600,
                    cursor:otpSending?"wait":"pointer",
                    display:"flex",alignItems:"center",justifyContent:"center",gap:8,
                    opacity:otpSending?0.6:1,
                  }}>
                  <svg width="16" height="20" viewBox="0 0 384 512" fill="#fff" aria-hidden="true">
                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                  </svg>
                  使用 Apple 登录
                </button>
              </>
            )}

            {/* 新用户专享 — 注册即送优惠券 CTA */}
            <div style={{
              marginTop:20,
              display:"flex",alignItems:"center",gap:12,
              padding:"14px 16px",
              background:"linear-gradient(135deg,#fff5f0,#ffe8de)",
              border:"1px solid #ffcab0",
              borderRadius:14,
              boxShadow:"0 2px 8px rgba(255,180,140,0.15)",
            }}>
              <div style={{
                flexShrink:0,width:38,height:38,borderRadius:"50%",
                background:"linear-gradient(135deg,#e03030,#c42020)",
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:18,boxShadow:"0 3px 8px rgba(224,48,48,0.3)",
              }}>🎁</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13.5,fontFamily:FF,fontWeight:700,color:"#1a1a1a",marginBottom:2}}>新用户专享 · 满 $50 立减 $5</div>
                <div style={{fontSize:11,fontFamily:FF,color:"#666"}}>注册即得欢迎券,第一单立刻省钱</div>
              </div>
            </div>

            <div style={{marginTop:32,textAlign:"center",fontSize:11,color:"#aaa",fontFamily:FF,lineHeight:1.7,padding:"0 12px"}}>
              点击「获取验证码」即表示已阅读并同意<br/>
              <button onClick={()=>setPanel("terms")} style={{background:"none",border:"none",color:"#666",textDecoration:"underline",cursor:"pointer",fontFamily:FF,fontSize:11,padding:0,margin:"0 2px"}}>《用户协议》</button>和<button onClick={()=>setPanel("privacy")} style={{background:"none",border:"none",color:"#666",textDecoration:"underline",cursor:"pointer",fontFamily:FF,fontSize:11,padding:0,margin:"0 2px"}}>《隐私政策》</button>
            </div>
          </>
        ):(
          <>
            <button onClick={()=>{setOtpStep("phone");setOtpCode("");setOtpErr(null);}} style={{display:"flex",alignItems:"center",gap:5,background:"none",border:"none",cursor:"pointer",padding:"4px 0",marginBottom:18,color:"#666",fontFamily:FF,fontSize:13}}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              返回
            </button>
            <div style={{fontSize:18,color:"#1a1a1a",fontFamily:SERIF,fontWeight:600,marginBottom:6}}>输入验证码</div>
            <div style={{fontSize:12.5,color:"#888",fontFamily:FF,marginBottom:24,lineHeight:1.6}}>
              已发送 6 位验证码至 <span style={{color:"#1a1a1a",fontWeight:500}}>+1 {otpPhone}</span>
            </div>

            {/* 6 位验证码:可见 6 格 + 透明 input 叠在最上面接收输入 */}
            <div style={{position:"relative",marginBottom:14}}>
              <div style={{display:"flex",justifyContent:"space-between",gap:8,pointerEvents:"none"}}>
                {[0,1,2,3,4,5].map(i=>{
                  const digit=otpCode[i]||"";
                  const focused=i===otpCode.length;
                  return(
                    <div key={i} style={{flex:1,aspectRatio:"1/1.15",maxWidth:50,border:`1.5px solid ${digit?"#1a1a1a":focused?"#999":"#ececec"}`,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,fontWeight:600,color:"#1a1a1a",fontFamily:"monospace",background:digit?"#fafaf9":"#fff",transition:"all 0.15s"}}>{digit}</div>
                  );
                })}
              </div>
              <input
                type="tel"
                inputMode="numeric"
                autoFocus
                value={otpCode}
                onChange={e=>setOtpCode(e.target.value.replace(/\D/g,"").slice(0,6))}
                onKeyDown={e=>{if(e.key==="Enter"&&otpCode.length>=4)doVerifyOtp();}}
                maxLength={6}
                style={{position:"absolute",inset:0,width:"100%",height:"100%",opacity:0,border:"none",outline:"none",caretColor:"transparent",fontSize:16}}
              />
            </div>
            {otpErr&&<div style={{fontSize:12,color:"#e03030",fontFamily:FF,marginTop:4,display:"flex",alignItems:"center",gap:5}}>⚠️ {otpErr}</div>}

            <button
              onClick={doVerifyOtp}
              disabled={otpSending||otpCode.length<4}
              style={{width:"100%",background:(otpSending||otpCode.length<4)?"#ccc":"linear-gradient(135deg,#1a1a1a,#333)",color:"#fff",border:"none",borderRadius:12,padding:"14px 0",fontSize:14,fontFamily:FF,fontWeight:600,cursor:(otpSending||otpCode.length<4)?"not-allowed":"pointer",marginTop:18,letterSpacing:"0.05em",boxShadow:(otpSending||otpCode.length<4)?"none":"0 4px 12px rgba(0,0,0,0.15)"}}
            >
              {otpSending?"登录中…":"登录"}
            </button>

            <div style={{marginTop:18,textAlign:"center"}}>
              <button
                onClick={()=>{if(resendIn===0)doSendOtp();}}
                disabled={resendIn>0}
                style={{background:"none",border:"none",cursor:resendIn>0?"default":"pointer",fontSize:12.5,color:resendIn>0?"#bbb":"#1a1a1a",fontFamily:FF,padding:8}}
              >
                {resendIn>0?`收不到? ${resendIn}s 后可重发`:"重发验证码"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 全屏图片 lightbox:左右滑动切图、双击放大、双指缩放、单指拖动
// ─────────────────────────────────────────────────────────────────────────────
function ImageLightbox({images,initialIdx=0,name,onClose}){
  const [idx,setIdx]=useState(initialIdx);
  const [scale,setScale]=useState(1);
  const [tx,setTx]=useState(0);
  const [ty,setTy]=useState(0);
  const lastTap=useRef(0);
  const startX=useRef(0),startY=useRef(0);
  const startTx=useRef(0),startTy=useRef(0);
  const startDist=useRef(0),startScale=useRef(1);
  const dragging=useRef(false);
  const pinching=useRef(false);

  // 切图 / 关闭时重置缩放
  const reset=()=>{setScale(1);setTx(0);setTy(0);};
  useEffect(()=>{reset();},[idx]);

  const distance=(t1,t2)=>Math.hypot(t1.clientX-t2.clientX,t1.clientY-t2.clientY);

  const onTouchStart=(e)=>{
    if(e.touches.length===2){
      pinching.current=true;
      dragging.current=false;
      startDist.current=distance(e.touches[0],e.touches[1]);
      startScale.current=scale;
    }else if(e.touches.length===1){
      dragging.current=true;
      pinching.current=false;
      startX.current=e.touches[0].clientX;
      startY.current=e.touches[0].clientY;
      startTx.current=tx;
      startTy.current=ty;
    }
  };
  const onTouchMove=(e)=>{
    if(pinching.current&&e.touches.length===2){
      e.preventDefault();
      const d=distance(e.touches[0],e.touches[1]);
      const next=Math.max(1,Math.min(4,startScale.current*(d/startDist.current)));
      setScale(next);
      if(next===1){setTx(0);setTy(0);}
    }else if(dragging.current&&e.touches.length===1){
      const dx=e.touches[0].clientX-startX.current;
      const dy=e.touches[0].clientY-startY.current;
      if(scale>1){
        e.preventDefault();
        setTx(startTx.current+dx);
        setTy(startTy.current+dy);
      }else{
        // scale=1 时,如果横向位移超过阈值就翻页
        if(Math.abs(dx)>Math.abs(dy)){
          // 跟随手指(可视反馈)
          setTx(dx);
        }
      }
    }
  };
  const onTouchEnd=(e)=>{
    // 单击图片以外的黑色区域 → 关闭(几乎没动 + scale=1 + tap 在 IMG 元素之外)
    const isTap=
      !pinching.current
      && e.changedTouches?.length===1
      && scale===1
      && Math.abs(tx-startTx.current)<6
      && Math.abs(ty-startTy.current)<6;
    if(isTap){
      const tgt=e.target;
      const tagName=tgt?.tagName?.toUpperCase?.()||"";
      // tap 落在背景(非 IMG / BUTTON / SVG)→ 关闭 lightbox
      if(tagName!=="IMG"&&tagName!=="BUTTON"&&tagName!=="SVG"&&tagName!=="LINE"&&tagName!=="PATH"){
        onClose?.();
        dragging.current=false;
        pinching.current=false;
        return;
      }
    }
    // 双击:300ms 内连续两次单指点击
    if(!pinching.current&&e.changedTouches?.length===1){
      const now=Date.now();
      if(now-lastTap.current<300&&Math.abs(tx-startTx.current)<6&&Math.abs(ty-startTy.current)<6){
        if(scale===1){setScale(2);}
        else reset();
        lastTap.current=0;
      }else{
        lastTap.current=now;
      }
    }
    // scale=1 时的左右滑动 → 翻页
    if(dragging.current&&scale===1){
      if(tx<-60&&idx<images.length-1){setIdx(i=>i+1);}
      else if(tx>60&&idx>0){setIdx(i=>i-1);}
      else setTx(0);
    }
    dragging.current=false;
    pinching.current=false;
  };

  const it=images[idx]||{};

  return(
    <div
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{
        position:"fixed",inset:0,background:"#000",zIndex:9999,
        display:"flex",alignItems:"center",justifyContent:"center",
        touchAction:"none",userSelect:"none",
      }}
    >
      {/* 关闭按钮 */}
      <button onClick={onClose} style={{
        position:"absolute",top:50,right:18,zIndex:2,
        width:36,height:36,borderRadius:"50%",
        background:"rgba(255,255,255,0.15)",border:"none",cursor:"pointer",
        display:"flex",alignItems:"center",justifyContent:"center",
        backdropFilter:"blur(8px)",
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>

      {/* 计数器 */}
      <div style={{position:"absolute",top:55,left:0,right:0,textAlign:"center",color:"#fff",fontSize:14,fontFamily:FF,fontWeight:500,letterSpacing:"0.04em"}}>
        {idx+1} / {images.length}
      </div>

      {/* 图 — Canvas 渲染 + LSB 隐写,视觉无差别但像素里嵌了用户标记 */}
      {it.imgUrl?(
        <div
          style={{
            position:"relative",
            transform:`translate(${tx}px,${ty}px) scale(${scale})`,
            transition:dragging.current||pinching.current?"none":"transform 0.25s ease",
          }}
        >
          <WatermarkedImage
            src={it.imgUrl}
            alt={name||""}
            style={{maxWidth:"100vw",maxHeight:"100vh",pointerEvents:"none",display:"block"}}
          />
        </div>
      ):(
        <div style={{color:"#888",fontFamily:FF,fontSize:14}}>暂无图片</div>
      )}

      {/* 提示 */}
      <div style={{position:"absolute",bottom:30,left:0,right:0,textAlign:"center",color:"rgba(255,255,255,0.55)",fontSize:11,fontFamily:FF}}>
        双击或双指缩放 · 左右滑动切换
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PRODUCT DETAIL PAGE — comprehensive: image carousel, price/promo, variants,
// delivery, service, tabs (detail / specs / reviews), recommendations, action bar
// ─────────────────────────────────────────────────────────────────────────────
function ProductImageCarousel({images,name,onTap}){
  const [idx,setIdx]=useState(0);
  const [drag,setDrag]=useState(0);
  const startX=useRef(0),dragging=useRef(false);
  const pD=(e)=>{dragging.current=true;startX.current=e.clientX??e.touches?.[0]?.clientX??0;};
  const pM=(e)=>{if(!dragging.current)return;setDrag((e.clientX??e.touches?.[0]?.clientX??0)-startX.current);};
  const pU=()=>{if(!dragging.current)return;dragging.current=false;if(Math.abs(drag)>50){setIdx(i=>Math.max(0,Math.min(images.length-1,i+(drag<0?1:-1))));}setDrag(0);};
  // 点击未拖动时触发全屏
  const moved=useRef(false);
  const pDownTap=(e)=>{moved.current=false;pD(e);};
  const pMoveTap=(e)=>{if(dragging.current&&Math.abs((e.clientX??e.touches?.[0]?.clientX??0)-startX.current)>5)moved.current=true;pM(e);};
  const pUpTap=(e)=>{const wasDrag=moved.current;pU(e);if(!wasDrag&&onTap)onTap(idx);};

  return(
    <div style={{position:"relative",overflow:"hidden",background:"#fff",touchAction:"pan-y",userSelect:"none",cursor:onTap?"zoom-in":"default"}} onPointerDown={pDownTap} onPointerMove={pMoveTap} onPointerUp={pUpTap} onPointerLeave={pU}>
      <div style={{display:"flex",transform:`translateX(calc(${-idx*100}% + ${drag}px))`,transition:dragging.current?"none":"transform 0.3s ease",willChange:"transform"}}>
        {images.map((it,i)=>(
          <div key={i} style={{flex:"0 0 100%"}}>
            <ProductImage img={it.img} imgUrl={it.imgUrl} name={name} aspect={1}/>
          </div>
        ))}
      </div>
      <div style={{position:"absolute",bottom:14,left:0,right:0,display:"flex",justifyContent:"center",gap:5,pointerEvents:"none"}}>
        {images.map((_,i)=>(
          <div key={i} style={{width:i===idx?16:5,height:5,borderRadius:3,background:i===idx?"#1a1a1a":"rgba(0,0,0,0.25)",transition:"all 0.2s"}}/>
        ))}
      </div>
      <div style={{position:"absolute",top:14,right:14,background:"rgba(0,0,0,0.5)",color:"#fff",fontSize:11,padding:"3px 9px",borderRadius:11,fontFamily:FF,pointerEvents:"none"}}>{idx+1}/{images.length}</div>
    </div>
  );
}

function StarRating({value,size=12}){
  return(
    <div style={{display:"inline-flex",gap:1}}>
      {[1,2,3,4,5].map(i=>(
        <svg key={i} width={size} height={size} viewBox="0 0 24 24" fill={i<=value?"#f5a623":"#e5e5e5"} stroke="none">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26"/>
        </svg>
      ))}
    </div>
  );
}

function ProductDetailPage({product,onBack,wishlist,setWishlist,cart,setCart,setTab,setCheckout,setProductDetail,loggedIn,products=[],setProfileInitialPanel,setChatPrefillProduct}){
  const realVariants=(product.variants||[]).filter(v=>v.active!==false);
  // 颜色和尺码可能是独立维度,各自去重
  const colors=[...new Map(realVariants.filter(v=>v.color_name).map(v=>[v.color_name,{name:v.color_name,hex:v.color_hex}])).values()];
  const sizesAll=[...new Set(realVariants.map(v=>v.size).filter(Boolean))];
  const showSizes=sizesAll.length>0;

  // 当前选中变体在购物车里已经放了几件
  const cartQtyOf=(variantId)=>cart.find(i=>i.id===product.id&&(i.variantId||null)===(variantId||null))?.qty||0;

  // 该 variant 是否还能再加(库存 - 已加 > 0)
  const variantHasAvailable=v=>{
    if(!v)return true;
    if(typeof v.stock!=="number")return true; // 未跟踪库存,放行
    return cartQtyOf(v.id)<v.stock;
  };
  const colorHasStock=name=>realVariants.filter(v=>v.color_name===name).some(variantHasAvailable);
  const sizeHasStockForSelColor=size=>{
    const list=selColor?realVariants.filter(v=>v.color_name===selColor&&v.size===size):realVariants.filter(v=>v.size===size);
    return list.some(variantHasAvailable);
  };

  const [qty,setQty]=useState(1);
  const [selColor,setSelColor]=useState(colors[0]?.name||null);
  const [selSize,setSelSize]=useState(showSizes?(sizesAll[0]||null):(sizesAll[0]||null));
  const [activeTab,setActiveTab]=useState("detail");
  const [scrollY,setScrollY]=useState(0);
  const [toast,setToast]=useState(null);
  const [lightboxIdx,setLightboxIdx]=useState(null); // null = 关闭;数字 = 打开+起始 idx
  // 邮编默认值 = 主页 geolocation 定位写入的 syj_zip_cache(7 天 TTL)
  // 没定位到就显示空 → 提示用户输入。打包成 APP 用系统原生定位,授权后实时拿到。
  // 用户在商品页改的是临时值,不持久化 — 下次再进商品页仍然回到定位邮编
  const [zipcode,setZipcode]=useState(()=>{
    try{
      // 一次性清掉旧版误写入的 syj_zip
      if(localStorage.getItem("syj_zip")){
        localStorage.removeItem("syj_zip");
      }
      const raw=localStorage.getItem("syj_zip_cache");
      if(raw){
        const {zip,ts}=JSON.parse(raw);
        const TTL=7*24*60*60*1000;
        if(zip&&/^\d{5}$/.test(zip)&&Date.now()-ts<TTL)return zip;
      }
    }catch{}
    return ""; // 没定位 → 空,显示「输入邮编」提示
  });

  // 如果当前没有有效定位缓存,商品页也尝试一次定位(场景:深链直进商品页,主页没机会定位)
  useEffect(()=>{
    let alive=true;
    try{
      const raw=localStorage.getItem("syj_zip_cache");
      if(raw){
        const {zip,ts}=JSON.parse(raw);
        const TTL=7*24*60*60*1000;
        if(zip&&/^\d{5}$/.test(zip)&&Date.now()-ts<TTL)return; // 有缓存,不重定位
      }
    }catch{}
    if(!("geolocation" in navigator))return;
    navigator.geolocation.getCurrentPosition(
      async(pos)=>{
        try{
          const{latitude:lat,longitude:lng}=pos.coords;
          const d=await mapsReverseGeocode(lat,lng);
          const z=d?.zip||"";
          if(alive&&/^\d{5}$/.test(z)){
            setZipcode(z);
            try{localStorage.setItem("syj_zip_cache",JSON.stringify({zip:z,ts:Date.now()}));}catch{}
          }
        }catch{}
      },
      ()=>{}, // 拒绝/失败 → 沉默,继续用 ZIPCODES[0]
      {enableHighAccuracy:false,timeout:8000,maximumAge:60_000}
    );
    return()=>{alive=false;};
  },[]);
  const [editingZip,setEditingZip]=useState(false);
  const [delivery,setDelivery]=useState(null);
  const [deliveryLoading,setDeliveryLoading]=useState(false);
  const [deliveryErr,setDeliveryErr]=useState("");

  // 登录用户首次进商品页 → 如果当前还没拿到 zip(主页定位也失败了),才异步用默认地址兜底
  useEffect(()=>{
    if(!loggedIn||zipcode)return; // 已有 zip(定位拿到了)就不覆盖
    let alive=true;
    getAddresses().then(list=>{
      if(!alive||zipcode)return;
      const def=(list||[]).find(a=>a.isDefault)||(list||[])[0];
      const z=def?.zip;
      if(z&&/^\d{5}$/.test(z))setZipcode(z);
    }).catch(()=>{});
    return()=>{alive=false;};
  },[loggedIn]); // eslint-disable-line

  // 拉送达预估 — 后端按 (zip, ship_date) 缓存,前端再叠一层 localStorage(版本 v2 避免旧缓存)
  // 同一邮编当天只调一次 API,客户改邮编就重拉
  useEffect(()=>{
    if(!/^\d{5}$/.test(zipcode))return;
    const todayKey=new Date().toLocaleDateString("en-CA",{timeZone:"America/Los_Angeles"});
    const cacheKey=`syj_delivery_v2_${zipcode}_${todayKey}`;
    try{
      const raw=localStorage.getItem(cacheKey);
      if(raw){
        const cached=JSON.parse(raw);
        setDelivery(cached);
        setDeliveryErr("");
        return;
      }
    }catch{}
    setDeliveryLoading(true);
    setDeliveryErr("");
    getDeliveryEstimate(zipcode).then(d=>{
      setDelivery(d);
      try{localStorage.setItem(cacheKey,JSON.stringify(d));}catch{}
      // 注意:此处 NOT 写 syj_zip — 只有用户主动确定时才写,见下面 saveZipEdit
    }).catch(err=>{
      setDelivery(null);
      setDeliveryErr(err.message||"无法查询配送时间");
    }).finally(()=>setDeliveryLoading(false));
  },[zipcode]);

  // 用户在商品页改邮编是临时性的,不写 localStorage —
  // 下次进商品默认值仍然回到主页定位的真实邮编
  const saveZipEdit=()=>setEditingZip(false);
  const scrollRef=useRef(null);
  const cartIconRef=useRef(null);
  const addBtnRef=useRef(null);
  const [fly,setFly]=useState(null);     // {sx,sy,ex,ey,key}
  const [cartPulse,setCartPulse]=useState(false); // 飞到顶时购物车图标抖一下
  const inWish=wishlist.includes(product.id);
  const cartCount=cart.reduce((a,i)=>a+(i.qty||0),0);

  // 加购飞入动画 — 从 add 按钮飞到右上角购物车图标
  const triggerFly=()=>{
    const start=addBtnRef.current?.getBoundingClientRect();
    const end=cartIconRef.current?.getBoundingClientRect();
    if(!start||!end)return;
    setFly({
      sx:start.left+start.width/2,
      sy:start.top+start.height/2,
      ex:end.left+end.width/2,
      ey:end.top+end.height/2,
      key:Date.now(),
    });
    // 0.7s 后清除并触发 pulse
    setTimeout(()=>{setFly(null);setCartPulse(true);setTimeout(()=>setCartPulse(false),350);},680);
  };

  // 当前选中的 variant:颜色 + 尺码同时匹配,匹配不到就回退到颜色匹配的第一个
  const selectedVariant=realVariants.find(v=>(!selColor||v.color_name===selColor)&&(!selSize||v.size===selSize))
    ||realVariants.find(v=>!selColor||v.color_name===selColor)
    ||null;

  const displayPrice=selectedVariant?.price?parseFloat(selectedVariant.price):product.price;
  const displayOrigPrice=product.origPrice; // variant 没有 orig_price 字段,沿用商品级
  const discount=displayOrigPrice&&displayOrigPrice>displayPrice?Math.round((1-displayPrice/displayOrigPrice)*100):0;
  const savings=displayOrigPrice?(displayOrigPrice-displayPrice).toFixed(2):0;

  // 主图集:把 variant 的色卡图 + 商品级的详情图合并去重
  // 选中色卡的图排前(用户先看到当前色),商品级图随后
  const variantImgs=selectedVariant?.images||[];
  const productImgs=product.images||[];
  const baseImages=[...new Set([...variantImgs,...productImgs])];
  const images=baseImages.length>0
    ?baseImages.map(url=>({imgUrl:url,img:product.img}))
    :[1,2,3,4].map(()=>({img:product.img,imgUrl:null}));

  const variantLabel=[selColor,selSize&&selSize!=="均码"?selSize:null].filter(Boolean).join(" ")||"标准款";

  // 当前选中变体(或无变体商品)还能加几件
  const remainingForSelected=(()=>{
    if(realVariants.length>0){
      if(!selectedVariant)return 0;
      if(typeof selectedVariant.stock!=="number")return Infinity;
      return Math.max(0,selectedVariant.stock-cartQtyOf(selectedVariant.id));
    }
    if(typeof product.totalStock!=="number")return Infinity;
    return Math.max(0,product.totalStock-cartQtyOf(null));
  })();
  const isOutOfStock=remainingForSelected<=0;
  const canIncreaseQty=qty<remainingForSelected;

  // 切换 variant 时,如果当前 qty 超过新变体的剩余,自动 clamp
  useEffect(()=>{
    if(qty>remainingForSelected){
      setQty(Math.max(1,remainingForSelected));
    }
  },[selectedVariant?.id]); // eslint-disable-line
  const colorList=[...new Set((product.variants||[]).map(v=>v.color_name).filter(Boolean))];
  const specs=[
    {k:"品牌",v:"十一家"},
    colorList.length>0?{k:"颜色",v:colorList.join(" / ")}:null,
    product.barcode?{k:"条形码",v:product.barcode}:null,
    product.sku?{k:"SKU",v:product.sku}:null,
  ].filter(Boolean);
  // 「猜你喜欢」— 后端个性化推荐(基于浏览/购买/收藏)
  const [personalRecos,setPersonalRecos]=useState([]);
  const [personalRecosLoading,setPersonalRecosLoading]=useState(true);
  useEffect(()=>{
    let alive=true;
    setPersonalRecosLoading(true);
    getRecommendations({limit:6,excludeId:product.id}).then(({products:rec})=>{
      if(!alive)return;
      setPersonalRecos((rec||[]).filter(p=>p.id!==product.id).slice(0,6));
    }).catch(()=>{
      if(!alive)return;
      setPersonalRecos([]);
    }).finally(()=>{
      if(alive)setPersonalRecosLoading(false);
    });
    return()=>{alive=false;};
  },[product.id]);

  // 浏览历史:进详情页时写一次,详情页底部展示最近看过
  useEffect(()=>{pushHistory(product.id);},[product.id]);
  const historyIds=loadHistory().filter(id=>id!==product.id);
  const historyProducts=historyIds.map(id=>products.find(p=>p.id===id)).filter(Boolean).slice(0,8);

  useEffect(()=>{
    const el=scrollRef.current;if(!el)return;
    const fn=()=>setScrollY(el.scrollTop);
    el.addEventListener("scroll",fn,{passive:true});
    return()=>el.removeEventListener("scroll",fn);
  },[]);

  const headerOp=Math.min(1,scrollY/220);
  const showName=scrollY>200;

  const showToast=(msg)=>{setToast(msg);setTimeout(()=>setToast(null),1400);};
  const handleWish=()=>{
    if(!loggedIn){onBack();setTab("profile");return;}
    setWishlist(w=>w.includes(product.id)?w.filter(x=>x!==product.id):[...w,product.id]);
  };
  const buildCartItem=()=>({
    ...product,
    price:displayPrice,
    sub:variantLabel!=="标准款"?variantLabel:product.sub,
    imgUrl:images[0]?.imgUrl||product.imgUrl,
    qty,
    variantId:selectedVariant?.id||null,
    variantInfo:selectedVariant?{color:selColor,size:selSize}:null,
  });
  const handleAddCart=()=>{
    if(qty>remainingForSelected){
      showToast(`库存仅剩 ${remainingForSelected} 件`);
      return;
    }
    triggerFly(); // 飞入动画
    setCart(c=>{
      const ex=c.find(i=>i.id===product.id&&i.variantId===(selectedVariant?.id||null));
      if(ex)return c.map(i=>i===ex?{...i,qty:i.qty+qty}:i);
      return[...c,buildCartItem()];
    });
  };
  const handleBuyNow=()=>{
    if(qty>remainingForSelected){
      showToast(`库存仅剩 ${remainingForSelected} 件`);
      return;
    }
    // 立即购买 = 幂等操作:已经在购物车的话**不累加**(用当前选择的 qty 覆盖),
    // 不在购物车则加进去。这样付款失败回来再次点击不会越加越多
    setCart(c=>{
      const ex=c.find(i=>i.id===product.id&&i.variantId===(selectedVariant?.id||null));
      if(ex)return c.map(i=>i===ex?{...i,qty:qty}:i);
      return[...c,buildCartItem()];
    });
    setProductDetail(null);
    setCheckout(true);
  };

  // ── 左滑返回手势 — 卡片堆叠效果 ──
  // 设计:从屏幕最左 20px 内开始的横滑触发;手指可一直滑到右侧,跟随无上限。
  //   松手:位移 > 80px → 整页继续向右滑出屏幕(动画 220ms),露出底层 tab 页;
  //         位移 ≤ 80px → 弹回原位
  const swipeRef=useRef({x0:0,y0:0,active:false,dx:0});
  const [swipeDx,setSwipeDx]=useState(0);
  const [swipeAnim,setSwipeAnim]=useState(false);
  const onSwipeStart=(e)=>{
    const t=e.touches?.[0];
    if(!t)return;
    // 只接受从屏幕最左 20px 内开始的触摸,防止跟商品图 carousel 等横滑冲突
    if(t.clientX>20){swipeRef.current.active=false;return;}
    swipeRef.current={x0:t.clientX,y0:t.clientY,active:true,dx:0};
    setSwipeAnim(false);
  };
  const onSwipeMove=(e)=>{
    const s=swipeRef.current;
    if(!s.active)return;
    const t=e.touches?.[0];
    if(!t)return;
    const dx=t.clientX-s.x0;
    const dy=t.clientY-s.y0;
    // 主要横向位移才算
    if(dx>0&&dx>Math.abs(dy)*1.2){
      s.dx=dx;
      setSwipeDx(dx); // 不再封顶 — 用户可以一直滑到屏幕最右
    }
  };
  const onSwipeEnd=()=>{
    const s=swipeRef.current;
    swipeRef.current={x0:0,y0:0,active:false,dx:0};
    if(s.active&&s.dx>80){
      // 卡片飞出 — 动画到屏幕宽度,完成后才 onBack(那一刻底层 tab 页就完全可见)
      setSwipeAnim(true);
      setSwipeDx(typeof window!=="undefined"?window.innerWidth:500);
      setTimeout(()=>{ onBack?.(); }, 220);
    }else if(s.active){
      // 弹回
      setSwipeAnim(true);
      setSwipeDx(0);
      setTimeout(()=>setSwipeAnim(false),220);
    }
  };

  return(
    <div
      onTouchStart={onSwipeStart}
      onTouchMove={onSwipeMove}
      onTouchEnd={onSwipeEnd}
      onTouchCancel={onSwipeEnd}
      style={{
        height:"100%",position:"relative",background:"#f5f5f4",
        transform:swipeDx>0?`translateX(${swipeDx}px)`:"none",
        transition:swipeAnim?"transform 0.22s cubic-bezier(0.32,0.72,0.4,1)":"none",
        willChange:"transform",
        // 卡片右边沿的阴影 — 滑动时让用户感觉自己在拿一张卡片,堆叠在底层页面之上
        boxShadow:swipeDx>0?"-6px 0 18px rgba(0,0,0,0.18)":"none",
      }}
    >
      {/* 左边沿返回手势的视觉提示(滑动 > 20px 时显示一个箭头) */}
      {swipeDx>20&&swipeDx<window.innerWidth*0.5&&(
        <div style={{position:"absolute",left:8,top:"50%",transform:"translateY(-50%)",zIndex:60,
          width:36,height:36,borderRadius:"50%",
          background:"rgba(0,0,0,0.45)",
          display:"flex",alignItems:"center",justifyContent:"center",
          opacity:Math.min(1,(swipeDx-20)/60),pointerEvents:"none",
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </div>
      )}
      {/* Sticky header — 透明 → 白色;App container 已处理 safe-area-top,这里只用小留白 */}
      <div style={{position:"absolute",top:0,left:0,right:0,zIndex:50,paddingTop:10,paddingLeft:14,paddingRight:14,paddingBottom:10,background:`rgba(255,255,255,${headerOp})`,backdropFilter:headerOp>0.05?"blur(8px)":"none",WebkitBackdropFilter:headerOp>0.05?"blur(8px)":"none",borderBottom:headerOp>0.6?"1px solid #eee":"none",transition:"border-bottom 0.2s"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <button onClick={onBack} style={{width:32,height:32,borderRadius:"50%",background:headerOp>0.5?"none":"rgba(255,255,255,0.85)",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0,boxShadow:headerOp>0.5?"none":"0 1px 3px rgba(0,0,0,0.1)"}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          {showName&&<span style={{fontSize:14,fontFamily:FF,color:"#1a1a1a",fontWeight:600,opacity:headerOp,maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{product.name}</span>}
          <div style={{display:"flex",gap:8}}>
            <button onClick={async()=>{
              const url=`https://us198.com/?p=${product.id}`;
              const text=`${product.name} — $${product.price}`;
              try{
                if(navigator.share){
                  await navigator.share({title:product.name,text,url});
                }else{
                  await navigator.clipboard?.writeText(`${text}\n${url}`);
                  showToast("链接已复制");
                }
              }catch{}
            }} style={{width:32,height:32,borderRadius:"50%",background:headerOp>0.5?"none":"rgba(255,255,255,0.85)",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0,boxShadow:headerOp>0.5?"none":"0 1px 3px rgba(0,0,0,0.1)"}}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
            </button>
            <button
              ref={cartIconRef}
              onClick={()=>{onBack();setTab("cart");}}
              style={{
                width:32,height:32,borderRadius:"50%",
                background:headerOp>0.5?"none":"rgba(255,255,255,0.85)",
                border:"none",cursor:"pointer",
                display:"flex",alignItems:"center",justifyContent:"center",
                padding:0,boxShadow:headerOp>0.5?"none":"0 1px 3px rgba(0,0,0,0.1)",
                position:"relative",
                transition:"transform 0.2s",
                transform:cartPulse?"scale(1.25)":"scale(1)",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="20" r="1.6"/><circle cx="18" cy="20" r="1.6"/><path d="M2 3h2.5l2.7 12.2a1.6 1.6 0 001.6 1.3h9.1a1.6 1.6 0 001.55-1.2L21 7H6"/></svg>
              {cartCount>0&&(
                <span style={{
                  position:"absolute",top:-3,right:-3,
                  background:"#e03030",color:"#fff",
                  borderRadius:"50%",minWidth:17,height:17,padding:"0 4px",
                  fontSize:9.5,fontFamily:FF,fontWeight:700,
                  display:"flex",alignItems:"center",justifyContent:"center",
                  border:"1.5px solid #fff",
                }}>{cartCount>99?"99+":cartCount}</span>
              )}
            </button>
          </div>
        </div>
      </div>

      <div ref={scrollRef} style={{overflowY:"auto",height:"100%",paddingBottom:"calc(130px + env(safe-area-inset-bottom))"}}>
        {/* 图片轮播 — variant 切换时通过 key 强制重置到第一张 */}
        <ProductImageCarousel key={selectedVariant?.id||"main"} images={images} name={product.name} onTap={i=>setLightboxIdx(i)}/>

        {/* 价格区 */}
        <div style={{background:"#fff",padding:"14px 16px 16px"}}>
          <div style={{display:"flex",alignItems:"baseline",gap:8,flexWrap:"wrap"}}>
            <span style={{fontSize:26,fontWeight:700,color:"#e03030",fontFamily:FF,letterSpacing:"-0.02em"}}>${displayPrice.toFixed(2)}</span>
            {displayOrigPrice&&<span style={{fontSize:14,color:"#bbb",textDecoration:"line-through",fontFamily:FF}}>${displayOrigPrice}</span>}
            {discount>0&&<span style={{background:"#ffe5e5",color:"#e03030",fontSize:11,fontWeight:600,padding:"2px 7px",borderRadius:4,fontFamily:FF}}>{discount}% off</span>}
            {displayOrigPrice&&<span style={{fontSize:11,color:"#888",fontFamily:FF}}>立省 ${savings}</span>}
          </div>
          <div style={{marginTop:10,fontSize:15,color:"#1a1a1a",fontFamily:FF,fontWeight:500,lineHeight:1.4}}>{product.name}</div>
          <div style={{marginTop:8,display:"flex",alignItems:"center",gap:10,fontSize:11,color:"#888",fontFamily:FF}}>
            {product.tag&&<span style={{background:"#1a1a1a",color:"#fff",fontSize:10,letterSpacing:"0.08em",padding:"2px 7px",borderRadius:3}}>{product.tag}</span>}
            {product.sold>10&&<span>周销量 {product.sold>=1000?`${(product.sold/1000).toFixed(1)}k+`:`${product.sold}+`}</span>}
          </div>
        </div>

        {/* 规格选择 + 数量 */}
        {(colors.length>0||showSizes||true)&&(
        <div style={{background:"#fff",marginTop:8,padding:"14px 16px"}}>
          {colors.length>0&&(
            <>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10}}>
                <span style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600}}>颜色</span>
                <span style={{fontSize:11,color:"#888",fontFamily:FF}}>{selColor||"请选择"}</span>
              </div>
              <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:showSizes?14:14}}>
                {colors.map(c=>{
                  const sel=selColor===c.name;
                  const oos=!colorHasStock(c.name);
                  return(
                    <button key={c.name} onClick={()=>!oos&&setSelColor(c.name)} disabled={oos} title={oos?`${c.name}(已售罄)`:c.name} style={{width:32,height:32,borderRadius:"50%",background:c.hex||"#ddd",border:sel?"2px solid #e03030":"2px solid #fff",boxShadow:sel?"0 0 0 1.5px #e03030":"0 0 0 1px #e8e8e8",cursor:oos?"not-allowed":"pointer",padding:0,transition:"all 0.15s",opacity:oos?0.32:1,position:"relative"}}>
                      {oos&&<span style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",pointerEvents:"none"}}><span style={{display:"block",width:34,height:1.5,background:"#999",transform:"rotate(-45deg)"}}/></span>}
                    </button>
                  );
                })}
              </div>
            </>
          )}
          {showSizes&&(
            <>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10}}>
                <span style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600}}>尺码</span>
                <span style={{fontSize:11,color:"#888",fontFamily:FF}}>{selSize||"请选择"}</span>
              </div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:14}}>
                {sizesAll.map(s=>{
                  const sel=selSize===s;
                  const oos=!sizeHasStockForSelColor(s);
                  return(
                    <button key={s} onClick={()=>!oos&&setSelSize(s)} disabled={oos} style={{padding:"7px 14px",background:sel?"#fff5f5":oos?"#fafafa":"#f5f5f4",border:`1.5px solid ${sel?"#e03030":"transparent"}`,color:sel?"#e03030":oos?"#bbb":"#555",borderRadius:18,fontSize:12,fontFamily:FF,fontWeight:sel?600:400,cursor:oos?"not-allowed":"pointer",textDecoration:oos?"line-through":"none",transition:"all 0.15s"}}>{s}</button>
                  );
                })}
              </div>
            </>
          )}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={{display:"flex",alignItems:"baseline",gap:8}}>
              <span style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600}}>数量</span>
              {Number.isFinite(remainingForSelected)&&remainingForSelected<10&&(
                <span style={{fontSize:11,color:remainingForSelected<=3?"#e03030":"#888",fontFamily:FF}}>
                  {remainingForSelected===0?"已达购买上限":`仅剩 ${remainingForSelected} 件可加`}
                </span>
              )}
            </div>
            <div style={{display:"flex",alignItems:"center",gap:0,border:"1.5px solid #ececec",borderRadius:8,overflow:"hidden"}}>
              <button onClick={()=>setQty(q=>Math.max(1,q-1))} disabled={qty<=1} style={{width:32,height:30,background:"#fff",border:"none",cursor:qty<=1?"not-allowed":"pointer",fontSize:16,color:qty<=1?"#ccc":"#666",fontFamily:FF}}>−</button>
              <span style={{minWidth:36,textAlign:"center",fontSize:13,fontFamily:FF,color:"#1a1a1a",fontWeight:500}}>{qty}</span>
              <button
                onClick={()=>{
                  if(canIncreaseQty)setQty(q=>q+1);
                  else showToast(`库存仅剩 ${remainingForSelected} 件`);
                }}
                disabled={!canIncreaseQty}
                style={{width:32,height:30,background:"#fff",border:"none",cursor:canIncreaseQty?"pointer":"not-allowed",fontSize:16,color:canIncreaseQty?"#666":"#ccc",fontFamily:FF}}
              >+</button>
            </div>
          </div>
        </div>
        )}

        {/* 配送 */}
        <div style={{background:"#fff",marginTop:8,padding:"14px 16px"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <span style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600}}>配送</span>
            {editingZip?(
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <input
                  autoFocus
                  value={zipcode}
                  onChange={e=>setZipcode(e.target.value.replace(/\D/g,"").slice(0,5))}
                  onBlur={saveZipEdit}
                  onKeyDown={e=>{if(e.key==="Enter")saveZipEdit();}}
                  inputMode="numeric"
                  placeholder="邮编"
                  style={{width:78,fontSize:11.5,fontFamily:FF,padding:"4px 8px",border:"1.5px solid #1a1a1a",borderRadius:6,outline:"none",color:"#1a1a1a",background:"#fff"}}
                />
                <button onMouseDown={e=>e.preventDefault()} onClick={saveZipEdit} style={{fontSize:11,color:"#1a1a1a",fontFamily:FF,background:"none",border:"none",cursor:"pointer",padding:"4px 2px"}}>确定</button>
              </div>
            ):(
              <button onClick={()=>setEditingZip(true)} style={{fontSize:11,color:zipcode?"#888":"#e03030",fontFamily:FF,background:"none",border:"none",cursor:"pointer",padding:0,display:"flex",alignItems:"center",gap:3}}>
                {zipcode||"输入邮编"}
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              </button>
            )}
          </div>
          <div style={{display:"flex",alignItems:"flex-start",gap:8,fontSize:12,color:"#444",fontFamily:FF,lineHeight:1.6}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3a9e3a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginTop:2,flexShrink:0}}><path d="M1 3h15v13H1z"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
            <div style={{flex:1,minWidth:0}}>
              {deliveryLoading?(
                <div style={{color:"#888"}}>查询配送时间…</div>
              ):deliveryErr?(
                <div style={{color:"#e03030",fontSize:11.5}}>⚠️ {deliveryErr}</div>
              ):delivery?(
                <>
                  <div>
                    预计 <span style={{color:"#1a1a1a",fontWeight:500}}>{formatDeliveryLabel(delivery.delivery_date)}</span>
                    {" 送达"}
                    {delivery.city&&<span style={{color:"#888"}}> · {delivery.city}{delivery.state?", "+delivery.state:""}</span>}
                  </div>
                  <div style={{fontSize:11,color:"#888",marginTop:2}}>如需更快送达,请联系客服</div>
                  <div style={{fontSize:11,color:"#888",marginTop:2}}>支持自提:{PICKUP_ADDRESS}</div>
                </>
              ):(
                <div style={{color:"#888"}}>请输入邮编查询送达时间</div>
              )}
            </div>
          </div>
        </div>

        {/* 服务保障 */}
        <div style={{background:"#fff",marginTop:8,padding:"14px 16px"}}>
          <div style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600,marginBottom:12}}>服务</div>
          <div style={{display:"flex",justifyContent:"space-between",gap:8}}>
            {[
              {ico:"✓",label:"正品保障"},
              {ico:"↺",label:"7 天无理由"},
              {ico:"⚡",label:"闪电发货"},
              {ico:"♡",label:"贴心客服"},
            ].map((s,i)=>(
              <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                <span style={{width:28,height:28,borderRadius:"50%",background:"#f5f5f4",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,color:"#3a9e3a"}}>{s.ico}</span>
                <span style={{fontSize:11,color:"#666",fontFamily:FF}}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Tab 区 */}
        <div style={{background:"#fff",marginTop:8}}>
          <div style={{display:"flex",borderBottom:"1px solid #f0f0f0"}}>
            {[
              {id:"detail",label:"商品详情"},
              {id:"specs",label:"规格参数"},
            ].map(t=>{
              const a=activeTab===t.id;
              return(
                <button key={t.id} onClick={()=>setActiveTab(t.id)} style={{flex:1,padding:"14px 0",background:"none",border:"none",borderBottom:`2px solid ${a?"#1a1a1a":"transparent"}`,cursor:"pointer",fontSize:13,fontFamily:FF,color:a?"#1a1a1a":"#888",fontWeight:a?600:400,marginBottom:-1}}>{t.label}</button>
              );
            })}
          </div>
          <div style={{padding:"16px"}}>
            {activeTab==="detail"&&(
              <div>
                {product.images&&product.images.length>0?(
                  product.images.map((url,i)=>(
                    <img key={i} src={url} alt={`${product.name||"商品"} 详情图 ${i+1}`} loading="lazy" decoding="async" style={{width:"100%",display:"block",borderRadius:10,marginBottom:10}}/>
                  ))
                ):(
                  <div style={{textAlign:"center",padding:"40px 0",color:"#bbb",fontSize:13,fontFamily:FF}}>暂无详情图</div>
                )}
              </div>
            )}
            {activeTab==="specs"&&(
              <div>
                {specs.map((s,i)=>(
                  <div key={i} style={{display:"flex",padding:"10px 0",borderBottom:i<specs.length-1?"1px solid #f5f5f4":"none"}}>
                    <span style={{flex:"0 0 90px",fontSize:12.5,color:"#888",fontFamily:FF}}>{s.k}</span>
                    <span style={{flex:1,fontSize:12.5,color:"#1a1a1a",fontFamily:FF}}>{s.v}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 猜你喜欢 — 个性化推荐(基于浏览/购买/收藏) */}
        {(personalRecosLoading||personalRecos.length>0)&&(
          <div style={{background:"#fff",marginTop:8,padding:"14px 0 4px"}}>
            <div style={{padding:"0 16px 12px",display:"flex",alignItems:"center",gap:6}}>
              <span style={{fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600}}>猜你喜欢</span>
              <span style={{fontSize:10,color:"#aaa",fontFamily:FF}}>· 为你精选</span>
            </div>
            {personalRecosLoading?(
              <div style={{padding:"20px 16px",fontSize:12,color:"#bbb",fontFamily:FF,textAlign:"center"}}>加载中…</div>
            ):(
              <div style={{display:"flex",gap:8,overflowX:"auto",padding:"0 14px 12px",scrollbarWidth:"none"}}>
                {personalRecos.map(r=>{
                  const rd=r.origPrice&&r.origPrice>r.price?Math.round((1-r.price/r.origPrice)*100):0;
                  return(
                    <div key={r.id} onClick={()=>{setProductDetail(r);scrollRef.current?.scrollTo({top:0});}} style={{flex:"0 0 110px",cursor:"pointer"}}>
                      <div style={{borderRadius:8,overflow:"hidden",position:"relative"}}>
                        <ProductImage img={r.img} imgUrl={r.imgUrl} name={r.name} aspect={1}/>
                        {rd>0&&<span style={{position:"absolute",top:5,left:5,background:"#e03030",color:"#fff",fontSize:9,fontWeight:600,padding:"1px 5px",borderRadius:3,fontFamily:FF}}>{rd}% off</span>}
                      </div>
                      <div style={{fontSize:11,color:"#222",fontFamily:FF,marginTop:5,lineHeight:1.3,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden",height:30}}>{r.name}</div>
                      <div style={{fontSize:13,fontWeight:700,color:"#e03030",fontFamily:FF,marginTop:3}}>${r.price}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* 最近看过 — 底部最后一节,加 marginBottom 给底部 action bar 留缓冲 */}
        {historyProducts.length>0&&(
          <div style={{background:"#fff",marginTop:8,padding:"14px 0 4px",marginBottom:60}}>
            <div style={{padding:"0 16px 12px",fontSize:13,color:"#1a1a1a",fontFamily:FF,fontWeight:600}}>最近看过</div>
            <div style={{display:"flex",gap:8,overflowX:"auto",padding:"0 14px 12px",scrollbarWidth:"none"}}>
              {historyProducts.map(r=>{
                const rd=r.origPrice&&r.origPrice>r.price?Math.round((1-r.price/r.origPrice)*100):0;
                return(
                  <div key={r.id} onClick={()=>{setProductDetail(r);scrollRef.current?.scrollTo({top:0});}} style={{flex:"0 0 110px",cursor:"pointer"}}>
                    <div style={{borderRadius:8,overflow:"hidden",position:"relative"}}>
                      <ProductImage img={r.img} imgUrl={r.imgUrl} name={r.name} aspect={1}/>
                      {rd>0&&<span style={{position:"absolute",top:5,left:5,background:"#e03030",color:"#fff",fontSize:9,fontWeight:600,padding:"1px 5px",borderRadius:3,fontFamily:FF}}>{rd}% off</span>}
                    </div>
                    <div style={{fontSize:11,color:"#222",fontFamily:FF,marginTop:5,lineHeight:1.3,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden",height:30}}>{r.name}</div>
                    <div style={{fontSize:13,fontWeight:700,color:"#e03030",fontFamily:FF,marginTop:3}}>${r.price}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* 底部操作栏 — 适配 iPhone home indicator(safe-area-inset-bottom) */}
      <div style={{position:"absolute",bottom:0,left:0,right:0,background:"#fff",borderTop:"1px solid #f0f0f0",padding:"10px 12px",paddingBottom:"calc(10px + env(safe-area-inset-bottom))",display:"flex",alignItems:"center",gap:10,zIndex:40}}>
        <button onClick={()=>{
          // 直接跳到在线客服聊天页 + 把当前商品挂到 ChatPanel(像淘宝/拼多多那样自动咨询当前商品)
          setProductDetail(null);
          setChatPrefillProduct?.({
            id: product.id,
            name: product.name,
            price: product.price,
            imgUrl: (product.images&&product.images[0])||product.imgUrl||null,
            img: product.img||null,
          });
          setProfileInitialPanel?.("chat");
          setTab("profile");
        }} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2,background:"none",border:"none",cursor:"pointer",padding:"4px 4px"}}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/></svg>
          <span style={{fontSize:9.5,color:"#666",fontFamily:FF}}>客服</span>
        </button>
        <button onClick={handleWish} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2,background:"none",border:"none",cursor:"pointer",padding:"4px 4px"}}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill={inWish?"#e03030":"none"} stroke={inWish?"#e03030":"#666"} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
          <span style={{fontSize:9.5,color:inWish?"#e03030":"#666",fontFamily:FF}}>{inWish?"已收藏":"收藏"}</span>
        </button>
        {(()=>{
          // 售罄(库存为 0)→ 已售罄;
          // 已达购买上限(已加到上限,但库存 > 0)→ 已加满
          // 否则正常
          const fullStockOut = (() => {
            if (realVariants.length>0) return selectedVariant && typeof selectedVariant.stock==="number" && selectedVariant.stock<=0;
            return typeof product.totalStock==="number" && product.totalStock<=0;
          })();
          const reached = isOutOfStock && !fullStockOut;
          const lockText = fullStockOut ? "已售罄" : reached ? "已达购买上限" : null;
          const locked = !!lockText;
          return (
            <>
              <button ref={addBtnRef} onClick={handleAddCart} disabled={locked} style={{flex:1,background:locked?"#ccc":"#ffb84d",color:"#fff",border:"none",borderRadius:24,padding:"11px 0",fontSize:14,fontWeight:600,cursor:locked?"not-allowed":"pointer",fontFamily:FF}}>{lockText||"加入购物车"}</button>
              <button onClick={handleBuyNow} disabled={locked} style={{flex:1,background:locked?"#ccc":"#e03030",color:"#fff",border:"none",borderRadius:24,padding:"11px 0",fontSize:14,fontWeight:600,cursor:locked?"not-allowed":"pointer",fontFamily:FF}}>{lockText||"立即购买"}</button>
            </>
          );
        })()}
      </div>

      {/* Toast */}
      {toast&&<div style={{position:"absolute",bottom:90,left:"50%",transform:"translateX(-50%)",background:"rgba(0,0,0,0.78)",color:"#fff",padding:"9px 18px",borderRadius:20,fontSize:12.5,fontFamily:FF,zIndex:60,whiteSpace:"nowrap"}}>{toast}</div>}

      {/* 飞入购物车小球 — 双层嵌套实现抛物线
          外层管 X(ease-in:慢起加速)+ 内层管 Y(ease-out:快起减速)
          两个不同 easing 同时进行 → 天然弧线 */}
      {fly&&(
        <div
          key={fly.key}
          style={{
            position:"fixed",
            left:fly.sx,top:fly.sy,
            width:30,height:30,marginLeft:-15,marginTop:-15,
            zIndex:10000,pointerEvents:"none",
            "--dx":`${fly.ex-fly.sx}px`,
            animation:"flyArcX 0.72s cubic-bezier(0.5, 0, 0.85, 0.4) forwards",
          }}
        >
          <div style={{
            width:"100%",height:"100%",
            "--dy":`${fly.ey-fly.sy}px`,
            animation:"flyArcY 0.72s cubic-bezier(0.18, 0.65, 0.4, 1) forwards",
          }}>
            <div style={{
              width:"100%",height:"100%",
              borderRadius:"50%",
              background:"linear-gradient(135deg,#ffb84d,#e03030)",
              boxShadow:"0 4px 14px rgba(224,48,48,0.45)",
              display:"flex",alignItems:"center",justifyContent:"center",
              color:"#fff",fontSize:18,fontWeight:700,fontFamily:FF,
              animation:"flyShrink 0.72s ease-in forwards",
            }}>+</div>
          </div>
        </div>
      )}

      {/* Lightbox 全屏图片预览 */}
      {lightboxIdx!==null&&<ImageLightbox images={images} initialIdx={lightboxIdx} name={product.name} onClose={()=>setLightboxIdx(null)}/>}

      <style>{`
        @keyframes flyArcX { to { transform: translateX(var(--dx)); } }
        @keyframes flyArcY { to { transform: translateY(var(--dy)); } }
        @keyframes flyShrink {
          0% { transform: scale(1); opacity: 1; }
          60% { transform: scale(0.8); opacity: 1; }
          100% { transform: scale(0.2); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 扫一扫识别同款 — 拍照/上传 → Claude Vision 提取 tag → 匹配商品
// ─────────────────────────────────────────────────────────────────────────────
function ImageSearchPanel({initialFile,onClose,onOpenProduct,onPickAgain}){
  const fileInputRef=useRef(null);
  // 初始 stage:有 initialFile 时直接 uploading,无则 pick(后备 — 现在不会走到)
  const [stage,setStage]=useState(initialFile?"uploading":"pick");
  const [previewUrl,setPreviewUrl]=useState(null);
  const [tags,setTags]=useState([]);
  const [products,setProducts]=useState([]);
  const [isFallback,setIsFallback]=useState(false);
  const [errMsg,setErrMsg]=useState("");

  // 选图(拍照 or 相册)→ 压缩 → base64 → 调后端
  const handleFile=async(file)=>{
    if(!file)return;
    setStage("uploading");setErrMsg("");
    try{
      const dataUrl=await new Promise((resolve,reject)=>{
        const reader=new FileReader();
        reader.onload=e=>{
          const img=new Image();
          img.onload=()=>{
            // 压缩到最大 1024px,JPEG 0.85 质量(控制在 ~300KB)
            const max=1024;
            let w=img.width,h=img.height;
            if(w>max||h>max){
              if(w>h){h=Math.round(h*max/w);w=max;}
              else{w=Math.round(w*max/h);h=max;}
            }
            const canvas=document.createElement("canvas");
            canvas.width=w;canvas.height=h;
            canvas.getContext("2d").drawImage(img,0,0,w,h);
            resolve(canvas.toDataURL("image/jpeg",0.85));
          };
          img.onerror=reject;
          img.src=e.target.result;
        };
        reader.onerror=reject;
        reader.readAsDataURL(file);
      });
      setPreviewUrl(dataUrl);
      const r=await imageSearch(dataUrl);
      setTags(r.tags||[]);
      setProducts(r.products||[]);
      setIsFallback(!!r.fallback);
      setStage("result");
    }catch(e){
      setErrMsg(e.message||"识别失败,请稍后重试");
      setStage("error");
    }
  };

  // 收到 initialFile(从 App 的隐藏 input 传过来)→ 立刻识别,跳过黑色 pick 页
  useEffect(()=>{
    if(initialFile)handleFile(initialFile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[initialFile]);

  // 重选 — 关闭面板,通知 App 再次打开系统相机/相册
  const reset=()=>{
    if(onPickAgain){onPickAgain();return;}
    setStage("pick");setPreviewUrl(null);setTags([]);setProducts([]);setErrMsg("");
    if(fileInputRef.current)fileInputRef.current.value="";
  };

  return(
    <div style={{position:"fixed",inset:0,background:"#000",zIndex:300,display:"flex",flexDirection:"column"}}>
      {/* 头部 — paddingTop 包含 safe-area-inset-top,避免跟 iOS 时间/电量重叠 */}
      <div style={{
        flexShrink:0,
        paddingTop:"calc(env(safe-area-inset-top) + 12px)",
        paddingLeft:16,paddingRight:16,paddingBottom:12,
        background:"linear-gradient(180deg,rgba(0,0,0,0.7),rgba(0,0,0,0))",
        display:"flex",alignItems:"center",gap:12,zIndex:10,
      }}>
        <button onClick={onClose} style={{
          background:"rgba(255,255,255,0.18)",border:"none",borderRadius:"50%",
          width:36,height:36,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
        <span style={{fontSize:16,fontFamily:SERIF,fontWeight:600,color:"#fff",flex:1}}>扫一扫识别同款</span>
      </div>

      {/* 内容区 */}
      <div style={{flex:1,overflowY:"auto",background:stage==="result"?"#f6f7f8":"#000"}}>
        {stage==="pick"&&(
          <div style={{padding:"40px 24px",color:"#fff",textAlign:"center"}}>
            <div style={{margin:"40px auto 24px",width:120,height:120,borderRadius:32,background:"rgba(255,255,255,0.1)",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>
                <circle cx="12" cy="13" r="4"/>
              </svg>
            </div>
            <div style={{fontSize:18,fontFamily:SERIF,fontWeight:600,marginBottom:8}}>找同款 / 找类似</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.7)",fontFamily:FF,marginBottom:36,lineHeight:1.6}}>
              拍一张商品图,或从相册选,<br/>AI 识别后自动找出店里相似的商品
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" capture="environment"
              onChange={e=>handleFile(e.target.files?.[0])}
              style={{display:"none"}}/>
            <button onClick={()=>fileInputRef.current?.click()} style={{
              width:"100%",maxWidth:300,
              padding:"14px 0",background:"#fff",color:"#1a1a1a",
              border:"none",borderRadius:14,fontSize:15,fontFamily:FF,fontWeight:600,
              cursor:"pointer",marginBottom:12,
            }}>📷 拍照 / 选图</button>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",fontFamily:FF,marginTop:24,padding:"0 20px",lineHeight:1.6}}>
              提示:图片越清晰,商品主体越明显,识别越准。<br/>识别结果按相似度从高到低排序。
            </div>
          </div>
        )}

        {stage==="uploading"&&(
          <div style={{padding:"60px 24px",color:"#fff",textAlign:"center"}}>
            {previewUrl&&<img src={previewUrl} alt="" style={{maxWidth:200,maxHeight:200,borderRadius:14,marginBottom:24,objectFit:"cover"}}/>}
            <div style={{fontSize:14,fontFamily:FF,marginBottom:8}}>AI 识别中…</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.6)",fontFamily:FF}}>正在分析图片特征,匹配店内商品</div>
            <div style={{margin:"24px auto",display:"flex",gap:6,justifyContent:"center"}}>
              {[0,1,2].map(i=><div key={i} style={{width:8,height:8,borderRadius:"50%",background:"#fff",opacity:0.4,animation:`pulse 1.2s ease-in-out ${i*0.2}s infinite`}}/>)}
            </div>
          </div>
        )}

        {stage==="error"&&(
          <div style={{padding:"60px 24px",color:"#fff",textAlign:"center"}}>
            <div style={{fontSize:42,marginBottom:14}}>😢</div>
            <div style={{fontSize:14,fontFamily:FF,marginBottom:18}}>{errMsg}</div>
            <button onClick={reset} style={{padding:"10px 28px",background:"#fff",color:"#1a1a1a",border:"none",borderRadius:10,fontSize:13,fontFamily:FF,fontWeight:600,cursor:"pointer"}}>重新选图</button>
          </div>
        )}

        {stage==="result"&&(
          <div>
            {/* 顶部识别结果总览 */}
            <div style={{padding:"14px 14px 12px",background:"#fff",display:"flex",gap:12,alignItems:"center"}}>
              {previewUrl&&<img src={previewUrl} alt="" style={{width:70,height:70,borderRadius:10,objectFit:"cover",flexShrink:0}}/>}
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,color:"#888",fontFamily:FF,marginBottom:4}}>识别关键词</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                  {tags.slice(0,8).map((t,i)=>(
                    <span key={i} style={{padding:"2px 8px",fontSize:10.5,background:"#f5f5f4",color:"#444",borderRadius:8,fontFamily:FF}}>{t}</span>
                  ))}
                </div>
              </div>
              <button onClick={reset} style={{flexShrink:0,padding:"6px 12px",background:"#1a1a1a",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontFamily:FF,cursor:"pointer"}}>重选</button>
            </div>

            {/* 商品结果 */}
            <div style={{padding:"12px 14px"}}>
              <div style={{fontSize:13,color:"#666",fontFamily:FF,marginBottom:10}}>
                {products.length>0
                  ? (isFallback
                      ? `店里没有完全相同的款式,这是为您挑的相近 ${products.length} 件`
                      : `找到 ${products.length} 件相似商品`)
                  : "暂无相似商品,试试换张图"}
              </div>
              {products.length>0&&(
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                  {products.map(p=>{
                    const dis=p.origPrice&&p.origPrice>p.price?Math.round((1-p.price/p.origPrice)*100):0;
                    return(
                      <div key={p.id} onClick={()=>onOpenProduct(p)} style={{background:"#fff",borderRadius:12,overflow:"hidden",cursor:"pointer",boxShadow:"0 1px 4px rgba(0,0,0,0.04)"}}>
                        <div style={{position:"relative",background:"#f5f5f4",aspectRatio:"1"}}>
                          {p.imgUrl&&<img src={p.imgUrl} alt={p.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>}
                          {dis>0&&<span style={{position:"absolute",top:6,left:6,background:"#e03030",color:"#fff",fontSize:10,fontWeight:600,padding:"2px 6px",borderRadius:4,fontFamily:FF}}>{dis}% off</span>}
                        </div>
                        <div style={{padding:"8px 10px"}}>
                          <div style={{fontSize:12,color:"#1a1a1a",fontFamily:FF,lineHeight:1.4,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden",minHeight:33,marginBottom:4}}>{p.name}</div>
                          <div style={{fontSize:14,fontWeight:700,color:"#e03030",fontFamily:FF}}>${p.price?.toFixed(2)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// APP ROOT
// ─────────────────────────────────────────────────────────────────────────────
export default function App(){
  // /config 拉到新值时(运费 / 客服电话 / 取货地址等更新)→ 强制 re-render,
  // 让 import 的 PICKUP_ADDRESS / FREE_SHIP_THRESHOLD 等 live binding 显示最新值
  const [, forceConfigRerender] = useReducer(x => x + 1, 0);
  useEffect(() => onConfigReady(() => forceConfigRerender()), []);

  const [tab,setTab]=useState("home");
  const [cart,setCart]=usePersistedState("syj_cart",[]);
  const [wishlist,setWishlist]=usePersistedState("syj_wishlist",[]);
  const [checkout,setCheckout]=useState(false);
  // 启动时根据 token 是否存在初始化 loggedIn,这样刷新页面后能立即拉云端数据
  const [loggedIn,setLoggedIn]=useState(()=>{
    try{return !!localStorage.getItem("syj_token");}catch{return false;}
  });
  // 商品详情栈:从 A 点进 B,返回应回到 A 而不是首页。
  // 维护一个数组,栈顶是当前显示的商品。
  // setProductDetail 行为:
  //   - 传 null → 清空栈(用于 tab 切换 / 跳客服 等"完全离开"场景)
  //   - 传新商品 → push
  // popProductDetail() → pop 一个;空了等于完全退出详情
  const [productStack,setProductStack]=useState([]);
  const productDetail=productStack.length>0?productStack[productStack.length-1]:null;
  const setProductDetail=useCallback((p)=>{
    if(p===null||p===undefined){
      setProductStack([]);
    }else if(typeof p==="object"){
      setProductStack(s=>{
        // 同一件商品重复点不入栈(避免栈无限增长)
        const top=s[s.length-1];
        if(top&&top.id===p.id)return s;
        return [...s,p];
      });
    }
  },[]);
  const popProductDetail=useCallback(()=>{
    setProductStack(s=>s.slice(0,-1));
  },[]);
  // 跨 tab 跳转钩子:商品详情点"客服"→ tab=profile + 直接打开 chat panel
  const [profileInitialPanel,setProfileInitialPanel]=useState(null);
  // 商品咨询 — 商品详情点"客服"时把当前商品挂上,ChatPanel 渲染商品卡 + 发送时带上
  const [chatPrefillProduct,setChatPrefillProduct]=useState(null);
  // 扫一扫:用户先选图(拍照/相册)→ 选中后自动开识别面板,跳过黑色 pick 页
  const [scanFile,setScanFile]=useState(null);
  const scanInputRef=useRef(null);
  const triggerScan=()=>{
    // 重置 value 让用户连续选同一张图也能触发 onChange
    if(scanInputRef.current){
      scanInputRef.current.value="";
      scanInputRef.current.click();
    }
  };
  const [products,setProducts]=useState([]);
  const [categories,setCategories]=useState([]);
  const [dataLoading,setDataLoading]=useState(true);
  const [dataError,setDataError]=useState(null);
  const cartCount=cart.reduce((a,i)=>a+i.qty,0);

  // ── App Tracking Transparency (iOS 14.5+) ─────────────────────────────
  // 接入点:Capacitor 打包成 native 后,在原生层面会调
  // ATTrackingManager.requestTrackingAuthorization() 弹系统对话框。
  // 前端这里只负责:启动后延迟 1.5s 调一次 window.__syjATT?.()
  // 让 Capacitor 插件接管。Web 端 window.__syjATT 不存在,自然不弹任何东西。
  // 拒绝/通过的结果存到 localStorage.syj_att_status,后续不再重复弹。
  useEffect(()=>{
    const status=(()=>{try{return localStorage.getItem("syj_att_status");}catch{return"";}})();
    if(status)return; // 已经处理过(allowed/denied/notDetermined但用户已看过)
    // 启动 1.5s 后再调,等界面显示完
    const t=setTimeout(async()=>{
      try{
        if(typeof window.__syjATT==="function"){
          const result=await window.__syjATT(); // 'authorized'|'denied'|'restricted'|'notDetermined'
          try{localStorage.setItem("syj_att_status",result||"asked");}catch{}
        }
      }catch{}
    },1500);
    return()=>clearTimeout(t);
  },[]);

  // ── 全局飞入购物车动画(各页面共用) ─────────────────────────────────
  const [globalFly,setGlobalFly]=useState(null); // {sx,sy,ex,ey,key} | null
  useEffect(()=>{
    window.__syjFlyToCart=(sourceRect)=>{
      const el=window.__syjCartIconEl;
      if(!sourceRect||!el)return;
      const end=el.getBoundingClientRect();
      setGlobalFly({
        sx:sourceRect.left+sourceRect.width/2,
        sy:sourceRect.top+sourceRect.height/2,
        ex:end.left+end.width/2,
        ey:end.top+end.height/2,
        key:Date.now(),
      });
      setTimeout(()=>setGlobalFly(null),720);
    };
    return()=>{delete window.__syjFlyToCart;};
  },[]);

  // ── 离线检测 ──────────────────────────────────────────────────────────
  // navigator.onLine 不太可靠(只看物理网卡),但配合心跳可以兜底
  const [isOnline,setIsOnline]=useState(()=>{
    try{return typeof navigator!=="undefined"?navigator.onLine!==false:true;}catch{return true;}
  });
  const [showBackOnline,setShowBackOnline]=useState(false);
  useEffect(()=>{
    const handleOnline=()=>{
      setIsOnline(true);
      setShowBackOnline(true);
      setTimeout(()=>setShowBackOnline(false),2500);
    };
    const handleOffline=()=>setIsOnline(false);
    window.addEventListener("online",handleOnline);
    window.addEventListener("offline",handleOffline);
    return()=>{
      window.removeEventListener("online",handleOnline);
      window.removeEventListener("offline",handleOffline);
    };
  },[]);

  // 从后端加载商品 + 分类
  // ⚠ 关键:Railway 冷启动首次请求经常返回 5xx 或 timeout。
  //   策略:
  //   1) 前 3 次快速重试(1s/2s 退避),期间保留骨架屏不闪错
  //   2) 仍然失败 → 显示错误页 + 重试按钮
  //   3) 即使错误页显示了,后台**继续**自动重试(5s → 10s → 30s → 60s 上限),
  //      用户什么都不点也会自动恢复 → 不需要手动操作
  //   4) 数据成功后调 window.__syjAppReady?.() 让 native splash 淡出
  //
  //   背景重试用 ref 维护,卸载时清掉 timer 防泄漏。
  const bgRetryRef = useRef({ timerId: null, cancelled: false });

  const loadData = useCallback(async (isRetry = false) => {
    try {
      const [prods, cats] = await Promise.all([getProducts(), getCategories()]);
      if (bgRetryRef.current.cancelled) return true;
      setProducts(prods);
      setCategories(cats);
      setDataLoading(false);
      setDataError(null);
      // 成功 → 清掉背景重试 timer
      if (bgRetryRef.current.timerId) {
        clearTimeout(bgRetryRef.current.timerId);
        bgRetryRef.current.timerId = null;
      }
      try { window.__syjAppReady?.(); } catch {}
      return true;
    } catch (err) {
      if (bgRetryRef.current.cancelled) return false;
      throw err;
    }
  }, []);

  useEffect(() => {
    bgRetryRef.current.cancelled = false;
    let attempt = 0;
    const FAST_MAX = 3;

    // 快速重试阶段:错误不显示
    const fastRetry = async () => {
      try {
        await loadData();
      } catch (err) {
        attempt += 1;
        if (attempt < FAST_MAX) {
          const backoff = attempt * 1000;
          console.warn(`[Init] fast retry ${attempt}/${FAST_MAX} in ${backoff}ms`, err?.message);
          bgRetryRef.current.timerId = setTimeout(fastRetry, backoff);
        } else {
          // 进入"显示错误 + 后台慢重试"阶段
          console.error("[Init] fast retries exhausted, switch to bg retry", err?.message);
          setDataError(err.message || "加载失败");
          setDataLoading(false);
          try { window.__syjAppReady?.(); } catch {}
          startBgRetry();
        }
      }
    };

    // 慢速背景重试:5s → 10s → 30s → 60s,直到成功
    let bgAttempt = 0;
    const startBgRetry = () => {
      const intervals = [5000, 10000, 30000, 60000];
      const next = intervals[Math.min(bgAttempt, intervals.length - 1)];
      bgAttempt += 1;
      bgRetryRef.current.timerId = setTimeout(async () => {
        try {
          await loadData(true);
          // 成功:错误清掉,UI 自动从错误页切回正常
        } catch {
          startBgRetry(); // 再排下一次
        }
      }, next);
    };

    setDataLoading(true);
    setDataError(null);
    fastRetry();

    return () => {
      bgRetryRef.current.cancelled = true;
      if (bgRetryRef.current.timerId) {
        clearTimeout(bgRetryRef.current.timerId);
        bgRetryRef.current.timerId = null;
      }
    };
  }, [loadData]);

  // ?p=<productId> 深链接处理 — 客户从客服聊天链接进来自动打开商品详情
  const deepLinkHandledRef = useRef(false);
  useEffect(() => {
    if (deepLinkHandledRef.current) return;
    if (!products || products.length === 0) return;
    try {
      const url = new URL(window.location.href);
      const pid = url.searchParams.get('p');
      if (!pid) return;
      const product = products.find(p => p.id === pid);
      if (product) {
        deepLinkHandledRef.current = true;
        setProductDetail(product);
        // 清掉 URL 上的 ?p=，刷新不再触发
        url.searchParams.delete('p');
        const newSearch = url.searchParams.toString();
        const newUrl = url.pathname + (newSearch ? `?${newSearch}` : '') + url.hash;
        window.history.replaceState(null, '', newUrl);
      } else {
        // 商品 ID 没在列表里 — 可能是下架了或者分页外。标记已处理，避免重复尝试
        deepLinkHandledRef.current = true;
        url.searchParams.delete('p');
        const newSearch = url.searchParams.toString();
        window.history.replaceState(null, '', url.pathname + (newSearch ? `?${newSearch}` : '') + url.hash);
      }
    } catch {}
  }, [products, setProductDetail]);

  // 手动重试(用户点错误页按钮)
  const retryDataLoad = useCallback(() => {
    setDataError(null);
    setDataLoading(true);
    if (bgRetryRef.current.timerId) {
      clearTimeout(bgRetryRef.current.timerId);
      bgRetryRef.current.timerId = null;
    }
    let attempt = 0;
    const tryLoad = async () => {
      try {
        await loadData();
      } catch (err) {
        attempt += 1;
        if (attempt < 3) {
          setTimeout(tryLoad, attempt * 1000);
          return;
        }
        setDataError(err.message || "加载失败");
        setDataLoading(false);
      }
    };
    tryLoad();
  }, [loadData]);

  // ── 云端同步:购物车 / 收藏 ──────────────────────────────────────────
  // 登录后:
  //  - 第一次拉云端,与本地合并(本地优先,因为可能是刚加的;但若本地为空,直接采用云端)
  //  - 之后任何本地改动自动 debounce 推送到云端
  // 退出登录:停止同步,本地 localStorage 保留为离线状态
  const cloudSyncRef = useRef({ pulledCart:false, pulledWish:false });
  // 跟踪上一次的登录状态,区分"首次启动游客 cold-start" vs "刚刚 logout"
  const prevLoggedInRef = useRef(loggedIn);
  // 登录态切换 → 拉云端做一次合并
  useEffect(()=>{
    const wasLoggedIn = prevLoggedInRef.current;
    prevLoggedInRef.current = loggedIn;
    if(!loggedIn){
      cloudSyncRef.current.pulledCart=false;
      cloudSyncRef.current.pulledWish=false;
      // 关键修复:从"已登录"切到"未登录"(即真正 logout)时清空本地 cart / wishlist
      //   不然下次登录时本地会跟云端重复合并 → 同款商品 qty 翻倍
      //   购物车在云端保留,下次登录从云端恢复,不会丢
      // 跳过初次 mount 的游客 cold-start(那时不应该清除游客积累的 cart)
      if(wasLoggedIn){
        setCart([]);
        setWishlist([]);
      }
      return;
    }
    let alive=true;
    (async()=>{
      try{
        const [cloudCart,cloudWish]=await Promise.all([
          getCloudCart().catch(()=>[]),
          getCloudWishlist().catch(()=>[]),
        ]);
        if(!alive)return;
        // 购物车合并:按 (productId+variantKey) 累加 qty。本地优先于云端(因刚加的更可信),
        // 如本地为空,直接用云端。
        setCart(local=>{
          if(!Array.isArray(cloudCart)||cloudCart.length===0)return local;
          if(!local||local.length===0)return cloudCart;
          const keyOf=(it)=>`${it.id||it.productId}__${it.variantKey||it.variant||""}`;
          const map=new Map();
          for(const it of cloudCart) map.set(keyOf(it),{...it});
          for(const it of local){
            const k=keyOf(it);
            if(map.has(k)) map.set(k,{...map.get(k),...it,qty:(map.get(k).qty||0)+(it.qty||0)});
            else map.set(k,{...it});
          }
          return [...map.values()];
        });
        // 收藏夹:取并集
        setWishlist(local=>{
          const cloudArr=Array.isArray(cloudWish)?cloudWish:[];
          if(cloudArr.length===0)return local;
          if(!local||local.length===0)return cloudArr;
          return [...new Set([...local,...cloudArr])];
        });
        cloudSyncRef.current.pulledCart=true;
        cloudSyncRef.current.pulledWish=true;
      }catch(e){
        console.warn("[cloud-sync] pull failed:",e?.message||e);
        // 即使失败也允许后续推送(用户的本地改动应优先上传)
        cloudSyncRef.current.pulledCart=true;
        cloudSyncRef.current.pulledWish=true;
      }
    })();
    return()=>{alive=false;};
  },[loggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // 购物车 → 云端(debounce)
  useEffect(()=>{
    if(!loggedIn||!cloudSyncRef.current.pulledCart)return;
    if(!isOnline)return; // 离线不尝试,等回联后单独触发
    const t=setTimeout(()=>{
      putCloudCart(cart).catch(e=>console.warn("[cloud-sync] cart push failed:",e?.message));
    },800);
    return()=>clearTimeout(t);
  },[cart,loggedIn,isOnline]);

  // 网络恢复 → 立即把本地购物车 / 收藏推一次到云端
  const wasOnlineRef = useRef(isOnline);
  useEffect(()=>{
    if(isOnline&&!wasOnlineRef.current&&loggedIn){
      putCloudCart(cart).catch(()=>{});
      putCloudWishlist(wishlist).catch(()=>{});
    }
    wasOnlineRef.current=isOnline;
  },[isOnline,loggedIn,cart,wishlist]);

  // 收藏 → 云端(debounce)
  useEffect(()=>{
    if(!loggedIn||!cloudSyncRef.current.pulledWish)return;
    const t=setTimeout(()=>{
      putCloudWishlist(wishlist).catch(e=>console.warn("[cloud-sync] wishlist push failed:",e?.message));
    },800);
    return()=>clearTimeout(t);
  },[wishlist,loggedIn]);

  // ── 版本检查(强制 / 软提示)─────────────────────────────────────
  // force = 当前版本低于 min_supported,UI 完全阻塞,只能升级
  // soft  = 当前版本低于 latest,显示一次性可关闭的横幅
  const [updateState,setUpdateState]=useState({force:false,soft:false,info:null});
  const [softDismissed,setSoftDismissed]=useState(()=>{
    try{return !!sessionStorage.getItem("syj_soft_update_dismissed");}catch{return false;}
  });
  useEffect(()=>{
    let alive=true;
    getAppVersion()
      .then(info=>{
        if(!alive||!info)return;
        const force=compareVersions(APP_VERSION,info.min_supported)<0;
        const soft =!force && compareVersions(APP_VERSION,info.latest)<0;
        setUpdateState({force,soft,info});
      })
      .catch(e=>console.warn("[version] check failed:",e?.message));
    return()=>{alive=false;};
  },[]);
  const updateUrl=(()=>{
    const info=updateState.info||{};
    const ua=(typeof navigator!=="undefined"?navigator.userAgent:"")||"";
    if(/iPhone|iPad|iPod/i.test(ua)) return info.ios_url||info.web_url||"";
    if(/Android/i.test(ua))           return info.android_url||info.web_url||"";
    return info.web_url||info.ios_url||info.android_url||"";
  })();
  const dismissSoft=()=>{
    try{sessionStorage.setItem("syj_soft_update_dismissed","1");}catch{}
    setSoftDismissed(true);
  };

  // 当前 tab 页面 — 永远渲染,这样 ProductDetail / Checkout 关闭(或左滑返回)
  // 时下面已经现成的页面在 → 滑动手势不再露出空白
  const renderTabPage=()=>{
    switch(tab){
      case "home":       return<HomePage       cart={cart} setCart={setCart} wishlist={wishlist} setWishlist={setWishlist} setTab={setTab} loggedIn={loggedIn} onOpenProduct={setProductDetail} products={products} categories={categories} loading={dataLoading} error={dataError} onRetry={retryDataLoad}/>;
      case "categories": return<CategoriesPage cart={cart} setCart={setCart} wishlist={wishlist} setWishlist={setWishlist} setTab={setTab} loggedIn={loggedIn} onOpenProduct={setProductDetail} products={products} categories={categories}/>;
      case "cart":       return<CartPage       cart={cart} setCart={setCart} setCheckout={setCheckout} wishlist={wishlist} setWishlist={setWishlist} setTab={setTab} loggedIn={loggedIn} onOpenProduct={setProductDetail} products={products}/>;
      case "profile":    return<ProfilePage    onLoginChange={setLoggedIn} wishlist={wishlist} setWishlist={setWishlist} setCart={setCart} onOpenProduct={setProductDetail} products={products} setTab={setTab} initialPanel={profileInitialPanel} onInitialPanelConsumed={()=>setProfileInitialPanel(null)} chatPrefillProduct={chatPrefillProduct} onChatPrefillConsumed={()=>setChatPrefillProduct(null)}/>;
      default:           return null;
    }
  };
  return(
    <div style={{
      width:"100%",
      // 在 web 浏览器上限制 430(给桌面预览看像 iPhone),native iOS 全屏(safe area 内)
      maxWidth:"100%",
      margin:"0 auto",
      // 100dvh = 动态视口高度,自动处理 iOS 浏览器/native 的状态栏 + home 指示条
      height:"100dvh",
      // safe-area:仅顶部状态栏 + 左右(刘海屏)留白
      // 底部 home 指示条留白由 NavBar 自己处理,避免 NavBar 上方出现 tab 背景色条带
      paddingTop:"env(safe-area-inset-top)",
      paddingLeft:"env(safe-area-inset-left)",
      paddingRight:"env(safe-area-inset-right)",
      boxSizing:"border-box",
      overflow:"hidden",
      // 状态栏(safe-area-top)始终白色 — 这样 iOS 状态栏的黑色时间/电量永远清晰可见
      // 页面内的粉色 hero 各自处理,不再侵入 App 容器
      background:"#ffffff",
      fontFamily:FF,
      display:"flex",flexDirection:"column",position:"relative",
    }}>
      <div style={{flex:1,overflow:"hidden",position:"relative"}}>
        {/* 永远渲染 tab 页面 — 即便商品详情 / 结算页打开,这层也保持 mount,
            左滑返回时下面已经是 ready 的页面,无空白闪烁。
            isolation: isolate 创建独立堆叠上下文,防止 HomePage 内部的 z-index:50
            浮动 header 穿透到上层 ProductDetailPage */}
        <div style={{position:"absolute",inset:0,isolation:"isolate"}}>{renderTabPage()}</div>

        {/* 商品详情栈:整个 stack 全部 mount,所有页面都在 translateX(0) 中央,
            通过 zIndex 分层(栈顶 zIndex 最高,盖在下面页面之上)。
            ProductDetailPage 自己内部处理 swipe-back 手势 — 用户从左边缘右
            滑时,栈顶页面自身 translateX 向右移动,**下面的页面已经在中央**
            自然露出来 — 不会再看到白色背景或"从右边突然跳出"。
            pointerEvents:none 让下层不接收触摸事件,避免穿透到底层页面。 */}
        {productStack.map((p, idx) => {
          const isTop = idx === productStack.length - 1;
          return (
            <div
              key={`pd-${idx}-${p.id}`}
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 5 + idx,
                // ⚠ 不要设 background — 否则当顶页 swipe-back 向右移动时,wrapper
                //   自己的白底还会盖住下层页面,看到的就是白色而非上一商品。
                //   ProductDetailPage 内部 root 自己有 #f5f5f4 背景覆盖,无需 wrapper 的。
                background: "transparent",
                isolation: "isolate",
                pointerEvents: isTop ? 'auto' : 'none',
              }}
            >
              <ProductDetailPage
                product={p}
                onBack={popProductDetail}
                wishlist={wishlist}
                setWishlist={setWishlist}
                cart={cart}
                setCart={setCart}
                setTab={t=>{setProductDetail(null);setTab(t);}}
                setCheckout={setCheckout}
                setProductDetail={setProductDetail}
                loggedIn={loggedIn}
                products={products}
                setProfileInitialPanel={setProfileInitialPanel}
                setChatPrefillProduct={setChatPrefillProduct}
              />
            </div>
          );
        })}

        {/* 结算页:浮在最上层,完全覆盖 */}
        {checkout&&(
          <div style={{position:"absolute",inset:0,zIndex:10,background:"#fff"}}>
            <CheckoutPage cart={cart} setCart={setCart} setCheckout={setCheckout} setTab={setTab}/>
          </div>
        )}
      </div>
      {!checkout&&!productDetail&&!updateState.force&&<NavBar tab={tab} setTab={setTab} cartCount={cartCount} onScan={triggerScan}/>}

      {/* 扫一扫:不带 capture,iOS 弹出系统 Action Sheet:「拍照 / 照片图库 / 文件」
          这样两个选项都明显,一眼就能找到从相册上传 */}
      <input
        ref={scanInputRef}
        type="file"
        accept="image/*"
        onChange={e=>{
          const f=e.target.files?.[0];
          if(f)setScanFile(f);
        }}
        style={{position:"fixed",left:-99999,top:-99999,width:0,height:0,opacity:0,pointerEvents:"none"}}
      />

      {/* 选好图后再打开识别面板,直接进识别状态(不显示黑色 pick 页) */}
      {scanFile&&(
        <ImageSearchPanel
          initialFile={scanFile}
          onClose={()=>setScanFile(null)}
          onPickAgain={()=>{setScanFile(null);setTimeout(triggerScan,80);}}
          onOpenProduct={p=>{setScanFile(null);setProductDetail(p);}}
        />
      )}

      {/* 全局动画 keyframes(给所有页面飞入动画用) */}
      <style>{`
        @keyframes flyArcX { to { transform: translateX(var(--dx)); } }
        @keyframes flyArcY { to { transform: translateY(var(--dy)); } }
        @keyframes flyShrink {
          0% { transform: scale(1); opacity: 1; }
          60% { transform: scale(0.8); opacity: 1; }
          100% { transform: scale(0.2); opacity: 0; }
        }
        @keyframes slideDown { from { transform: translateY(-100%); } to { transform: translateY(0); } }
      `}</style>

      {/* 全局飞入购物车动画 — 所有页面共用 */}
      {globalFly&&(
        <div
          key={globalFly.key}
          style={{
            position:"fixed",
            left:globalFly.sx,top:globalFly.sy,
            width:30,height:30,marginLeft:-15,marginTop:-15,
            zIndex:10000,pointerEvents:"none",
            "--dx":`${globalFly.ex-globalFly.sx}px`,
            animation:"flyArcX 0.72s cubic-bezier(0.5, 0, 0.85, 0.4) forwards",
          }}
        >
          <div style={{
            width:"100%",height:"100%",
            "--dy":`${globalFly.ey-globalFly.sy}px`,
            animation:"flyArcY 0.72s cubic-bezier(0.18, 0.65, 0.4, 1) forwards",
          }}>
            <div style={{
              width:"100%",height:"100%",
              borderRadius:"50%",
              background:"linear-gradient(135deg,#ffb84d,#e03030)",
              boxShadow:"0 4px 14px rgba(224,48,48,0.45)",
              display:"flex",alignItems:"center",justifyContent:"center",
              color:"#fff",fontSize:18,fontWeight:700,fontFamily:FF,
              animation:"flyShrink 0.72s ease-in forwards",
            }}>+</div>
          </div>
        </div>
      )}

      {/* 离线提示条 */}
      {!isOnline&&(
        <div style={{position:"absolute",top:0,left:0,right:0,padding:"8px 14px",background:"#fee2e2",color:"#991b1b",fontFamily:FF,fontSize:12,display:"flex",alignItems:"center",gap:8,zIndex:9000,boxShadow:"0 1px 4px rgba(0,0,0,0.1)"}}>
          <span style={{fontSize:14}}>📡</span>
          <span style={{flex:1,lineHeight:1.5}}>当前离线 · 商品仍可浏览,加购物车将在恢复网络后同步</span>
        </div>
      )}
      {/* 回联提示(2.5s 自动消失) */}
      {showBackOnline&&isOnline&&(
        <div style={{position:"absolute",top:0,left:0,right:0,padding:"8px 14px",background:"#dcfce7",color:"#166534",fontFamily:FF,fontSize:12,display:"flex",alignItems:"center",gap:8,zIndex:9000,animation:"slideDown 0.3s"}}>
          <span style={{fontSize:14}}>✓</span>
          <span style={{flex:1,lineHeight:1.5}}>网络已恢复 · 数据正在同步</span>
        </div>
      )}

      {/* 软更新横幅(可关) */}
      {updateState.soft&&!updateState.force&&!softDismissed&&(
        <div style={{position:"absolute",top:0,left:0,right:0,padding:"10px 14px",background:"#1a1a1a",color:"#fff",fontFamily:FF,fontSize:12.5,display:"flex",alignItems:"center",gap:10,zIndex:9000,boxShadow:"0 2px 8px rgba(0,0,0,0.2)"}}>
          <span style={{flex:1,lineHeight:1.5}}>新版本 v{updateState.info?.latest} 已发布{updateState.info?.notice?` · ${updateState.info.notice}`:""}</span>
          {updateUrl&&<button onClick={()=>{window.location.href=updateUrl;}} style={{background:"#fff",color:"#1a1a1a",border:"none",borderRadius:5,padding:"5px 11px",fontSize:11.5,fontFamily:FF,fontWeight:600,cursor:"pointer"}}>更新</button>}
          <button onClick={dismissSoft} style={{background:"transparent",color:"#aaa",border:"none",fontSize:18,padding:0,width:24,cursor:"pointer",lineHeight:1}}>×</button>
        </div>
      )}

      {/* 强制更新遮罩 */}
      {updateState.force&&(
        <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.85)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
          <div style={{background:"#fff",borderRadius:16,padding:"30px 24px",textAlign:"center",maxWidth:320,width:"100%"}}>
            <div style={{fontSize:42,marginBottom:14}}>📲</div>
            <div style={{fontSize:17,fontFamily:SERIF,fontWeight:600,color:"#1a1a1a",marginBottom:10}}>需要升级</div>
            <div style={{fontSize:13,color:"#666",fontFamily:FF,lineHeight:1.7,marginBottom:6}}>
              当前版本 v{APP_VERSION} 已停止支持。
            </div>
            <div style={{fontSize:13,color:"#666",fontFamily:FF,lineHeight:1.7,marginBottom:20}}>
              请升级到 v{updateState.info?.latest||updateState.info?.min_supported}{updateState.info?.notice?`:${updateState.info.notice}`:""}
            </div>
            {updateUrl?(
              <button onClick={()=>{window.location.href=updateUrl;}} style={{...primaryBtn,width:"100%"}}>立即升级</button>
            ):(
              <div style={{fontSize:11.5,color:"#aaa",fontFamily:FF}}>请到 App Store / Google Play 搜索「十一家」更新</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
